import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import {
  LayoutDashboard, ShoppingCart, Package, Users, Wallet, Layers,
  Sun, Moon, Globe, LogOut, Plus, Search, Printer, Truck, DollarSign,
  X, CheckCircle2, Send, Trash2, Pencil, Filter, FileDown, ChevronRight, ChevronDown, ChevronLeft,
  AlertTriangle, TrendingUp, Boxes, Receipt, Settings, MessageCircle,
  Sparkles, Grid, Ruler, FileText, Factory, Upload, Bell, BellOff, CreditCard, ClipboardList, RefreshCw,
  Home, ChevronsLeft, ChevronsRight, Mail, Share2, Eye, EyeOff, Check, CalendarDays, Camera, FileSignature, Target, MapPin, Tag, Palette, AlertCircle, Download,
  Brain, Zap, Cpu, Info, Building2, History, Phone, Shield, BarChart2, Bot, Radio, Copy, Barcode, PlusCircle, ShieldAlert, ExternalLink, Loader2, Image as ImageIcon, Clock, RotateCcw, HardDrive, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { AuthCtx, UICtx, StockSettingsCtx } from "../appContexts";
import { fmt, fmtDate, fmtDT, ono, MONEY, convertPrice } from "../appUtils";
import { translations, CURRENCY_SYMBOL, STATUS_LABELS } from "../mock";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JsBarcode from "jsbarcode";
import * as XLSX from "xlsx";
import { jspm, useJspm, JSPM_STATUS_LABEL } from "../lib/jspm";
import { QUOTE_BRANDS, BrandSelectModal, defaultQuoteNotes, WHATSAPP_FIELDS } from "../quotes";
import { BUILTIN_THEMES, generateThemeCSS, getCustomThemes, persistCustomThemes, getStoredActiveThemeId, setStoredActiveThemeId, getStoredAnimationsEnabled, setStoredAnimationsEnabled, getStoredCursorId, setStoredCursorId, getStoredClockStyle, setStoredClockStyle } from "../themeEngine";
import { AppearanceSettings, ThemeBackgroundFX } from "../themeSettings";
import { MatrixPasteModal, MatrixRaiseModal, MatrixHistoryModal, MatrixTraceModal, NewMatrixModal, parseGrid, gridToTsv, copyToClipboard } from "../matrixTools";
import { PaymentSettings } from "../cardPayment";
import { DEFAULT_EMAIL_TEMPLATES, logoToZPLGF, loadLogoDataUrl, printViaIframe } from "../utils/pdfUtils";
import { PRICE_RULE_PRODUCT_TYPES, PRICE_RULE_FABRIC_GROUPS } from "../utils/quoteUtils";
import { isSoundEnabled, setSoundEnabled } from "../utils/soundUtils";
import { makeTr } from "../i18n";
import logo from "../assets/zen-logo.png";
import KartelaModule from "./KartelaPage";
import FormEditorPage from "./FormEditorPage";
import { ModuleIcon, PName, MODULE_GRADIENTS, ROLE_LABELS_TR } from "../components/layout/Sidebar";

function BotsHubSettings() {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("");    // "", "telegram", "whatsapp"
  const [logQuery, setLogQuery] = useState("");
  const [logLimit, setLogLimit] = useState(50);
  const [busy, setBusy] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const [testPhone, setTestPhone] = useState("");

  const buildLogUrl = () => {
    const params = new URLSearchParams();
    params.set("limit", String(logLimit));
    if (logFilter) params.set("platform", logFilter);
    if (logQuery && logQuery.trim()) params.set("q", logQuery.trim());
    return `/bots/logs?${params.toString()}`;
  };

  const loadAll = async () => {
    try {
      const [s, c, l] = await Promise.all([
        api.get("/bots/status"),
        api.get("/bots/config"),
        api.get(buildLogUrl()),
      ]);
      setStatus(s.data);
      setCfg(c.data);
      const ld = l.data; setLogs(Array.isArray(ld) ? ld : (ld?.logs || []));
    } catch (e) {
      toast.error("Bot durumu yüklenemedi");
    }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    api.get(buildLogUrl()).then(({ data }) => { setLogs(Array.isArray(data) ? data : (data?.logs || [])); }).catch(() => {});
  }, [logFilter, logLimit]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(buildLogUrl()).then(({ data }) => { setLogs(Array.isArray(data) ? data : (data?.logs || [])); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [logQuery]);

  const setBusyKey = (k, v) => setBusy((prev) => ({ ...prev, [k]: v }));

  const setupTgWebhook = async () => {
    setBusyKey("tgSetup", true);
    try {
      const { data } = await api.post("/bots/telegram/setup-webhook", {});
      if (data.ok) toast.success("Telegram webhook bu fork'a kuruldu");
      else toast.error("Kurulum başarısız");
      await loadAll();
    } catch (e) { toast.error("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setBusyKey("tgSetup", false); }
  };

  const setupWaWebhook = async () => {
    setBusyKey("waSetup", true);
    try {
      const { data } = await api.post("/bots/whatsapp/setup-webhook", {});
      if (data.ok) toast.success("WhatsApp (Green API) webhook bu fork'a kuruldu");
      else toast.error("Kurulum başarısız");
      await loadAll();
    } catch (e) { toast.error("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setBusyKey("waSetup", false); }
  };

  const sendTgTest = async () => {
    setBusyKey("tgTest", true);
    try {
      await api.post("/bots/telegram/send-test", {});
      toast.success("Telegram test mesajı gönderildi");
      await loadAll();
    } catch (e) { toast.error("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setBusyKey("tgTest", false); }
  };

  const sendWaTest = async () => {
    if (!testPhone) { toast.error("Telefon numarası girin (ör: 05321234567)"); return; }
    setBusyKey("waTest", true);
    try {
      await api.post("/bots/whatsapp/send-test", { phone: testPhone });
      toast.success("WhatsApp test mesajı gönderildi");
      await loadAll();
    } catch (e) { toast.error("Hata: " + (e.response?.data?.detail || e.message)); }
    finally { setBusyKey("waTest", false); }
  };

  const saveConfig = async () => {
    const changed = Object.keys(form).filter((k) => (form[k] || "").trim().length > 0);
    if (!changed.length) { toast.error("Değişiklik yok"); return; }
    setSavingCfg(true);
    try {
      const payload = {};
      changed.forEach((k) => { payload[k] = form[k].trim(); });
      const { data } = await api.put("/bots/config", payload);
      toast.success(`Güncellendi: ${(data.updated || []).join(", ")}`);
      setForm({});
      await loadAll();
    } catch (e) { toast.error("Kayıt hatası: " + (e.response?.data?.detail || e.message)); }
    finally { setSavingCfg(false); }
  };

  const StatusPill = ({ live, err }) => (
    <span
      data-testid="bot-status-pill"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
        live
          ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30"
          : "bg-rose-500/15 text-rose-500 border border-rose-500/30"
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`} />
      {live ? "CANLI" : (err ? "HATA" : "PASİF")}
    </span>
  );

  const tg = status?.telegram || {};
  const wa = status?.whatsapp || {};

  const copyText = (t) => {
    navigator.clipboard?.writeText(t).then(() => toast.success("Kopyalandı"));
  };

  return (
    <div className="space-y-5" data-testid="bots-hub">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">Bot & Haberleşme Merkezi</h3>
        </div>
        <button onClick={loadAll} className="zen-btn-ghost !text-xs" data-testid="bots-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Yenile
        </button>
      </div>
      <p className="text-xs text-slate-500 -mt-3">
        Telegram ve WhatsApp botlarının canlı durumu, secrets yönetimi, tek-tık webhook kurulumu ve son mesaj logları.
        Ayarlar şifreli olarak <code className="text-[10px]">bot_config</code> koleksiyonunda saklanır ve fork'lar arası kaybolmaz.
      </p>

      {/* Durum kartları */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Telegram Kartı */}
        <div className="zen-panel p-4 space-y-3" data-testid="tg-status-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-sky-500" />
              <div>
                <div className="font-bold text-sm">Telegram Bot</div>
                <div className="text-[10px] text-slate-500">
                  {tg.bot?.username ? `@${tg.bot.username}` : "—"}
                </div>
              </div>
            </div>
            <StatusPill live={tg.live} err={tg.error || tg.lastError} />
          </div>

          <div className="text-[11px] space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">Webhook:</span>
              <span className={`font-mono text-[10px] truncate max-w-[220px] ${tg.webhookMatch ? "text-emerald-500" : "text-rose-500"}`} title={tg.webhookUrl}>
                {tg.webhookUrl || "—"}
              </span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">Beklenen:</span>
              <span className="font-mono text-[10px] truncate max-w-[220px]" title={tg.expectedWebhookUrl}>{tg.expectedWebhookUrl || "—"}</span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">Owner chat_id:</span>
              <span className="font-mono">{tg.ownerChatId || "—"}</span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">Bekleyen güncelleme:</span>
              <span>{tg.pendingUpdates ?? 0}</span>
            </div>
            {tg.lastError && (
              <div className="text-rose-500 text-[10px] p-2 rounded bg-rose-500/10 border border-rose-500/20">
                Son hata: {tg.lastError}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={setupTgWebhook}
              disabled={busy.tgSetup}
              className="zen-btn-primary !text-xs"
              data-testid="tg-setup-webhook-btn"
            >
              <Radio className="w-3.5 h-3.5" />
              {busy.tgSetup ? "Kuruluyor..." : "Telegram Webhook'unu Kur"}
            </button>
            <button
              onClick={sendTgTest}
              disabled={busy.tgTest}
              className="zen-btn-ghost !text-xs"
              data-testid="tg-test-btn"
            >
              <Send className="w-3.5 h-3.5" />
              {busy.tgTest ? "Gönderiliyor..." : "Test Mesajı Gönder"}
            </button>
          </div>
        </div>

        {/* WhatsApp Kartı */}
        <div className="zen-panel p-4 space-y-3" data-testid="wa-status-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-500" />
              <div>
                <div className="font-bold text-sm">WhatsApp (Green API)</div>
                <div className="text-[10px] text-slate-500">Instance {wa.idInstance || "—"}</div>
              </div>
            </div>
            <StatusPill live={wa.live} err={wa.error} />
          </div>

          <div className="text-[11px] space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">State:</span>
              <span className={wa.authorized ? "text-emerald-500 font-bold" : "text-rose-500"}>
                {wa.state || "—"}
              </span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">Webhook:</span>
              <span className={`font-mono text-[10px] truncate max-w-[220px] ${wa.webhookMatch ? "text-emerald-500" : "text-rose-500"}`} title={wa.webhookUrl}>
                {wa.webhookUrl || "—"}
              </span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">Beklenen:</span>
              <span className="font-mono text-[10px] truncate max-w-[220px]" title={wa.expectedWebhookUrl}>{wa.expectedWebhookUrl || "—"}</span>
            </div>
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500">Incoming:</span>
              <span>{wa.incomingWebhook || "—"}</span>
            </div>
            {wa.error && (
              <div className="text-rose-500 text-[10px] p-2 rounded bg-rose-500/10 border border-rose-500/20">
                Hata: {wa.error}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={setupWaWebhook}
              disabled={busy.waSetup}
              className="zen-btn-primary !text-xs"
              data-testid="wa-setup-webhook-btn"
            >
              <Radio className="w-3.5 h-3.5" />
              {busy.waSetup ? "Kuruluyor..." : "WhatsApp Webhook'unu Kur"}
            </button>
            <input
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="05321234567"
              className="px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs w-32"
              data-testid="wa-test-phone-input"
            />
            <button
              onClick={sendWaTest}
              disabled={busy.waTest}
              className="zen-btn-ghost !text-xs"
              data-testid="wa-test-btn"
            >
              <Send className="w-3.5 h-3.5" />
              {busy.waTest ? "..." : "Test Gönder"}
            </button>
            </div>
        </div>
      </div>

      {/* Secrets Yönetimi */}
      <div className="zen-panel p-4 space-y-3" data-testid="bots-secrets-panel">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-500" />
          <h4 className="font-bold text-sm">Ayarlar & Anahtarlar</h4>
        </div>
        <p className="text-[11px] text-slate-500">
          Değerler <b>Fernet</b> ile şifrelenip <code className="text-[10px]">bot_config</code> koleksiyonunda saklanır.
          Boş bırakılan alanlar dokunulmaz. Kaydettikten sonra otomatik olarak aktif hale gelir (yeniden başlatma gerekmez).
        </p>

        {cfg && (
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { k: "TELEGRAM_BOT_TOKEN",       l: "Telegram Bot Token",         ph: "BotFather'dan alınan token" },
              { k: "TELEGRAM_ALLOWED_CHAT_ID", l: "Telegram Owner Chat ID",     ph: "Sahibin sayısal chat_id'si" },
              { k: "GREEN_API_ID_INSTANCE",    l: "Green API Instance ID",      ph: "710xxxxxxxx" },
              { k: "GREEN_API_TOKEN",          l: "Green API Token",            ph: "Green API konsolundan" },
              { k: "GREEN_API_URL",            l: "Green API Base URL",         ph: "https://api.green-api.com" },
            ].map(({ k, l, ph }) => (
              <div key={k} className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">{l}</label>
                <input
                  type="password"
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  placeholder={cfg[k]?.hasValue ? `Mevcut: ${cfg[k]?.masked}` : ph}
                  className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono"
                  data-testid={`bot-secret-${k.toLowerCase()}`}
                />
              </div>
            ))}
            {/* ── Telegram Satış Ekibi Chat ID ── */}
            <div className="md:col-span-2 space-y-1">
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Satış Ekibi Telegram Chat ID (Opsiyonel)</label>
              <input
                type="password"
                value={form["TELEGRAM_SALES_CHAT_ID"] || ""}
                onChange={(e) => setForm({ ...form, TELEGRAM_SALES_CHAT_ID: e.target.value })}
                placeholder={cfg["TELEGRAM_SALES_CHAT_ID"]?.hasValue ? `Mevcut: ${cfg["TELEGRAM_SALES_CHAT_ID"]?.masked}` : "Bayi bildirimleri için ayrı grup/kullanıcı chat ID'si"}
                className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono"
                data-testid="bot-secret-telegram-sales-chat-id"
              />
              <p className="text-[10px] text-slate-400">Bayi sipariş/teklif oluşturduğunda hem admin hem bu chat'e bildirim gider.</p>
            </div>

            {/* ── Webhook URLs ── */}
            {[
              { k: "TELEGRAM_WEBHOOK_URL", l: "Telegram Webhook URL", ph: "https://sunucunuz.com/api/telegram/webhook" },
              { k: "WHATSAPP_WEBHOOK_URL", l: "Green API Webhook URL", ph: "https://sunucunuz.com/api/webhooks/whatsapp" },
            ].map(({ k, l, ph }) => (
              <div key={k} className="space-y-1 md:col-span-2">
                <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">{l}</label>
                <input type="url" value={form[k] ?? cfg[k]?.value ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  placeholder={ph} className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono"
                  data-testid={`bot-webhook-${k.toLowerCase()}`} />
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={() => { setForm({}); toast.info("Değişiklikler iptal edildi"); }}
            className="zen-btn-ghost !text-xs"
          >
            Vazgeç
          </button>
          <button
            onClick={saveConfig}
            disabled={savingCfg}
            className="zen-btn-primary !text-xs"
            data-testid="bot-secrets-save"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {savingCfg ? "Kaydediliyor..." : "Şifreli Kaydet"}
          </button>
        </div>
      </div>

      {/* Loglar */}
      <div className="zen-panel p-4 space-y-3" data-testid="bots-logs-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-sky-500" />
            <h4 className="font-bold text-sm">Mesaj & Cevap Geçmişi</h4>
            <span className="text-[10px] text-slate-500">({logs.length} kayıt)</span>
          </div>
          <div className="flex items-center gap-1">
            {[
              { v: "", l: "Tümü" },
              { v: "telegram", l: "Telegram" },
              { v: "whatsapp", l: "WhatsApp" },
            ].map(({ v, l }) => (
              <button
                key={v || "all"}
                onClick={() => setLogFilter(v)}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold ${
                  logFilter === v
                    ? "bg-[#E5322A] text-white"
                    : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
                data-testid={`bots-log-filter-${v || "all"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Arama + limit */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={logQuery}
              onChange={(e) => setLogQuery(e.target.value)}
              placeholder="Metin veya numarada ara (ör: teklif, 05321234567)…"
              className="w-full pl-8 pr-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs"
              data-testid="bots-log-search"
            />
          </div>
          <select
            value={logLimit}
            onChange={(e) => setLogLimit(Number(e.target.value))}
            className="px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs"
            data-testid="bots-log-limit"
          >
            <option value={10}>Son 10</option>
            <option value={20}>Son 20</option>
            <option value={50}>Son 50</option>
          </select>
          {logQuery && (
            <button
              onClick={() => setLogQuery("")}
              className="zen-btn-ghost !text-[11px]"
              data-testid="bots-log-search-clear"
            >
              <X className="w-3 h-3" /> Temizle
            </button>
          )}
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-500">
            {logQuery ? "Aramanızla eşleşen kayıt bulunamadı." : "Henüz log kaydı yok."}
          </div>
        ) : (
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {logs.map((log) => {
              const isIn = log.direction === "in";
              const platformColor = log.platform === "telegram" ? "sky" : "emerald";
              return (
                <div
                  key={log.id}
                  className={`p-2.5 rounded border ${
                    isIn
                      ? `border-${platformColor}-500/30 bg-${platformColor}-500/5`
                      : "border-slate-300/50 dark:border-slate-700/50 bg-slate-100/50 dark:bg-slate-800/30"
                  }`}
                  data-testid="bot-log-row"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      log.platform === "telegram"
                        ? "bg-sky-500/20 text-sky-600"
                        : "bg-emerald-500/20 text-emerald-600"
                    }`}>
                      {log.platform.toUpperCase()}
                    </span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      isIn ? "bg-blue-500/20 text-blue-600" : "bg-slate-500/20 text-slate-600"
                    }`}>
                      {isIn ? "← GELEN" : "→ GİDEN"}
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">{log.chat}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">
                      {new Date(log.at).toLocaleString("tr-TR")}
                    </span>
                  </div>
                  <div className="text-xs whitespace-pre-wrap break-words">{log.text}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function DbBackupSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadFullBackup = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("zen_token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/admin/backup/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zen_erp_yedek_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Yedek dosyası indirildi!");
    } catch (e) {
      toast.error("Yedek indirilemedi: " + e.message);
    } finally {
      setDownloading(false);
    }
  };

  const load = async () => {
    try { setCfg((await api.get("/db-backup/config")).data); }
    catch { toast.error("Yedek ayarları yüklenemedi"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/db-backup/config", cfg); toast.success("Yedek ayarları kaydedildi"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Kayıt hatası"); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!window.confirm("Şimdi manuel yedekleme başlatılsın mı? Tüm koleksiyonlar ZIP olarak hazırlanıp gönderilecek.")) return;
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/db-backup/run");
      setResult(data);
      if (data.ok) toast.success(`Yedekleme tamamlandı: ${data.filename} (${data.size_kb} KB)`);
      else toast.warning("Yedekleme kısmen tamamlandı");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Yedekleme başarısız"); }
    finally { setRunning(false); }
  };

  if (!cfg) return <div className="zen-panel p-8 text-center text-sm text-slate-500">Yükleniyor...</div>;

  const DAYS = [
    { v: 1, l: "Pazartesi" }, { v: 2, l: "Salı" }, { v: 3, l: "Çarşamba" },
    { v: 4, l: "Perşembe" }, { v: 5, l: "Cuma" }, { v: 6, l: "Cumartesi" }, { v: 7, l: "Pazar" },
  ];
  const CHANNELS = [
    { v: "whatsapp", l: "WhatsApp", desc: "Bot ayarlarındaki numara" },
    { v: "email",    l: "E-posta",  desc: "Bağlı e-posta hesabı" },
    { v: "telegram", l: "Telegram", desc: "Bot owner chat_id" },
  ];
  const toggleChannel = (ch) => {
    const curr = cfg.channels || [];
    setCfg({ ...cfg, channels: curr.includes(ch) ? curr.filter(c => c !== ch) : [...curr, ch] });
  };

  return (
    <div className="space-y-5" data-testid="db-backup-panel">
      {/* ── Tam Yedek İndir ── */}
      <div className="zen-panel p-4 border-2 border-dashed border-[#E5322A]/40 bg-[#E5322A]/5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <HardDrive className="w-8 h-8 text-[#E5322A] mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-extrabold text-sm">Tam Program Yedeği İndir</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Tüm kaynak kod + canlı veritabanı verisi + kurulum dosyaları (Docker, .env)<br/>
              Her şey kaybolsa bile bu dosyadan sıfırdan kurulum yapılabilir.
            </div>
          </div>
        </div>
        <button
          onClick={downloadFullBackup}
          disabled={downloading}
          className="zen-btn-primary !text-sm !px-5 !py-2.5 flex items-center gap-2 flex-shrink-0"
          data-testid="full-backup-download-btn"
        >
          {downloading
            ? <><Loader2 className="w-4 h-4 animate-spin" /> İndiriliyor...</>
            : <><HardDrive className="w-4 h-4" /> Yedeği İndir</>}
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Download className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">Otomatik Veritabanı Yedeği</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="zen-btn-ghost !text-xs" data-testid="db-backup-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
          <button onClick={runNow} disabled={running} className="zen-btn-primary !text-xs" data-testid="db-backup-run-now">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {running ? "Yedekleniyor..." : "Şimdi Yedekle"}
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-3">
        Tüm koleksiyonlar (siparişler, müşteriler, teklifler, matrisler...) JSON formatında ZIP'e sıkıştırılıp WhatsApp/e-posta/Telegram ile gönderilir.
        Şifreli veriler (e-posta şifresi, bot token) yedeklere <b>dahil edilmez</b>.
      </p>

      {result && (
        <div className={`zen-panel p-3 text-xs space-y-1 ${result.ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="db-backup-result">
          <div className="font-bold">{result.ok ? "Yedekleme tamamlandı" : "Kısmen tamamlandı"}: <span className="font-mono">{result.filename}</span></div>
          {Object.entries(result.results || {}).map(([ch, r]) => (
            <div key={ch} className={r === "ok" ? "text-emerald-600" : "text-rose-500"}>
              {ch}: {r === "ok" ? "Gönderildi" : r}
            </div>
          ))}
        </div>
      )}

      <div className="zen-panel p-4 space-y-4">
        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-bold text-sm">Haftalık Otomatik Yedekleme</div>
            <div className="text-[11px] text-slate-500">Belirlenen gün ve saatte otomatik olarak çalışır</div>
          </div>
          <button
            onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.enabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
            data-testid="db-backup-enabled-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${cfg.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        {/* Zamanlama */}
        <div className="grid md:grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Gün</span>
            <select value={cfg.day_of_week || 2} onChange={(e) => setCfg({ ...cfg, day_of_week: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" data-testid="db-backup-day-select">
              {DAYS.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Saat (Türkiye saati)</span>
            <select value={cfg.hour ?? 8} onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" data-testid="db-backup-hour-select">
              {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>)}
            </select>
          </label>
        </div>

        {/* Kanallar */}
        <div className="space-y-2">
          <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Gönderim Kanalları</label>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map(({ v, l, desc }) => {
              const active = (cfg.channels || []).includes(v);
              return (
                <button key={v} onClick={() => toggleChannel(v)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${active ? "border-[#E5322A]/40 bg-[#E5322A]/10 text-[#E5322A]" : "border-slate-300 dark:border-slate-700 text-slate-500 hover:border-slate-400"}`}
                  data-testid={`db-backup-channel-${v}`}>
                  <span className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0 ${active ? "border-[#E5322A] bg-[#E5322A]" : "border-slate-400"}`}>
                    {active && <Check className="w-2.5 h-2.5 text-white" />}
                  </span>
                  <div className="text-left">
                    <div>{l}</div>
                    <div className="text-[10px] text-slate-400 font-normal">{desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Numara/adres override'lar */}
        {(cfg.channels || []).includes("whatsapp") && (
          <label className="space-y-1 block">
            <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
              WhatsApp Numarası <span className="font-normal text-slate-400">(boş = Bot ayarlarındaki sahip numarası)</span>
            </span>
            <input type="tel" value={cfg.wa_phone || ""} onChange={(e) => setCfg({ ...cfg, wa_phone: e.target.value })}
              placeholder="905321234567" className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono"
              data-testid="db-backup-wa-phone" />
          </label>
        )}
        {(cfg.channels || []).includes("email") && (
          <label className="space-y-1 block">
            <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
              E-posta Alıcısı <span className="font-normal text-slate-400">(boş = bağlı e-posta hesabınıza)</span>
            </span>
            <input type="email" value={cfg.email_to || ""} onChange={(e) => setCfg({ ...cfg, email_to: e.target.value })}
              placeholder="admin@firma.com" className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono"
              data-testid="db-backup-email-to" />
          </label>
        )}
      </div>

      {/* Son yedek durumu */}
      {cfg.last_run_at && (
        <div className={`zen-panel p-3 text-xs ${cfg.last_run_status === "ok" ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="db-backup-last-run">
          <div className="flex items-center gap-2 flex-wrap">
            <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <span className="text-slate-500">Son yedek:</span>
            <span className="font-semibold">{new Date(cfg.last_run_at).toLocaleString("tr-TR")}</span>
            <span className={`ml-auto font-bold ${cfg.last_run_status === "ok" ? "text-emerald-600" : cfg.last_run_status === "partial" ? "text-amber-600" : "text-rose-500"}`}>
              {cfg.last_run_status === "ok" ? "Başarılı" : cfg.last_run_status === "partial" ? "Kısmen" : "Hata"}
            </span>
          </div>
          {cfg.last_filename && <div className="mt-1 text-slate-400 font-mono text-[10px]">{cfg.last_filename}</div>}
          {cfg.last_run_error && cfg.last_run_status !== "ok" && (
            <div className="mt-1 text-rose-500 text-[10px] font-mono whitespace-pre-wrap">{cfg.last_run_error}</div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="zen-btn-primary !text-xs" data-testid="db-backup-save">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </button>
      </div>
    </div>
  );
}

function ExternalConnectionsSettings() {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const FIELDS = [
    ["telegram_api_base", "Telegram API Temel Adresi", "https://api.telegram.org"],
    ["tcmb_rates_url", "TCMB Kur Servisi", "https://www.tcmb.gov.tr/kurlar/today.xml"],
    ["iyzico_api_base", "iyzico Ödeme Servisi", "https://api.iyzipay.com"],
    ["serper_images_url", "Görsel Arama Servisi", "https://google.serper.dev/images"],
    ["expo_push_url", "Mobil Bildirim Servisi", "https://exp.host/--/api/v2/push/send"],
    ["ip_geolocation_url", "IP Konum Servisi", "http://ip-api.com/json/{ip}"],
    ["reverse_geocode_url", "Adres Çözümleme Servisi", "https://nominatim.openstreetmap.org/reverse"],
  ];
  const load = async () => {
    try { const { data } = await api.get("/connection-settings"); setValues(data.values || {}); }
    catch { toast.error("Bağlantı ayarları yüklenemedi"); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/connection-settings", { values }); toast.success(`${data.updated.length} bağlantı güncellendi`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Bağlantı ayarları kaydedilemedi"); }
    finally { setSaving(false); }
  };
  return <div className="space-y-5" data-testid="external-connections-panel">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2"><Globe className="w-5 h-5 text-[#E5322A]" /><h3 className="text-lg font-extrabold">Dış Bağlantılar</h3></div>
      <button onClick={load} className="zen-btn-ghost !text-xs" data-testid="external-connections-refresh"><RefreshCw className="w-3.5 h-3.5" /> Yenile</button>
    </div>
    <p className="text-xs text-slate-500 -mt-3">Buradaki adresler kayıt anında aktif olur ve veritabanında saklandığı için güncelleme ile kaybolmaz.</p>
    <div className="zen-panel p-4 grid md:grid-cols-2 gap-4">
      {FIELDS.map(([key, label, placeholder]) => <label key={key} className="space-y-1 block">
        <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">{label}</span>
        <input type="url" value={values[key] || ""} placeholder={placeholder} onChange={(e) => setValues(v => ({ ...v, [key]: e.target.value }))}
          className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" data-testid={`external-url-${key}`} />
      </label>)}
    </div>
    <div className="flex justify-end"><button onClick={save} disabled={saving} className="zen-btn-primary !text-xs" data-testid="external-connections-save"><CheckCircle2 className="w-3.5 h-3.5" />{saving ? "Kaydediliyor..." : "Kaydet ve Uygula"}</button></div>
  </div>;
}

function BayilojiEntegrasyonSettings() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [skipped, setSkipped] = useState(null);
  const [loadingSkipped, setLoadingSkipped] = useState(false);
  const [promotingSkipped, setPromotingSkipped] = useState(false);

  const loadStatus = () =>
    api.get("/bayiloji/promote-status").then(r => setStatus(r.data)).catch(() => {});

  useEffect(() => { loadStatus(); }, []);

  const run = async () => {
    if (!window.confirm("Bayiloji geçmiş siparişleri ve ödemeler ZEN ERP'ye aktarılacak. Devam edilsin mi?")) return;
    setRunning(true);
    try {
      const r = await api.post("/bayiloji/promote");
      await loadStatus();
      toast.success(`Aktarım tamamlandı: ${r.data.counts?.orders || 0} sipariş, ${r.data.counts?.payments || 0} ödeme ZEN ERP'ye eklendi.`);
    } catch (e) {
      toast.error("Aktarım başarısız: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const loadSkipped = async () => {
    setLoadingSkipped(true);
    try {
      const r = await api.get("/bayiloji/skipped-analysis");
      setSkipped(r.data);
    } catch (e) {
      toast.error("Analiz başarısız: " + e.message);
    } finally {
      setLoadingSkipped(false);
    }
  };

  const runSkipped = async () => {
    if (!window.confirm(`${skipped?.customerCount || 0} arşiv cari için minimal kart oluşturulacak ve kayıtları aktarılacak. Devam?`)) return;
    setPromotingSkipped(true);
    try {
      const r = await api.post("/bayiloji/promote-skipped");
      setSkipped(null);
      await loadStatus();
      toast.success(`${r.data.createdCustomers} yeni cari oluşturuldu, ${r.data.promoted?.counts?.orders || 0} sipariş + ${r.data.promoted?.counts?.payments || 0} ödeme eklendi.`);
    } catch (e) {
      toast.error("İşlem başarısız: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPromotingSkipped(false);
    }
  };

  const isDone = status?.done;
  return (
    <div className="zen-panel space-y-4" data-testid="bayiloji-entegrasyon-panel">
      <div className="flex items-center gap-2">
        <History className="w-5 h-5 text-amber-400" />
        <h3 className="text-lg font-extrabold">Bayiloji Veri Entegrasyonu</h3>
        {isDone && <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold">Tamamlandı</span>}
      </div>
      <p className="text-sm text-slate-400">
        Bayiloji'den aktarılan geçmiş sipariş ve ödemeleri ZEN ERP'nin ana koleksiyonlarına taşır.
        Bu işlemin ardından eski siparişler <strong>Satış Siparişleri</strong> listesinde, ödemeler
        ise <strong>cari ekstre</strong> içinde görünür. <br />
        <span className="text-amber-400">Bakiyeler değişmez</span> — aktarım yalnızca görünürlük içindir.
      </p>
      {status && (
        <div className="rounded-lg bg-white/5 p-3 text-sm space-y-1">
          <div className="text-slate-400">Kaynak kayıtlar:</div>
          <div className="flex gap-4 flex-wrap">
            {Object.entries(status.sourceCounts || {}).map(([k, v]) => (
              <span key={k} className="text-slate-200"><span className="font-bold text-white">{v}</span> {k}</span>
            ))}
          </div>
          {isDone && status.counts && (
            <div className="mt-2 pt-2 border-t border-white/10">
              <div className="text-emerald-400 font-semibold">ZEN ERP'ye aktarılan:</div>
              <div className="flex gap-4 flex-wrap text-slate-200">
                <span><span className="font-bold text-white">{status.counts.orders || 0}</span> sipariş</span>
                <span><span className="font-bold text-white">{status.counts.payments || 0}</span> ödeme</span>
                {status.counts.skipped > 0 && (
                  <button
                    onClick={loadSkipped}
                    className="text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors"
                    data-testid="skipped-analysis-btn"
                  >
                    {status.counts.skipped} atlandı — detay gör
                  </button>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">Tamamlanma: {status.completedAt ? new Date(status.completedAt).toLocaleString("tr-TR") : "—"}</div>
            </div>
          )}
        </div>
      )}

      {/* ── Atlanmış kayıt analizi ── */}
      {loadingSkipped && <div className="text-sm text-slate-400 animate-pulse">Analiz ediliyor...</div>}
      {skipped && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="skipped-analysis-panel">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-bold text-amber-300">
              {skipped.customerCount} Arşiv Cari — {skipped.total} Kayıt
            </span>
          </div>
          <p className="text-xs text-slate-400">
            Bu müşteriler Bayiloji'de <strong>arşivlenmiş</strong> olduğu için ZEN ERP'ye cari kaydı açılmamıştı.
            Aşağıda "Arşiv Olarak Ekle" butonuyla bu carileri <em>pasif</em> statüde ZEN ERP'ye ekleyebilir,
            geçmiş kayıtlarını görünür hale getirebilirsin.
          </p>
          <div className="overflow-x-auto rounded border border-white/10">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-slate-400">
                  <th className="text-left px-2 py-1.5">Cari Adı</th>
                  <th className="text-center px-2 py-1.5">Sipariş</th>
                  <th className="text-center px-2 py-1.5">Ödeme</th>
                  <th className="text-right px-2 py-1.5">EUR</th>
                  <th className="text-right px-2 py-1.5">TL</th>
                </tr>
              </thead>
              <tbody>
                {skipped.customers.map((c, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-2 py-1.5 font-medium text-slate-200">{c.name}</td>
                    <td className="text-center px-2 py-1.5 text-slate-300">{c.orders}</td>
                    <td className="text-center px-2 py-1.5 text-slate-300">{c.payments}</td>
                    <td className="text-right px-2 py-1.5 text-slate-300">{c.totalEur > 0 ? `€${c.totalEur.toLocaleString("tr-TR", {minimumFractionDigits:2})}` : "—"}</td>
                    <td className="text-right px-2 py-1.5 text-slate-300">{c.totalTry > 0 ? `₺${c.totalTry.toLocaleString("tr-TR", {minimumFractionDigits:2})}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            onClick={runSkipped}
            disabled={promotingSkipped}
            className="zen-btn-secondary !text-xs w-full"
            data-testid="promote-skipped-btn"
          >
            {promotingSkipped ? "İşleniyor..." : `${skipped.customerCount} Arşiv Cariyi Pasif Olarak Ekle ve Kayıtları Aktar`}
          </button>
        </div>
      )}

      {!isDone ? (
        <button
          onClick={run}
          disabled={running}
          className="zen-btn-primary !text-sm"
          data-testid="bayiloji-promote-btn"
        >
          {running ? "Aktarılıyor..." : "Geçmiş Kayıtları ZEN ERP'ye Aktar"}
        </button>
      ) : (
        <div className="text-sm text-emerald-400 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Entegrasyon tamamlandı — tüm eşleşen kayıtlar ZEN ERP'de görünüyor.
        </div>
      )}
    </div>
  );
}

function SystemUpdateSettings() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [rollingBack, setRollingBack] = useState("");

  const loadStatus = async () => {
    try { setStatus((await api.get("/system-update/status")).data); }
    catch { setStatus({ state: "unavailable", message: "Güncelleme aracı erişilemiyor" }); }
  };
  const loadHistory = async () => {
    setLoadingHistory(true);
    try { const { data } = await api.get("/system-update/history"); setHistory(Array.isArray(data) ? data : []); }
    catch { setHistory([]); }
    finally { setLoadingHistory(false); }
  };

  useEffect(() => {
    loadStatus(); loadHistory();
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
  }, []);

  const submit = async () => {
    if (!file) return toast.error("Önce ZIP dosyasını seçin");
    if (!window.confirm("Kod güncellemesi başlatılsın mı? Veritabanına dokunulmaz; hata olursa önceki kod geri yüklenir.")) return;
    setBusy(true);
    const form = new FormData(); form.append("file", file);
    try {
      await api.post("/system-update/apply", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Güncelleme paketi sıraya alındı");
      await loadStatus();
      setTimeout(loadHistory, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || "Güncelleme başlatılamadı"); }
    finally { setBusy(false); }
  };

  const rollback = async (backupId, label) => {
    if (!window.confirm(`"${label}" sürümüne geri dönülsün mü?\n\nMevcut kod önce yedeklenecek, ardından seçilen sürüm yeniden derlenecek.`)) return;
    setRollingBack(backupId);
    try {
      await api.post("/system-update/rollback", { backup_id: backupId });
      toast.success("Geri dönüş başlatıldı — durum kısmından takip edebilirsiniz");
      await loadStatus();
      setTimeout(loadHistory, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || "Geri dönüş başlatılamadı"); }
    finally { setRollingBack(""); }
  };

  const state = status?.state || "loading";
  const isBusy = ["validating", "backing_up", "deploying", "rolling_back", "queued"].includes(state);
  const stateColor = { success: "text-emerald-600", rollback_success: "text-emerald-600", rolled_back: "text-amber-600", rollback_failed: "text-rose-600", unavailable: "text-slate-500" }[state] || (isBusy ? "text-sky-600" : "text-slate-600");

  const UPDATE_STEPS = [
    { keys: ["queued"], label: "Paket Alındı" },
    { keys: ["validating"], label: "Doğrulama" },
    { keys: ["backing_up", "rolling_back"], label: "Yedekleme" },
    { keys: ["deploying"], label: "Derleme" },
    { keys: ["success", "rollback_success"], label: "Tamamlandı" },
  ];
  const isFailed = ["rolled_back", "rollback_failed"].includes(state);
  const activeStepIdx = UPDATE_STEPS.findIndex(s => s.keys.includes(state));
  const showSteps = isBusy || isFailed || state === "success" || state === "rollback_success";
  const failedStepIdx = isFailed ? 3 : -1; // hata her zaman derleme aşamasında/sonrasında oluşur

  const fmtBackupDate = (at) => {
    try {
      if (at && at.includes("T")) return new Date(at).toLocaleString("tr-TR");
      const m = at && at.match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/);
      if (m) return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}:${m[6]}`;
    } catch {}
    return at;
  };
  const typeLabel = (t) => ({ update_backup: "Güncelleme Yedeği", update_success: "Başarılı Güncelleme", pre_rollback: "Geri Dönüş Öncesi", unknown: "Yedek" }[t] || t || "Yedek");

  return (
    <div className="space-y-5" data-testid="system-update-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">ZIP ile Sistem Güncelleme</h3>
        </div>
        <button onClick={() => { loadStatus(); loadHistory(); }} className="zen-btn-ghost !text-xs" data-testid="system-update-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Yenile
        </button>
      </div>

      {/* Durum + Yükleme */}
      <div className="zen-panel p-5 space-y-4">
        <div className={`flex items-center gap-2 text-xs font-bold ${stateColor}`} data-testid="system-update-status">
          {isBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Durum: {status?.message || "Kontrol ediliyor"}
        </div>

        {showSteps && (
          <div className="flex items-center gap-1.5 py-1" data-testid="system-update-steps">
            {UPDATE_STEPS.map((step, idx) => {
              const failed = isFailed && idx === failedStepIdx;
              const done = !isFailed && (state === "success" || state === "rollback_success" || idx < activeStepIdx);
              const active = !isFailed && idx === activeStepIdx && isBusy;
              return (
                <React.Fragment key={step.label}>
                  <div className="flex flex-col items-center gap-1 min-w-[52px]" data-testid={`system-update-step-${idx}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-colors
                      ${failed ? "border-rose-500 bg-rose-500/15 text-rose-600" :
                        done ? "border-emerald-500 bg-emerald-500/15 text-emerald-600" :
                        active ? "border-sky-500 bg-sky-500/15 text-sky-600" :
                        "border-slate-300 dark:border-slate-600 text-slate-400"}`}>
                      {failed ? "✕" : done ? "✓" : active ? <Loader2 className="w-3 h-3 animate-spin" /> : idx + 1}
                    </div>
                    <span className={`text-[9px] text-center leading-tight font-semibold ${failed ? "text-rose-600" : done ? "text-emerald-600" : active ? "text-sky-600" : "text-slate-400"}`}>{step.label}</span>
                  </div>
                  {idx < UPDATE_STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mb-4 rounded ${failed ? "bg-rose-300" : done || (active && idx < activeStepIdx) ? "bg-emerald-400" : idx < activeStepIdx ? "bg-emerald-400" : "bg-slate-200 dark:bg-slate-700"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <input type="file" accept=".zip,application/zip" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="system-update-file-input" />
        <div className="flex justify-end">
          <button onClick={submit} disabled={busy || isBusy} className="zen-btn-primary !text-xs" data-testid="system-update-apply">
            {busy ? "Yükleniyor..." : "Doğrula ve Güncelle"}
          </button>
        </div>
      </div>

      {/* Sürüm Geçmişi */}
      <div className="zen-panel p-4 space-y-3" data-testid="system-update-history-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-sky-500" />
            <h4 className="font-bold text-sm">Sürüm Geçmişi & Tek Tık Geri Dönüş</h4>
            <span className="text-[10px] text-slate-500">({history.length} yedek)</span>
          </div>
          <button onClick={loadHistory} disabled={loadingHistory} className="zen-btn-ghost !text-xs" data-testid="system-update-history-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loadingHistory ? "animate-spin" : ""}`} />
          </button>
        </div>
        <p className="text-[11px] text-slate-500">
          Her ZIP güncellemesi öncesi mevcut kod otomatik yedeklenir. Geri dönüş sırasında da mevcut kod yedekleneceğinden hiçbir şey kaybolmaz. <code className="text-[10px]">.env</code> dosyaları hiçbir zaman yedeklere dahil edilmez.
        </p>
        {history.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-500">
            {loadingHistory ? "Yükleniyor..." : "Henüz yedek bulunamadı. İlk ZIP güncellemesinden sonra yedekler burada listelenir."}
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {history.map((b, i) => (
              <div key={b.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 hover:border-sky-400/40 transition-colors" data-testid={`history-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    {i === 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-600">SON YEDEK</span>}
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${b.type === "pre_rollback" ? "bg-amber-500/20 text-amber-600" : "bg-sky-500/20 text-sky-600"}`}>
                      {typeLabel(b.type || b.state)}
                    </span>
                  </div>
                  <div className="text-xs font-semibold truncate" title={b.label}>{b.label}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{fmtBackupDate(b.at)}{b.size_kb ? ` · ${b.size_kb} KB` : ""}</div>
                </div>
                <button
                  onClick={() => rollback(b.id, b.label)}
                  disabled={isBusy || rollingBack === b.id}
                  className="zen-btn-ghost !text-xs shrink-0 !text-sky-600 hover:!bg-sky-500/10 disabled:opacity-50"
                  data-testid={`rollback-btn-${i}`}
                  title="Bu sürüme geri dön"
                >
                  {rollingBack === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Geri Dön
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ E-POSTA OTOMATİK BAĞLANTI (SMTP akıllı tespit + test) ============
// Bilinen sağlayıcı alan adlarından SMTP host/port/güvenlik tahmini — backend'deki
// SMTP_PROVIDER_MAP ile birebir aynı (canlı önizleme için client-side kopya).
const SMTP_PROVIDER_MAP_FE = {
  "gmail.com":      { host: "smtp.gmail.com",      port: 587, secure: "starttls", provider: "Gmail" },
  "googlemail.com": { host: "smtp.gmail.com",      port: 587, secure: "starttls", provider: "Gmail" },
  "outlook.com":    { host: "smtp.office365.com",  port: 587, secure: "starttls", provider: "Outlook / Microsoft 365" },
  "office365.com":  { host: "smtp.office365.com",  port: 587, secure: "starttls", provider: "Outlook / Microsoft 365" },
  "hotmail.com":    { host: "smtp.office365.com",  port: 587, secure: "starttls", provider: "Outlook / Microsoft 365" },
  "live.com":       { host: "smtp.office365.com",  port: 587, secure: "starttls", provider: "Outlook / Microsoft 365" },
  "yahoo.com":      { host: "smtp.mail.yahoo.com", port: 587, secure: "starttls", provider: "Yahoo" },
  "yandex.com":     { host: "smtp.yandex.com",     port: 465, secure: "ssl",      provider: "Yandex" },
  "yandex.com.tr":  { host: "smtp.yandex.com",     port: 465, secure: "ssl",      provider: "Yandex" },
  "zoho.com":       { host: "smtp.zoho.com",       port: 587, secure: "starttls", provider: "Zoho" },
  "icloud.com":     { host: "smtp.mail.me.com",    port: 587, secure: "starttls", provider: "iCloud" },
  "me.com":         { host: "smtp.mail.me.com",    port: 587, secure: "starttls", provider: "iCloud" },
};
function detectSmtpProviderFE(email) {
  const domain = (email.includes("@") ? email.split("@")[1] : "").trim().toLowerCase();
  if (!domain) return null;
  if (SMTP_PROVIDER_MAP_FE[domain]) return SMTP_PROVIDER_MAP_FE[domain];
  if (domain.endsWith(".onmicrosoft.com")) return SMTP_PROVIDER_MAP_FE["outlook.com"];
  return { host: `smtp.${domain}`, port: 587, secure: "starttls", provider: "Özel / Bilinmeyen (kurumsal)" };
}
const MS_DOMAINS = ["outlook.com", "office365.com", "hotmail.com", "live.com"];

function EmailConnectionSettings() {
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(null); // backend'den gelen mevcut kayıt
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpSecure, setSmtpSecure] = useState("starttls");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const detected = useMemo(() => detectSmtpProviderFE(email), [email]);
  const isMicrosoft = detected && MS_DOMAINS.includes((email.split("@")[1] || "").toLowerCase());
  const isGmail = detected && (email.split("@")[1] || "").toLowerCase().includes("gmail");

  const load = async () => {
    try {
      const { data } = await api.get("/email-settings");
      if (data.configured) {
        setSaved(data);
        setEmail(data.email || "");
        setSmtpHost(data.smtpHost || "");
        setSmtpPort(data.smtpPort || 587);
        setSmtpSecure(data.smtpSecure || "starttls");
        const det = detectSmtpProviderFE(data.email || "");
        const isManual = !!det && (data.smtpHost !== det.host || Number(data.smtpPort) !== det.port || data.smtpSecure !== det.secure);
        setManualOverride(isManual);
      }
    } catch (e) { toast.error("E-posta ayarları yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleManual = () => {
    setManualOverride((v) => {
      const turningOn = !v;
      if (turningOn && detected) {
        setSmtpHost(detected.host); setSmtpPort(detected.port); setSmtpSecure(detected.secure);
      }
      return turningOn;
    });
  };

  const handleSave = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Geçerli bir e-posta adresi girin"); return; }
    if (!password.trim()) { toast.error("Şifre girin"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/email-settings", {
        email: email.trim(), password,
        smtpHost: manualOverride ? smtpHost : undefined,
        smtpPort: manualOverride ? smtpPort : undefined,
        smtpSecure: manualOverride ? smtpSecure : undefined,
      });
      toast.success(`Kaydedildi — ${data.provider} (${data.smtpHost}:${data.smtpPort})`);
      setPassword("");
      await load();
    } catch (e) { toast.error("Kayıt hatası: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await api.post("/email-settings/test", {});
      toast.success("Test e-postası gönderildi — bağlantı Aktif ✓");
      await load();
    } catch (e) { toast.error("Bağlantı testi başarısız: " + (e.response?.data?.detail || e.message)); await load(); }
    finally { setTesting(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("E-posta bağlantısı kaldırılsın mı?")) return;
    try {
      await api.delete("/email-settings");
      setSaved(null); setEmail(""); setPassword(""); setManualOverride(false);
      toast.success("Bağlantı kaldırıldı");
    } catch (e) { toast.error("Kaldırılamadı"); }
  };

  if (loading) return <div className="zen-panel p-8 text-center text-sm text-slate-500">Yükleniyor...</div>;

  return (
    <div className="space-y-4" data-testid="email-connect-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">E-posta Otomatik Bağlantı</h3>
        </div>
        {saved && (
          <span
            data-testid="email-connect-status-pill"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
              saved.status === "active"
                ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30"
                : saved.status === "error"
                  ? "bg-rose-500/15 text-rose-500 border border-rose-500/30"
                  : "bg-amber-500/15 text-amber-600 border border-amber-500/30"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${saved.status === "active" ? "bg-emerald-500 animate-pulse" : saved.status === "error" ? "bg-rose-500" : "bg-amber-500"}`} />
            {saved.status === "active" ? "AKTİF" : saved.status === "error" ? "HATA" : "DOĞRULANMADI"}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 -mt-3">
        Bu ekran sadece Admin'e görünür. Bağlanan hesap ileride otomatik teklif/hatırlatma e-postaları göndermek için kullanılır.
        Şifre <b>Fernet</b> ile şifrelenip saklanır.
      </p>

      <div className="zen-panel p-4 space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">E-posta Adresi</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@firma.com"
              className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-sm"
              data-testid="email-connect-email-input"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Şifre</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={saved ? "Değiştirmek için yeni şifre girin" : "E-posta şifresi / uygulama şifresi"}
                className="w-full px-3 py-2 pr-9 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-sm"
                data-testid="email-connect-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="email-connect-password-toggle"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Akıllı tespit önizleme */}
        {detected && (
          <div className="rounded-lg border border-sky-400/30 bg-sky-500/5 p-3 space-y-1.5" data-testid="email-connect-detected-provider">
            <div className="flex items-center gap-2 text-[12px] font-bold text-sky-600 dark:text-sky-400">
              <Info className="w-3.5 h-3.5" /> Algılanan Sağlayıcı: {detected.provider}
            </div>
            <div className="text-[11px] text-slate-500 font-mono">
              Sunucu: {detected.host} : {detected.port} · {detected.secure === "ssl" ? "SSL" : "STARTTLS"}
            </div>
            {manualOverride && (
              <div className="text-[11px] text-amber-600 dark:text-amber-400 font-mono">
                Kaydedilecek (manuel): {smtpHost} : {smtpPort} · {smtpSecure === "ssl" ? "SSL" : "STARTTLS"}
              </div>
            )}
            {isGmail && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                Not: Gmail normal şifreyle SMTP girişine izin vermez — Google hesabınızdan <b>Uygulama Şifresi</b> oluşturup onu girin.
              </p>
            )}
            {isMicrosoft && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                Not: Outlook/Microsoft 365 hesapları için de e-posta şifreniz (2FA açıksa Uygulama Şifresi) kullanılır.
              </p>
            )}
            {!isGmail && !isMicrosoft && detected.provider.includes("Bilinmeyen") && (
              <p className="text-[11px] text-slate-500">
                Bu bir kurumsal/özel alan adı — sunucu tahmini olabilir. Yanlışsa "Sunucu Ayarlarını Manuel Düzenle" ile değiştirin.
              </p>
            )}
            <button
              type="button"
              onClick={toggleManual}
              className="text-[11px] font-semibold text-sky-600 hover:underline"
              data-testid="email-connect-manual-toggle"
            >
              {manualOverride ? "Otomatik tespite dön" : "Sunucu Ayarlarını Manuel Düzenle"}
            </button>
          </div>
        )}

        {manualOverride && (
          <div className="grid md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">SMTP Sunucu</label>
              <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" data-testid="email-connect-smtp-host-input" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Port</label>
              <input type="number" value={smtpPort} onChange={(e) => setSmtpPort(Number(e.target.value))} className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" data-testid="email-connect-smtp-port-input" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Güvenlik</label>
              <select value={smtpSecure} onChange={(e) => setSmtpSecure(e.target.value)} className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" data-testid="email-connect-smtp-secure-select">
                <option value="starttls">STARTTLS</option>
                <option value="ssl">SSL</option>
              </select>
            </div>
          </div>
        )}

        {saved?.lastError && saved.status === "error" && (
          <div className="text-[11px] text-rose-500 p-2 rounded bg-rose-500/10 border border-rose-500/20" data-testid="email-connect-error">
            Son hata: {saved.lastError}
          </div>
        )}
        {saved?.lastTestedAt && (
          <div className="text-[10px] text-slate-400">Son test: {new Date(saved.lastTestedAt).toLocaleString("tr-TR")}</div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {saved && (
            <button onClick={handleDisconnect} className="zen-btn-ghost !text-xs text-rose-500" data-testid="email-connect-disconnect-btn">
              <Trash2 className="w-3.5 h-3.5" /> Bağlantıyı Kaldır
            </button>
          )}
          <button onClick={handleSave} disabled={saving} className="zen-btn-primary !text-xs" data-testid="email-connect-save-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> {saving ? "Kaydediliyor..." : "Kaydet & Bağlan"}
          </button>
          <button onClick={handleTest} disabled={testing || !saved} className="zen-btn-ghost !text-xs" data-testid="email-connect-test-btn">
            <Send className="w-3.5 h-3.5" /> {testing ? "Test ediliyor..." : "Test Bağlantısını Gönder"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ BELGE METİNLERİ (Yazıcı çıktısı üzerinde görünecek metinler) ============
// Bayiloji-tarzı: her belge tipi için sabit/statik metin alanlarını (başlık, alt bilgi,
// KDV notu, imza satırı, ödeme koşulu, garanti, teşekkür...) tek panelden düzenle.
// Not: Her field için `def` = mevcut PDF/HTML şablonlarında kullanılan varsayılan Türkçe metin.
// Kullanıcı ilk açtığında DB boş olsa bile şablonların gerçek metinlerini görüp düzenleyebilir.
const DOC_TEXT_TEMPLATES = [
  {
    id: "teklif",
    label: "Teklif (A4)",
    icon: FileSignature,
    paper: "A4",
    fields: [
      { k: "title",          l: "Belge Başlığı",              def: "TEKLİF",                                              type: "text" },
      { k: "subtitle",       l: "Alt Başlık",                 def: "Sayın müşterimize özel fiyat teklifidir.",           type: "text" },
      { k: "kdv_note",       l: "KDV Notu",                   def: "FİYATLARIMIZA KDV DAHİL DEĞİLDİR.",                  type: "text" },
      { k: "validity_note",  l: "Geçerlilik Notu",            def: "Bu teklif düzenlenme tarihinden itibaren 15 gün geçerlidir.", type: "text" },
      { k: "payment_terms",  l: "Ödeme Koşulları",            def: "%50 sipariş anında avans · %50 teslimat öncesi.",    type: "textarea" },
      { k: "delivery_note",  l: "Teslim Süresi Notu",         def: "Sipariş onayından itibaren 10 iş günü içinde teslim edilir.", type: "text" },
      { k: "warranty_note",  l: "Garanti Notu",               def: "Motorlar 2 yıl, kumaş ve mekanizma 1 yıl garantilidir.", type: "text" },
      { k: "thanks_message", l: "Teşekkür Mesajı",            def: "Bize güvendiğiniz için teşekkür ederiz.",            type: "text" },
      { k: "footer_note",    l: "Alt Not (Dip Yazı)",         def: "ZEN Gölgelendirme Sistemleri · www.zenperde.com",     type: "textarea" },
      { k: "signature_left", l: "İmza Alanı (Sol)",           def: "Hazırlayan",                                          type: "text" },
      { k: "signature_right",l: "İmza Alanı (Sağ)",           def: "Onaylayan / Kaşe & İmza",                             type: "text" },
    ],
  },
  {
    id: "teklif_a5",
    label: "Hızlı Teklif (A5)",
    icon: FileSignature,
    paper: "A5",
    fields: [
      { k: "title",          l: "Belge Başlığı",              def: "HIZLI TEKLİF",                                        type: "text" },
      { k: "kdv_note",       l: "KDV Notu",                   def: "KDV Hariç",                                           type: "text" },
      { k: "validity_note",  l: "Geçerlilik",                 def: "Bu teklif 7 gün geçerlidir.",                         type: "text" },
      { k: "footer_note",    l: "Dip Yazı",                   def: "ZEN Gölgelendirme · 0212 XXX",                        type: "text" },
      { k: "signature_right",l: "İmza Etiketi",               def: "Kaşe / İmza",                                         type: "text" },
    ],
  },
  {
    id: "fatura",
    label: "Fatura (A4)",
    icon: Receipt,
    paper: "A4",
    fields: [
      { k: "title",              l: "Belge Başlığı",          def: "FATURA",                                              type: "text" },
      { k: "kdv_note",           l: "KDV Notu",               def: "Fiyatlara KDV Dahil Değildir.",                       type: "text" },
      { k: "payment_terms",      l: "Ödeme Vadesi",           def: "Fatura tarihinden itibaren 30 gün içinde ödenmelidir.", type: "text" },
      { k: "bank_info_label",    l: "Banka Bilgileri Başlığı",def: "Ödeme İçin Banka Bilgileri",                          type: "text" },
      { k: "invoice_disclaimer", l: "Yasal Uyarı",            def: "Bu belge Vergi Usul Kanunu uyarınca düzenlenmiştir.", type: "textarea" },
      { k: "footer_note",        l: "Alt Not",                def: "ZEN Perde · İstanbul · Küçükyalı VD.",                type: "textarea" },
      { k: "signature_left",     l: "İmza (Sol)",             def: "Düzenleyen",                                          type: "text" },
      { k: "signature_right",    l: "İmza (Sağ)",             def: "Yetkili İmza / Kaşe",                                 type: "text" },
    ],
  },
  {
    id: "siparis",
    label: "Sipariş Formu (A4)",
    icon: ShoppingCart,
    paper: "A4",
    fields: [
      { k: "title",          l: "Belge Başlığı",              def: "SİPARİŞ FORMU",                                       type: "text" },
      { k: "order_note",     l: "Sipariş Notu",               def: "Sipariş, avans tahsilatı yapıldıktan sonra üretime alınır. Ölçüler müşteri sorumluluğundadır.", type: "textarea" },
      { k: "delivery_note",  l: "Teslim Notu",                def: "Öngörülen teslim süresi: 10 iş günü.",                type: "text" },
      { k: "payment_terms",  l: "Ödeme",                      def: "%50 sipariş anında · %50 teslimatta.",                type: "text" },
      { k: "footer_note",    l: "Alt Not",                    def: "İptal ve iade için 3 iş günü içinde bildirim gerekir.", type: "textarea" },
      { k: "signature_left", l: "İmza (Sol)",                 def: "Sipariş Veren",                                       type: "text" },
      { k: "signature_right",l: "İmza (Sağ)",                 def: "Yetkili İmza",                                        type: "text" },
    ],
  },
  {
    id: "irsaliye",
    label: "Sevk İrsaliyesi (A4)",
    icon: Truck,
    paper: "A4",
    fields: [
      { k: "title",              l: "Belge Başlığı",          def: "SEVK İRSALİYESİ",                                     type: "text" },
      { k: "cargo_note",         l: "Kargo Uyarısı",          def: "Kargo hasarları teslim anında sürücüye tutanak ile bildirilmelidir. Aksi durumda sorumluluk kabul edilmez.", type: "textarea" },
      { k: "receiver_signature", l: "Teslim Alan İmza",       def: "Teslim Alan (Ad Soyad · İmza)",                       type: "text" },
      { k: "sender_signature",   l: "Teslim Eden İmza",       def: "Teslim Eden",                                         type: "text" },
      { k: "footer_note",        l: "Alt Not",                def: "İrsaliye 3 nüsha olarak düzenlenmiştir.",             type: "textarea" },
    ],
  },
  {
    id: "kargo_etiketi",
    label: "Kargo Etiketi (10×15)",
    icon: Tag,
    paper: "LBL_10x15",
    fields: [
      { k: "title",         l: "Etiket Başlığı",              def: "KARGO ETİKETİ",                                        type: "text" },
      { k: "sender_line",   l: "Gönderici Satırı",            def: "Gönderici: ZEN Perde · İstanbul · 0212 XXX XX XX",     type: "text" },
      { k: "handling_note", l: "Taşıma Uyarısı",              def: "⚠ DİKKATLİ TAŞIYIN · KIRILABILIR · BU TARAFI ÜSTTE TUTUN", type: "text" },
      { k: "footer_note",   l: "Alt Not",                     def: "Sipariş No / Barkod aşağıda basılır.",                type: "text" },
    ],
  },
  {
    id: "kasa_etiketi",
    label: "Kasa/Motor Etiketi (5×5)",
    icon: Package,
    paper: "LBL_5x5",
    fields: [
      { k: "title",         l: "Etiket Başlığı",              def: "ZEN PERDE",                                            type: "text" },
      { k: "warranty_note", l: "Garanti Notu",                def: "Motor 2 yıl · Mekanizma 1 yıl garantilidir",           type: "text" },
      { k: "contact_line",  l: "Servis / İletişim",           def: "Servis: 0212 XXX XX XX · info@zenperde.com",           type: "text" },
      { k: "qr_note",       l: "QR Kod Yazısı",               def: "Servis / kayıt için QR kodu okutunuz",                 type: "text" },
    ],
  },
  {
    id: "uretim_foyu",
    label: "Üretim Föyü (A4)",
    icon: Factory,
    paper: "A4",
    fields: [
      { k: "title",                l: "Belge Başlığı",        def: "ÜRETİM FÖYÜ",                                          type: "text" },
      { k: "qc_note",              l: "Kalite Kontrol Notu",  def: "Sevkiyat öncesi kalite kontrol onayı zorunludur. Uygun olmayan ürünler sevk edilemez.", type: "textarea" },
      { k: "production_signature", l: "Üretim Onay Etiketi",  def: "Üretim Sorumlusu (Ad · İmza)",                         type: "text" },
      { k: "qc_signature",         l: "QC Onay Etiketi",      def: "Kalite Kontrol (Ad · İmza)",                           type: "text" },
      { k: "footer_note",          l: "Alt Not",              def: "Bu belge iç kullanım içindir.",                        type: "textarea" },
    ],
  },
  {
    id: "makbuz",
    label: "Tahsilat Makbuzu (A5)",
    icon: CreditCard,
    paper: "A5",
    fields: [
      { k: "title",          l: "Belge Başlığı",              def: "TAHSİLAT MAKBUZU",                                     type: "text" },
      { k: "amount_label",   l: "Tutar Etiketi",              def: "Alınan Tutar",                                         type: "text" },
      { k: "reason_label",   l: "Açıklama Etiketi",           def: "Tahsilat Konusu",                                      type: "text" },
      { k: "footer_note",    l: "Alt Not",                    def: "Bu makbuz iki nüsha olarak düzenlenmiş olup, tahsilat karşılığında verilir.", type: "text" },
      { k: "signature_left", l: "İmza (Sol)",                 def: "Tahsilat Yapan",                                       type: "text" },
      { k: "signature_right",l: "İmza (Sağ)",                 def: "Ödeme Yapan",                                          type: "text" },
    ],
  },
  {
    id: "barkod_etiketi",
    label: "Barkod Etiketi (Kalem/Koli)",
    icon: Barcode,
    paper: "LBL_10x6",
    fields: [
      { k: "title",         l: "Etiket Başlığı",              def: "KALEM ETİKETİ",                                        type: "text" },
      { k: "company_line",  l: "Firma / Marka Satırı",        def: "ZEN PERDE · Mekanik Gölgelendirme Sistemleri",         type: "text" },
      { k: "handling_note", l: "Taşıma / Uyarı Notu",        def: "⚠ DİKKATLİ TAŞIYINIZ · KIRILABILIR",                   type: "text" },
      { k: "extra_line",    l: "Ek Bilgi Satırı (isteğe bağlı)", def: "",                                                 type: "text" },
      { k: "footer_note",   l: "Alt Not / İletişim",          def: "Servis: 0212 XXX XX XX · info@zenperde.com",           type: "text" },
      { k: "default_size",  l: "Varsayılan Etiket Boyutu",    def: "LBL_10x6",                                             type: "select",
        options: [
          { v: "LBL_6x4",   l: "60 × 40 mm  (küçük kalem etiketi)" },
          { v: "LBL_8x5",   l: "80 × 50 mm  (orta kalem etiketi)" },
          { v: "LBL_10x6",  l: "100 × 60 mm  (geniş etiket / varsayılan)" },
          { v: "LBL_10x15", l: "100 × 150 mm (kargo etiketi boyutu)" },
        ],
      },
    ],
  },
];

// Kağıt boyutları (mm cinsinden fiziksel ölçü)
const DOC_PAPER_SIZES = {
  A4:        { label: "A4",              w: 210, h: 297,  aspect: "portrait" },
  A5:        { label: "A5",              w: 148, h: 210,  aspect: "portrait" },
  A6:        { label: "A6",              w: 105, h: 148,  aspect: "portrait" },
  LBL_10x15: { label: "Etiket 10×15",   w: 100, h: 150,  aspect: "portrait" },
  LBL_5x5:   { label: "Etiket 5×5",     w: 50,  h: 50,   aspect: "square"   },
  LBL_8x5:   { label: "Etiket 8×5",     w: 80,  h: 50,   aspect: "landscape"},
  LBL_10x6:  { label: "Etiket 10×6",    w: 100, h: 60,   aspect: "landscape"},
  LBL_6x4:   { label: "Etiket 6×4",     w: 60,  h: 40,   aspect: "landscape"},
};

function DocumentTextsSettings() {
  const [activeDoc, setActiveDoc] = useState("teklif");
  const [values, setValues] = useState({});        // { "teklif__title": "TEKLİF FORMU", ... }
  const [initialValues, setInitialValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paperOverride, setPaperOverride] = useState({}); // per-doc paper size override

  const currentDoc = DOC_TEXT_TEMPLATES.find((d) => d.id === activeDoc) || DOC_TEXT_TEMPLATES[0];
  const currentPaper = DOC_PAPER_SIZES[paperOverride[activeDoc] || currentDoc.paper || "A4"];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings", { params: { category: "document_texts" } });
        const dbMap = {};
        (data || []).forEach((r) => { dbMap[r.key] = r.value; });
        // Merge: DB varsa DB, yoksa şablonun `def` değerini kullan
        const merged = {};
        DOC_TEXT_TEMPLATES.forEach((d) => {
          d.fields.forEach((f) => {
            const k = `${d.id}__${f.k}`;
            merged[k] = (dbMap[k] !== undefined && dbMap[k] !== null) ? dbMap[k] : (f.def || "");
          });
        });
        // Kağıt boyutu tercihini oku
        const paperMap = {};
        DOC_TEXT_TEMPLATES.forEach((d) => {
          const pk = `${d.id}__paper_size`;
          if (dbMap[pk]) paperMap[d.id] = dbMap[pk];
        });
        setPaperOverride(paperMap);
        setValues(merged);
        setInitialValues(merged);
      } catch (e) { toast.error("Belge metinleri yüklenemedi"); }
      finally { setLoading(false); }
    })();
  }, []);

  const compoundKey = (docId, fieldKey) => `${docId}__${fieldKey}`;
  const getVal = (fieldKey) => values[compoundKey(activeDoc, fieldKey)] ?? "";
  const setVal = (fieldKey, v) => setValues((prev) => ({ ...prev, [compoundKey(activeDoc, fieldKey)]: v }));

  const isDirty = useMemo(() => {
    const allKeys = new Set([...Object.keys(values), ...Object.keys(initialValues)]);
    for (const k of allKeys) {
      if ((values[k] || "") !== (initialValues[k] || "")) return true;
    }
    return false;
  }, [values, initialValues]);

  const changePaper = async (newPaper) => {
    setPaperOverride((prev) => ({ ...prev, [activeDoc]: newPaper }));
    try {
      await api.post("/settings", {
        id: `DT-${activeDoc.toUpperCase().slice(0,4)}-PAPER`,
        category: "document_texts",
        key: `${activeDoc}__paper_size`,
        value: newPaper,
        label: `${currentDoc.label} — Kağıt Boyutu`,
        active: true,
        createdAt: new Date().toISOString(),
      });
      toast.success(`Kağıt boyutu: ${DOC_PAPER_SIZES[newPaper]?.label}`);
    } catch (e) { toast.error("Kağıt boyutu kaydedilemedi"); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const changed = [];
      Object.keys(values).forEach((k) => {
        if ((values[k] || "") !== (initialValues[k] || "")) changed.push(k);
      });
      for (const k of changed) {
        const [docId, fieldKey] = k.split("__");
        const doc = DOC_TEXT_TEMPLATES.find((d) => d.id === docId);
        const fld = doc?.fields.find((f) => f.k === fieldKey);
        await api.post("/settings", {
          id: `DT-${docId.toUpperCase().slice(0,4)}-${fieldKey.toUpperCase().slice(0,10)}`,
          category: "document_texts",
          key: k,
          value: values[k] || "",
          label: `${doc?.label || docId} — ${fld?.l || fieldKey}`,
          active: true,
          createdAt: new Date().toISOString(),
        });
      }
      setInitialValues({ ...values });
      toast.success(`${changed.length} alan kaydedildi`);
    } catch (e) { toast.error("Kayıt hatası: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const resetDoc = () => {
    if (!window.confirm(`"${currentDoc.label}" için tüm metinler sistem varsayılanına (fabrika ayarları) dönsün mü?`)) return;
    const reset = { ...values };
    currentDoc.fields.forEach((f) => { reset[compoundKey(activeDoc, f.k)] = f.def || ""; });
    setValues(reset);
    toast.info("Varsayılan metinler yüklendi. Kaydetmek için «Tümünü Kaydet»'e basın.");
  };

  const IconCur = currentDoc.icon;

  // Cetvel çizim değerleri: A4-relative ölçek (A4 = referans, diğer boyutlar oransal küçülür/büyür)
  const A4_MAX_W = 460;                                        // A4 için önizleme genişliği px
  const paperScale = A4_MAX_W / 210;                           // 1mm → px (A4 referans)
  const previewW = currentPaper.w * paperScale;                // A5=324, A4=460, LBL_10x15=219, LBL_5x5=109
  const previewH = currentPaper.h * paperScale;
  const rulerTicksX = Math.floor(currentPaper.w / 10);
  const rulerTicksY = Math.floor(currentPaper.h / 10);

  return (
    <div className="space-y-4" data-testid="document-texts-panel">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">Belge Metinleri</h3>
          <span className="text-[10px] text-slate-500 hidden sm:block">
            (Yazıcı çıktısı üzerinde görünen sabit yazılar)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetDoc}
            className="zen-btn-ghost !text-xs"
            data-testid="dt-reset-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Varsayılana Dön
          </button>
          <button
            onClick={saveAll}
            disabled={saving || !isDirty}
            className="zen-btn-primary !text-xs"
            data-testid="dt-save-btn"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {saving ? "Kaydediliyor..." : "Tümünü Kaydet"}
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Her belge tipi için başlık, alt not, KDV notu, imza satırı, garanti/ödeme koşulları gibi
        sabit metinleri buradan tek yerden düzenle. Alanlar mevcut şablonların varsayılan Türkçe metinleriyle önceden dolduruldu.
      </p>

      {loading ? (
        <div className="zen-panel p-8 text-center text-sm text-slate-500">Yükleniyor...</div>
      ) : (
        <div className="grid lg:grid-cols-[220px_1fr] gap-4">
          {/* Sol: Belge seçici */}
          <nav className="zen-panel p-2 h-fit lg:sticky lg:top-24" data-testid="dt-doc-nav">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2 py-1.5">
              Belge Tipi
            </div>
            <div className="space-y-0.5">
              {DOC_TEXT_TEMPLATES.map((d) => {
                const Icon = d.icon;
                const active = activeDoc === d.id;
                const filled = d.fields.filter((f) => (values[compoundKey(d.id, f.k)] || "").trim()).length;
                return (
                  <button
                    key={d.id}
                    onClick={() => setActiveDoc(d.id)}
                    data-testid={`dt-doc-${d.id}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-semibold text-left transition-colors ${
                      active
                        ? "bg-[#E5322A] text-white"
                        : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate flex-1">{d.label}</span>
                    <span className={`text-[9px] font-mono px-1.5 rounded ${
                      active ? "bg-white/25 text-white" : "bg-slate-200 dark:bg-slate-700 text-slate-500"
                    }`}>
                      {filled}/{d.fields.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Sağ: Form + Önizleme */}
          <div className="space-y-4 min-w-0">
            {/* Kağıt boyutu + önizleme kartı */}
            <div className="zen-panel p-4" data-testid="dt-preview-card">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-sky-500" />
                  <h4 className="font-bold text-sm">Önizleme — {currentDoc.label}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-500">Kağıt Boyutu:</label>
                  <select
                    value={paperOverride[activeDoc] || currentDoc.paper || "A4"}
                    onChange={(e) => changePaper(e.target.value)}
                    className="px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono"
                    data-testid="dt-paper-size-select"
                  >
                    {Object.entries(DOC_PAPER_SIZES).map(([k, p]) => (
                      <option key={k} value={k}>{p.label} ({p.w}×{p.h}mm)</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cetvelli önizleme wrapper */}
              <div className="mx-auto" style={{ width: previewW + 20, maxWidth: "100%" }} data-testid="dt-ruler-preview">
                {/* Üst cetvel */}
                <div className="relative" style={{ marginLeft: 20, height: 16 }}>
                  <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-t flex">
                    {Array.from({ length: rulerTicksX + 1 }).map((_, i) => (
                      <div key={i} className="relative" style={{ width: paperScale * 10 }}>
                        <div className="absolute left-0 top-0 h-full w-px bg-slate-400 dark:bg-slate-600" />
                        <span className="absolute left-0.5 top-0 text-[8px] text-slate-500 dark:text-slate-400 font-mono" style={{ lineHeight: "16px" }}>
                          {i}
                        </span>
                      </div>
                    ))}
                    <span className="absolute right-1 top-0 text-[8px] text-slate-400 font-mono" style={{ lineHeight: "16px" }}>cm</span>
                  </div>
                </div>

                <div className="flex">
                  {/* Sol cetvel */}
                  <div className="relative w-5" style={{ height: previewH }}>
                    <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-l flex flex-col">
                      {Array.from({ length: rulerTicksY + 1 }).map((_, i) => (
                        <div key={i} className="relative" style={{ height: paperScale * 10 }}>
                          <div className="absolute left-0 top-0 h-px w-full bg-slate-400 dark:bg-slate-600" />
                          <span className="absolute left-0.5 top-0.5 text-[8px] text-slate-500 dark:text-slate-400 font-mono">
                            {i}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Kağıt */}
                  <div
                    data-zen-paper="true"
                    className="bg-white text-slate-900 shadow-md relative overflow-hidden border-t border-r border-b border-slate-300"
                    style={{ width: previewW, height: previewH }}
                  >
                    {/* ── Barkod Etiketi Özel Önizleme ── */}
                    {activeDoc === "barkod_etiketi" ? (
                      <div style={{ padding: Math.max(4, previewW * 0.04), height: "100%", display: "flex", flexDirection: "column", fontFamily: "Arial, sans-serif", fontSize: Math.max(5, previewW * 0.065) + "px", color: "#000", boxSizing: "border-box" }}>
                        {/* Üst satır: marka + başlık */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #ccc", paddingBottom: 2, marginBottom: 3 }}>
                          <span style={{ fontWeight: 900, color: "#E5322A", fontSize: Math.max(7, previewW * 0.08) + "px" }}>ZEN</span>
                          <span style={{ fontWeight: 800, fontSize: Math.max(5, previewW * 0.06) + "px", letterSpacing: 0.5 }}>{getVal("title") || "KALEM ETİKETİ"}</span>
                        </div>
                        {/* Firma satırı */}
                        <div style={{ fontSize: Math.max(4, previewW * 0.048) + "px", color: "#555", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {getVal("company_line") || "ZEN PERDE · Mekanik Gölgelendirme"}
                        </div>
                        {/* Mock müşteri/ürün */}
                        <div style={{ fontWeight: 700, fontSize: Math.max(6, previewW * 0.07) + "px", marginBottom: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Ahmet Yılmaz</div>
                        <div style={{ fontWeight: 800, fontSize: Math.max(7, previewW * 0.08) + "px", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Zip Perde · 120×160 cm</div>
                        <div style={{ fontSize: Math.max(5, previewW * 0.058) + "px", color: "#444", marginBottom: 1 }}>Kalem 1/3 · Adet: 2</div>
                        {/* Uyarı notu */}
                        {getVal("handling_note") && (
                          <div style={{ fontSize: Math.max(4, previewW * 0.045) + "px", color: "#b45309", marginBottom: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{getVal("handling_note")}</div>
                        )}
                        {/* Simge barkod çizgileri */}
                        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
                          <div style={{ display: "flex", gap: 1, height: Math.max(10, previewH * 0.15) + "px", alignItems: "flex-end" }}>
                            {[3,1,2,1,3,1,2,1,2,3,1,2,1,3,1,2,1,3,2,1,2,3,1,2,1].map((w, i) => (
                              <div key={i} style={{ width: w + "px", background: i % 2 === 0 ? "#000" : "#fff", height: "100%", flexShrink: 0 }} />
                            ))}
                          </div>
                          <div style={{ fontSize: Math.max(4, previewW * 0.042) + "px", color: "#777", letterSpacing: 0.5 }}>SİP-2026-001-P1</div>
                        </div>
                        {/* Footer */}
                        {getVal("footer_note") && (
                          <div style={{ fontSize: Math.max(3.5, previewW * 0.038) + "px", color: "#888", marginTop: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{getVal("footer_note")}</div>
                        )}
                      </div>
                    ) : (
                    <div className="p-4 h-full flex flex-col text-[10px] font-serif">
                      <div className="flex items-start justify-between mb-2 border-b border-slate-300 pb-2">
                        <div>
                          <div className="text-[11px] font-black">ZEN PERDE</div>
                          <div className="text-[8px] text-slate-600">Mekanik Perde ve Gölgelendirme Sistemleri</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[14px] font-black tracking-wider" style={{ color: "#E5322A" }}>
                            {getVal("title") || currentDoc.fields[0]?.def || "BELGE"}
                          </div>
                          {getVal("subtitle") && <div className="text-[8px] text-slate-600 mt-0.5">{getVal("subtitle")}</div>}
                        </div>
                      </div>

                      <div className="text-[8px] text-slate-500 space-y-0.5 mb-2">
                        <div><b>Sayın</b> Ahmet Yılmaz — İstanbul</div>
                        <div>Belge No: {activeDoc.toUpperCase()}-2026-001 · Tarih: 19.08.2026</div>
                      </div>

                      <div className="bg-slate-100 h-[30%] rounded flex items-center justify-center text-slate-400 text-[9px]">
                        [Ürün / Kalem Tablosu]
                      </div>

                      <div className="mt-2 space-y-1 text-[7.5px] leading-tight">
                        {getVal("kdv_note") && <div className="text-slate-700"><b>KDV:</b> {getVal("kdv_note")}</div>}
                        {getVal("validity_note") && <div className="text-slate-700"><b>Geçerlilik:</b> {getVal("validity_note")}</div>}
                        {getVal("payment_terms") && <div className="text-slate-700"><b>Ödeme:</b> {getVal("payment_terms")}</div>}
                        {getVal("delivery_note") && <div className="text-slate-700"><b>Teslim:</b> {getVal("delivery_note")}</div>}
                        {getVal("warranty_note") && <div className="text-slate-700"><b>Garanti:</b> {getVal("warranty_note")}</div>}
                        {getVal("cargo_note") && <div className="text-slate-700"><b>Not:</b> {getVal("cargo_note")}</div>}
                        {getVal("qc_note") && <div className="text-slate-700"><b>QC:</b> {getVal("qc_note")}</div>}
                        {getVal("handling_note") && <div className="text-slate-700"><b>⚠</b> {getVal("handling_note")}</div>}
                        {getVal("invoice_disclaimer") && <div className="text-slate-500 italic">{getVal("invoice_disclaimer")}</div>}
                        {getVal("order_note") && <div className="text-slate-700 italic">{getVal("order_note")}</div>}
                      </div>

                      {(getVal("signature_left") || getVal("signature_right") || getVal("receiver_signature") || getVal("sender_signature") || getVal("production_signature") || getVal("qc_signature")) && (
                        <div className="mt-auto flex justify-between text-[8px] pt-3">
                          <div className="text-center">
                            <div className="h-6 border-b border-slate-400 w-28" />
                            <div className="mt-0.5">{getVal("signature_left") || getVal("sender_signature") || getVal("production_signature") || ""}</div>
                          </div>
                          <div className="text-center">
                            <div className="h-6 border-b border-slate-400 w-28" />
                            <div className="mt-0.5">{getVal("signature_right") || getVal("receiver_signature") || getVal("qc_signature") || ""}</div>
                          </div>
                        </div>
                      )}

                      {getVal("thanks_message") && (
                        <div className="mt-2 text-center text-[8px] italic text-slate-600">{getVal("thanks_message")}</div>
                      )}
                      {getVal("footer_note") && (
                        <div className="mt-1 text-center text-[7px] text-slate-500 whitespace-pre-wrap">{getVal("footer_note")}</div>
                      )}
                      {getVal("sender_line") && (
                        <div className="mt-1 text-center text-[8px] text-slate-700 font-bold">{getVal("sender_line")}</div>
                      )}
                      {getVal("contact_line") && (
                        <div className="mt-1 text-center text-[8px] text-slate-700">{getVal("contact_line")}</div>
                      )}
                      {getVal("qr_note") && (
                        <div className="mt-1 text-center text-[7px] text-slate-500">{getVal("qr_note")}</div>
                      )}
                    </div>
                    )} {/* end barkod_etiketi ternary */}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 text-center mt-2">
                {currentPaper.label} · {currentPaper.w}×{currentPaper.h} mm · Cetvel: cm
              </p>
            </div>

            {/* Form alanları */}
            <div className="zen-panel p-4 space-y-3" data-testid="dt-form-panel">
              <div className="flex items-center gap-2">
                <IconCur className="w-4 h-4 text-[#E5322A]" />
                <h4 className="font-bold text-sm">{currentDoc.label} — Düzenlenebilir Metinler</h4>
                <span className="text-[10px] text-slate-500">({currentDoc.fields.length} alan)</span>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {currentDoc.fields.map((f) => (
                  <div key={f.k} className={`space-y-1 ${f.type === "textarea" ? "md:col-span-2" : ""}`}>
                    <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 flex items-center justify-between">
                      <span>{f.l}</span>
                      <span className="text-[9px] font-mono text-slate-400">{f.k}</span>
                    </label>
                    {f.type === "textarea" ? (
                      <textarea
                        rows={2}
                        value={getVal(f.k)}
                        onChange={(e) => setVal(f.k, e.target.value)}
                        placeholder={f.def}
                        className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs resize-y"
                        data-testid={`dt-field-${activeDoc}-${f.k}`}
                      />
                    ) : f.type === "select" ? (
                      <select
                        value={getVal(f.k) || f.def}
                        onChange={(e) => setVal(f.k, e.target.value)}
                        className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#131B2E] text-xs"
                        data-testid={`dt-field-${activeDoc}-${f.k}`}
                      >
                        {(f.options || []).map((opt) => (
                          <option key={opt.v} value={opt.v}>{opt.l}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={getVal(f.k)}
                        onChange={(e) => setVal(f.k, e.target.value)}
                        placeholder={f.def}
                        className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs"
                        data-testid={`dt-field-${activeDoc}-${f.k}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              {isDirty && (
                <div className="text-[11px] text-amber-500 flex items-center gap-1 pt-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Kaydedilmemiş değişiklikler var — «Tümünü Kaydet»'e tıklayın.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ JSPM (JSPrintManager) YAZICI BAĞLANTI AYARLARI ============
// Silent print için PDF paylaşımı, yazıcı seçimi, canlı bağlantı durumu.
// Ayarlar > Yazıcılar > "Yazıcı Bağlantısı (JSPM)" alt sekmesi.
const JSPM_PRINTER_ROLES = [
  { key: "label",     l: "Etiket Yazıcısı (ESC/POS)", desc: "Kargo, kasa/motor etiketleri (küçük termal)" },
  { key: "zpl_label", l: "Zebra Etiket Yazıcısı (ZPL)", desc: "Zebra/Godex — barkodlar çok daha net basılır" },
  { key: "document",  l: "Belge Yazıcısı",             desc: "Teklif, fatura, sipariş (A4)" },
  { key: "receipt",   l: "Makbuz Yazıcısı",            desc: "Tahsilat makbuzu (A5)" },
];

// ─── ZPL yardımcısı: bir sipariş için Zebra Programming Language etiketi üret
// ZPL şablon editöründen okur; kaydedilmiş şablon yoksa fallback varsayılan (100×60 mm) kullanır.
// 203 dpi → 8 dot/mm dönüşümü ile mm koordinatları ZPL dot cinsine çevrilir.
const ZPL_PRESET_SIZES = [
  { key: "5x5",    w: 50,  h: 50,  label: "5×5 cm" },
  { key: "8x5",    w: 80,  h: 50,  label: "8×5 cm" },
  { key: "10x15",  w: 100, h: 150, label: "10×15 cm" },
  { key: "10x6",   w: 100, h: 60,  label: "10×6 cm (varsayılan)" },
  { key: "custom", w: 0,   h: 0,   label: "Özel Boyut" },
];

// Tüm template editörlerinde ortak veri alanları + element tipleri
const ZPL_FIELD_TYPES = [
  // --- Metin Alanları (isText: true) ---
  { key: "static_text",          l: "Statik Metin",        fill: "ZEN PERDE",     isText: true,  resolver: (_, v) => v || "" },
  { key: "field_customer_name",  l: "Müşteri Adı",         fill: "Ahmet Yılmaz",  isText: true,  resolver: (o) => (o?.customerName || "Ahmet Yılmaz").slice(0, 40) },
  { key: "field_customer_city",  l: "Müşteri Şehri",       fill: "İstanbul",      isText: true,  resolver: (o) => (o?.customerCity || o?.city || "İstanbul").slice(0, 24) },
  { key: "field_project_name",   l: "Proje Adı",           fill: "İst. Projesi",  isText: true,  resolver: (o) => (o?.projectName || "").slice(0, 40) },
  { key: "field_order_no",       l: "Sipariş No",          fill: "SP-000",        isText: true,  resolver: (o) => (o?.orderNo || o?.id || "SP-000").toString().slice(0, 18) },
  { key: "field_product_name",   l: "Ürün Tipi",           fill: "Stor Perde",    isText: true,  resolver: (o) => (o?.items?.[0]?.product || o?.productType || "Ürün").slice(0, 40) },
  { key: "field_dimensions",     l: "Ürün Ölçüleri",       fill: "150×200 cm",    isText: true,  resolver: (o) => { const i = o?.items?.[0]; return i ? `${i.widthCm||150}×${i.heightCm||200} cm` : "150×200 cm"; }},
  { key: "field_product_width",  l: "Ürün Genişliği",      fill: "150 cm",        isText: true,  resolver: (o) => o?.items?.[0]?.widthCm ? `${o.items[0].widthCm} cm` : "150 cm" },
  { key: "field_product_height", l: "Ürün Yüksekliği",     fill: "200 cm",        isText: true,  resolver: (o) => o?.items?.[0]?.heightCm ? `${o.items[0].heightCm} cm` : "200 cm" },
  { key: "field_m2",             l: "Alan (m²)",           fill: "3.00 m²",       isText: true,  resolver: (o) => { const i = o?.items?.[0]; return `${(((i?.widthCm||150)*(i?.heightCm||200))/10000).toFixed(2)} m²`; }},
  { key: "field_quantity",       l: "Adet",                fill: "1 ad",          isText: true,  resolver: (o) => `${o?.items?.[0]?.qty || o?.totalQty || 1} ad` },
  { key: "field_fabric_code",    l: "Kumaş Kodu",          fill: "BLACKOUT-01",   isText: true,  resolver: (o) => (o?.fabricKodu || o?.items?.[0]?.fabricCode || "").slice(0, 30) },
  { key: "field_ral_code",       l: "RAL / Renk Kodu",     fill: "7016 Texture",  isText: true,  resolver: (o) => (o?.ralKodu || o?.items?.[0]?.ralCode || "").slice(0, 20) },
  { key: "field_date",           l: "Tarih (bugün)",       fill: "01.01.2026",    isText: true,  resolver: () => new Date().toLocaleDateString("tr-TR") },
  { key: "field_deadline",       l: "Termin Tarihi",       fill: "15.01.2026",    isText: true,  resolver: (o) => o?.deadline ? new Date(o.deadline).toLocaleDateString("tr-TR") : "" },
  { key: "field_note",           l: "Sipariş Notu",        fill: "Kırılabilir!",  isText: true,  resolver: (o) => (o?.note || o?.deliveryNote || "").slice(0, 40) },
  // --- Özel Elementler ---
  { key: "barcode",    l: "Barkod (Code128)",  fill: "",  isText: false, isBarcode: true,  resolver: (o) => (o?.orderNo || o?.id || "ZEN000000").toString().slice(0, 20) },
  { key: "hline",      l: "Yatay Çizgi",       fill: "",  isText: false, isShape: true,    resolver: () => "" },
  { key: "rect",       l: "Dikdörtgen Kutu",   fill: "",  isText: false, isShape: true,    resolver: () => "" },
  { key: "image_logo", l: "Firma Logosu",      fill: "",  isText: false, isImage: true,    resolver: () => "" },
];

// mm → ZPL dot (203 dpi)
const MM_TO_DOT = 8;

// ZPL üret: template = { size, customW?, customH?, elements: [...] }
function buildLabelZPLFromTemplate(order, template, logoGF = null) {
  const isCustom = template.size === "custom";
  const preset = isCustom
    ? { w: template.customW || 100, h: template.customH || 60 }
    : (ZPL_PRESET_SIZES.find((p) => p.key === template.size) || ZPL_PRESET_SIZES[3]);
  const widthDot = Math.round(preset.w * MM_TO_DOT);
  const heightDot = Math.round(preset.h * MM_TO_DOT);
  const parts = [
    "^XA",
    "^CI28",
    `^PW${widthDot}`,
    `^LL${heightDot}`,
    "^LH0,0",
  ];
  (template.elements || []).forEach((el) => {
    const x = Math.round((el.x_mm || 0) * MM_TO_DOT);
    const y = Math.round((el.y_mm || 0) * MM_TO_DOT);
    const fs = Math.max(10, Math.min(120, el.fontSize || 26));
    const def = ZPL_FIELD_TYPES.find((t) => t.key === el.type);
    const value = def ? def.resolver(order, el.text || "") : "";
    const rotation = el.rotation ? "R" : "N";   // R=90° döndür, N=normal

    if (el.type === "barcode") {
      const bHeightDot = Math.round((el.heightMm || 15) * MM_TO_DOT);
      parts.push(`^FO${x},${y}^BY3,3,${bHeightDot}^BCN,${bHeightDot},Y,N,N^FD${(value || "").replace(/\^/g, " ")}^FS`);
    } else if (el.type === "hline") {
      const lw = Math.round((el.widthMm || (preset.w - 4)) * MM_TO_DOT);
      const lh = Math.max(2, Math.min(20, el.fontSize || 3));
      parts.push(`^FO${x},${y}^GB${lw},${lh},${lh}^FS`);
    } else if (el.type === "rect") {
      const rw = Math.round((el.widthMm || 40) * MM_TO_DOT);
      const rh = Math.round((el.heightMm || 20) * MM_TO_DOT);
      const thick = Math.max(2, Math.min(20, el.borderThick || 3));
      parts.push(`^FO${x},${y}^GB${rw},${rh},${thick}^FS`);
    } else if (el.type === "image_logo") {
      // ^GF: gerçek bitmap logosu (logoGF ile) veya metin placeholder
      if (logoGF) {
        parts.push(`^FO${x},${y}${logoGF}^FS`);
      } else {
        parts.push(`^FO${x},${y}^A0N,20,20^FDLOGO^FS`);
      }
    } else {
      const safe = (value || "").replace(/\^/g, " ").replace(/~/g, "-");
      parts.push(`^FO${x},${y}^A0${rotation},${fs},${fs}^FD${safe}^FS`);
    }
  });
  parts.push("^PQ1,0,1,Y", "^XZ");
  return parts.join("\n");
}

// Varsayılan şablon: 10×6 cm etiket (mevcut kod tabanının orijinal boyutu)
const ZPL_DEFAULT_TEMPLATE = {
  size: "10x6",
  elements: [
    { id: "e1", type: "static_text",         x_mm: 4,  y_mm: 3,  fontSize: 50, text: "ZEN PERDE" },
    { id: "e2", type: "static_text",         x_mm: 4,  y_mm: 11, fontSize: 22, text: "Mekanik Perde ve Gölgelendirme" },
    { id: "e3", type: "hline",               x_mm: 4,  y_mm: 16, fontSize: 3,  widthMm: 92 },
    { id: "e4", type: "field_customer_name", x_mm: 4,  y_mm: 18, fontSize: 30 },
    { id: "e5", type: "field_customer_city", x_mm: 4,  y_mm: 24, fontSize: 24, text: "Şehir: " },
    { id: "e6", type: "barcode",             x_mm: 4,  y_mm: 30, fontSize: 3,  heightMm: 15 },
    { id: "e7", type: "field_order_no",      x_mm: 60, y_mm: 3,  fontSize: 36, text: "#" },
    { id: "e8", type: "field_date",          x_mm: 60, y_mm: 10, fontSize: 20 },
    { id: "e9", type: "field_note",          x_mm: 4,  y_mm: 52, fontSize: 20 },
  ],
};

async function loadZPLTemplate(sizeKey) {
  try {
    const { data } = await api.get("/settings", { params: { category: "zpl_templates" } });
    const row = (data || []).find((r) => r.key === sizeKey);
    if (row && row.value) {
      try { return JSON.parse(row.value); } catch { /* ignore */ }
    }
  } catch (_) {}
  return null;
}

// Bir sipariş için ZPL üretir. Kaydedilmiş şablon yoksa varsayılan template kullanır.
export async function buildLabelZPL(order, sizeKey = "10x6") {
  const tpl = (await loadZPLTemplate(sizeKey)) || ZPL_DEFAULT_TEMPLATE;
  return buildLabelZPLFromTemplate(order, tpl);
}

// HTML etiket: template elementlerinden CSS absolute-positioned HTML oluşturur
export async function buildHtmlFromLabelTemplate(order, template, logoDataUrl = "") {
  const isCustom = template.size === "custom";
  const preset = isCustom
    ? { w: template.customW || 100, h: template.customH || 60 }
    : (ZPL_PRESET_SIZES.find((p) => p.key === template.size) || { w: 100, h: 60 });

  const htmlEls = (template.elements || []).map((el) => {
    const def = ZPL_FIELD_TYPES.find((t) => t.key === el.type);
    const value = def ? def.resolver(order, el.text || "") : "";
    const x = (el.x_mm || 0).toFixed(2);
    const y = (el.y_mm || 0).toFixed(2);
    const ptSize = ((el.fontSize || 26) * 0.354 * 0.125 * 3.78).toFixed(1);

    if (el.type === "rect") {
      const rw = (el.widthMm || 40).toFixed(2);
      const rh = (el.heightMm || 20).toFixed(2);
      const thick = (el.borderThick || 0.3).toFixed(2);
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${rw}mm;height:${rh}mm;border:${thick}mm solid #000;box-sizing:border-box;"></div>`;
    }
    if (el.type === "image_logo") {
      const iw = (el.widthMm || 30).toFixed(2);
      const ih = (el.heightMm || 15).toFixed(2);
      const imgSrc = logoDataUrl || "";
      return imgSrc
        ? `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${iw}mm;height:${ih}mm;"><img src="${imgSrc}" style="width:100%;height:100%;object-fit:contain;" /></div>`
        : `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${iw}mm;height:${ih}mm;border:0.2mm solid #ccc;display:flex;align-items:center;justify-content:center;font-size:6pt;color:#999;">LOGO</div>`;
    }
    if (el.type === "hline") {
      const lw = (el.widthMm || preset.w - 4).toFixed(2);
      const lh = Math.max(0.3, (el.fontSize || 3) * 0.03).toFixed(2);
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${lw}mm;height:${lh}mm;background:#000;"></div>`;
    }
    if (el.type === "barcode") {
      const bw = (el.widthMm || 40).toFixed(2);
      const bh = (el.heightMm || 15).toFixed(2);
      const safe = String(value || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${bw}mm;height:${bh}mm;border:0.2mm solid #ccc;display:flex;align-items:center;justify-content:center;font-family:monospace;font-size:5pt;">▐▌▌▐▌▐▌▐▌▐▌▐▐▌<br>${safe}</div>`;
    }
    const rot = el.rotation ? "transform:rotate(90deg);transform-origin:top left;" : "";
    const safe = String(value || "").replace(/</g, "&lt;");
    return `<div style="position:absolute;left:${x}mm;top:${y}mm;font-size:${ptSize}pt;font-weight:700;font-family:Arial,sans-serif;white-space:nowrap;${rot}">${safe}</div>`;
  }).join("\n");

  return `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><meta name="color-scheme" content="light">
<style>
@page{size:${preset.w}mm ${preset.h}mm;margin:0;}
*{box-sizing:border-box;}
html,body{width:${preset.w}mm;height:${preset.h}mm;background:#fff;margin:0;padding:0;overflow:hidden;}
</style></head><body>
<div style="position:relative;width:${preset.w}mm;height:${preset.h}mm;">${htmlEls}</div>
</body></html>`;
}

function JSPMConnectionSettings() {
  const { status, connected, connecting, statusLabel, printers, activePort, activeScheme, lastError } = useJspm();
  const [assignments, setAssignments] = useState({});     // { label: "EPSON T20", document: "HP LaserJet", ... }
  const [portsInput, setPortsInput] = useState("26443, 29443, 8181, 8182");
  const [hostInput, setHostInput] = useState("localhost");
  const [secure, setSecure] = useState(false);            // false = ws://, true = wss://
  const [reconnecting, setReconnecting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanFound, setScanFound] = useState([]);         // Otomatik keşif sonucu

  // İlk yükleyişte kayıtlı portları oku
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings", { params: { category: "jspm_config" } });
        (data || []).forEach((r) => {
          if (r.key === "ports" && r.value) setPortsInput(r.value);
          if (r.key === "host" && r.value) setHostInput(r.value);
          if (r.key === "secure") setSecure(r.value === "true" || r.value === "1");
        });
      } catch (_) {}
    })();
  }, []);

  const savePortsAndReconnect = async (nextPortsInput = null, nextSecure = null) => {
    const useInput = nextPortsInput ?? portsInput;
    const useSecure = nextSecure ?? secure;
    const parsed = useInput.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => n > 0 && n < 65536);
    if (!parsed.length) { toast.error("En az 1 geçerli port girin (1-65535)"); return; }
    if (!hostInput.trim()) { toast.error("Yazıcı sunucusu adresi gerekli"); return; }
    setReconnecting(true);
    try {
      await Promise.all([
        api.post("/settings", {
          id: "JSPM-PORTS", category: "jspm_config", key: "ports",
          value: parsed.join(","), label: "JSPM WebSocket portları", active: true,
          createdAt: new Date().toISOString(),
        }),
        api.post("/settings", {
          id: "JSPM-HOST", category: "jspm_config", key: "host",
          value: hostInput.trim(), label: "JSPM WebSocket sunucusu", active: true,
          createdAt: new Date().toISOString(),
        }),
        api.post("/settings", {
          id: "JSPM-SECURE", category: "jspm_config", key: "secure",
          value: useSecure ? "true" : "false", label: "JSPM WSS (secure) mod", active: true,
          createdAt: new Date().toISOString(),
        }),
      ]);
      toast.success(`Portlar kaydedildi: ${parsed.join(", ")} · ${useSecure ? "WSS" : "WS"} · Yeniden bağlanılıyor...`);
      await jspm.reinit(useSecure, parsed, hostInput.trim());
    } catch (e) { toast.error("Kayıt hatası: " + (e.response?.data?.detail || e.message)); }
    finally { setReconnecting(false); }
  };

  // Yaygın JSPM portlarını paralel tara ve yanıt verenleri bul
  const scanCommonPorts = async () => {
    setScanning(true);
    setScanFound([]);
    const COMMON_PORTS = [26443, 29443, 57069, 8181, 8182, 63318, 54692, 8080, 9100, 23443, 12315, 8443, 8188, 8189];
    try {
      const results = await Promise.all(
        COMMON_PORTS.map((p) => jspm.probePort(secure ? "wss" : "ws", p).then((r) => ({ port: p, ...r })))
      );
      const found = results.filter((r) => r.ok).map((r) => r.port);
      setScanFound(found);
      if (found.length === 0) {
        toast.error("Yaygın portlarda JSPM Client bulunamadı — Client çalışıyor mu?");
      } else {
        toast.success(`Bulunanlar: ${found.join(", ")} — 'Otomatik Ayarla' ile aktifleştir`);
      }
    } catch (e) { toast.error("Tarama hatası: " + e.message); }
    finally { setScanning(false); }
  };

  const applyDiscovered = async () => {
    if (!scanFound.length) return;
    const csv = scanFound.join(", ");
    setPortsInput(csv);
    await savePortsAndReconnect(csv);
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings", { params: { category: "jspm_printers" } });
        const map = {};
        (data || []).forEach((r) => { map[r.key] = r.value; });
        setAssignments(map);
      } catch (e) { /* sessiz */ }
      finally { setLoading(false); }
    })();
  }, []);

  const saveAssignment = async (roleKey, printerName) => {
    setSaving(true);
    try {
      await api.post("/settings", {
        id: `JSPM-${roleKey.toUpperCase()}`,
        category: "jspm_printers",
        key: roleKey,
        value: printerName || "",
        label: `JSPM ${roleKey} yazıcısı`,
        active: true,
        createdAt: new Date().toISOString(),
      });
      setAssignments((prev) => ({ ...prev, [roleKey]: printerName }));
      toast.success(`${roleKey === "label" ? "Etiket" : roleKey === "document" ? "Belge" : "Makbuz"} yazıcısı kaydedildi`);
    } catch (e) { toast.error("Kayıt hatası: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const testPrint = async (roleKey) => {
    const printer = assignments[roleKey];
    if (!printer) { toast.error("Önce yazıcı seçin"); return; }
    setTesting((prev) => ({ ...prev, [roleKey]: true }));
    try {
      await jspm.testPrint(printer);
      toast.success(`${printer} → test etiketi gönderildi`);
    } catch (e) { toast.error("Test hatası: " + e.message); }
    finally { setTesting((prev) => ({ ...prev, [roleKey]: false })); }
  };

  const refreshPrinters = async () => {
    if (!connected) { toast.error("Önce JSPM Client'a bağlanmalısınız"); return; }
    await jspm.refreshPrinters();
    toast.success("Yazıcı listesi yenilendi");
  };

  const StatusPill = (
    <span
      data-testid="jspm-status-pill"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold ${
        connecting
          ? "bg-amber-500/15 text-amber-600 border border-amber-500/40"
          : (connected
              ? "bg-emerald-500/15 text-emerald-600 border border-emerald-500/40"
              : "bg-rose-500/15 text-rose-600 border border-rose-500/40")
      }`}
    >
      <span className={`w-2.5 h-2.5 rounded-full ${
        connecting ? "bg-amber-500 animate-pulse" : (connected ? "bg-emerald-500 animate-pulse" : "bg-rose-500")
      }`} />
      {connecting ? "Bağlanıyor..." : (connected ? "Bağlı" : "Bağlı Değil")} · {statusLabel}
      {activePort ? ` · :${activePort}` : ""}
    </span>
  );

  return (
    <div className="space-y-5" data-testid="jspm-panel">
      {/* Başlık + durum */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">Yazıcı Bağlantısı (JSPM)</h3>
        </div>
        {StatusPill}
      </div>
      <div className="zen-panel p-3 border border-amber-400/50 bg-amber-50/10 flex items-start gap-2 -mt-1" data-testid="jspm-version-notice">
        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-[11px] text-amber-700 dark:text-amber-300">
            <b>Önemli:</b> Bu ERP, <b>JSPrintManager (JSPM) Sürüm 6</b> ile çalışmaktadır.
            Bilgisayarınızda <b>JSPM Client v6</b> kurulu olmalıdır (varsayılan port: <code className="text-[10px]">26443</code>).
            {" "}v7, v8 veya v9 sürümleri <b>uyumlu değildir</b>.
          </p>
          {!connected && (
            <a
              href="https://www.neodynamic.com/downloads/jspm/jspm6-6.0.24.325-win.exe"
              download="jspm6-6.0.24.325-win.exe"
              data-testid="jspm-direct-download-btn"
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-md bg-sky-500 hover:bg-sky-400 text-white text-[11px] font-bold transition-colors shadow"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="w-3.5 h-3.5" />
              JSPrintManager v6 İndir (.exe)
            </a>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-3">
        <b>JSPrintManager Client v6</b> uygulaması bilgisayarınızda çalışıyor olmalı (varsayılan port: <code>26443</code>).
        Kurulu değilse yukarıdaki butonu kullanın ya da{" "}
        <a href="https://www.neodynamic.com/products/printing/js-print-manager/download/" target="_blank" rel="noopener noreferrer" className="text-sky-500 underline">
          Neodynamic resmi indirme sayfasını
        </a>{" "}
        ziyaret edin. Bağlandığında yazıcılar otomatik listelenir.
      </p>

      {/* Bağlantı bilgi kartı */}
      <div className="zen-panel p-4">
        <div className="grid md:grid-cols-3 gap-4 text-[11px]">
          <div>
            <div className="text-slate-500 uppercase text-[9px] tracking-wider mb-1">Durum</div>
            <div className={`font-bold ${connected ? "text-emerald-500" : "text-rose-500"}`}>
              {statusLabel}
            </div>
          </div>
          <div>
            <div className="text-slate-500 uppercase text-[9px] tracking-wider mb-1">WSStatus Kodu</div>
            <div className="font-mono">{status}</div>
          </div>
          <div>
            <div className="text-slate-500 uppercase text-[9px] tracking-wider mb-1">Bulunan Yazıcı Sayısı</div>
            <div className="font-mono">{printers.length}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={refreshPrinters}
            disabled={!connected}
            className="zen-btn-ghost !text-xs"
            data-testid="jspm-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Yazıcı Listesini Yenile
          </button>
          {connecting && (
            <span className="text-[11px] text-amber-600 flex items-center gap-1" data-testid="jspm-connecting-note">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              JSPM Bağlanıyor... (portlar taranıyor: 8181 → 8182)
            </span>
          )}
          {!connecting && !connected && (
            <span className="text-[11px] text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              JSPM Client kapalı veya başlatılamadı. Detaylı hata için tarayıcı konsolunu (F12) açın.
            </span>
          )}
        </div>
        {lastError && !connecting && (
          <details className="mt-2 text-[10px] text-slate-500" data-testid="jspm-last-error">
            <summary className="cursor-pointer text-rose-500 font-bold">Son Hata Ayrıntısı</summary>
            <pre className="whitespace-pre-wrap p-2 mt-1 bg-rose-500/5 border border-rose-500/20 rounded text-[10px] font-mono">{lastError}</pre>
          </details>
        )}
      </div>

      {/* Manuel Port Ayarı */}
      <div className="zen-panel p-4 space-y-2" data-testid="jspm-manual-port-card">
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-amber-500" />
          <h4 className="font-bold text-sm">Manuel Port & Bağlantı Ayarı</h4>
        </div>
        <p className="text-[11px] text-slate-500">
          JSPM Client farklı bir portta çalışıyorsa buraya yaz. Birden fazla port virgülle ayrılabilir — sırayla denenir.
          Varsayılan (JSPM v9): <code className="text-[10px]">29443, 8181, 8182, 63318, 54692</code> · Her port için önce ws sonra wss otomatik denenir.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="text" value={hostInput} onChange={(e) => setHostInput(e.target.value)} placeholder="localhost veya 192.168.1.50"
            className="w-full sm:w-56 px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" data-testid="jspm-host-input" />
          <input
            type="text"
            value={portsInput}
            onChange={(e) => setPortsInput(e.target.value)}
            placeholder="29443, 8181, 8182, 63318, 54692"
            className="flex-1 min-w-[240px] px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono"
            data-testid="jspm-ports-input"
          />
          <label className="flex items-center gap-1.5 text-[11px] font-semibold cursor-pointer select-none" data-testid="jspm-secure-toggle-label">
            <input
              type="checkbox"
              checked={secure}
              onChange={(e) => setSecure(e.target.checked)}
              className="accent-[#E5322A]"
              data-testid="jspm-secure-toggle"
            />
            WSS (Güvenli)
          </label>
          <button
            onClick={() => savePortsAndReconnect()}
            disabled={reconnecting}
            className="zen-btn-primary !text-xs"
            data-testid="jspm-ports-save-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${reconnecting ? "animate-spin" : ""}`} />
            {reconnecting ? "Bağlanıyor..." : "Kaydet & Yeniden Bağlan"}
          </button>
        </div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1">
          <Info className="w-3 h-3" /> Aktif şema: <code className="text-[10px] font-mono">{(secure ? "wss" : "ws")}://localhost:PORT</code>
          {secure ? " · Sertifika sorunu varsa WSS'i kapatın." : " · Sertifika sorunu yaşamamak için önerilir (localhost)."}
        </div>
        {activePort && connected && (
          <div className="text-[11px] text-emerald-500 font-semibold" data-testid="jspm-active-port-info">
            ✓ Aktif bağlantı: {activeScheme || (secure ? "wss" : "ws")}://localhost:{activePort}
          </div>
        )}
      </div>

      {/* SSL Sertifika Güven Yardımcısı */}
      {!connected && (
        <div className="zen-panel p-4 space-y-2 border border-amber-400/40 bg-amber-50/5" data-testid="jspm-ssl-trust-card">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-500" />
            <h4 className="font-bold text-sm text-amber-600 dark:text-amber-400">SSL Sertifika Güven Adımı</h4>
          </div>
          <p className="text-[11px] text-slate-500">
            HTTPS üzerinden çalışırken tarayıcı, JSPM Client'ın öz-imzalı sertifikasını reddedebilir.
            Aşağıdaki butona tıklayın, yeni sekmede <b>"Bu siteye ilerle"</b> veya <b>"Güvenlik İstisnası Ekle"</b> diyerek sertifikayı bir kez kabul edin. Sonra bu sayfaya dönüp yeniden bağlanın.
          </p>
          <div className="flex flex-wrap gap-2">
            {[26443, 8181, 8182].map((p) => (
              <button
                key={p}
                onClick={() => window.open(`https://localhost:${p}`, "_blank")}
                className="zen-btn-ghost !text-xs border border-amber-400 text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                data-testid={`jspm-trust-cert-btn-${p}`}
              >
                <ExternalLink className="w-3 h-3" />
                Sertifikaya Güven: :{p}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-400">Sertifika kabul edildikten sonra "Kaydet &amp; Yeniden Bağlan" butonunu kullanın.</p>
        </div>
      )}

      {/* Otomatik Port Keşfi */}
      <div className="zen-panel p-4 space-y-2" data-testid="jspm-autodiscover-card">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-sky-500" />
          <h4 className="font-bold text-sm">Port Otomatik Keşif</h4>
        </div>
        <p className="text-[11px] text-slate-500">
          JSPM Client'ın hangi portta çalıştığını bilmiyorsan yaygın 10 portu paralel tarayıp cevap verenleri bulur:
          <code className="text-[10px] ml-1">54692, 8181, 8182, 8080, 9100, 23443, 12315, 8443, 8188, 8189</code>
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={scanCommonPorts}
            disabled={scanning}
            className="zen-btn-ghost !text-xs"
            data-testid="jspm-scan-btn"
          >
            <Radio className={`w-3.5 h-3.5 ${scanning ? "animate-pulse" : ""}`} />
            {scanning ? "Taranıyor..." : "Yaygın Portları Tara"}
          </button>
          {scanFound.length > 0 && (
            <>
              <span className="text-[11px] font-bold text-emerald-500" data-testid="jspm-scan-found">
                Bulunanlar: {scanFound.join(", ")}
              </span>
              <button
                onClick={applyDiscovered}
                className="zen-btn-primary !text-xs"
                data-testid="jspm-scan-apply"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Otomatik Ayarla
              </button>
            </>
          )}
          {scanFound.length === 0 && !scanning && (
            <span className="text-[10px] text-slate-500">Henüz tarama yapılmadı</span>
          )}
        </div>
      </div>

      {/* Yazıcı Rol Atamaları */}
      <div className="zen-panel p-4 space-y-3" data-testid="jspm-assignments">
        <div className="flex items-center gap-2 mb-1">
          <Printer className="w-4 h-4 text-sky-500" />
          <h4 className="font-bold text-sm">Yazıcı Rol Ataması</h4>
        </div>
        <p className="text-[11px] text-slate-500">
          Her belge türü için farklı bir yazıcı seçebilirsiniz. Sessiz Yazdır özelliğinde bu atamalar kullanılır.
        </p>
        {loading ? (
          <div className="text-xs text-slate-500 text-center py-4">Yükleniyor...</div>
        ) : (
          <div className="grid md:grid-cols-1 gap-3">
            {JSPM_PRINTER_ROLES.map((role) => (
              <div
                key={role.key}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-[#22304A]"
                data-testid={`jspm-role-${role.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{role.l}</div>
                  <div className="text-[10px] text-slate-500">{role.desc}</div>
                </div>
                <select
                  value={assignments[role.key] || ""}
                  onChange={(e) => saveAssignment(role.key, e.target.value)}
                  disabled={!connected || printers.length === 0 || saving}
                  className="px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs min-w-[220px]"
                  data-testid={`jspm-role-select-${role.key}`}
                >
                  <option value="">— Yazıcı seçin —</option>
                  {printers.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <button
                  onClick={() => testPrint(role.key)}
                  disabled={!connected || !assignments[role.key] || testing[role.key]}
                  className="zen-btn-ghost !text-xs"
                  data-testid={`jspm-role-test-${role.key}`}
                  title="Test etiketi gönder (ESC/POS raw)"
                >
                  <Send className="w-3.5 h-3.5" />
                  {testing[role.key] ? "..." : "Test"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bilgi kutusu */}
      <div className="zen-panel p-3 text-[11px] bg-sky-500/5 border-sky-500/30" data-testid="jspm-info-box">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="font-bold text-sky-600 dark:text-sky-400">Sessiz Yazdır Nasıl Çalışır?</div>
            <div className="text-slate-600 dark:text-slate-400">
              Bir teklif / etiket sayfasında "Yazdır" butonuna bastığınızda ERP, sunucudan PDF üretir ve JSPM Client aracılığıyla
              seçili yazıcıya <b>tarayıcı penceresi açmadan</b> gönderir. Bunun için yukarıda ilgili role yazıcı atanmış olmalıdır.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ ZPL ŞABLON EDİTÖRÜ (Görsel Drag-Drop) ============
function ZPLTemplateEditor() {
  const [sizeKey, setSizeKey] = useState("10x6");
  const [customW, setCustomW] = useState(100);
  const [customH, setCustomH] = useState(60);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null); // { id, handle, startX, startY, startEl }
  const canvasRef = useRef(null);
  const [leftTab, setLeftTab] = useState("palet"); // "palet" | "inspector" | "kod"
  const [showGrid, setShowGrid] = useState(false);
  const [wrapW, setWrapW] = useState(850);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(e => setWrapW(e[0]?.contentRect?.width || 850));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Efektif preset: özel boyut için customW/H kullan
  const preset = sizeKey === "custom"
    ? { key: "custom", w: customW || 100, h: customH || 60, label: "Özel Boyut" }
    : (ZPL_PRESET_SIZES.find((p) => p.key === sizeKey) || ZPL_PRESET_SIZES[3]);

  // Canvas: sağ paneli dolduracak şekilde - dinamik ölçek
  const maxCanvasH = Math.min(700, Math.max(400, window.innerHeight * 0.66));
  const canvasScale = Math.min(Math.max(3, (wrapW - 56) / preset.w), maxCanvasH / preset.h);
  const canvasW = preset.w * canvasScale;
  const canvasH = preset.h * canvasScale;

  // ZPL kod önizlemesi (Kod sekmesi için)
  const zplCode = useMemo(() => {
    const demoOrder = { orderNo: "DEMO-2026-001", customerName: "Test Müşteri", customerCity: "İstanbul", note: "Test etiketi", projectName: "Demo Proje", items: [] };
    try { return buildLabelZPLFromTemplate(demoOrder, { size: sizeKey, ...(sizeKey === "custom" ? { customW, customH } : {}), elements }); }
    catch { return ""; }
  }, [elements, sizeKey, customW, customH]);

  const loadForSize = async (key) => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings", { params: { category: "zpl_templates" } });
      const row = (data || []).find((r) => r.key === key);
      let tpl = null;
      if (row?.value) { try { tpl = JSON.parse(row.value); } catch { /* */ } }
      if (!tpl) {
        tpl = key === "10x6" ? ZPL_DEFAULT_TEMPLATE : { size: key, elements: [] };
      }
      if (tpl.customW) setCustomW(tpl.customW);
      if (tpl.customH) setCustomH(tpl.customH);
      setElements(tpl.elements || []);
      setSelectedId(null);
    } catch { toast.error("Şablon yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadForSize(sizeKey); /* eslint-disable-next-line */ }, [sizeKey]);

  const addElement = (type) => {
    const def = ZPL_FIELD_TYPES.find((t) => t.key === type);
    const isShape = def?.isShape;
    const isBarcode = def?.isBarcode;
    const isImage = def?.isImage;
    const newEl = {
      id: `e${Date.now()}`,
      type,
      x_mm: 4, y_mm: 4,
      fontSize: isShape ? 3 : (isBarcode ? 3 : 26),
      rotation: 0,
      text: def?.fill || "",
      ...(isBarcode ? { heightMm: 15, widthMm: 50 } : {}),
      ...((isShape && type === "hline") ? { widthMm: preset.w - 8 } : {}),
      ...((isShape && type === "rect") ? { widthMm: 40, heightMm: 20, borderThick: 3 } : {}),
      ...(isImage ? { widthMm: 30, heightMm: 12 } : {}),
    };
    setElements((prev) => [...prev, newEl]);
    setSelectedId(newEl.id);
  };

  const updateSelected = (patch) => {
    setElements((prev) => prev.map((e) => (e.id === selectedId ? { ...e, ...patch } : e)));
  };

  const deleteSelected = () => {
    setElements((prev) => prev.filter((e) => e.id !== selectedId));
    setSelectedId(null);
  };

  // Sürükle başlat
  const startDrag = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(id);
    const el = elements.find((x) => x.id === id);
    if (!el) return;
    setDragState({ id, startMouseX: e.clientX, startMouseY: e.clientY, startX: el.x_mm, startY: el.y_mm });
  };

  const selectOnly = (e, id) => {
    e.stopPropagation();
    setSelectedId(id);
  };

  // Yeniden boyutlandır başlat
  const startResize = (e, id, handle) => {
    e.stopPropagation();
    e.preventDefault();
    const el = elements.find((x) => x.id === id);
    if (!el) return;
    setResizeState({ id, handle, startMouseX: e.clientX, startMouseY: e.clientY, startEl: { ...el } });
  };

  useEffect(() => {
    if (!dragState && !resizeState) return;
    const onMove = (e) => {
      if (dragState) {
        const dxMm = (e.clientX - dragState.startMouseX) / canvasScale;
        const dyMm = (e.clientY - dragState.startMouseY) / canvasScale;
        const newX = Math.max(0, Math.min(preset.w - 2, dragState.startX + dxMm));
        const newY = Math.max(0, Math.min(preset.h - 2, dragState.startY + dyMm));
        setElements((prev) => prev.map((el) => (el.id === dragState.id ? { ...el, x_mm: newX, y_mm: newY } : el)));
      }
      if (resizeState) {
        const dxMm = (e.clientX - resizeState.startMouseX) / canvasScale;
        const dyMm = (e.clientY - resizeState.startMouseY) / canvasScale;
        const sel = resizeState.startEl;
        const updates = {};
        if (resizeState.handle === "se") {
          updates.widthMm = Math.max(5, (sel.widthMm || 40) + dxMm);
          updates.heightMm = Math.max(5, (sel.heightMm || 20) + dyMm);
        } else if (resizeState.handle === "e") {
          updates.widthMm = Math.max(5, (sel.widthMm || 40) + dxMm);
        } else if (resizeState.handle === "s") {
          updates.heightMm = Math.max(5, (sel.heightMm || 20) + dyMm);
        }
        setElements((prev) => prev.map((el) => (el.id === resizeState.id ? { ...el, ...updates } : el)));
      }
    };
    const onUp = () => { setDragState(null); setResizeState(null); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragState, resizeState, canvasScale, preset.w, preset.h]);

  const savePreset = async () => {
    setSaving(true);
    try {
      const payload = { size: sizeKey, ...(sizeKey === "custom" ? { customW, customH } : {}), elements };
      await api.post("/settings", {
        id: `ZPL-${sizeKey.toUpperCase()}`,
        category: "zpl_templates",
        key: sizeKey,
        value: JSON.stringify(payload),
        label: `ZPL Şablon ${preset.label}`,
        active: true,
        createdAt: new Date().toISOString(),
      });
      toast.success(`${preset.label} şablonu kaydedildi`);
    } catch (e) { toast.error("Kayıt hatası: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const testPrint = async () => {
    if (!jspm.isConnected()) { toast.error("JSPM Client bağlı değil"); return; }
    setTesting(true);
    try {
      const { data } = await api.get("/settings", { params: { category: "jspm_printers" } });
      const cfg = {};
      (data || []).forEach((r) => { cfg[r.key] = r.value; });
      const printer = cfg["zpl_label"];
      if (!printer) { toast.error("Zebra yazıcı seçilmemiş"); return; }
      const demoOrder = { orderNo: "DEMO-2026-001", customerName: "Test Müşteri", customerCity: "İstanbul", note: "Test etiketi", projectName: "Demo Proje", items: [{ product: "Stor Perde", widthCm: 150, heightCm: 200, qty: 2, fabricCode: "BLACKOUT-01", ralCode: "7016" }] };

      // Logo ^GF bitmap'ini yükle (varsa)
      let logoGF = null;
      if (elements.some((el) => el.type === "image_logo")) {
        try {
          const logoUrl = await loadLogoDataUrl();
          if (logoUrl) {
            const logoEl = elements.find((el) => el.type === "image_logo");
            logoGF = await logoToZPLGF(logoUrl, logoEl?.widthMm || 30, logoEl?.heightMm || 12);
          }
        } catch (e) { console.warn("[ZPL Logo]", e); }
      }

      const zpl = buildLabelZPLFromTemplate(demoOrder, { size: sizeKey, ...(sizeKey === "custom" ? { customW, customH } : {}), elements }, logoGF);
      await jspm.silentPrintZPL(printer, zpl);
      toast.success("Test etiketi Zebra'ya gönderildi");
    } catch (e) { toast.error("Test hatası: " + e.message); }
    finally { setTesting(false); }
  };

  const duplicateToSize = async (targetKey) => {
    if (targetKey === sizeKey) { toast.error("Aynı boyuta kopyalanamaz"); return; }
    if (elements.length === 0) { toast.error("Kopyalanacak element yok"); return; }
    const target = ZPL_PRESET_SIZES.find((p) => p.key === targetKey);
    if (!target) return;
    const tw = targetKey === "custom" ? customW : target.w;
    const th = targetKey === "custom" ? customH : target.h;
    const scaleX = tw / preset.w; const scaleY = th / preset.h;
    const scaleFont = Math.min(scaleX, scaleY);
    const scaled = elements.map((el) => ({
      ...el,
      id: `e${Date.now()}${Math.floor(Math.random() * 1000)}`,
      x_mm: Math.max(0, Math.min(tw - 2, (el.x_mm || 0) * scaleX)),
      y_mm: Math.max(0, Math.min(th - 2, (el.y_mm || 0) * scaleY)),
      fontSize: el.type === "hline" ? el.fontSize : Math.max(10, Math.round((el.fontSize || 26) * scaleFont)),
      ...(el.widthMm !== undefined ? { widthMm: Math.max(5, el.widthMm * scaleX) } : {}),
      ...(el.heightMm !== undefined ? { heightMm: Math.max(5, el.heightMm * scaleY) } : {}),
    }));
    try {
      const payload = { size: targetKey, ...(targetKey === "custom" ? { customW: tw, customH: th } : {}), elements: scaled };
      await api.post("/settings", { id: `ZPL-${targetKey.toUpperCase()}`, category: "zpl_templates", key: targetKey, value: JSON.stringify(payload), label: `ZPL Şablon ${target?.label || targetKey}`, active: true, createdAt: new Date().toISOString() });
      toast.success(`${preset.label} → ${target?.label || targetKey} kopyalandı`);
      setSizeKey(targetKey);
    } catch (e) { toast.error("Kopyalama hatası: " + (e.response?.data?.detail || e.message)); }
  };

  const selected = elements.find((e) => e.id === selectedId);
  const selectedDef = selected ? ZPL_FIELD_TYPES.find((t) => t.key === selected.type) : null;

  // Canvas'ta element render et
  const renderCanvasEl = (el) => {
    const def = ZPL_FIELD_TYPES.find((t) => t.key === el.type);
    const isSel = el.id === selectedId;
    const pxFontSize = Math.max(6, el.fontSize * canvasScale * 0.125);
    const selBorder = isSel ? "2px solid #E5322A" : "1px dashed transparent";
    const commonStyle = { position: "absolute", left: el.x_mm * canvasScale, top: el.y_mm * canvasScale, cursor: "move", userSelect: "none", touchAction: "none" };

    // Yeniden boyutlandırma tutamaçları (seçili rect/barcode/image için)
    const hasResize = isSel && (el.type === "rect" || el.type === "barcode" || el.type === "image_logo" || el.type === "hline");
    const resizeHandles = hasResize ? (
      <>
        <div onPointerDown={(e) => startResize(e, el.id, "se")} style={{ position: "absolute", right: -4, bottom: -4, width: 8, height: 8, background: "#E5322A", cursor: "se-resize", borderRadius: 2 }} />
        <div onPointerDown={(e) => startResize(e, el.id, "e")} style={{ position: "absolute", right: -4, top: "50%", transform: "translateY(-50%)", width: 8, height: 8, background: "#E5322A", cursor: "e-resize", borderRadius: 2 }} />
        <div onPointerDown={(e) => startResize(e, el.id, "s")} style={{ position: "absolute", bottom: -4, left: "50%", transform: "translateX(-50%)", width: 8, height: 8, background: "#E5322A", cursor: "s-resize", borderRadius: 2 }} />
      </>
    ) : null;

    if (el.type === "hline") return (
      <div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`zpl-el-${el.id}`}
        style={{ ...commonStyle, width: (el.widthMm || preset.w - 8) * canvasScale, height: Math.max(2, el.fontSize), background: isSel ? "#E5322A" : "#0f172a", outline: isSel ? "2px solid #E5322A" : "none" }}>
        {resizeHandles}
      </div>
    );
    if (el.type === "barcode") return (
      <div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`zpl-el-${el.id}`}
        style={{ ...commonStyle, width: (el.widthMm || 50) * canvasScale, height: (el.heightMm || 15) * canvasScale, background: "repeating-linear-gradient(90deg,#0f172a 0 2px,transparent 2px 4px,#0f172a 4px 5px,transparent 5px 8px)", outline: isSel ? "2px solid #E5322A" : "1px dashed #94a3b8", display: "flex", alignItems: "flex-end", justifyContent: "center", fontSize: 8, color: "#0f172a", paddingBottom: 2, fontFamily: "monospace" }}
        title="Code128 Barkod">
        {def?.fill || ""}
        {resizeHandles}
      </div>
    );
    if (el.type === "rect") return (
      <div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`zpl-el-${el.id}`}
        style={{ ...commonStyle, width: (el.widthMm || 40) * canvasScale, height: (el.heightMm || 20) * canvasScale, border: `2px solid ${isSel ? "#E5322A" : "#0f172a"}`, background: "transparent", outline: isSel ? "2px solid rgba(229,50,42,0.3)" : "none" }}>
        {resizeHandles}
      </div>
    );
    if (el.type === "image_logo") return (
      <div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`zpl-el-${el.id}`}
        style={{ ...commonStyle, width: (el.widthMm || 30) * canvasScale, height: (el.heightMm || 12) * canvasScale, border: `1px dashed ${isSel ? "#E5322A" : "#94a3b8"}`, background: isSel ? "rgba(229,50,42,0.08)" : "rgba(0,0,0,0.03)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#94a3b8", fontFamily: "monospace" }}>
        LOGO
        {resizeHandles}
      </div>
    );
    // Metin elementleri
    const previewText = el.type === "static_text" ? (el.text || def?.fill || "…") : (def?.fill || el.text || "");
    const rotStyle = el.rotation ? { transform: "rotate(90deg)", transformOrigin: "top left" } : {};
    return (
      <div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`zpl-el-${el.id}`}
        style={{ ...commonStyle, fontSize: pxFontSize, fontWeight: 700, fontFamily: "'Arial', 'Helvetica Neue', sans-serif", color: "#0f172a", whiteSpace: "nowrap", padding: "0 2px", outline: isSel ? selBorder : (dragState?.id === el.id ? "1px solid #94a3b8" : "1px dashed transparent"), background: isSel ? "rgba(229,50,42,0.08)" : "transparent", ...rotStyle }}>
        {previewText}
      </div>
    );
  };

  // TEXT alanları için dropdown listesi (sadece isText elementleri + barcode)
  const TEXT_FIELD_OPTIONS = ZPL_FIELD_TYPES.filter((t) => t.isText || t.isBarcode);

  return (
    <div className="space-y-3" data-testid="zpl-editor-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Barcode className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">ZPL Şablon Editörü</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button className="zen-btn-ghost !text-xs" data-testid="zpl-duplicate-btn">
              <Copy className="w-3.5 h-3.5" /> Boyuta Kopyala <ChevronDown className="w-3 h-3 ml-0.5" />
            </button>
            <div className="absolute right-0 top-full mt-1 zen-panel p-1 min-w-[180px] shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-30" data-testid="zpl-duplicate-menu">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 px-2 py-1">Hedef boyut</div>
              {ZPL_PRESET_SIZES.filter((s) => s.key !== sizeKey).map((s) => (
                <button key={s.key} onClick={() => duplicateToSize(s.key)} data-testid={`zpl-duplicate-to-${s.key}`}
                  className="w-full text-left px-2 py-1.5 rounded text-[11px] font-semibold hover:bg-[#E5322A] hover:text-white">
                  → {s.label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={testPrint} disabled={testing} className="zen-btn-ghost !text-xs" data-testid="zpl-test-btn">
            <Send className="w-3.5 h-3.5" /> {testing ? "..." : "Test Yazdır"}
          </button>
          <button onClick={savePreset} disabled={saving} className="zen-btn-primary !text-xs" data-testid="zpl-save-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> {saving ? "..." : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Boyut seçici */}
      <div className="zen-panel p-2.5 flex items-center gap-2 flex-wrap" data-testid="zpl-size-selector">
        <span className="text-[11px] font-bold text-slate-500 mr-1">BOYUT:</span>
        {ZPL_PRESET_SIZES.map((s) => (
          <button key={s.key} onClick={() => setSizeKey(s.key)} data-testid={`zpl-size-${s.key}`}
            className={`px-3 py-1 rounded text-[11px] font-bold ${sizeKey === s.key ? "bg-[#E5322A] text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>
            {s.label}
          </button>
        ))}
        {sizeKey === "custom" && (
          <div className="flex items-center gap-1 ml-2">
            <input type="number" min="20" max="400" value={customW} onChange={(e) => setCustomW(Number(e.target.value || 100))}
              className="w-16 px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" placeholder="mm" data-testid="zpl-custom-w" />
            <span className="text-[10px] text-slate-500">×</span>
            <input type="number" min="20" max="400" value={customH} onChange={(e) => setCustomH(Number(e.target.value || 60))}
              className="w-16 px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" placeholder="mm" data-testid="zpl-custom-h" />
            <span className="text-[10px] text-slate-500">mm</span>
          </div>
        )}
        <span className="text-[10px] text-slate-400 ml-auto font-mono">{preset.w}×{preset.h}mm · {Math.round(preset.w * MM_TO_DOT)}×{Math.round(preset.h * MM_TO_DOT)} dot</span>
      </div>

      {loading ? (
        <div className="zen-panel p-8 text-center text-sm text-slate-500">Yükleniyor...</div>
      ) : (
        <div className="flex gap-3 items-start">
          {/* ── SOL PANEL: Palet / Inspector / Kod ── */}
          <div className="w-52 flex-shrink-0 flex flex-col gap-2">
            {/* Sol panel sekme çubuğu */}
            <div className="zen-panel p-1 flex gap-1">
              {[["palet","Palet"], ["inspector","Inspector"], ["kod","ZPL Kodu"]].map(([id, label]) => (
                <button key={id} onClick={() => setLeftTab(id)}
                  className={`flex-1 py-1 rounded text-[11px] font-bold transition-colors ${leftTab === id ? "bg-[#E5322A] text-white" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Palet sekmesi */}
            {leftTab === "palet" && (
              <div className="zen-panel p-3 space-y-1" data-testid="zpl-palette">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Veri Alanları</div>
                {ZPL_FIELD_TYPES.filter((t) => t.isText).map((t) => (
                  <button key={t.key} onClick={() => addElement(t.key)} data-testid={`zpl-add-${t.key}`}
                    className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold text-left bg-slate-50 dark:bg-slate-800 hover:bg-[#E5322A] hover:text-white transition-colors">
                    <PlusCircle className="w-3 h-3 shrink-0" /><span className="truncate">{t.l}</span>
                  </button>
                ))}
                <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-2 mb-0.5">Şekil & Özel</div>
                {ZPL_FIELD_TYPES.filter((t) => !t.isText).map((t) => (
                  <button key={t.key} onClick={() => addElement(t.key)} data-testid={`zpl-add-${t.key}`}
                    className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold text-left bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-500 hover:text-white transition-colors">
                    <PlusCircle className="w-3 h-3 shrink-0" /><span className="truncate">{t.l}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Inspector sekmesi */}
            {leftTab === "inspector" && (
              <div className="zen-panel p-3 space-y-2.5" data-testid="zpl-inspector">
                <div className="flex items-center gap-2 mb-1">
                  <Info className="w-3.5 h-3.5 text-sky-500" />
                  <div className="text-[11px] font-bold">Element Özellikleri</div>
                </div>
                {!selected ? (
                  <div className="text-[11px] text-slate-500 py-4 text-center">Önizlemede bir element seçin</div>
                ) : (
                  <>
                    {(selectedDef?.isText || selectedDef?.isBarcode) && (
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Veri Kaynağı</label>
                        <select value={selected.type}
                          onChange={(e) => updateSelected({ type: e.target.value, text: ZPL_FIELD_TYPES.find(t => t.key === e.target.value)?.fill || selected.text })}
                          className="w-full px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs"
                          data-testid="zpl-inspector-field-type">
                          {TEXT_FIELD_OPTIONS.map((t) => (<option key={t.key} value={t.key}>{t.l}</option>))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] text-slate-500">Tip: <span className="font-bold text-[#E5322A]">{selectedDef?.l || selected.type}</span></div>
                      {(selectedDef?.isText || selectedDef?.isBarcode) && (
                        <button onClick={() => updateSelected({ rotation: selected.rotation ? 0 : 1 })}
                          className={`text-[10px] px-2 py-0.5 rounded border font-semibold transition-colors ${selected.rotation ? "bg-indigo-500 text-white border-indigo-500" : "border-slate-300 text-slate-600 hover:bg-indigo-50"}`}
                          data-testid="zpl-inspector-rotation">{selected.rotation ? "↺ Dikey" : "→ Yatay"}</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">X (mm)</label>
                        <input type="number" min="0" max={preset.w} step="0.5" value={Math.round(selected.x_mm * 10) / 10}
                          onChange={(e) => updateSelected({ x_mm: parseFloat(e.target.value || 0) })}
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" data-testid="zpl-inspector-x" />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Y (mm)</label>
                        <input type="number" min="0" max={preset.h} step="0.5" value={Math.round(selected.y_mm * 10) / 10}
                          onChange={(e) => updateSelected({ y_mm: parseFloat(e.target.value || 0) })}
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" data-testid="zpl-inspector-y" />
                      </div>
                    </div>
                    {selected.type !== "hline" && selected.type !== "barcode" && selected.type !== "rect" && selected.type !== "image_logo" && (
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Font Boyutu (dot)</label>
                        <input type="number" min="10" max="120" step="2" value={selected.fontSize || 26}
                          onChange={(e) => updateSelected({ fontSize: parseInt(e.target.value || 26) })}
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" data-testid="zpl-inspector-fontsize" />
                      </div>
                    )}
                    {(selected.type === "rect" || selected.type === "barcode" || selected.type === "image_logo") && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Genişlik (mm)</label>
                          <input type="number" min="5" max={preset.w} step="1" value={Math.round((selected.widthMm || 40) * 10) / 10}
                            onChange={(e) => updateSelected({ widthMm: parseFloat(e.target.value || 40) })}
                            className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Yükseklik (mm)</label>
                          <input type="number" min="5" max={preset.h} step="1" value={Math.round((selected.heightMm || 20) * 10) / 10}
                            onChange={(e) => updateSelected({ heightMm: parseFloat(e.target.value || 20) })}
                            className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" />
                        </div>
                      </div>
                    )}
                    {selected.type === "hline" && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Genişlik (mm)</label>
                          <input type="number" min="5" max={preset.w} step="1" value={selected.widthMm || preset.w - 8}
                            onChange={(e) => updateSelected({ widthMm: parseFloat(e.target.value || 0) })}
                            className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Kalınlık (dot)</label>
                          <input type="number" min="1" max="20" value={selected.fontSize || 3}
                            onChange={(e) => updateSelected({ fontSize: parseInt(e.target.value || 3) })}
                            className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" />
                        </div>
                      </div>
                    )}
                    {selected.type === "rect" && (
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Kenar Kalınlığı (dot)</label>
                        <input type="number" min="1" max="20" value={selected.borderThick || 3}
                          onChange={(e) => updateSelected({ borderThick: parseInt(e.target.value || 3) })}
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" />
                      </div>
                    )}
                    {selected.type === "static_text" && (
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Metin İçeriği</label>
                        <input type="text" value={selected.text || ""} onChange={(e) => updateSelected({ text: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" data-testid="zpl-inspector-text" />
                      </div>
                    )}
                    {selectedDef?.isText && selected.type !== "static_text" && (
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Önek (opsiyonel)</label>
                        <input type="text" value={selected.text || ""} onChange={(e) => updateSelected({ text: e.target.value })}
                          placeholder="Ör: 'Sipariş: '" className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" />
                      </div>
                    )}
                    <button onClick={deleteSelected}
                      className="w-full mt-1 px-2 py-1.5 rounded text-[11px] font-bold bg-rose-500/15 text-rose-500 border border-rose-500/30 hover:bg-rose-500/25"
                      data-testid="zpl-inspector-delete">
                      <Trash2 className="w-3 h-3 inline-block mr-1" /> Sil
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Kod sekmesi: Oluşturulan ZPL */}
            {leftTab === "kod" && (
              <div className="zen-panel p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">ZPL Kodu</div>
                  <button onClick={() => { navigator.clipboard?.writeText(zplCode); toast.success("Kopyalandı"); }}
                    className="zen-btn-ghost !text-[10px] !py-0.5">
                    <Copy className="w-3 h-3" /> Kopyala
                  </button>
                </div>
                <textarea readOnly value={zplCode}
                  className="w-full font-mono text-[10px] bg-slate-950 text-green-400 p-2 rounded resize-none leading-relaxed"
                  style={{ minHeight: "360px" }} spellCheck={false} />
                <p className="text-[9px] text-slate-400">Demo sipariş verileriyle üretilmiştir. "Test Yazdır" butonu gerçek Zebra'ya gönderir.</p>
              </div>
            )}
          </div>

          {/* ── SAĞ PANEL: Büyük Beyaz Önizleme ── */}
          <div ref={wrapRef} className="flex-1 zen-panel p-4 flex flex-col gap-3" data-testid="zpl-canvas-wrapper">
            {/* Önizleme araç çubuğu */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Önizleme</span>
              <span className="text-[10px] font-mono text-slate-400">{preset.w}×{preset.h}mm</span>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setShowGrid(v => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-semibold border transition-colors ${showGrid ? "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-400" : "border-slate-300 dark:border-slate-600 text-slate-500 hover:border-indigo-400"}`}
                  data-testid="zpl-grid-toggle">
                  <Grid className="w-3.5 h-3.5" /> {showGrid ? "Grid Kapat" : "mm Grid"}
                </button>
                <span className="text-[10px] text-slate-400">{elements.length} element</span>
              </div>
            </div>
            {/* Canvas */}
            <div className="flex justify-center">
              <div ref={canvasRef} onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
                className="relative"
                style={{
                  width: canvasW,
                  height: canvasH,
                  background: '#ffffff',
                  colorScheme: 'light',
                  backgroundImage: showGrid
                    ? "linear-gradient(rgba(99,102,241,0.12) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,0.12) 1px,transparent 1px)"
                    : "none",
                  backgroundSize: showGrid ? `${canvasScale * 1}px ${canvasScale * 1}px` : undefined,
                  border: "1.5px solid #cbd5e1",
                  boxShadow: "0 4px 32px rgba(0,0,0,0.22), 0 1px 4px rgba(0,0,0,0.08)",
                }}
                data-testid="zpl-canvas">
                {/* 5mm ana ızgara */}
                {showGrid && (
                  <div style={{
                    position: "absolute", inset: 0, pointerEvents: "none",
                    backgroundImage: "linear-gradient(rgba(99,102,241,0.25) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,0.25) 1px,transparent 1px)",
                    backgroundSize: `${canvasScale * 5}px ${canvasScale * 5}px`,
                  }} />
                )}
                {elements.map(renderCanvasEl)}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 text-center">
              {showGrid ? "Grid: 1mm ince · 5mm koyu" : "Grid kapalı"} · 203 dpi = 8 dot/mm · Sürükleyerek yerleştirin
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
// ============ SETTINGS ============
function SettingsModule() {
  const { user } = useContext(AuthCtx);
  const { zenThemeId, setZenThemeId, customThemes, saveCustomTheme, deleteCustomTheme, animationsEnabled, setAnimationsEnabled, cursorId, setCursorId, clockStyle, setClockStyle, tr } = useContext(UICtx);

  // ── Bölüm → Ana Sekme Eşlemesi ────────────────────────────────
  const TAB_TO_MAIN = {
    payments: "finans", currency_kdv: "finans", banka_bilgileri: "finans", payment_methods: "finans", bayi_yonetimi: "finans", sales_statuses: "finans",
    ai_engine: "ai_botlar", patch_center: "ai_botlar", api_keys: "ai_botlar", ai_contacts: "ai_botlar", bots_hub: "ai_botlar", bot_logs: "ai_botlar", whatsapp: "ai_botlar", whatsapp_security: "ai_botlar", admin_chat: "ai_botlar", usage_monitor: "ai_botlar",
    kartela: "urun_form", form_editor: "urun_form", motor_prices: "urun_form", zip_pricing: "urun_form", rustik_prices: "urun_form", warehouses: "urun_form", cargo_companies: "urun_form", units: "urun_form",
    printers: "belge_baski", pdf_sablonlar: "belge_baski", email_templates: "belge_baski", email_connect: "belge_baski", send_logs: "belge_baski",
    general: "sistem", appearance: "sistem", locale: "sistem", notifications: "sistem", users_roles: "sistem", two_factor: "sistem", backup: "sistem", system_cleanup: "sistem", external_connections: "sistem", system_update: "sistem", db_backup: "sistem", bayiloji_entegrasyon: "sistem",
  };

  const [mainTab, setMainTab] = useState("ai_botlar");
  const [tab, setTab] = useState(null); // null = kart grid görünümü
  const [searchQuery, setSearchQuery] = useState("");

  const navigateTo = useCallback((sectionId) => {
    const mt = TAB_TO_MAIN[sectionId];
    if (mt) setMainTab(mt);
    setTab(sectionId);
    setSearchQuery("");
  }, []);

  // Harici tetikleyiciler (Dashboard badge, Sidebar vb.)
  useEffect(() => {
    const init = window.__zenSettingsInit;
    if (init && init.tab) {
      const mt = TAB_TO_MAIN[init.tab];
      if (mt) setMainTab(mt);
      setTab(init.tab);
      window.__zenSettingsInit = { innerTab: init.innerTab };
      setTimeout(() => { window.__zenSettingsInit = null; }, 500);
    }
  }, []);

  useEffect(() => {
    const handleZenNav = () => {
      const init = window.__zenSettingsInit;
      if (init && init.tab) {
        const mt = TAB_TO_MAIN[init.tab];
        if (mt) setMainTab(mt);
        setTab(init.tab);
        window.__zenSettingsInit = null;
      }
    };
    window.addEventListener("zenSettingsNav", handleZenNav);
    return () => window.removeEventListener("zenSettingsNav", handleZenNav);
  }, []);

  // ── Ana Sekmeler ───────────────────────────────────────────────
  const MAIN_TABS = [
    { id: "finans",     label: "Finans & Banka",      emoji: "💰", color: "#34d399" },
    { id: "ai_botlar",  label: "Yapay Zeka & Botlar",  emoji: "🤖", color: "#8b5cf6" },
    { id: "urun_form",  label: "Ürün & Form",          emoji: "🏗️", color: "#0ea5e9" },
    { id: "belge_baski",label: "Belge & Baskı",        emoji: "📄", color: "#f59e0b" },
    { id: "sistem",     label: "Sistem & Genel",       emoji: "⚙️", color: "#64748b" },
  ];

  // ── Bölümler (açıklama + ana sekme ataması ile) ────────────────
  const SECTIONS = [
    { id: "payments",        label: tr("Ödeme Ayarları"),        icon: CreditCard,   mainTab: "finans",     color: "#34d399", desc: "iyzico kart ödeme entegrasyonu ve ödeme akışları",        roles: ["admin"] },
    { id: "currency_kdv",    label: tr("Döviz & KDV"),           icon: DollarSign,   mainTab: "finans",     color: "#34d399", desc: "Güncel döviz kurları ve KDV oranı ayarları" },
    { id: "banka_bilgileri", label: tr("Banka Hesapları"),       icon: Building2,    mainTab: "finans",     color: "#34d399", desc: "QNB ve diğer banka IBAN bilgileri",                        roles: ["admin"] },
    { id: "payment_methods", label: tr("Ödeme Yöntemleri"),      icon: CreditCard,   mainTab: "finans",     color: "#34d399", desc: "Havale, nakit, kart ödeme seçenekleri",                    roles: ["admin"] },
    { id: "bayi_yonetimi",   label: tr("Bayi Yönetimi"),         icon: Users,        mainTab: "finans",     color: "#34d399", desc: "Bayi fiyat grupları ve iskonto matrisleri" },
    { id: "sales_statuses",  label: tr("Satış Durumları"),       icon: CheckCircle2, mainTab: "finans",     color: "#34d399", desc: "Teklif → Onaylı → Üretimde durum etiketleri" },
    { id: "ai_engine",       label: tr("AI Motoru"),             icon: Brain,        mainTab: "ai_botlar",  color: "#8b5cf6", desc: "MiMo tek zeka motoru ve API anahtarı" },
    { id: "patch_center",    label: tr("Yama Merkezi"),          icon: Wrench,       mainTab: "ai_botlar",  color: "#f59e0b", desc: "MiMo ile program düzeltme: yama öner, uygula, geri al" },
    { id: "api_keys",        label: tr("API Anahtarları"),       icon: Sparkles,     mainTab: "ai_botlar",  color: "#8b5cf6", desc: "Harici entegrasyon API anahtarları",                       roles: ["admin"] },
    { id: "ai_contacts",     label: tr("AI Numaraları"),         icon: Phone,        mainTab: "ai_botlar",  color: "#8b5cf6", desc: "WhatsApp bot numaraları ve AI kişiler",                    roles: ["admin"] },
    { id: "bots_hub",        label: tr("Bot & Haberleşme"),      icon: Bot,          mainTab: "ai_botlar",  color: "#8b5cf6", desc: "Telegram ve WhatsApp bot bağlantı yönetimi",               roles: ["admin"] },
    { id: "bot_logs",        label: tr("Bot Logları"),           icon: History,      mainTab: "ai_botlar",  color: "#8b5cf6", desc: "Bot mesaj geçmişi ve hata kayıtları",                      roles: ["admin"] },
    { id: "whatsapp",        label: tr("WhatsApp Teklif Metni"), icon: Send,         mainTab: "ai_botlar",  color: "#8b5cf6", desc: "Müşteriye gönderilen teklif mesaj şablonu" },
    { id: "quote_reminders", label: tr("Teklif Hatırlatıcı"),   icon: Bell,         mainTab: "ai_botlar",  color: "#25D366", desc: "Açık teklifler için otomatik WhatsApp hatırlatması" },
    { id: "whatsapp_security",label: tr("WhatsApp Güvenlik"),    icon: Shield,       mainTab: "ai_botlar",  color: "#8b5cf6", desc: "WhatsApp bot güvenlik kısıtlamaları" },
    { id: "admin_chat",      label: tr("Yönetici AI Chat"),      icon: Sparkles,     mainTab: "ai_botlar",  color: "#8b5cf6", desc: "Zen ERP Wingman yönetici AI sohbeti",                      roles: ["admin"] },
    { id: "usage_monitor",   label: tr("Kredi & Kullanım"),      icon: BarChart2,    mainTab: "ai_botlar",  color: "#8b5cf6", desc: "API token ve kredi kullanım takibi",                       roles: ["admin"] },
    { id: "kartela",         label: tr("Kartela Yönetimi"),      icon: Grid,         mainTab: "urun_form",  color: "#0ea5e9", desc: "Kumaş ve ürün kartela görsel yönetimi" },
    ...(user?.role === "admin" ? [
      { id: "form_editor",   label: tr("Form Editörü"),          icon: FileSignature,mainTab: "urun_form",  color: "#0ea5e9", desc: "Sipariş formu alanlarını özelleştir ve yönet" },
    ] : []),
    { id: "motor_prices",    label: tr("Motor Alış Fiyatları"),  icon: DollarSign,   mainTab: "urun_form",  color: "#0ea5e9", desc: "Perde motor alış fiyat matrisleri",                        roles: ["admin"] },
    { id: "somfy_prices",    label: tr("Somfy Aksesuar Fiyatları"), icon: Package,   mainTab: "urun_form",  color: "#e5322a", desc: "Somfy sensör ve kumanda alış/satış fiyatları (ZIP)",          roles: ["admin"] },
    { id: "zip_pricing",     label: tr("Zip Fiyatlandırma"),     icon: Zap,          mainTab: "urun_form",  color: "#f59e0b", desc: "Zip ürünleri için motor ek/çıkarma ve iskonto kuralları",   roles: ["admin"] },
    { id: "rustik_prices",   label: tr("Rustik Fiyat Listesi"), icon: DollarSign,   mainTab: "urun_form",  color: "#a16207", desc: "Kanallı, Ahşap ve Antik rustik alış/satış fiyat cetvelleri", roles: ["admin"] },
    { id: "warehouses",      label: tr("Depo Konumları"),        icon: Boxes,        mainTab: "urun_form",  color: "#0ea5e9", desc: "Depo lokasyonları ve adres bilgileri" },
    { id: "cargo_companies", label: tr("Kargo Firmaları"),       icon: Truck,        mainTab: "urun_form",  color: "#0ea5e9", desc: "Kargo firma isim ve telefon bilgileri" },
    { id: "units",           label: tr("Ölçü Birimleri"),        icon: Ruler,        mainTab: "urun_form",  color: "#0ea5e9", desc: "m², m, adet gibi ölçü birimi tanımları" },
    { id: "printers",        label: tr("Yazıcılar & Şablonlar"), icon: Printer,      mainTab: "belge_baski",color: "#f59e0b", desc: "JSPM, ZPL etiket ve Evrensel Şablon havuzu" },
    { id: "pdf_sablonlar",   label: tr("PDF Şablonları"),        icon: FileDown,     mainTab: "belge_baski",color: "#f59e0b", desc: "Teklif ve fatura PDF çıktı şablonları" },
    { id: "email_templates", label: tr("E-posta Şablonları"),    icon: Send,         mainTab: "belge_baski",color: "#f59e0b", desc: "Müşteriye gönderilen e-posta içerik şablonları" },
    { id: "email_connect",   label: tr("E-posta Bağlantısı"),    icon: Mail,         mainTab: "belge_baski",color: "#f59e0b", desc: "SMTP e-posta sunucusu bağlantı ayarları",                  roles: ["admin"] },
    { id: "send_logs",       label: tr("Gönderim Geçmişi"),      icon: History,      mainTab: "belge_baski",color: "#f59e0b", desc: "E-posta ve WhatsApp gönderim kayıtları",                   roles: ["admin"] },
    { id: "general",         label: tr("Genel & Firma"),         icon: Home,         mainTab: "sistem",     color: "#64748b", desc: "Firma adı, logo ve genel sistem bilgileri" },
    { id: "appearance",      label: tr("Görünüm"),               icon: Layers,       mainTab: "sistem",     color: "#64748b", desc: "Tema, renk paleti ve animasyon tercihleri" },
    { id: "locale",          label: tr("Dil & Bölge"),           icon: Globe,        mainTab: "sistem",     color: "#64748b", desc: "Dil, para birimi ve tarih formatı ayarları" },
    { id: "notifications",   label: tr("Bildirim Ayarları"),     icon: Bell,         mainTab: "sistem",     color: "#64748b", desc: "Push bildirim ve alarm tercihleri" },
    { id: "users_roles",     label: tr("Kullanıcılar & Roller"), icon: Users,        mainTab: "sistem",     color: "#64748b", desc: "Kullanıcı hesapları ve yetki rol yönetimi" },
    { id: "two_factor",      label: tr("2 Adımlı Doğrulama"),    icon: Shield,       mainTab: "sistem",     color: "#64748b", desc: "Admin girişi için Authenticator / e-posta kodu",           roles: ["admin"] },
    { id: "backup",          label: tr("Yedek / Geri Yükle"),    icon: FileDown,     mainTab: "sistem",     color: "#64748b", desc: "Veri yedekleme ve geri yükleme işlemleri",                 roles: ["admin"] },
    { id: "external_connections", label: tr("Dış Bağlantılar"), icon: Globe, mainTab: "sistem", color: "#64748b", desc: "API, ödeme, bildirim ve konum servis adresleri", roles: ["admin"] },
    { id: "system_update", label: tr("Sistem Güncelleme"), icon: Upload, mainTab: "sistem", color: "#64748b", desc: "ZIP paketiyle güvenli kod güncelleme", roles: ["admin"] },
    { id: "db_backup", label: tr("Veritabanı Yedeği"), icon: Download, mainTab: "sistem", color: "#0ea5e9", desc: "Haftalık otomatik yedek — WhatsApp/e-posta", roles: ["admin"] },
    { id: "system_cleanup",  label: tr("Sistem Temizliği"),      icon: Trash2,       mainTab: "sistem",     color: "#64748b", desc: "Geçici veri temizleme ve optimizasyon",                    roles: ["admin"] },
    { id: "bayiloji_entegrasyon", label: tr("Bayiloji Entegrasyonu"), icon: History, mainTab: "sistem", color: "#f59e0b", desc: "Bayiloji geçmiş verilerini ZEN ERP ana listelerine taşı", roles: ["admin"] },
  ].filter(s => !s.roles || s.roles.includes(user?.role || "admin"));

  // ── Arama filtresi ─────────────────────────────────────────────
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return SECTIONS.filter(s =>
      s.label.toLowerCase().includes(q) ||
      (s.desc || "").toLowerCase().includes(q)
    );
  }, [searchQuery, SECTIONS]);

  const visibleSections = searchQuery.trim()
    ? searchResults
    : SECTIONS.filter(s => s.mainTab === mainTab);

  const activeSection = SECTIONS.find(s => s.id === tab);

  // ── İçerik render ─────────────────────────────────────────────
  const renderContent = () => {
    if (tab === "general") return <div className="space-y-4"><CompanyInfoSettings /><StockTrackingToggle /><SoundToggleSettings /></div>;
    if (tab === "kartela") return <KartelaModule />;
    if (tab === "whatsapp") return <WhatsappTemplateSettings tr={tr} />;
    if (tab === "quote_reminders") return <QuoteReminderSettings tr={tr} />;
    if (tab === "whatsapp_security") return <WhatsappSecuritySettings tr={tr} />;
    if (tab === "payments") return <PaymentSettings />;
    if (tab === "appearance") return <AppearanceSettings zenThemeId={zenThemeId} setZenThemeId={setZenThemeId} customThemes={customThemes} saveCustomTheme={saveCustomTheme} deleteCustomTheme={deleteCustomTheme} animationsEnabled={animationsEnabled} setAnimationsEnabled={setAnimationsEnabled} cursorId={cursorId} setCursorId={setCursorId} clockStyle={clockStyle} setClockStyle={setClockStyle} tr={tr} />;
    if (tab === "ai_engine") return <div className="space-y-4"><AiEngineSettings /></div>;
    if (tab === "patch_center") return <div className="space-y-4"><PatchCenterSettings /></div>;
    if (tab === "api_keys") return <div className="space-y-4"><SettingsCategoryEditor category="api_keys" label={SECTIONS.find(s => s.id === "api_keys")?.label} /></div>;
    if (tab === "currency_kdv") return <DovizKdvSettings />;
    if (tab === "motor_prices") return <MotorPricesSection />;
    if (tab === "somfy_prices") return <SomfyAksesuarSection />;
    if (tab === "zip_pricing") return <ZipPricingSettings />;
    if (tab === "rustik_prices") return <RustikPriceListSettings />;
    if (tab === "bayi_yonetimi") return <BayiYonetimiSettings />;
    if (tab === "pdf_sablonlar") return <PdfSablonSettings />;
    if (tab === "banka_bilgileri") return <BankHesaplariSettings />;
    if (tab === "printers") return <WriticiAyarlariSettings />;
    if (tab === "ai_contacts") return <AiContactsSettings />;
    if (tab === "bots_hub") return <BotsHubSettings />;
    if (tab === "bot_logs") return <BotLogsModule />;
    if (tab === "usage_monitor") return <UsageMonitorSettings />;
    if (tab === "backup") return <BackupSettings />;
    if (tab === "external_connections") return <ExternalConnectionsSettings />;
    if (tab === "system_update") return <SystemUpdateSettings />;
    if (tab === "db_backup") return <DbBackupSettings />;
    if (tab === "admin_chat") return <AdminChatSettings />;
    if (tab === "system_cleanup") return <SystemCleanupSettings />;
    if (tab === "bayiloji_entegrasyon") return <BayilojiEntegrasyonSettings />;
    if (tab === "locale") return <LocaleSettings />;
    if (tab === "notifications") return <NotificationsSettings />;
    if (tab === "sales_statuses") return <SalesStatusesSettings />;
    if (tab === "warehouses") return <SimpleListSettings category="warehouses" title="Depo Konumları" icon="Boxes" fields={[{k:"key",l:"Kod",ph:"IST-ANA"},{k:"value",l:"Depo Adı",ph:"İstanbul Ana Depo"},{k:"label",l:"Adres",ph:"Bağcılar, İstanbul",opt:true}]} />;
    if (tab === "payment_methods") return <SimpleListSettings category="payment_methods" title="Ödeme Yöntemleri" icon="CreditCard" fields={[{k:"key",l:"Kod",ph:"havale"},{k:"value",l:"Görünen Ad",ph:"Banka Havalesi"},{k:"label",l:"Açıklama",ph:"EFT / Havale",opt:true}]} />;
    if (tab === "cargo_companies") return <SimpleListSettings category="cargo_companies" title="Kargo Firmaları" icon="Truck" fields={[{k:"key",l:"Kod",ph:"aras"},{k:"value",l:"Firma Adı",ph:"Aras Kargo"},{k:"label",l:"Telefon",ph:"+90 212 444 0000",opt:true}]} />;
    if (tab === "units") return <SimpleListSettings category="units" title="Ölçü Birimleri" icon="Ruler" fields={[{k:"key",l:"Kısa Kod",ph:"m2"},{k:"value",l:"Tam Ad",ph:"Metrekare"},{k:"label",l:"Açıklama",ph:"opsiyonel",opt:true}]} />;
    if (tab === "email_templates") return <EmailTemplatesSettings />;
    if (tab === "email_connect") return <EmailConnectionSettings />;
    if (tab === "send_logs") return <SendLogsSettings />;
    if (tab === "users_roles") return <UsersRolesSettings />;
    if (tab === "two_factor") return <TwoFactorSettings />;
    if (tab === "form_editor") return <FormEditorPage />;
    return <SettingsCategoryEditor category={tab} label={SECTIONS.find(s => s.id === tab)?.label} />;
  };

  return (
    <div className="space-y-0 zen-anim-up">

      {/* ── Başlık + Arama ──────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-3 flex-1">
          <ModuleIcon tab="settings" icon={Settings} />
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">{tr("Ayarlar")}</h2>
            {tab && activeSection && (
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                <span>{MAIN_TABS.find(m => m.id === activeSection.mainTab)?.emoji}</span>
                <span>{MAIN_TABS.find(m => m.id === activeSection.mainTab)?.label}</span>
                <ChevronRight className="w-3 h-3" />
                <span style={{ color: activeSection.color }}>{activeSection.label}</span>
              </div>
            )}
          </div>
        </div>
        {/* Arama kutusu */}
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <input
            data-testid="settings-search-input"
            type="text"
            placeholder="Ayar ara... (Gemini, PDF, Kargo...)"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); if (tab) setTab(null); }}
            className="zen-input !pl-9 !pr-8 !py-2 w-full !text-sm"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ── Ana Sekme Pilleri ──────────────────────────────────────────── */}
      {!searchQuery && (
        <div className="flex gap-1.5 flex-wrap mb-4">
          {MAIN_TABS.map(t => (
            <button
              key={t.id}
              data-testid={`settings-main-tab-${t.id}`}
              onClick={() => { setMainTab(t.id); setTab(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
                mainTab === t.id && !tab
                  ? "text-white border-white/15 bg-white/10"
                  : "text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/4"
              }`}
              style={mainTab === t.id && !tab ? { borderColor: `${t.color}40`, background: `${t.color}15`, color: t.color } : {}}
            >
              <span>{t.emoji}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Arama Sonuçları Banner ─────────────────────────────────────── */}
      {searchQuery && (
        <div className="mb-4 flex items-center gap-2 text-sm">
          <Search className="w-4 h-4 text-slate-500" />
          <span className="text-slate-600 dark:text-slate-400"><span className="text-slate-900 dark:text-white font-bold">{searchResults.length}</span> sonuç bulundu</span>
          <button onClick={() => setSearchQuery("")} className="ml-auto text-xs text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors">Temizle</button>
        </div>
      )}

      {/* ── Kart Grid VEYA Bölüm İçeriği ──────────────────────────────── */}
      {tab === null ? (
        /* KART GRİD */
        <div>
          {visibleSections.length === 0 && searchQuery && (
            <div className="text-center py-16 text-slate-500">
              "<span className="text-slate-700 dark:text-slate-300 font-semibold">{searchQuery}</span>" ile eşleşen ayar bulunamadı.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {(searchQuery ? searchResults : visibleSections).map(s => {
              const Icon = s.icon;
              const col = s.color || "#64748b";
              const targetMT = MAIN_TABS.find(m => m.id === s.mainTab);
              return (
                <button
                  key={s.id}
                  data-testid={`settings-card-${s.id}`}
                  onClick={() => navigateTo(s.id)}
                  className="group text-left p-5 rounded-2xl border transition-all duration-200 relative overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${col}0e, ${col}04)`,
                    borderColor: `${col}25`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = `${col}55`; e.currentTarget.style.background = `linear-gradient(135deg, ${col}18, ${col}08)`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = `${col}25`; e.currentTarget.style.background = `linear-gradient(135deg, ${col}0e, ${col}04)`; }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2.5 rounded-xl transition-colors" style={{ background: `${col}18` }}>
                      <Icon className="w-5 h-5" style={{ color: col }} />
                    </div>
                    {/* Info tooltip */}
                    {s.desc && (
                      <div className="relative group/tip" onClick={e => e.stopPropagation()}>
                        <div className="w-5 h-5 rounded-full border border-slate-300 dark:border-white/15 text-[10px] text-slate-500 dark:text-slate-600 flex items-center justify-center hover:border-slate-400 dark:hover:border-white/30 hover:text-slate-700 dark:hover:text-slate-300 transition-colors cursor-default select-none">i</div>
                        <div className="absolute right-0 top-6 w-52 p-3 rounded-xl border border-white/10 bg-[#0d1525]/97 backdrop-blur-xl text-[11px] text-slate-400 leading-relaxed z-50 opacity-0 pointer-events-none group-hover/tip:opacity-100 group-hover/tip:pointer-events-auto transition-opacity shadow-2xl">
                          {s.desc}
                        </div>
                      </div>
                    )}
                  </div>
                  {searchQuery && targetMT && (
                    <div className="text-[10px] font-semibold mb-1" style={{ color: `${col}99` }}>{targetMT.emoji} {targetMT.label}</div>
                  )}
                  <div className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">{s.label}</div>
                  {s.desc && <div className="text-[11px] text-slate-600 dark:text-slate-500 leading-relaxed line-clamp-2">{s.desc}</div>}
                  <ChevronRight className="absolute bottom-4 right-4 w-4 h-4 opacity-0 group-hover:opacity-40 transition-opacity" style={{ color: col }} />
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        /* BÖLÜM İÇERİĞİ */
        <div>
          {/* Geri butonu */}
          <div className="flex items-center gap-2 mb-4">
            <button
              data-testid={`settings-back-btn`}
              onClick={() => setTab(null)}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors group"
            >
              <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              Ayarlar Anasayfası
            </button>
            {activeSection && (
              <>
                <ChevronRight className="w-3 h-3 text-slate-700" />
                <span className="text-xs font-semibold" style={{ color: activeSection.color }}>{activeSection.label}</span>
              </>
            )}
          </div>
          {/* Alt sekme navigasyonu (sadece ilgili bölümde) */}
          <div className="min-w-0">
            {renderContent()}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ayarlar: Yeniden Kullanılabilir Liste Editörü (Kargo, Ödeme, Depo, Birim) ───────
function SimpleListSettings({ category, title, icon: iconName, fields }) {
  const Icon = { Boxes, CreditCard, Truck, Ruler, Bell, Globe }[iconName] || Boxes;
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings", { params: { category } }).then(({ data }) => setRows(data || []));
  useEffect(() => { load(); }, [category]);

  const handleAdd = async () => {
    const key = (form.key || "").trim();
    const value = (form.value || "").trim();
    if (!key || !value) { toast.error("Kod ve ad zorunludur"); return; }
    setSaving(true);
    try {
      await api.post("/settings", { category, key, value, label: form.label || "" });
      setForm({}); load();
    } finally { setSaving(false); }
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await api.put(`/settings/${id}`, { category, key: editForm.key, value: editForm.value, label: editForm.label || "" });
      setEditId(null); load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/settings/${id}`); load();
  };

  return (
    <div className="zen-panel p-6 space-y-4 text-slate-900 dark:text-slate-100">
      <h3 className="text-base font-bold flex items-center gap-2">
        <Icon className="w-4 h-4 text-sky-500" /> {title}
      </h3>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${fields.length}, 1fr) auto` }}>
        {fields.map(f => (
          <div key={f.k}>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">
              {f.l}{f.opt && <span className="text-slate-400 normal-case font-normal"> (opsiyonel)</span>}
            </label>
            <input className="zen-input w-full" placeholder={f.ph} value={form[f.k] || ""}
              onChange={e => setForm(v => ({ ...v, [f.k]: e.target.value }))} />
          </div>
        ))}
        <div className="flex items-end">
          <button onClick={handleAdd} disabled={saving} className="zen-btn-primary w-full flex items-center justify-center gap-1">
            <Plus className="w-4 h-4" /> Ekle
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">Henüz kayıt yok</p>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-[#22304A]">
          {rows.map(r => (
            <div key={r.id} className="py-2.5 flex items-center gap-3">
              {editId === r.id ? (
                <>
                  {fields.map(f => (
                    <input key={f.k} className="zen-input flex-1 text-xs" placeholder={f.ph}
                      value={editForm[f.k] || ""} onChange={e => setEditForm(v => ({ ...v, [f.k]: e.target.value }))} />
                  ))}
                  <button onClick={() => handleSave(r.id)} className="zen-btn-primary !py-1 !px-3 !text-xs flex items-center gap-1"><Check className="w-3 h-3" />Kaydet</button>
                  <button onClick={() => setEditId(null)} className="zen-btn-ghost !py-1 !px-3 !text-xs">İptal</button>
                </>
              ) : (
                <>
                  <span className="font-mono text-xs bg-slate-100 dark:bg-[#1A2640] px-2 py-0.5 rounded">{r.key}</span>
                  <span className="flex-1 text-sm font-medium truncate">{r.value}</span>
                  {r.label && <span className="text-xs text-slate-400 truncate hidden md:block">{r.label}</span>}
                  <button onClick={() => { setEditId(r.id); setEditForm({ key: r.key, value: r.value, label: r.label || "" }); }}
                    className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors p-1">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(r.id)} className="text-slate-400 hover:text-rose-500 transition-colors p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ayarlar: Satış Durumları ─────────────────────────────────────────────────
function SalesStatusesSettings() {
  const ALL_STATUS_KEYS = [
    { key: "teklif",        tr: "Teklif",                    default: "sky"     },
    { key: "onay_bekliyor", tr: "Onay Bekliyor",             default: "indigo"  },
    { key: "imalata_hazir", tr: "Onaylandı / İmalata Hazır", default: "emerald" },
    { key: "imalatta",      tr: "İmalatta",                  default: "amber"   },
    { key: "hazir",         tr: "Hazır",                     default: "violet"  },
    { key: "kargolanacak",  tr: "Kargoya Verilecek",         default: "orange"  },
    { key: "yollandi",      tr: "Yollandı",                  default: "emerald" },
    { key: "odendi",        tr: "Ödendi",                    default: "green"   },
    { key: "tamamlandi",    tr: "Tamamlandı",                default: "teal"    },
    { key: "iptal",         tr: "İptal",                     default: "rose"    },
  ];
  const COLOR_OPTIONS = [
    { name: "sky",     bg: "bg-sky-500",     label: "Mavi" },
    { name: "indigo",  bg: "bg-indigo-500",  label: "Lacivert" },
    { name: "emerald", bg: "bg-emerald-500", label: "Zümrüt" },
    { name: "teal",    bg: "bg-teal-500",    label: "Teal" },
    { name: "green",   bg: "bg-green-500",   label: "Yeşil" },
    { name: "amber",   bg: "bg-amber-500",   label: "Sarı" },
    { name: "orange",  bg: "bg-orange-500",  label: "Turuncu" },
    { name: "violet",  bg: "bg-violet-500",  label: "Mor" },
    { name: "rose",    bg: "bg-rose-500",    label: "Kırmızı" },
    { name: "slate",   bg: "bg-slate-500",   label: "Gri" },
  ];

  const [colors, setColors] = useState({}); // { statusKey: colorName }
  const [rowIds, setRowIds] = useState({});  // { statusKey: settingId }
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/settings", { params: { category: "order_statuses" } }).then(({ data }) => {
      const c = {}, ids = {};
      (data || []).forEach(r => {
        if (r.key.endsWith("_color")) {
          const k = r.key.replace("_color", "");
          c[k] = r.value;
          ids[k] = r.id;
        }
      });
      setColors(c);
      setRowIds(ids);
    }).catch(() => {});
  }, []);

  const saveColor = async (statusKey, colorName) => {
    setColors(prev => ({ ...prev, [statusKey]: colorName }));
    setSaving(statusKey);
    try {
      const settKey = `${statusKey}_color`;
      if (rowIds[statusKey]) {
        await api.put(`/settings/${rowIds[statusKey]}`, { category: "order_statuses", key: settKey, value: colorName, label: `${statusKey} rengi` });
      } else {
        const { data } = await api.post("/settings", { category: "order_statuses", key: settKey, value: colorName, label: `${statusKey} rengi` });
        setRowIds(prev => ({ ...prev, [statusKey]: data.id }));
      }
      toast.success("Renk kaydedildi");
    } catch { toast.error("Kaydedilemedi"); } finally { setSaving(null); }
  };

  const resetAll = async () => {
    setSaving("all");
    try {
      await Promise.all(ALL_STATUS_KEYS.map(s => saveColor(s.key, s.default)));
      toast.success("Tüm renkler sıfırlandı");
    } catch {} finally { setSaving(null); }
  };

  const DOT_BG = { sky:"bg-sky-500", indigo:"bg-indigo-500", emerald:"bg-emerald-500", teal:"bg-teal-500", green:"bg-green-500", amber:"bg-amber-500", orange:"bg-orange-500", violet:"bg-violet-500", rose:"bg-rose-500", slate:"bg-slate-500" };

  return (
    <div className="zen-panel p-6 space-y-5 text-slate-900 dark:text-slate-100" data-testid="sales-statuses-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Sipariş Durum Renkleri
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">Her durumun rengini tıklayarak değiştirin — siparişler listesine anında yansır</p>
        </div>
        <button onClick={resetAll} disabled={saving === "all"} className="zen-btn-ghost !text-xs !py-1">Varsayılana Sıfırla</button>
      </div>
      <div className="space-y-2">
        {ALL_STATUS_KEYS.map(s => {
          const currentColor = colors[s.key] || s.default;
          return (
            <div key={s.key} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#111827]" data-testid={`status-color-row-${s.key}`}>
              <span className={`w-3.5 h-3.5 rounded-full flex-shrink-0 ${DOT_BG[currentColor] || "bg-slate-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{s.tr}</div>
                <div className="text-[10px] font-mono text-slate-400">{s.key}</div>
              </div>
              <div className="flex gap-1 flex-wrap justify-end">
                {COLOR_OPTIONS.map(c => (
                  <button
                    key={c.name}
                    data-testid={`color-pick-${s.key}-${c.name}`}
                    title={c.label}
                    onClick={() => saveColor(s.key, c.name)}
                    disabled={saving === s.key}
                    className={`w-5 h-5 rounded-full transition-all ${c.bg}
                      ${currentColor === c.name ? "ring-2 ring-offset-1 ring-white dark:ring-offset-[#111827] scale-125" : "opacity-60 hover:opacity-100 hover:scale-110"}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Ayarlar: E-posta Şablonları (Ekstre / Teklif) ─────────────────────────
function EmailTemplatesSettings() {
  const TEMPLATE_KEYS = [
    { key: "statement_email", label: "Ekstre E-postası", vars: ["musteri_adi", "bakiye", "para_birimi", "firma_adi"] },
    { key: "quote_email",     label: "Teklif E-postası",  vars: ["musteri_adi", "teklif_no", "toplam", "para_birimi", "firma_adi"] },
  ];
  const [rowsById, setRowsById] = useState({});   // key -> { id, subject, body }
  const [forms, setForms] = useState({});         // key -> { subject, body }
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const { data } = await api.get("/settings", { params: { category: "email_templates" } });
    const byId = {}; const nextForms = {};
    TEMPLATE_KEYS.forEach(({ key }) => {
      const row = (data || []).find((r) => r.key === key);
      const val = row?.value?.subject ? row.value : DEFAULT_EMAIL_TEMPLATES[key];
      if (row) byId[key] = row.id;
      nextForms[key] = { subject: val.subject, body: val.body };
    });
    setRowsById(byId); setForms(nextForms);
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (key, label) => {
    setSaving(key);
    try {
      const payload = { category: "email_templates", key, label, value: forms[key] };
      if (rowsById[key]) await api.put(`/settings/${rowsById[key]}`, payload);
      else await api.post("/settings", payload);
      toast.success(`${label} kaydedildi`);
      await load();
    } catch (e) { toast.error("Kaydedilemedi"); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-4" data-testid="email-templates-panel">
      <div className="flex items-center gap-2">
        <Send className="w-5 h-5 text-[#E5322A]" />
        <h3 className="text-lg font-extrabold">E-posta Şablonları</h3>
      </div>
      <p className="text-xs text-slate-500 -mt-2">Ekstre ve teklif e-postalarının konu/metin şablonlarını buradan özelleştirebilirsiniz. Değişkenler gönderim sırasında otomatik doldurulur.</p>
      {TEMPLATE_KEYS.map(({ key, label, vars }) => (
        <div key={key} className="zen-panel p-4 space-y-2.5" data-testid={`email-template-${key}`}>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold">{label}</h4>
            <div className="flex gap-1 flex-wrap justify-end">
              {vars.map((v) => (
                <span key={v} className="zen-chip !text-[10px] bg-slate-500/10 text-slate-500 font-mono">{`{{${v}}}`}</span>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Konu</label>
            <input
              className="zen-input w-full text-sm"
              value={forms[key]?.subject || ""}
              onChange={(e) => setForms((f) => ({ ...f, [key]: { ...f[key], subject: e.target.value } }))}
              data-testid={`email-template-${key}-subject`}
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Mesaj Metni</label>
            <textarea
              rows={5}
              className="zen-input w-full text-xs font-mono"
              value={forms[key]?.body || ""}
              onChange={(e) => setForms((f) => ({ ...f, [key]: { ...f[key], body: e.target.value } }))}
              data-testid={`email-template-${key}-body`}
            />
          </div>
          <div className="flex justify-end">
            <button onClick={() => handleSave(key, label)} disabled={saving === key} className="zen-btn-primary !text-xs" data-testid={`email-template-${key}-save`}>
              <Check className="w-3.5 h-3.5" /> {saving === key ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Ayarlar: Gönderim Geçmişi (E-posta / WhatsApp log) ────────────────────
function SendLogsSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/send-logs").then(({ data }) => setRows(data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) =>
    !q || (r.refLabel || "").toLowerCase().includes(q.toLowerCase()) || (r.to || "").toLowerCase().includes(q.toLowerCase())
  );
  const typeLabel = { statement: "Ekstre", quote: "Teklif" };

  return (
    <div className="space-y-4" data-testid="send-logs-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Send className="w-5 h-5 text-[#E5322A]" />
          <h3 className="text-lg font-extrabold">Gönderim Geçmişi</h3>
        </div>
        <div className="flex items-center gap-2 zen-panel px-3">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Müşteri / alıcı ara..." className="bg-transparent outline-none text-sm py-2 w-56" data-testid="send-logs-search" />
        </div>
      </div>
      <div className="zen-panel overflow-hidden overflow-x-auto">
        <table className="zen-table">
          <thead><tr><th>Tarih</th><th>Tür</th><th>Alıcı</th><th>Kanal</th><th>Konu</th><th>Durum</th><th>Gönderen</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-slate-500 text-sm">Yükleniyor...</td></tr>}
            {!loading && filtered.map((r, i) => (
              <tr key={i} data-testid="send-log-row">
                <td className="text-xs whitespace-nowrap">{r.sentAt ? new Date(r.sentAt).toLocaleDateString("tr-TR") + " · " + new Date(r.sentAt).toLocaleTimeString("tr-TR") : "-"}</td>
                <td><span className="zen-chip bg-[#E5322A]/10 text-[#E5322A] !text-[10px]">{typeLabel[r.refType] || r.refType}</span></td>
                <td className="font-semibold text-sm">{r.refLabel || "-"}<div className="text-[10px] text-slate-400">{r.to}</div></td>
                <td>
                  {r.channel === "email"
                    ? <span className="inline-flex items-center gap-1 text-sky-500 text-xs"><Mail className="w-3.5 h-3.5" /> E-posta</span>
                    : <span className="inline-flex items-center gap-1 text-emerald-500 text-xs"><Send className="w-3.5 h-3.5" /> WhatsApp</span>}
                </td>
                <td className="text-xs text-slate-500 max-w-[220px] truncate" title={r.subject}>{r.subject || "-"}</td>
                <td>
                  {r.status === "sent"
                    ? <span className="zen-chip bg-emerald-500/10 text-emerald-500 !text-[10px]">Başarılı</span>
                    : <span className="zen-chip bg-rose-500/10 text-rose-500 !text-[10px]" title={r.error}>Başarısız</span>}
                </td>
                <td className="text-xs text-slate-400">{r.sentBy}</td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500 text-sm">Henüz gönderim kaydı yok</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Ayarlar: Kullanıcılar & Roller ─────────────────────────────────────────
function TwoFactorSettings() {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null); // {secret, qrCode}
  const [totpCode, setTotpCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/2fa/status").then(({ data }) => setStatus(data));
  useEffect(() => { load(); }, []);

  const startTotpSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup-totp"); setSetupData(data); }
    catch { toast.error("Kurulum başlatılamadı"); }
    setBusy(false);
  };

  const confirmTotp = async () => {
    if (!totpCode.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { method: "totp", code: totpCode.trim() });
      toast.success("Authenticator etkinleştirildi");
      setSetupData(null); setTotpCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kod hatalı"); }
    setBusy(false);
  };

  const sendEmailSetupCode = async () => {
    setBusy(true);
    try { await api.post("/auth/2fa/send-setup-email-code"); setEmailCodeSent(true); toast.success("Kod e-postanıza gönderildi"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
    setBusy(false);
  };

  const confirmEmail = async () => {
    if (!emailCode.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { method: "email", code: emailCode.trim() });
      toast.success("E-posta doğrulama etkinleştirildi");
      setEmailCode(""); setEmailCodeSent(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kod hatalı"); }
    setBusy(false);
  };

  const disable = async (method) => {
    if (!window.confirm("Bu doğrulama yöntemini kapatmak istediğinize emin misiniz?")) return;
    await api.post("/auth/2fa/disable", { method });
    toast.success("Kapatıldı"); load();
  };

  if (!status) return null;

  return (
    <div className="space-y-4">
      <div className="zen-panel p-5">
        <h3 className="font-bold text-sm mb-1">2 Adımlı Doğrulama</h3>
        <p className="text-[11px] text-slate-400">Sadece admin hesabınız için — girişte şifrenize ek olarak bir kod istenir. Her iki yöntemi de açabilirsiniz, girişte hangisini kullanacağınızı seçersiniz.</p>
      </div>

      {/* Authenticator */}
      <div className="zen-panel p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-sm">Authenticator Uygulaması</h4>
            <p className="text-[11px] text-slate-400">Google Authenticator / Authy ile QR kod okutarak kurulur</p>
          </div>
          {status.totpEnabled ? (
            <button data-testid="totp-disable-btn" onClick={() => disable("totp")} className="text-xs font-semibold text-rose-500 hover:underline">Kapat</button>
          ) : !setupData ? (
            <button data-testid="totp-setup-btn" onClick={startTotpSetup} disabled={busy} className="zen-btn-primary !text-xs !py-1.5">Kur</button>
          ) : null}
        </div>
        {status.totpEnabled && <div className="text-xs text-emerald-500 font-semibold">✓ Etkin</div>}
        {setupData && (
          <div className="flex flex-col sm:flex-row items-center gap-4 pt-2 border-t border-slate-200 dark:border-[#22304A]">
            <img src={setupData.qrCode} alt="QR" className="w-36 h-36 rounded-lg border border-slate-200 dark:border-[#22304A]" data-testid="totp-qr-code" />
            <div className="flex-1 space-y-2">
              <p className="text-[11px] text-slate-400">QR kodu Authenticator uygulamanızla okutun, sonra uygulamada görünen 6 haneli kodu girin.</p>
              <div className="flex gap-2">
                <input data-testid="totp-confirm-code" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                  className="zen-input !w-32 text-center font-mono tracking-widest" placeholder="000000" />
                <button data-testid="totp-confirm-btn" onClick={confirmTotp} disabled={busy} className="zen-btn-primary !text-xs">Etkinleştir</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* E-posta OTP */}
      <div className="zen-panel p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-sm">E-posta ile Doğrulama</h4>
            <p className="text-[11px] text-slate-400">Kod hesap e-postanıza ({status.email || "e-posta tanımlı değil"}) gönderilir</p>
          </div>
          {status.emailEnabled ? (
            <button data-testid="email-2fa-disable-btn" onClick={() => disable("email")} className="text-xs font-semibold text-rose-500 hover:underline">Kapat</button>
          ) : (
            <button data-testid="email-2fa-send-btn" onClick={sendEmailSetupCode} disabled={busy || !status.email} className="zen-btn-primary !text-xs !py-1.5">
              {emailCodeSent ? "Tekrar Gönder" : "Kod Gönder"}
            </button>
          )}
        </div>
        {status.emailEnabled && <div className="text-xs text-emerald-500 font-semibold">✓ Etkin</div>}
        {!status.emailEnabled && emailCodeSent && (
          <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-[#22304A]">
            <input data-testid="email-2fa-confirm-code" value={emailCode} onChange={e => setEmailCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
              className="zen-input !w-32 text-center font-mono tracking-widest" placeholder="000000" />
            <button data-testid="email-2fa-confirm-btn" onClick={confirmEmail} disabled={busy} className="zen-btn-primary !text-xs">Etkinleştir</button>
          </div>
        )}
      </div>
    </div>
  );
}

function UsersRolesSettings() {
  const ROLES = [
    { value: "admin",      label: "Yönetici", desc: "Tam erişim" },
    { value: "sales",      label: "Satış",  desc: "Sipariş, teklif, CRM" },
    { value: "production", label: "Üretim", desc: "Siparişler (görüntüle/durum)" },
    { value: "dealer",     label: "Bayi",   desc: "Fiyat sorgulama, sipariş, cari, teklif" },
  ];
  const roleColor = {
    admin:      "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    sales:      "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
    production: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    dealer:     "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  };
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [form, setForm] = useState({ username: "", email: "", name: "", role: "sales", password: "", phone: "", countryCode: "+90", customerId: "", allowedProductGroups: [] });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/auth/users").then(({ data }) => setUsers(data || []));
  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data || []));
    api.get("/product-groups").then(({ data }) => setProductGroups(data || []));
  }, []);

  const handleAdd = async () => {
    if (!form.username.trim() || !form.name.trim() || !form.password) { toast.error("Kullanıcı adı, ad ve şifre zorunludur"); return; }
    if (form.role === "dealer" && (!form.customerId || !form.phone.trim())) { toast.error("Bayi için bağlı cari ve telefon zorunludur"); return; }
    setSaving(true);
    try {
      await api.post("/auth/users", form);
      setForm({ username: "", email: "", name: "", role: "sales", password: "", phone: "", countryCode: "+90", customerId: "", allowedProductGroups: [] }); load(); toast.success("Kullanıcı eklendi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await api.put(`/auth/users/${id}`, editForm);
      setEditId(null); load(); toast.success("Güncellendi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kullanıcı silinsin mi?")) return;
    try { await api.delete(`/auth/users/${id}`); load(); toast.success("Silindi"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="space-y-4 text-slate-900 dark:text-slate-100">
      <div className="zen-panel p-4">
        <h3 className="text-base font-bold flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-sky-500" /> Kullanıcılar & Roller</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          {ROLES.map(r => (
            <div key={r.value} className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 ${roleColor[r.value]}`}>
              <Shield className="w-3.5 h-3.5" /> {r.label} <span className="font-normal opacity-70">— {r.desc}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="zen-panel p-4 space-y-3">
        <h4 className="text-sm font-bold">Yeni Kullanıcı Ekle</h4>
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Kullanıcı Adı <span className="text-rose-400">*</span></label>
            <input className="zen-input w-full" placeholder="kullanici_adi" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} data-testid="user-form-username" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Ad Soyad <span className="text-rose-400">*</span></label>
            <input className="zen-input w-full" placeholder="Ahmet Yılmaz" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">E-posta <span className="text-slate-400 font-normal">(şifre sıfırlama için, opsiyonel)</span></label>
            <input type="email" className="zen-input w-full" placeholder="kullanici@firma.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Şifre <span className="text-rose-400">*</span></label>
            <input type="password" className="zen-input w-full" placeholder="En az 6 karakter" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">{form.role === "dealer" ? "Telefon (Bayi Girişi)" : "Telefon (WhatsApp)"}</label>
            {form.role === "dealer" ? (
              <div className="flex gap-2">
                <select className="zen-input !w-24" value={form.countryCode} onChange={e => setForm(f => ({ ...f, countryCode: e.target.value }))}>
                  {["+90","+49","+31","+32","+33","+44","+971","+966","+1","+7"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input className="zen-input flex-1" placeholder="5xx xxx xx xx" value={form.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="user-form-phone" />
              </div>
            ) : (
              <input className="zen-input w-full" placeholder="+905321234567" value={form.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="user-form-phone" />
            )}
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Rol</label>
            <select className="zen-input w-full" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        {form.role === "dealer" && (
          <div className="grid sm:grid-cols-2 gap-2 pt-2 border-t border-slate-200 dark:border-[#22304A]">
            <div>
              <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Bağlı Cari <span className="text-rose-400">*</span></label>
              <select className="zen-input w-full" value={form.customerId} onChange={e => setForm(f => ({ ...f, customerId: e.target.value }))} data-testid="user-form-customer">
                <option value="">— Cari Seçin —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Görebileceği Ürün Grupları <span className="text-rose-400">* en az 1 seçin</span> <span className="text-slate-400 font-normal">— seçilmeyenler tamamen gizlenir</span></label>
              <div className="flex flex-wrap gap-1.5">
                {productGroups.map(g => {
                  const active = (form.allowedProductGroups || []).includes(g.slug);
                  return (
                    <button key={g.slug} type="button" data-testid={`user-form-group-${g.slug}`}
                      onClick={() => setForm(f => ({ ...f, allowedProductGroups: active ? f.allowedProductGroups.filter(s => s !== g.slug) : [...(f.allowedProductGroups || []), g.slug] }))}
                      className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${active ? "bg-teal-500 text-white border-teal-500" : "border-slate-200 dark:border-[#22304A] text-slate-500"}`}>
                      {g.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <button onClick={handleAdd} disabled={saving} className="zen-btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> {saving ? "Ekleniyor…" : "Kullanıcı Ekle"}
        </button>
      </div>
      <div className="zen-panel p-4 space-y-2">
        <h4 className="text-sm font-bold mb-3">Mevcut Kullanıcılar ({users.length})</h4>
        {users.map(u => (
          <div key={u.id} className="border border-slate-200 dark:border-[#22304A] rounded-xl overflow-hidden">
            {editId === u.id ? (
              <div className="p-3 space-y-2 bg-slate-50 dark:bg-[#111827]">
                <div className="grid sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Kullanıcı Adı</label>
                    <input className="zen-input w-full font-mono" value={editForm.username ?? u.username ?? ""} onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))} data-testid={`user-edit-username-${u.id}`} />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Ad Soyad</label>
                    <input className="zen-input w-full" value={editForm.name || ""} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">E-posta <span className="text-slate-400 font-normal">(şifre sıfırlama)</span></label>
                    <input type="email" className="zen-input w-full" placeholder="kullanici@firma.com" value={editForm.email ?? u.email ?? ""} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Rol</label>
                    <select className="zen-input w-full" value={editForm.role || u.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Yeni Şifre <span className="normal-case font-normal text-slate-400">(boş = değiştirme)</span></label>
                    <input type="password" className="zen-input w-full" placeholder="Boş bırakılırsa şifre değişmez" value={editForm.password || ""} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">{(editForm.role || u.role) === "dealer" ? "Telefon (Bayi Girişi)" : "Telefon (WhatsApp)"}</label>
                    {(editForm.role || u.role) === "dealer" ? (
                      <div className="flex gap-2">
                        <select className="zen-input !w-24" value={editForm.countryCode ?? u.countryCode ?? "+90"} onChange={e => setEditForm(f => ({ ...f, countryCode: e.target.value }))}>
                          {["+90","+49","+31","+32","+33","+44","+971","+966","+1","+7"].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input className="zen-input flex-1" value={editForm.phone ?? u.phone ?? ""} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} data-testid={`user-edit-phone-${u.id}`} />
                      </div>
                    ) : (
                      <input className="zen-input w-full" placeholder="+905321234567" value={editForm.phone ?? u.phone ?? ""} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} data-testid={`user-edit-phone-${u.id}`} />
                    )}
                  </div>
                </div>
                {(editForm.role || u.role) === "dealer" && (
                  <div className="grid sm:grid-cols-2 gap-2 pt-2 border-t border-slate-200 dark:border-[#22304A]">
                    <div>
                      <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Bağlı Cari</label>
                      <select className="zen-input w-full" value={editForm.customerId ?? u.customerId ?? ""} onChange={e => setEditForm(f => ({ ...f, customerId: e.target.value }))} data-testid={`user-edit-customer-${u.id}`}>
                        <option value="">— Cari Seçin —</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Görebileceği Ürün Grupları</label>
                      <div className="flex flex-wrap gap-1.5">
                        {productGroups.map(g => {
                          const cur = editForm.allowedProductGroups ?? u.allowedProductGroups ?? [];
                          const active = cur.includes(g.slug);
                          return (
                            <button key={g.slug} type="button" data-testid={`user-edit-group-${u.id}-${g.slug}`}
                              onClick={() => setEditForm(f => ({ ...f, allowedProductGroups: active ? cur.filter(s => s !== g.slug) : [...cur, g.slug] }))}
                              className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${active ? "bg-teal-500 text-white border-teal-500" : "border-slate-200 dark:border-[#22304A] text-slate-500"}`}>
                              {g.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => handleSave(u.id)} disabled={saving} className="zen-btn-primary !text-xs flex items-center gap-1"><Check className="w-3 h-3" /> Kaydet</button>
                  <button onClick={() => setEditId(null)} className="zen-btn-ghost !text-xs">İptal</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3">
                <div className="w-8 h-8 rounded-full bg-[#E5322A]/10 text-[#E5322A] flex items-center justify-center font-bold text-sm">
                  {(u.name || u.email).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{u.name || "—"}</div>
                  <div className="text-xs text-slate-400 truncate">
                    {u.username && <span className="font-mono text-slate-500">@{u.username}</span>}
                    {u.email && <span className="ml-1">{u.email}</span>}
                    {u.phone && <span className="text-slate-500"> · {u.phone}</span>}
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${roleColor[u.role] || "bg-slate-100 text-slate-600"}`}>{ROLE_LABELS_TR[u.role] || u.role}</span>
                <button onClick={() => { setEditId(u.id); setEditForm({ username: u.username || "", name: u.name, email: u.email || "", role: u.role, password: "", phone: u.phone || "", countryCode: u.countryCode || "+90", customerId: u.customerId || "", allowedProductGroups: u.allowedProductGroups || [] }); }}
                  className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => handleDelete(u.id)} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        ))}
        {users.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Kullanıcı bulunamadı</p>}
      </div>
    </div>
  );
}

// ─── Ayarlar: Bildirim Ayarları ───────────────────────────────────────────────
function NotificationsSettings() {
  const ITEMS = [
    {
      key: "notify_quote_telegram",
      label: "Yeni Teklif — Telegram",
      desc: "Teklif kaydedildiğinde sahibine Telegram mesajı gönderilir",
      icon: "📄",
    },
    {
      key: "notify_reminder_telegram",
      label: "Hatırlatıcı Alarmları — Telegram",
      desc: "Vadesi gelen hatırlatıcılar Telegram üzerinden bildirilir",
      icon: "⏰",
    },
    {
      key: "notify_usage_whatsapp",
      label: "Kullanım Uyarısı — WhatsApp",
      desc: "Günlük token eşiği aşıldığında Tam Erişim numaralarına WhatsApp gönderilir",
      icon: "📊",
    },
  ];

  const [vals, setVals] = useState({});
  const [ids, setIds] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/settings", { params: { category: "notifications" } }).then(({ data }) => {
      const v = {}, i = {};
      (data || []).forEach(r => { v[r.key] = r.value !== "false"; i[r.key] = r.id; });
      // varsayılan: hepsi açık
      ITEMS.forEach(item => { if (!(item.key in v)) v[item.key] = true; });
      setVals(v); setIds(i);
    });
  }, []);

  const toggle = async (key) => {
    const newVal = !vals[key];
    setVals(v => ({ ...v, [key]: newVal }));
    setSaving(key);
    try {
      const payload = { category: "notifications", key, value: newVal ? "true" : "false", label: ITEMS.find(i => i.key === key)?.label || key };
      if (ids[key]) {
        await api.put(`/settings/${ids[key]}`, payload);
      } else {
        const { data } = await api.post("/settings", payload);
        setIds(i => ({ ...i, [key]: data.id }));
      }
    } catch { setVals(v => ({ ...v, [key]: !newVal })); }
    finally { setSaving(null); }
  };

  return (
    <div className="zen-panel p-6 space-y-5 text-slate-900 dark:text-slate-100">
      <div>
        <h3 className="text-base font-bold flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-500" /> Bildirim Ayarları
        </h3>
        <p className="text-xs text-slate-400 mt-0.5">Hangi olayların bildirim göndereceğini buradan yönetin</p>
      </div>
      <div className="space-y-3">
        {ITEMS.map(item => (
          <div key={item.key}
            className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#111827]">
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">{item.icon}</span>
              <div>
                <div className="text-sm font-semibold">{item.label}</div>
                <div className="text-xs text-slate-400 mt-0.5">{item.desc}</div>
              </div>
            </div>
            <button
              data-testid={`notif-toggle-${item.key}`}
              onClick={() => toggle(item.key)}
              disabled={saving === item.key}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                vals[item.key] ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${vals[item.key] ? "translate-x-5" : ""}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Ayarlar > Dil & Bölge
function LocaleSettings() {
  const { lang, toggleLang } = useContext(UICtx);
  return (
    <div className="zen-panel p-6 space-y-6 text-slate-900 dark:text-slate-100">
      <div>
        <h3 className="text-base font-bold flex items-center gap-2">
          <Globe className="w-4 h-4 text-sky-500" /> Dil & Bölge
        </h3>
        <p className="text-xs text-slate-400 mt-0.5">Arayüz dilini seçin — değişiklik anında uygulanır</p>
      </div>
      <div className="flex gap-3">
        <button
          data-testid="locale-tr-btn"
          onClick={() => lang !== "tr" && toggleLang()}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all border-2 ${
            lang === "tr"
              ? "bg-[#E5322A] border-[#E5322A] text-white shadow-md"
              : "border-slate-200 dark:border-[#22304A] text-slate-500 dark:text-slate-400 hover:border-[#E5322A] hover:text-[#E5322A]"
          }`}
        >
          <span className="text-lg">🇹🇷</span> Türkçe
        </button>
        <button
          data-testid="locale-en-btn"
          onClick={() => lang !== "en" && toggleLang()}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all border-2 ${
            lang === "en"
              ? "bg-[#E5322A] border-[#E5322A] text-white shadow-md"
              : "border-slate-200 dark:border-[#22304A] text-slate-500 dark:text-slate-400 hover:border-[#E5322A] hover:text-[#E5322A]"
          }`}
        >
          <span className="text-lg">🇬🇧</span> English
        </button>
      </div>
      <p className="text-xs text-slate-400">
        Seçilen dil tarayıcıya kaydedilir — her oturumda hatırlanır.
      </p>
    </div>
  );
}

// Ayarlar > AI Motoru: MIMO — TEK ZEKA (24.10.2026)
function AiEngineSettings() {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [keyVal, setKeyVal] = useState("");
  const [keySaved, setKeySaved] = useState(false);
  const [keyRowId, setKeyRowId] = useState(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    api.get("/settings", { params: { category: "ai_config" } }).then(({ data }) => {
      const rows = data || [];
      const krow = rows.find(r => r.key === "mimo_api_key");
      if (krow) { setKeyVal(krow.value || ""); setKeyRowId(krow.id); }
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fields = [
        { key: "active_provider", value: "mimo",          label: "Aktif AI saglayici" },
        { key: "active_model",    value: "mimo-v2.6-pro", label: "Aktif model adi" },
        { key: "base_url",        value: "https://api.xiaomimimo.com/v1", label: "API Temel URL" },
      ];
      for (const f of fields) {
        const { data: rows } = await api.get("/settings", { params: { category: "ai_config" } });
        const row = (rows || []).find(r => r.key === f.key);
        if (row) await api.put(`/settings/${row.id}`, { category: "ai_config", ...f });
        else await api.post("/settings", { category: "ai_config", ...f });
      }
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  const handleSaveKey = async () => {
    setSaving(true);
    try {
      const body = { category: "ai_config", key: "mimo_api_key", value: keyVal, label: "MiMo API Anahtari" };
      if (keyRowId) await api.put(`/settings/${keyRowId}`, body);
      else { const { data } = await api.post("/settings", body); setKeyRowId(data?.id || null); }
      setKeySaved(true); setTimeout(() => setKeySaved(false), 2500);
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/ai/chat", { message: "Merhaba, hangi model ve saglayicisin? Kisaca tanit.", sessionId: "engine-test-" + Date.now() });
      const reply = (data.reply || data.response || "").slice(0, 220);
      const isErr = !reply || reply.toLowerCase().includes("hata") || reply.toLowerCase().includes("error");
      setTestResult({ ok: !isErr, reply: reply || "Yanit alinamadi" });
    } catch (e) {
      setTestResult({ ok: false, reply: e?.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  return (
    <div className="zen-panel p-6 space-y-5 text-slate-900 dark:text-slate-100">
      <div>
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-violet-500" />
          <h3 className="text-base font-extrabold">AI Motoru — MiMo (Tek Zeka)</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">Tek motor</span>
        </div>
        <p className="text-xs text-slate-400 mt-1">ERP Chat, Form Sihirbazi, Wingman, WhatsApp/Telegram botlari, ekstre/fatura tarama ve sesli islemler TEK yapay zeka uzerinden calisir.</p>
      </div>

      <div className="rounded-xl border border-violet-200 dark:border-violet-800/50 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm font-extrabold">MiMo V2.6 Pro</div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Ana model</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">Kontorlu</span>
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
          Kontor bittiginde veya hata olusursa <b>otomatik</b> olarak ucretsiz yedek modele gecilir:
          <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[10px]">mimo-v2.6-flash</span>
          — boylece isleriniz durmaz. Kullanim kayitlarinda "mimo_fallback" olarak gorunur.
        </div>
        <div className="grid sm:grid-cols-3 gap-2 text-[11px]">
          <div className="rounded-lg bg-white/60 dark:bg-black/20 px-3 py-2"><div className="text-slate-400">Model</div><div className="font-mono">mimo-v2.6-pro</div></div>
          <div className="rounded-lg bg-white/60 dark:bg-black/20 px-3 py-2"><div className="text-slate-400">Yedek model</div><div className="font-mono">mimo-v2.6-flash</div></div>
          <div className="rounded-lg bg-white/60 dark:bg-black/20 px-3 py-2"><div className="text-slate-400">Adres</div><div className="font-mono">api.xiaomimimo.com</div></div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button onClick={handleSave} disabled={saving} className="zen-btn-primary !text-xs" data-testid="mimo-engine-save">
            {saved ? "Kaydedildi" : "Kaydet"}
          </button>
          <button onClick={handleTest} disabled={testing} className="zen-btn-ghost !text-xs" data-testid="mimo-engine-test">
            {testing ? "Test ediliyor..." : "Baglanti Test Et"}
          </button>
          {testResult && (
            <span className={`text-[11px] ${testResult.ok ? "text-emerald-600" : "text-rose-500"}`} data-testid="mimo-engine-test-result">
              {testResult.ok ? "Baglanti OK — " : "Hata — "}{testResult.reply}
            </span>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-[#22304A] p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-extrabold">MiMo API Anahtari</h4>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">Sifreli kaydedilir</span>
        </div>
        <p className="text-xs text-slate-400">Kontor bittiginde sistem sizi uyarir; ucretsiz yedek model otomatik devralir.</p>
        <div className="relative">
          <input
            type={showKey ? "text" : "password"}
            value={keyVal}
            onChange={(e) => setKeyVal(e.target.value)}
            placeholder="sk-... formatinda MiMo API anahtari"
            className="zen-input pr-10"
            data-testid="mimo-api-key-input"
          />
          <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-2 top-2 text-slate-400 hover:text-slate-600" title="Goster/gizle">
            {showKey ? "Gizle" : "Goster"}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleSaveKey} disabled={saving} className="zen-btn-primary !text-xs" data-testid="mimo-api-key-save">
            {keySaved ? "Kaydedildi" : "Kaydet"}
          </button>
          <span className="text-xs text-slate-400">Anahtar yalnizca sunucunuzda saklanir.</span>
        </div>
      </div>
    </div>
  );
}

function CompanyInfoSettings() {
  const { tr } = useContext(UICtx);
  const { user } = useContext(AuthCtx);
  const FIELDS = [
    { key: "company_name",    label: "Firma Adı",      placeholder: "ZEN ERP" },
    { key: "company_phone",   label: "Telefon",         placeholder: "+90 (212) 555 0000" },
    { key: "company_email",   label: "E-posta",         placeholder: "info@firma.com" },
    { key: "company_web",     label: "Web Sitesi",      placeholder: "www.firma.com" },
    { key: "company_address", label: "Adres",           placeholder: "İstanbul, Türkiye" },
  ];
  const [vals, setVals] = useState({});
  const [ids, setIds] = useState({});   // key → setting id
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings", { params: { category: "company" } }).then(({ data }) => {
      const v = {}, i = {};
      (data || []).forEach(r => { v[r.key] = r.value || ""; i[r.key] = r.id; });
      setVals(v);
      setIds(i);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const f of FIELDS) {
        const val = vals[f.key] || "";
        if (ids[f.key]) {
          await api.put(`/settings/${ids[f.key]}`, { category: "company", key: f.key, value: val, label: f.label });
        } else {
          const { data } = await api.post("/settings", { category: "company", key: f.key, value: val, label: f.label });
          setIds(prev => ({ ...prev, [f.key]: data.id }));
        }
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="zen-panel p-6 space-y-5 text-slate-900 dark:text-slate-100">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold">Firma Bilgileri</h3>
        <span className="text-xs text-slate-400">PDF, Telegram ve belgelerde görünür</span>
      </div>

      {/* Oturum bilgisi — salt okunur */}
      <div className="grid md:grid-cols-2 gap-2 text-sm bg-slate-50 dark:bg-[#0E1728] rounded-lg px-4 py-3">
        <div><span className="text-slate-400 text-xs">Kullanıcı:</span> {user?.name} ({user?.email})</div>
        <div><span className="text-slate-400 text-xs">Rol:</span> {user?.role}</div>
      </div>

      {/* Düzenlenebilir firma alanları */}
      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map(f => (
          <div key={f.key} className={f.key === "company_address" ? "md:col-span-2" : ""}>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">{f.label}</label>
            <input
              data-testid={`company-field-${f.key}`}
              className="zen-input w-full !py-2"
              placeholder={f.placeholder}
              value={vals[f.key] || ""}
              onChange={e => setVals(prev => ({ ...prev, [f.key]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          data-testid="company-settings-save"
          onClick={handleSave}
          disabled={saving}
          className="zen-btn-primary flex items-center gap-2 !py-2 !px-5"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
        {saved && <span className="text-green-600 dark:text-green-400 text-sm font-semibold">✓ Kaydedildi</span>}
        <span className="text-xs text-slate-400 ml-auto">PDF ve Telegram tekliflerinde otomatik kullanılır</span>
      </div>
    </div>
  );
}

// Ayarlar > Genel: Kumaş/Parça Stok Takibi aç/kapa + düşük stok eşiği (Setting: category=stock)
function StockTrackingToggle() {
  const stock = useContext(StockSettingsCtx);
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState(10);
  const load = async () => {
    const { data } = await api.get("/settings", { params: { category: "stock" } });
    setRows(data || []);
    const th = (data || []).find((x) => x.key === "stock_min_threshold");
    if (th) setThreshold(Number(th.value) || 10);
  };
  useEffect(() => { load(); }, []);

  const upsert = async (key, value, label) => {
    const existing = rows.find((r) => r.key === key);
    if (existing) await api.put(`/settings/${existing.id}`, { ...existing, value });
    else await api.post("/settings", { category: "stock", key, value, label });
    await load();
    stock.refresh();
  };

  return (
    <div className="zen-panel p-6 space-y-4" data-testid="stock-tracking-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold flex items-center gap-2"><Boxes className="w-4.5 h-4.5 text-[#E5322A]" /> Kumaş / Parça Stok Takibi</h3>
          <p className="text-xs text-slate-500 mt-1">Açıksa: sipariş onaylandığında kumaş (en×boy×adet metre) ve yedek parça (adet) stoktan otomatik düşülür, iptal edilirse geri eklenir. Kapalıysa stok alanları/uyarıları hiçbir yerde görünmez.</p>
        </div>
        <button
          data-testid="stock-tracking-toggle"
          onClick={() => upsert("stock_tracking_enabled", !stock.enabled, "Stok Takibi Aktif")}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${stock.enabled ? "bg-[#E5322A]" : "bg-slate-300 dark:bg-[#22304A]"}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${stock.enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>
      {stock.enabled && (
        <div className="flex items-center gap-3 pt-3 border-t border-slate-200 dark:border-[#22304A]">
          <label className="text-xs font-semibold text-slate-500 shrink-0">Düşük Stok Uyarı Eşiği</label>
          <input
            data-testid="stock-threshold-input"
            type="number" step="0.5" className="zen-input !py-1.5 !w-28"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            onBlur={() => upsert("stock_min_threshold", threshold, "Düşük Stok Eşiği")}
          />
          <span className="text-xs text-slate-400">Bu değerin altındaki kalemler kırmızı/sarı uyarı ile işaretlenir.</span>
        </div>
      )}
    </div>
  );
}

// Ayarlar > Genel: Panel Ses Tercihi
function SoundToggleSettings() {
  const [enabled, setEnabled] = useState(isSoundEnabled);

  const toggle = () => {
    const next = !enabled;
    setSoundEnabled(next);
    setEnabled(next);
    if (next) {
      // Açıldığını göstermek için ince bir bip çal
      import("../utils/soundUtils").then(({ playBeep }) => playBeep("soft"));
    }
    toast.success(next ? "Panel sesleri açıldı" : "Panel sesleri kapatıldı");
  };

  return (
    <div className="zen-panel p-6" data-testid="sound-toggle-settings">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-base font-bold flex items-center gap-2">
            {enabled
              ? <Bell className="w-4 h-4 text-[#E5322A]" />
              : <BellOff className="w-4 h-4 text-slate-400" />}
            Panel Sesleri
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Hatırlatıcı ve bildirim bipleri. Açıkken yalnızca ince, kısa sesler çalar — rahatsız edici alarm sesi yoktur.
          </p>
        </div>
        <button
          data-testid="sound-toggle-btn"
          onClick={toggle}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${enabled ? "bg-[#E5322A]" : "bg-slate-300 dark:bg-[#22304A]"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>
    </div>
  );
}

// Ayarlar > WhatsApp Teklif Hatırlatıcı
function QuoteReminderSettings({ tr }) {
  const [enabled, setEnabled]   = useState(false);
  const [days, setDays]         = useState(2);
  const [template, setTemplate] = useState("");
  const [saving, setSaving]     = useState(false);
  const [running, setRunning]   = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const DEFAULT_TPL = "Merhaba {musteri_adi}! 👋\n\nZEN Perde sistemleri olarak tarafınıza ilettiğimiz *{teklif_no}* nolu teklifimiz incelemenizi bekliyor.\n\n✅ Teklif geçerlilik süresi: {gecerlilik_gunu} gün\n\nHerhangi bir sorunuz veya değişiklik talebiniz için bize ulaşmaktan çekinmeyin. 🤝\n\n_ZEN Mekanizmalı Perde Sistemleri_";

  const load = async () => {
    try {
      const { data } = await api.get("/quote-reminders/settings");
      setEnabled(data.enabled);
      setDays(data.delay_days ?? 2);
      setTemplate(data.message_template || DEFAULT_TPL);
    } catch (e) { toast.error("Yüklenemedi"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/quote-reminders/settings", { enabled, delay_days: days, message_template: template });
      toast.success("Kaydedildi");
    } catch { toast.error("Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/quote-reminders/run-now");
      setLastResult(data);
      if (data.skipped) toast.info("Hatırlatıcı devre dışı — önce aktif edin");
      else if (data.error) toast.error("Hata: " + data.error);
      else toast.success(`Gönderildi: ${data.sent ?? 0} teklif`);
    } catch { toast.error("Çalıştırılamadı"); }
    finally { setRunning(false); }
  };

  return (
    <div className="zen-panel p-6 space-y-5" data-testid="quote-reminder-settings">
      <div>
        <h3 className="text-base font-bold flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#25D366]" /> WhatsApp Teklif Hatırlatıcı
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Açık (gönderilmiş) teklifler belirtilen gün sonra hâlâ onaylanmamışsa müşteriye otomatik WhatsApp mesajı gönderir.
        </p>
      </div>

      {/* Aktif / Pasif */}
      <div className="flex items-center justify-between zen-panel p-4 rounded-xl">
        <div>
          <div className="font-semibold text-sm">Otomatik Hatırlatıcı</div>
          <div className="text-xs text-slate-500">{enabled ? "Aktif — saatlik kontrol çalışıyor" : "Pasif"}</div>
        </div>
        <button
          data-testid="quote-reminder-toggle"
          onClick={() => setEnabled(v => !v)}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${enabled ? "bg-[#25D366]" : "bg-slate-300 dark:bg-[#22304A]"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>

      {/* Kaç gün sonra */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Kaç gün sonra gönderilsin?</label>
        <div className="flex items-center gap-3">
          <input
            data-testid="quote-reminder-days"
            type="number" min={1} max={30}
            value={days}
            onChange={e => setDays(Math.max(1, Number(e.target.value)))}
            className="zen-input w-20 text-center"
          />
          <span className="text-sm text-slate-500">gün sonra hatırlat</span>
        </div>
      </div>

      {/* Mesaj şablonu */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
          Mesaj Şablonu
          <span className="ml-2 text-slate-400 font-normal">
            {"→ "}değişkenler: <code className="zen-mono">{"{musteri_adi} {teklif_no} {gecerlilik_gunu}"}</code>
          </span>
        </label>
        <textarea
          data-testid="quote-reminder-template"
          rows={7}
          value={template}
          onChange={e => setTemplate(e.target.value)}
          className="zen-input w-full font-mono text-xs resize-none"
        />
        <button onClick={() => setTemplate(DEFAULT_TPL)} className="zen-btn-ghost !text-xs">Varsayılana sıfırla</button>
      </div>

      {/* Son çalıştırma sonucu */}
      {lastResult && !lastResult.skipped && (
        <div className="text-xs zen-panel px-4 py-3 rounded-xl space-y-0.5 text-slate-600 dark:text-slate-300">
          <div>Son çalıştırma — Gönderildi: <strong>{lastResult.sent ?? 0}</strong> · Telefonsuz: <strong>{lastResult.no_phone ?? 0}</strong> · Hatalı: <strong>{lastResult.failed ?? 0}</strong></div>
        </div>
      )}

      {/* Kaydet + Şimdi Çalıştır */}
      <div className="flex gap-3 flex-wrap">
        <button data-testid="quote-reminder-save" onClick={save} disabled={saving} className="zen-btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Kaydet
        </button>
        <button data-testid="quote-reminder-run-now" onClick={runNow} disabled={running} className="zen-btn-ghost flex items-center gap-2">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} Şimdi Çalıştır
        </button>
      </div>
    </div>
  );
}

// Ayarlar > WhatsApp Teklif Metni: IBAN/geçerlilik/imza alanları + serbest şablon (Setting: category=whatsapp)
function WhatsappTemplateSettings({ tr }) {
  const [rows, setRows] = useState([]);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings", { params: { category: "whatsapp" } });
    setRows(data || []);
    const v = {};
    WHATSAPP_FIELDS.forEach((f) => {
      const row = (data || []).find((r) => r.key === f.key);
      v[f.key] = row ? row.value : f.def;
    });
    setVals(v);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      for (const f of WHATSAPP_FIELDS) {
        const existing = rows.find((r) => r.key === f.key);
        if (existing) await api.put(`/settings/${existing.id}`, { value: vals[f.key] ?? "" });
        else await api.post("/settings", { category: "whatsapp", key: f.key, value: vals[f.key] ?? "", label: f.label });
      }
      toast.success(tr("Kaydedildi"));
      await load();
    } catch (e) { toast.error(tr("Kaydedilemedi")); }
    finally { setSaving(false); }
  };

  const reset = () => setVals(Object.fromEntries(WHATSAPP_FIELDS.map((f) => [f.key, f.def])));

  return (
    <div className="zen-panel p-6 space-y-4" data-testid="whatsapp-template-settings">
      <div>
        <h3 className="text-base font-bold flex items-center gap-2"><Send className="w-4 h-4 text-[#25D366]" /> {tr("WhatsApp Teklif Metni")}</h3>
        <p className="text-xs text-slate-500 mt-1">
          {tr("Teklif WhatsApp ile paylaşılırken kullanılacak IBAN, geçerlilik ve imza bilgileri. Şablonda kullanılabilir değişkenler:")}{" "}
          <span className="zen-mono">{"{marka} {teklifNo} {musteri} {tutar} {gecerlilik} {link} {iban} {imza}"}</span>
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {WHATSAPP_FIELDS.map((f) => (
          <div key={f.key} className={f.multiline ? "md:col-span-2" : ""}>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1">{f.label}</label>
            {f.multiline ? (
              <textarea
                data-testid={`wa-field-${f.key}`}
                rows={f.key === "wa_template" ? 9 : 3}
                className="zen-input !text-xs w-full zen-mono"
                value={vals[f.key] || ""}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
              />
            ) : (
              <input
                data-testid={`wa-field-${f.key}`}
                className="zen-input !text-xs w-full"
                value={vals[f.key] || ""}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-[#22304A]">
        <button onClick={save} disabled={saving} data-testid="wa-template-save-btn" className="zen-btn-primary !text-xs">{saving ? tr("Kaydediliyor...") : tr("Kaydet")}</button>
        <button onClick={reset} data-testid="wa-template-reset-btn" className="zen-btn-ghost !text-xs">{tr("Varsayılana Dön")}</button>
      </div>
    </div>
  );
}

// ─── WhatsApp Güvenlik & Bilinmeyen Numara Yönetimi ────────────────────────────
const DEFAULT_UNKNOWN_MSG =
  "Merhaba! 🌿 *Zen Gölgelendirme*'ye hoş geldiniz.\n\nMesajınız alındı. Ekibimiz en kısa sürede sizinle iletişime geçecektir.\n\n_Bu otomatik bir karşılama mesajıdır._";

function WhatsappSecuritySettings({ tr }) {
  const [msg, setMsg] = useState(DEFAULT_UNKNOWN_MSG);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unknownMsgs, setUnknownMsgs] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [rowId, setRowId] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  // Mesai saati dışı
  const [bhStart, setBhStart] = useState("09:00");
  const [bhEnd, setBhEnd] = useState("18:00");
  const [bhDays, setBhDays] = useState([1,2,3,4,5,6]);
  const [offMsg, setOffMsg] = useState("Merhaba! 🌙 *Zen Gölgelendirme*'ye hoş geldiniz.\n\nŞu an mesai saatleri dışındayız. Mesajınız alındı, iş günü başladığında ekibimiz sizinle iletişime geçecektir.\n\n_Mesai saatlerimiz: Pazartesi–Cumartesi 09:00–18:00_");
  const [offActive, setOffActive] = useState(false);
  const [bhRowIds, setBhRowIds] = useState({});

  useEffect(() => {
    api.get("/settings", { params: { category: "whatsapp" } }).then(({ data }) => {
      const rows = data || [];
      const row = rows.find(r => r.key === "unknown_contact_message");
      if (row) { setMsg(row.value); setRowId(row.id); }
      const actRow = rows.find(r => r.key === "unknown_contact_active");
      if (actRow) { setActive(actRow.value !== "false"); setActiveRowId(actRow.id); }
      // Mesai ayarları
      const ids = {};
      const startRow = rows.find(r => r.key === "business_hours_start");
      if (startRow) { setBhStart(startRow.value); ids.business_hours_start = startRow.id; }
      const endRow = rows.find(r => r.key === "business_hours_end");
      if (endRow) { setBhEnd(endRow.value); ids.business_hours_end = endRow.id; }
      const daysRow = rows.find(r => r.key === "business_days");
      if (daysRow) { setBhDays(daysRow.value.split(",").map(Number).filter(Boolean)); ids.business_days = daysRow.id; }
      const offActRow = rows.find(r => r.key === "off_hours_active");
      if (offActRow) { setOffActive(offActRow.value === "true"); ids.off_hours_active = offActRow.id; }
      const offMsgRow = rows.find(r => r.key === "off_hours_message");
      if (offMsgRow) { setOffMsg(offMsgRow.value); ids.off_hours_message = offMsgRow.id; }
      setBhRowIds(ids);
    }).catch(() => {});
    loadUnknown();
  }, []);

  const loadUnknown = async () => {
    setLoadingMsgs(true);
    try {
      const { data } = await api.get("/whatsapp/unknown-messages");
      setUnknownMsgs(data || []);
    } catch {} finally { setLoadingMsgs(false); }
  };

  const saveActive = async (val) => {
    setActive(val);
    try {
      const strVal = val ? "true" : "false";
      if (activeRowId) await api.put(`/settings/${activeRowId}`, { category: "whatsapp", key: "unknown_contact_active", value: strVal, label: "Otomatik Karşılama Aktif" });
      else {
        const { data } = await api.post("/settings", { category: "whatsapp", key: "unknown_contact_active", value: strVal, label: "Otomatik Karşılama Aktif" });
        setActiveRowId(data.id);
      }
    } catch { toast.error("Toggle kaydedilemedi"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (rowId) await api.put(`/settings/${rowId}`, { category: "whatsapp", key: "unknown_contact_message", value: msg, label: "Bilinmeyen Numara Karşılama" });
      else { const { data } = await api.post("/settings", { category: "whatsapp", key: "unknown_contact_message", value: msg, label: "Bilinmeyen Numara Karşılama" }); setRowId(data.id); }
      // Mesai ayarlarını kaydet
      const bhSettings = [
        { key: "business_hours_start", value: bhStart, label: "Mesai Başlangıç Saati" },
        { key: "business_hours_end",   value: bhEnd,   label: "Mesai Bitiş Saati" },
        { key: "business_days",        value: bhDays.join(","), label: "Mesai Günleri" },
        { key: "off_hours_active",     value: offActive ? "true" : "false", label: "Mesai Dışı Mesaj Aktif" },
        { key: "off_hours_message",    value: offMsg,  label: "Mesai Dışı Mesaj" },
      ];
      const newIds = { ...bhRowIds };
      for (const s of bhSettings) {
        if (newIds[s.key]) {
          await api.put(`/settings/${newIds[s.key]}`, { category: "whatsapp", ...s });
        } else {
          const { data } = await api.post("/settings", { category: "whatsapp", ...s });
          newIds[s.key] = data.id;
        }
      }
      setBhRowIds(newIds);
      toast.success("Ayarlar kaydedildi");
    } catch { toast.error("Kayıt hatası"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Açıklama */}
      <div className="zen-panel p-4 space-y-3" data-testid="wa-security-panel">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-500" /> Bot Güvenlik Mantığı
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Sistem yalnızca <b>Yetkili Numaralar</b> listesindeki kişilere fiyat, cari ve ERP verisi sunar.
            Tanımadığı bir numara mesaj attığında aşağıdaki karşılama mesajını gönderir, <b>AI devreye girmez</b> ve hiçbir veri paylaşılmaz.
          </p>
        </div>
        <div className="rounded-xl bg-emerald-500/5 border border-emerald-400/20 p-3 flex gap-2 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Korumalı veriler:</b> Ürün fiyatları, müşteri carisi, teklif kalemleri, stok ve ERP işlemleri.<br/>
            Bilinmeyen numaralar yalnızca aşağıdaki mesajı alır — başka hiçbir şey gönderilmez.
          </div>
        </div>

        {/* Otomatik Karşılama Toggle */}
        <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-[#2a3a54] bg-slate-50 dark:bg-[#141e2e] px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Otomatik Karşılama Mesajı</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {active ? "Bilinmeyen numaralara otomatik yanıt gönderiliyor" : "Kapalı — bilinmeyen numaralara yanıt verilmeyecek"}
            </p>
          </div>
          <button
            data-testid="wa-auto-reply-toggle"
            onClick={() => saveActive(!active)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
              ${active ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${active ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className={active ? "" : "opacity-50 pointer-events-none"}>
          <label className="text-[11px] font-semibold text-slate-500 block mb-1">
            Karşılama Mesajı <span className="text-slate-400">(WhatsApp markdown destekler)</span>
          </label>
          <textarea
            data-testid="wa-unknown-msg-input"
            className="zen-input w-full !text-xs zen-mono"
            rows={6}
            value={msg}
            onChange={e => setMsg(e.target.value)}
            disabled={!active}
          />
        </div>

        {/* ── Mesai Saatleri ──────────────────────────────────────────────── */}
        <div className={`space-y-3 pt-3 border-t border-slate-200 dark:border-[#22304A] ${active ? "" : "opacity-50 pointer-events-none"}`}>
          <div className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-sky-500" /> Mesai Saatleri (Türkiye saatiyle)
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-slate-500">Açılış</label>
              <input type="time" value={bhStart} onChange={e => setBhStart(e.target.value)} className="zen-input !py-1 !text-xs w-28" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-slate-500">Kapanış</label>
              <input type="time" value={bhEnd} onChange={e => setBhEnd(e.target.value)} className="zen-input !py-1 !text-xs w-28" />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[{d:1,l:"Pzt"},{d:2,l:"Sal"},{d:3,l:"Çar"},{d:4,l:"Per"},{d:5,l:"Cum"},{d:6,l:"Cmt"},{d:7,l:"Paz"}].map(({d,l}) => (
              <button
                key={d} type="button"
                onClick={() => setBhDays(prev => prev.includes(d) ? prev.filter(x=>x!==d) : [...prev,d].sort())}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition ${bhDays.includes(d) ? "bg-sky-500 text-white border-sky-500" : "border-slate-300 dark:border-[#2a3a54] text-slate-500 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}
              >{l}</button>
            ))}
          </div>
        </div>

        {/* ── Mesai Dışı Mesaj ─────────────────────────────────────────────── */}
        <div className={`space-y-2 pt-3 border-t border-slate-200 dark:border-[#22304A] ${active ? "" : "opacity-50 pointer-events-none"}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-2">
              <Moon className="w-3.5 h-3.5 text-violet-500" /> Mesai Saati Dışı Mesajı
            </p>
            <button
              data-testid="wa-off-hours-toggle"
              onClick={() => setOffActive(v => !v)}
              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${offActive ? "bg-violet-500" : "bg-slate-300 dark:bg-slate-600"}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${offActive ? "translate-x-5" : "translate-x-1"}`} />
            </button>
          </div>
          <p className="text-[11px] text-slate-400">Mesai saatleri dışında (gece, tatil) bilinmeyen numaraya farklı mesaj gönderilir.</p>
          <textarea
            data-testid="wa-off-hours-msg-input"
            className={`zen-input w-full !text-xs zen-mono ${offActive ? "" : "opacity-40"}`}
            rows={5}
            value={offMsg}
            onChange={e => setOffMsg(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving || !active} data-testid="wa-security-save-btn" className="zen-btn-primary !text-xs disabled:opacity-50">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
          <button onClick={() => setMsg(DEFAULT_UNKNOWN_MSG)} disabled={!active} className="zen-btn-ghost !text-xs disabled:opacity-50">Varsayılana Dön</button>
        </div>
      </div>

      {/* Bilinmeyen mesajlar listesi */}
      <div className="zen-panel p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" /> Bilinmeyen Numaralardan Gelen Mesajlar
          </h3>
          <button onClick={loadUnknown} className="zen-btn-ghost !text-xs !py-1">Yenile</button>
        </div>
        {loadingMsgs ? (
          <div className="text-xs text-slate-400">Yükleniyor…</div>
        ) : unknownMsgs.length === 0 ? (
          <div className="text-xs text-slate-400 py-4 text-center">
            Henüz bilinmeyen numaradan mesaj yok.
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {unknownMsgs.map(m => (
              <div key={m.id} className="zen-panel !bg-amber-500/5 border-amber-400/20 p-3 space-y-1" data-testid={`wa-unknown-msg-${m.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-amber-600 dark:text-amber-400 font-mono">+{m.phone}</span>
                  <span className="text-[10px] text-slate-400">{new Date(m.receivedAt).toLocaleString("tr-TR")}</span>
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300">{m.message}</div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400">
          Bu numaraları <b>Yetkili Numaralar</b> sekmesine ekleyerek bot erişimi verebilirsiniz.
        </p>
      </div>
    </div>
  );
}

// Generic key/value editor per settings category
function SettingsCategoryEditor({ category, label }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nk, setNk] = useState(""); const [nv, setNv] = useState(""); const [nl, setNl] = useState("");
  const [showNewValue, setShowNewValue] = useState(false);
  const [revealed, setRevealed] = useState(new Set());
  const isSecret = category === "api_keys";
  const toggleReveal = (id) => setRevealed((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/settings?category=${category}`);
      setRows(data);
    } catch (e) { toast.error("Yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [category]);

  const add = async () => {
    if (!nk.trim()) { toast.error("Anahtar (key) gerekli"); return; }
    try {
      await api.post("/settings", { category, key: nk, value: nv, label: nl });
      toast.success("Eklendi"); setNk(""); setNv(""); setNl(""); load();
    } catch (e) { toast.error("Eklenemedi"); }
  };
  const upd = async (r, field, val) => {
    try { await api.put(`/settings/${r.id}`, { [field]: val }); load(); }
    catch (e) { toast.error("Güncellenemedi"); }
  };
  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try { await api.delete(`/settings/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error("Silinemedi"); }
  };

  const HINTS = {
    printers: "Etiket/A4 yazıcı adı ve varsayılan boyutu. Örn: 'Zebra ZD420' / '100×50mm'",
    sales_statuses: "Özel sipariş durumları. Örn: 'Onay Bekliyor' / 'İmalatta' / 'Yollandı' / 'Ödendi'",
    currency_override: "Elle kur girişi (TCMB otomatik yerine). Örn: 'EUR_TL' / '55.20'",
    warehouses: "Depo/konum kodu. Örn: 'IST-ANA' / 'İstanbul Ana Depo' · Adres alanı label'a yazılabilir",
    tax: "KDV oranları ve stopaj. Örn: 'KDV_STOR' / '10' · 'KDV_MOTOR' / '20'",
    users_roles: "Rol tanımları (admin/sales/production dışında). Örn: 'imalat_sef' / 'İmalat Şefi'",
    email_templates: "Şablon adı / içerik (HTML mail body). Örn: 'siparis_onay' / kod",
    payment_methods: "Ödeme yöntemleri. Örn: 'havale' / 'Banka Havalesi'",
    cargo_companies: "Kargo firma listesi. Örn: 'aras' / 'Aras Kargo' · label: telefon",
    units: "Ölçü birimleri. Örn: 'm2' / 'Metrekare' · 'adet' / 'Adet'",
    notifications: "Bildirim tercihleri. Örn: 'siparis_geldi_mail' / 'true'",
    backup: "Yedekleme yolları / bulut sağlayıcı. Örn: 'gdrive_folder' / kod",
    api_keys: "3. parti servis anahtarları. Değerler veritabanında şifrelenir, sadece admin görebilir/düzenleyebilir. Örn: 'stripe_test' / sk_...",
    locale: "Dil/bölge/format. Örn: 'default_lang' / 'tr' · 'timezone' / 'Europe/Istanbul'",
    appearance: "Tema özellikleri. Örn: 'accent_color' / '#E5322A'",
  };

  return (
    <div className="space-y-4">
      <div className="zen-panel p-4">
        <h3 className="text-base font-bold">{label}</h3>
        <div className="text-xs text-slate-500 mt-1">{HINTS[category] || "Anahtar/değer eklerken burası kullanılır."}</div>
      </div>

      {/* Add form */}
      <div className="zen-panel p-4 grid md:grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-end">
        <div>
          <label className="block text-[10px] font-semibold uppercase mb-1">Anahtar (key)</label>
          <input className="zen-input" placeholder="örn: EUR_TL" value={nk} onChange={(e) => setNk(e.target.value)} />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase mb-1">Değer</label>
          {isSecret ? (
            <div className="relative">
              <input
                type={showNewValue ? "text" : "password"}
                className="zen-input !pr-9"
                placeholder="sk_live_... · gizli anahtar"
                value={nv}
                onChange={(e) => setNv(e.target.value)}
                data-testid="setting-new-value-input"
              />
              <button
                type="button"
                onClick={() => setShowNewValue((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="setting-new-value-toggle"
              >
                {showNewValue ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          ) : (
            <input className="zen-input" placeholder="55.20 · Aras Kargo · vb." value={nv} onChange={(e) => setNv(e.target.value)} />
          )}
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase mb-1">Etiket / Açıklama</label>
          <input className="zen-input" placeholder="opsiyonel" value={nl} onChange={(e) => setNl(e.target.value)} />
        </div>
        <button onClick={add} className="zen-btn-primary" data-testid={`set-add-${category}`}><Plus className="w-4 h-4" /> Ekle</button>
      </div>

      {/* List */}
      <div className="zen-panel p-3 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center text-sm text-slate-500">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">Bu kategoride henüz kayıt yok.</div>
        ) : (
          <table className="zen-table !text-xs">
            <thead><tr><th>Anahtar</th><th>Değer</th><th>Etiket</th><th className="w-16">Aktif</th><th className="w-10"></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="zen-mono font-bold">{r.key}</td>
                  <td>
                    {isSecret ? (
                      <div className="relative">
                        <input
                          key={`${r.id}-${revealed.has(r.id)}`}
                          type={revealed.has(r.id) ? "text" : "password"}
                          defaultValue={r.value}
                          onBlur={(e) => e.target.value !== r.value && upd(r, "value", e.target.value)}
                          className="zen-input !py-1 !text-xs !pr-8"
                          data-testid={`setting-value-input-${r.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => toggleReveal(r.id)}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          data-testid={`setting-value-toggle-${r.id}`}
                        >
                          {revealed.has(r.id) ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      </div>
                    ) : (
                      <input defaultValue={r.value} onBlur={(e) => e.target.value !== r.value && upd(r, "value", e.target.value)} className="zen-input !py-1 !text-xs" />
                    )}
                  </td>
                  <td><input defaultValue={r.label || ""} onBlur={(e) => e.target.value !== (r.label || "") && upd(r, "label", e.target.value)} className="zen-input !py-1 !text-xs" /></td>
                  <td className="text-center"><input type="checkbox" checked={!!r.active} onChange={(e) => upd(r, "active", e.target.checked)} /></td>
                  <td><button onClick={() => del(r.id)} className="p-1 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}



// ─── Ayarlar > AI Numaraları (Telegram + WhatsApp) ──────────────────────────
function AiContactsSettings() {
  const { tr } = useContext(UICtx);
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", chatId: "", platform: "telegram", role: "limited", active: true, notes: "" });

  const load = async () => setRows((await api.get("/ai-contacts")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("İsim zorunlu"); return; }
    if (editItem) await api.put(`/ai-contacts/${editItem.id}`, form);
    else await api.post("/ai-contacts", form);
    toast.success("Kaydedildi");
    setShowForm(false); setEditItem(null);
    setForm({ name: "", phone: "", chatId: "", platform: "telegram", role: "limited", active: true, notes: "" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/ai-contacts/${id}`);
    toast.success("Silindi"); load();
  };

  const PLAT_STYLES = { telegram: "bg-sky-500/10 text-sky-600", whatsapp: "bg-emerald-500/10 text-emerald-600" };
  const ROLE_STYLES = { full: "bg-[#E5322A]/10 text-[#E5322A]", limited: "bg-slate-200 dark:bg-[#22304A] text-slate-500" };

  return (
    <div className="space-y-4 zen-anim-up" data-testid="ai-contacts-settings">
      <div className="zen-panel p-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="font-bold">AI İletişim Numaraları</h3>
            <p className="text-xs text-slate-500 mt-0.5">Telegram ve WhatsApp üzerinden AI ile yazışma yetkisi verilen kişiler.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="ai-contact-add-self-btn"
              onClick={() => { setEditItem(null); setForm({ name: "Admin", phone: "", chatId: "", platform: "whatsapp", role: "full", active: true, notes: "Yönetici" }); setShowForm(true); }}
              className="zen-btn-ghost !text-xs !py-1.5 !px-3 border-emerald-400/40 text-emerald-600 dark:text-emerald-400">
              <Phone className="w-3.5 h-3.5" /> Kendi Numaram
            </button>
            <button data-testid="ai-contact-add-btn" onClick={() => { setEditItem(null); setForm({ name: "", phone: "", chatId: "", platform: "telegram", role: "limited", active: true, notes: "" }); setShowForm(true); }} className="zen-btn-primary !text-xs"><Plus className="w-3.5 h-3.5" /> Kişi Ekle</button>
          </div>
        </div>

        {/* Bilgi kutusu */}
        <div className="zen-panel !p-3 bg-sky-500/5 border-sky-400/20 text-xs text-sky-700 dark:text-sky-300 space-y-1 mb-3">
          <div className="font-semibold">Roller:</div>
          <div>🔴 <strong>Tam Erişim</strong> — Tüm AI komutları (yalnızca siz)</div>
          <div>⚪ <strong>Sınırlı</strong> — Sadece fiyat sorgulama ve teklif talebi</div>
        </div>

        {rows.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">Henüz kişi eklenmedi</div>
        ) : (
          <table className="zen-table text-sm">
            <thead><tr><th>İsim</th><th>Platform</th><th>Tel / Chat ID</th><th>Rol</th><th>Durum</th><th></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`ai-contact-row-${r.id}`}>
                  <td className="font-semibold">{r.name}</td>
                  <td><span className={`zen-chip text-[10px] ${PLAT_STYLES[r.platform]}`}>{r.platform === "telegram" ? "Telegram" : "WhatsApp"}</span></td>
                  <td className="font-mono text-xs">{r.platform === "telegram" ? (r.chatId || "—") : (r.phone || "—")}</td>
                  <td><span className={`zen-chip text-[10px] ${ROLE_STYLES[r.role]}`}>{r.role === "full" ? "Tam Erişim" : "Sınırlı"}</span></td>
                  <td><span className={`zen-chip text-[10px] ${r.active ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-400/10 text-slate-400"}`}>{r.active ? "Aktif" : "Pasif"}</span></td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditItem(r); setForm({ ...r }); setShowForm(true); }} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(r.id)} className="p-1.5 hover:bg-rose-500/10 text-rose-500 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="zen-panel w-full max-w-md space-y-4 zen-anim-up">
            <div className="flex items-center justify-between">
              <h3 className="font-bold">{editItem ? "Kişiyi Düzenle" : "Yeni Kişi Ekle"}</h3>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-slate-500 block mb-1">İsim *</label>
                <input className="zen-input" placeholder="Ali Yılmaz" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="aic-name" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Platform</label>
                <select className="zen-input" value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))} data-testid="aic-platform">
                  <option value="telegram">Telegram</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Rol</label>
                <select className="zen-input" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} data-testid="aic-role">
                  <option value="full">Tam Erişim</option>
                  <option value="limited">Sınırlı (Fiyat + Teklif)</option>
                </select>
              </div>
              {form.platform === "telegram" ? (
                <div className="col-span-2">
                  <label className="text-xs text-slate-500 block mb-1">Telegram Chat ID</label>
                  <input className="zen-input font-mono" placeholder="123456789" value={form.chatId} onChange={e => setForm(p => ({ ...p, chatId: e.target.value }))} data-testid="aic-chatid" />
                  <p className="text-[10px] text-slate-400 mt-1">Bota mesaj gönderince Chat ID otomatik kaydedilir</p>
                </div>
              ) : (
                <div className="col-span-2">
                  <label className="text-xs text-slate-500 block mb-1">WhatsApp Numarası</label>
                  <input className="zen-input" placeholder="+905551234567" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="aic-phone" />
                  <div className="zen-panel !p-2 mt-1 bg-amber-500/5 border-amber-400/20 text-[10px] text-amber-600 dark:text-amber-400">WhatsApp bot henüz bağlanmadı. Numara şimdi kaydedilir, bot bağlandığında aktif olur.</div>
                </div>
              )}
              <div>
                <label className="text-xs text-slate-500 block mb-1">Durum</label>
                <select className="zen-input" value={form.active ? "true" : "false"} onChange={e => setForm(p => ({ ...p, active: e.target.value === "true" }))} data-testid="aic-active">
                  <option value="true">Aktif</option>
                  <option value="false">Pasif</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Not</label>
                <input className="zen-input" placeholder="Opsiyonel" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="zen-btn-ghost">İptal</button>
              <button data-testid="aic-save-btn" onClick={save} className="zen-btn-primary">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ayarlar > Bot Logları ────────────────────────────────────────────────────
function BotLogsModule() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState("");
  const [direction, setDirection] = useState("");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(50);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (platform) params.set("platform", platform);
      if (direction) params.set("direction", direction);
      if (query.trim()) params.set("q", query.trim());
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/bots/logs?${params.toString()}`);
      setLogs(Array.isArray(data) ? data : (data?.logs || []));
      setTotal(data?.total ?? (Array.isArray(data) ? data.length : 0));
    } catch { toast.error("Loglar yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [platform, direction, limit, dateFrom, dateTo]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [query]);

  const PLATS = [{ v: "", l: "Tüm Platformlar" }, { v: "whatsapp", l: "WhatsApp" }, { v: "telegram", l: "Telegram" }];
  const DIRS = [{ v: "", l: "Tüm Yönler" }, { v: "in", l: "← Gelen" }, { v: "out", l: "→ Giden" }];

  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex items-center gap-3">
        <History className="w-5 h-5 text-sky-500" />
        <h3 className="text-lg font-bold">Bot Logları</h3>
        <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">{total} kayıt</span>
        <button onClick={load} className="ml-auto zen-btn-ghost !py-1 !px-2 text-xs flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Yenile
        </button>
      </div>

      {/* Filtreler */}
      <div className="zen-panel p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {PLATS.map(({ v, l }) => (
          <button key={v || "all-p"} onClick={() => setPlatform(v)}
            className={`px-2.5 py-1.5 rounded text-[11px] font-semibold transition-colors ${platform === v ? "bg-[#E5322A] text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            {l}
          </button>
        ))}
        {DIRS.map(({ v, l }) => (
          <button key={v || "all-d"} onClick={() => setDirection(v)}
            className={`px-2.5 py-1.5 rounded text-[11px] font-semibold transition-colors ${direction === v ? "bg-sky-500 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Metin veya numara ara…"
            className="w-full pl-8 pr-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" />
        </div>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs"
          title="Başlangıç tarihi" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs"
          title="Bitiş tarihi" />
        <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
          className="px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs">
          <option value={25}>Son 25</option>
          <option value={50}>Son 50</option>
          <option value={100}>Son 100</option>
          <option value={200}>Son 200</option>
        </select>
        {(query || dateFrom || dateTo) && (
          <button onClick={() => { setQuery(""); setDateFrom(""); setDateTo(""); }}
            className="zen-btn-ghost !text-[11px] !py-1 flex items-center gap-1">
            <X className="w-3 h-3" /> Temizle
          </button>
        )}
      </div>

      {/* Log listesi */}
      {loading ? (
        <div className="text-center py-8 text-xs text-slate-500">Yükleniyor…</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-xs text-slate-500">
          {query || platform || direction ? "Aramanızla eşleşen kayıt bulunamadı." : "Henüz log kaydı yok."}
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
          {logs.map((log) => {
            const isIn = log.direction === "in";
            const isWA = log.platform === "whatsapp";
            return (
              <div key={log.id || log.at}
                className={`p-3 rounded-lg border text-xs ${isIn
                  ? isWA ? "border-emerald-500/30 bg-emerald-500/5" : "border-sky-500/30 bg-sky-500/5"
                  : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40"}`}
                data-testid="bot-log-entry">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isWA ? "bg-emerald-500/20 text-emerald-600" : "bg-sky-500/20 text-sky-600"}`}>
                    {isWA ? "WhatsApp" : "Telegram"}
                  </span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isIn ? "bg-blue-500/20 text-blue-600" : "bg-slate-400/20 text-slate-500"}`}>
                    {isIn ? "← GELEN" : "→ GİDEN"}
                  </span>
                  <span className="font-mono text-[10px] text-slate-500">{log.chat}</span>
                  {log.meta?.from && <span className="text-[10px] text-slate-400">{log.meta.from}</span>}
                  <span className="ml-auto text-[10px] text-slate-400 tabular-nums whitespace-nowrap">
                    {new Date(log.at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="text-[11px] leading-relaxed whitespace-pre-wrap break-words text-slate-700 dark:text-slate-300 max-h-24 overflow-y-auto">{log.text}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Ayarlar > Kredi & Kullanım İzleyici ─────────────────────────────────────
function UsageMonitorSettings() {
  const [stats, setStats]         = useState(null);
  const [balance, setBalance]     = useState(null);
  const [threshold, setThreshold] = useState(500000);
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/usage/stats").then(r => setStats(r.data)).catch(() => {}),
      api.get("/usage/balance").then(r => setBalance(r.data)).catch(() => {}),
      api.get("/usage/alert-config").then(r => setThreshold(r.data.threshold)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const saveThreshold = async () => {
    setSaving(true);
    try {
      await api.post("/usage/alert-config", { threshold });
      toast.success("Uyarı eşiği kaydedildi");
    } catch { toast.error("Kayıt hatası"); } finally { setSaving(false); }
  };

  const STATUS_COLORS = {
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    configured: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    not_configured: "bg-slate-100 text-slate-500 dark:bg-slate-700/30 dark:text-slate-400",
  };
  const STATUS_LABELS = { active: "Aktif", configured: "Yapılandırıldı", not_configured: "Yapılandırılmadı" };

  const today = stats?.today || {};
  const pct = today.threshold ? Math.min(100, Math.round((today.tokens / today.threshold) * 100)) : 0;

  return (
    <div className="space-y-4">
      {/* Anlık API Durumu */}
      <div className="zen-panel p-4">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-500" /> Anlık API Durumu
        </h3>
        {loading ? (
          <p className="text-xs text-slate-400 animate-pulse">Yükleniyor...</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {(balance || []).map((api_item, i) => (
              <div key={i} className="flex items-start justify-between rounded-xl border border-slate-200 dark:border-[#22304A] p-3 bg-slate-50 dark:bg-[#0d1524]">
                <div>
                  <p className="font-semibold text-sm">{api_item.name}</p>
                  {api_item.api && <p className="text-xs text-slate-400">{api_item.api}</p>}
                  {api_item.role && <p className="text-xs text-slate-500 mt-0.5 italic">{api_item.role}</p>}
                  {api_item.note && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{api_item.note}</p>}
                  {api_item.balance != null && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">
                      Bakiye: {api_item.balance} {api_item.currency || ""}
                    </p>
                  )}
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLORS[api_item.status] || STATUS_COLORS.configured}`}>
                  {STATUS_LABELS[api_item.status] || api_item.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bugünkü Kullanım */}
      <div className="zen-panel p-4">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-blue-500" /> Bugünkü Kullanım
        </h3>
        {loading ? <p className="text-xs text-slate-400 animate-pulse">Yükleniyor...</p> : (
          <div className="space-y-3">
            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>{today.tokens?.toLocaleString() || 0} token</span>
                <span>Eşik: {today.threshold?.toLocaleString()}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-200 dark:bg-[#1A2640] overflow-hidden">
                <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-500" : pct >= 70 ? "bg-amber-400" : "bg-emerald-500"}`}
                     style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-slate-400 mt-1">{pct}% kullanıldı — Tahmini maliyet: <b className="text-slate-600 dark:text-slate-300">${today.costEst ?? 0}</b></p>
            </div>
            {/* Kaynak dağılımı */}
            {today.bySource && Object.keys(today.bySource).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">Kaynak Dağılımı</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(today.bySource).map(([k, v]) => (
                    <span key={k} className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-mono">
                      {k}: {v.toLocaleString()}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* 7 günlük tablo */}
            {stats?.byDate && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">Son 7 Gün</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(stats.byDate).reverse().map(([d, t]) => {
                        const barW = stats.total?.tokens ? Math.max(4, Math.round((t / Math.max(...Object.values(stats.byDate))) * 100)) : 4;
                        return (
                          <tr key={d} className="border-b border-slate-100 dark:border-[#22304A] last:border-0">
                            <td className="py-1 text-slate-500 w-24">{d.slice(5)}</td>
                            <td className="py-1 w-full">
                              <div className="h-1.5 rounded-full bg-blue-500/20">
                                <div className="h-full rounded-full bg-blue-500" style={{ width: `${barW}%` }} />
                              </div>
                            </td>
                            <td className="py-1 text-right font-mono text-slate-600 dark:text-slate-300 pl-2">{t.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <p className="text-xs text-slate-400 border-t border-slate-100 dark:border-[#22304A] pt-2">
              Toplam kayıtlı: <b className="text-slate-600 dark:text-slate-300">{stats?.total?.tokens?.toLocaleString() || 0} token</b> — {stats?.total?.calls || 0} çağrı
            </p>
          </div>
        )}
      </div>

      {/* Uyarı Eşiği */}
      <div className="zen-panel p-4">
        <h3 className="font-bold mb-1 flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-500" /> Uyarı Sistemi
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Günlük kullanım bu eşiği aştığında <b>AI Numaraları</b>'nda kayıtlı Tam Erişim WhatsApp numaralarına otomatik uyarı gönderilir.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 block mb-1">Günlük Token Eşiği</label>
            <input
              data-testid="usage-threshold-input"
              type="number"
              min={1000}
              step={10000}
              value={threshold}
              onChange={e => setThreshold(parseInt(e.target.value) || 500000)}
              className="zen-input text-sm w-full"
            />
          </div>
          <button data-testid="usage-threshold-save" onClick={saveThreshold} disabled={saving}
                  className="zen-btn-primary !text-xs mt-5">
            {saving ? "..." : "Kaydet"}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          500.000 token ≈ ~100-150 ERP sorgusu. Tahmini günlük maliyet: ~${(threshold * 9 / 1_000_000).toFixed(3)}
        </p>
      </div>
    </div>
  );
}


// ─── Ayarlar > Yedek / Geri Yükleme ─────────────────────────────────────────
function BackupSettings() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const doExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(resp.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zen-erp-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Yedek başarıyla indirildi");
    } catch (e) {
      toast.error("Yedek alınamadı: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const doImport = async (file) => {
    if (!file) return;
    const confirmed = window.confirm(
      restoreMode === "full_reset"
        ? "⚠ TAM SIFIRLAMA: Mevcut tüm veriler silinecek ve yedekten yüklenecek. Emin misiniz?"
        : "Yedekteki eksik kayıtlar eklenecek. Mevcut veriler korunur. Devam edilsin mi?"
    );
    if (!confirmed) return;
    setImporting(true); setResult(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const resp = await api.post("/backup/restore", { mode: restoreMode, data });
      setResult(resp.data);
      toast.success("Geri yükleme tamamlandı");
    } catch (e) {
      toast.error("Geri yükleme hatası: " + (e?.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4 zen-anim-up" data-testid="backup-settings">
      {/* Export */}
      <div className="zen-panel p-5 space-y-3">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center"><FileDown className="w-5 h-5 text-emerald-500" /></div>
          <div>
            <h3 className="font-bold">Veri Yedekleme</h3>
            <p className="text-xs text-slate-500">Tüm veriler (müşteri, teklif, katalog, ayarlar, kartela...) tek JSON dosyasına</p>
          </div>
        </div>
        <button data-testid="backup-export-btn" onClick={doExport} disabled={exporting} className="zen-btn-primary disabled:opacity-50">
          {exporting ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Hazırlanıyor...</> : <><FileDown className="w-4 h-4" />Yedeği İndir (.json)</>}
        </button>
      </div>

      {/* Import */}
      <div className="zen-panel p-5 space-y-4">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-[#E5322A]/10 flex items-center justify-center"><Upload className="w-5 h-5 text-[#E5322A]" /></div>
          <div>
            <h3 className="font-bold">Veri Geri Yükleme</h3>
            <p className="text-xs text-slate-500">Daha önce alınan JSON yedekten geri yükle</p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-500">Geri Yükleme Modu</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { val: "merge", icon: "✚", title: "Sadece Eksikleri Ekle", desc: "Mevcut veriler korunur, yalnızca yeni kayıtlar eklenir" },
              { val: "full_reset", icon: "⚠", title: "Tam Sıfırla + Yükle", desc: "Mevcut tüm veriler silinir, yedek tam olarak yüklenir" },
            ].map(m => (
              <button key={m.val} data-testid={`restore-mode-${m.val}`} onClick={() => setRestoreMode(m.val)}
                className={`p-3 rounded-xl border text-left transition-all ${restoreMode === m.val ? (m.val === "full_reset" ? "border-[#E5322A] bg-[#E5322A]/5" : "border-emerald-500 bg-emerald-500/5") : "border-slate-200 dark:border-[#22304A]"}`}>
                <div className="text-sm font-bold">{m.icon} {m.title}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <button data-testid="backup-import-btn" onClick={() => fileRef.current?.click()} disabled={importing} className={`zen-btn-primary disabled:opacity-50 ${restoreMode === "full_reset" ? "!bg-[#E5322A]" : ""}`}>
          {importing ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Yükleniyor...</> : <><Upload className="w-4 h-4" />JSON Yedek Seç</>}
        </button>
        <input ref={fileRef} type="file" accept=".json" className="hidden" data-testid="backup-file-input"
          onChange={e => { doImport(e.target.files[0]); e.target.value = ""; }} />

        {result && (
          <div className="zen-panel !p-3 space-y-1 bg-emerald-500/5 border-emerald-400/20 text-xs max-h-48 overflow-y-auto" data-testid="backup-result">
            <div className="font-bold text-emerald-600 dark:text-emerald-400">Geri Yükleme Sonucu ({result.mode})</div>
            {Object.entries(result.stats || {}).map(([col, s]) => (
              <div key={col} className="text-slate-500">{col}: +{s.inserted} eklendi, {s.skipped} atlandı</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ayarlar > Yönetici AI Chat (Claude Sonnet) ───────────────────────────────
function AdminChatSettings() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(() => `ses-${Date.now()}`);
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/admin-chat/sessions");
      setSessions(data || []);
    } catch {}
  };

  useEffect(() => { loadSessions(); }, []);

  const saveSession = async (msgs, sid) => {
    if (!msgs.length) return;
    try {
      await api.post("/admin-chat/sessions", { messages: msgs, sessionId: sid });
      loadSessions();
    } catch {}
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const newHistory = [...messages, { role: "user", content: text }];
    setMessages(newHistory);
    setLoading(true);
    try {
      const resp = await api.post("/admin-chat", { message: text, history: messages });
      const updated = [...newHistory, { role: "assistant", content: resp.data.reply }];
      setMessages(updated);
      await saveSession(updated, currentSessionId);
    } catch (e) {
      setMessages([...newHistory, { role: "assistant", content: `Hata: ${e?.response?.data?.detail || e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const newSession = () => {
    setMessages([]);
    setCurrentSessionId(`ses-${Date.now()}`);
  };

  const loadSession = (s) => {
    setMessages(s.messages || []);
    setCurrentSessionId(s.id);
    setShowHistory(false);
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/admin-chat/sessions/${id}`);
    loadSessions();
    if (currentSessionId === id) newSession();
  };

  return (
    <div className="flex gap-3 h-[600px]">
      {/* Geçmiş Panel */}
      <div className={`zen-panel flex flex-col transition-all overflow-hidden ${showHistory ? "w-56 shrink-0" : "w-0 border-0 p-0"}`}>
        {showHistory && (
          <>
            <div className="px-3 pt-3 pb-2 border-b border-slate-200 dark:border-[#22304A] text-xs font-bold shrink-0 flex justify-between items-center">
              <span>Geçmiş Oturumlar</span>
              <button onClick={newSession} className="zen-btn-ghost !py-0.5 !px-2 !text-[10px]">+ Yeni</button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1 p-2">
              {sessions.length === 0 && <div className="text-[11px] text-slate-400 p-2">Henüz kayıtlı oturum yok</div>}
              {sessions.map(s => (
                <div key={s.id}
                  data-testid={`admin-chat-session-${s.id}`}
                  onClick={() => loadSession(s)}
                  className={`group flex items-start gap-1 p-2 rounded-lg cursor-pointer text-[11px] hover:bg-slate-100 dark:hover:bg-[#0C1220] ${currentSessionId === s.id ? "bg-slate-100 dark:bg-[#0C1220]" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{s.title || "Oturum"}</div>
                    <div className="text-slate-400">{new Date(s.createdAt).toLocaleDateString("tr-TR")} · {s.messageCount} mesaj</div>
                  </div>
                  <button onClick={(e) => deleteSession(s.id, e)} className="opacity-0 group-hover:opacity-100 hover:text-rose-500 mt-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Ana Chat */}
      <div className="zen-panel flex flex-col flex-1 zen-anim-up" data-testid="admin-chat-settings">
        <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-[#22304A] shrink-0">
          <div className="w-9 h-9 rounded-lg bg-violet-500/10 flex items-center justify-center"><Sparkles className="w-5 h-5 text-violet-500" /></div>
          <div>
            <h3 className="font-bold">Yönetici AI Chat</h3>
            <p className="text-xs text-slate-500">ZEN ERP kod asistanı — Claude Sonnet · Türkçe</p>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => { setShowHistory(h => !h); if (!showHistory) loadSessions(); }}
              data-testid="admin-chat-history-btn"
              className={`zen-btn-ghost !text-xs !py-1 flex items-center gap-1.5 ${showHistory ? "!text-violet-500 !border-violet-400" : ""}`}>
              <History className="w-3.5 h-3.5" /> Geçmiş
            </button>
            <button onClick={newSession} className="zen-btn-ghost !text-xs !py-1">Yeni</button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-12">
              <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p>ZEN ERP hakkında herhangi bir soruyu sorabilirsiniz.</p>
              <p className="text-xs mt-1">Kod hatası, yeni özellik önerisi, mantık açıklaması...</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${m.role === "user" ? "bg-[#E5322A] text-white" : "bg-violet-500/10 text-violet-500"}`}>
                {m.role === "user" ? "S" : "AI"}
              </div>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#E5322A] text-white rounded-tr-sm" : "bg-slate-100 dark:bg-[#131B2E] rounded-tl-sm"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-violet-500/10 flex items-center justify-center text-xs text-violet-500 font-bold">AI</div>
              <div className="bg-slate-100 dark:bg-[#131B2E] rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex gap-1"><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} /></div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="p-3 border-t border-slate-200 dark:border-[#22304A] flex gap-2 shrink-0">
          <input
            data-testid="admin-chat-input"
            className="zen-input flex-1"
            placeholder="Soru sor veya kod hatası yaz..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            disabled={loading}
          />
          <button data-testid="admin-chat-send" onClick={send} disabled={loading || !input.trim()} className="zen-btn-primary !px-4 disabled:opacity-50">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── Ayarlar > Motor Alış Fiyatları ─────────────────────────────────────────
const MOTOR_GROUP_LABELS = {
  somfy_tirajli: "Somfy — Tirajlı Ray",
  somfy_stor:    "Somfy — Stor / Zebra Tüp",
  somfy_cap25:   "Somfy — Çap 25 / Plise",
  nice_era:      "Nice — Era / Nano",
  nice_genel:    "Nice — Nexus / Genel",
  mosel_genel:   "Mosel Motorları",
  becker_genel:  "Becker Motorları",
  aok_genel:     "AOK Motorları",
  novo_dikey:    "Novo — Dikey / Zip",
  dikey:         "Dikey Motorları",
  stor:          "Stor Motorları",
  tirajli_ray:   "Tirajlı Ray Motorları",
  jaluzi_plise:  "Jaluzi / Plise Motorları",
  ahsap:         "Ahşap Jaluzi Motorları",
  diger:         "Diğer Motorlar",
};
const MOTOR_GROUP_OPTIONS = Object.entries(MOTOR_GROUP_LABELS).map(([k, v]) => ({ value: k, label: v }));
// ──────────────────────────────────────────────────────────────────────────────
// RUSTİK FİYAT LİSTESİ
// ──────────────────────────────────────────────────────────────────────────────
const RUSTIK_WIDTHS_13 = [100, 125, 150, 175, 200, 225, 250, 275, 300, 325, 350, 375, 400];
const RUSTIK_WIDTHS_7  = [100, 150, 200, 250, 300, 350, 400];

const RUSTIK_KANALLI_DATA = {
  tekli:  { label:"Tekli (AK 1-2-3)", alis:[990,1040,1090,1340,1390,1440,1690,1740,1790,1840,2090,2140,2190], satis:[1980,2080,2180,2680,2780,2880,3380,3480,3580,3680,4180,4280,4380] },
  ciftli: { label:"Çiftli (4-5-6-7)",  alis:[1430,1530,1630,1960,2060,2160,2490,2590,2690,2790,3120,3220,3320], satis:[2860,3060,3260,3920,4120,4320,4980,5180,5380,5580,6240,6440,6640] },
};
const RUSTIK_AHSAP_DATA = {
  alis:  [545,681,818,954,1090,1226,1363,1499,1635,1771,1908,2044,2180],
  satis: [1090,1362,1636,1908,2180,2452,2726,2998,3270,3542,3816,4088,4360],
};
const RUSTIK_ANTIK_DATA = {
  "30mm": { label:"Ø30 MM", alis:[1750,2140,3125,3375,3750,4250,4750], satis:[3500,4280,6250,6750,7500,8500,9500] },
  "40mm": { label:"Ø40 MM", alis:[1875,2500,3250,3500,4000,4375,4875], satis:[3750,5000,6500,7000,8000,8750,9750] },
};
const RUSTIK_PARCALAR = {
  kanalli: [
    {label:"Boru (20lik Kanallı)", birim:"MT",   alis:200, satis:400},
    {label:"Ayak Tekli",           birim:"ADET", alis:200, satis:400},
    {label:"Ayak Çiftli",          birim:"ADET", alis:230, satis:460},
    {label:"Başlık Alüminyum",     birim:"ADET", alis:195, satis:390},
    {label:"Tapa Başlık",          birim:"ADET", alis:90,  satis:180},
  ],
  ahsap: [
    {label:"Boru Ø28 (tüm renkler)", birim:"MT", alis:545, satis:1090},
    {label:"Ray",                     birim:"MT", alis:140, satis:280},
  ],
  antik: [
    {label:"Ray Kanallı", birim:"MT", alis:275, satis:550},
  ],
};

function RustikPriceListSettings() {
  const { user } = useContext(AuthCtx);
  const [activeTab, setActiveTab] = useState("kanalli");
  const [priceView, setPriceView] = useState("both"); // both | alis | satis
  if (user?.role !== "admin") return null;

  const fmtTL = (n) => `${n.toLocaleString("tr-TR")} ₺`;
  const tabs = [
    { id: "kanalli", label: "Alüminyum Kanallı" },
    { id: "ahsap",   label: "Ahşap Rustik" },
    { id: "antik",   label: "Antik & Retro" },
  ];

  const MatrixTable = ({ widths, rows, parcalar }) => (
    <div className="space-y-4">
      {/* Matrix */}
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] border-collapse">
          <thead>
            <tr className="bg-amber-50 dark:bg-amber-900/20">
              <th className="border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-left font-bold text-slate-600 dark:text-slate-300 min-w-[120px]">Model</th>
              <th className="border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-center font-bold text-slate-500 w-14">Tür</th>
              {widths.map(w => (
                <th key={w} className="border border-slate-200 dark:border-slate-700 px-1.5 py-1.5 text-center font-bold text-slate-500 min-w-[52px]">{w}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <React.Fragment key={ri}>
                {(priceView === "both" || priceView === "alis") && (
                  <tr className={ri % 2 === 0 ? "" : "bg-slate-50/60 dark:bg-slate-800/20"}>
                    <td className="border border-slate-200 dark:border-slate-700 px-2 py-1.5 font-semibold text-slate-700 dark:text-slate-200 text-[10px]">{row.label}</td>
                    <td className="border border-slate-200 dark:border-slate-700 px-1.5 py-1 text-center text-[9px] font-bold text-slate-400 bg-slate-50 dark:bg-slate-800/30">Alış</td>
                    {row.alis.map((p, i) => (
                      <td key={i} className="border border-slate-200 dark:border-slate-700 px-1.5 py-1 text-center font-mono text-slate-500 dark:text-slate-400">{p.toLocaleString("tr-TR")}</td>
                    ))}
                  </tr>
                )}
                {(priceView === "both" || priceView === "satis") && (
                  <tr className="bg-emerald-50/50 dark:bg-emerald-900/10">
                    {priceView === "satis" && <td className="border border-slate-200 dark:border-slate-700 px-2 py-1.5 font-semibold text-slate-700 dark:text-slate-200 text-[10px]">{row.label}</td>}
                    {priceView === "both"  && <td className="border border-slate-200 dark:border-slate-700 px-2 py-1 text-[9px] text-slate-300 dark:text-slate-600 italic">&nbsp;</td>}
                    <td className="border border-slate-200 dark:border-slate-700 px-1.5 py-1 text-center text-[9px] font-bold text-emerald-600">Satış</td>
                    {row.satis.map((p, i) => (
                      <td key={i} className="border border-slate-200 dark:border-slate-700 px-1.5 py-1 text-center font-mono font-semibold text-emerald-700 dark:text-emerald-400">{p.toLocaleString("tr-TR")}</td>
                    ))}
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Parçalar */}
      {parcalar?.length > 0 && (
        <div>
          <div className="text-[10px] font.bold uppercase tracking-wider text-slate-400 mb-1.5">Bireysel Parçalar</div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/40 text-[10px] uppercase text-slate-400">
                <th className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-left">Ürün</th>
                <th className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-center">Birim</th>
                {(priceView === "both" || priceView === "alis")  && <th className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-right">Alış ₺</th>}
                {(priceView === "both" || priceView === "satis") && <th className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-right text-emerald-600">Satış ₺</th>}
              </tr>
            </thead>
            <tbody>
              {parcalar.map((p, i) => (
                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/20">
                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 font-medium text-slate-700 dark:text-slate-200">{p.label}</td>
                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-center text-slate-400">{p.birim}</td>
                  {(priceView === "both" || priceView === "alis")  && <td className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-right font-mono text-slate-500">{p.alis.toLocaleString("tr-TR")}</td>}
                  {(priceView === "both" || priceView === "satis") && <td className="border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">{p.satis.toLocaleString("tr-TR")}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Başlık */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">Rustik Ray Fiyat Listesi</h2>
          <p className="text-xs text-slate-400 mt-0.5">Tedarikçi alış fiyatları ve %100 kar marjlı satış fiyatları</p>
        </div>
        <div className="flex gap-1.5">
          {[{v:"both",l:"Alış + Satış"},{v:"alis",l:"Alış"},{v:"satis",l:"Satış"}].map(o => (
            <button key={o.v} onClick={() => setPriceView(o.v)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition ${priceView === o.v ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-amber-400"}`}>
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {/* Tip Sekmeleri */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-0">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition -mb-px ${activeTab === t.id ? "border-amber-500 text-amber-600 dark:text-amber-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="zen-panel p-4">
        {activeTab === "kanalli" && (
          <MatrixTable
            widths={RUSTIK_WIDTHS_13}
            rows={[RUSTIK_KANALLI_DATA.tekli, RUSTIK_KANALLI_DATA.ciftli]}
            parcalar={RUSTIK_PARCALAR.kanalli}
          />
        )}
        {activeTab === "ahsap" && (
          <MatrixTable
            widths={RUSTIK_WIDTHS_13}
            rows={[{ label: "Tüm Renkler (545₺ bazlı)", ...RUSTIK_AHSAP_DATA }]}
            parcalar={RUSTIK_PARCALAR.ahsap}
          />
        )}
        {activeTab === "antik" && (
          <MatrixTable
            widths={RUSTIK_WIDTHS_7}
            rows={[RUSTIK_ANTIK_DATA["30mm"], RUSTIK_ANTIK_DATA["40mm"]]}
            parcalar={RUSTIK_PARCALAR.antik}
          />
        )}
      </div>

      <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-4 text-xs text-slate-500 space-y-1">
        <div className="font-semibold text-slate-600 dark:text-slate-300">Notlar</div>
        <div>• Fiyatlar KDV %20 hariçtir. Tüm fiyatlar Türk Lirası (₺) cinsindendir.</div>
        <div>• Ray ek fiyatı: Kanallı 400 ₺/mt · Ahşap 280 ₺/mt · Antik & Retro 550 ₺/mt (satış)</div>
        <div>• Ray isteğe bağlıdır. Fiyat cetvelindeki ölçüler takım halinde Halkalı ve Raysız olarak hesaplanmıştır.</div>
        <div>• Sipariş formunda genişlik (En) girildiğinde fiyat otomatik hesaplanır.</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// ZIP FİYATLANDIRMA AYARLARI
// ──────────────────────────────────────────────────────────────────────────────
function ZipPricingSettings() {
  const { user } = useContext(AuthCtx);
  const [rules, setRules] = useState({ moselSolarExtra: 150, yayliDiscount: 50, moselRtsDiscount: 30 });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings?category=pricing_rules")
      .then(({ data }) => {
        const row = (data || []).find(r => r.key === "zip_pricing");
        if (row?.value && typeof row.value === "object") {
          setRules(prev => ({ ...prev, ...row.value }));
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  if (user?.role !== "admin") return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/settings", {
        category: "pricing_rules",
        key: "zip_pricing",
        label: "Zip Motor ve İskonto Kuralları",
        value: rules,
      });
      toast.success("Zip fiyatlandırma kuralları kaydedildi");
    } catch {
      toast.error("Kayıt hatası");
    }
    setSaving(false);
  };

  const Field = ({ label, desc, field, suffix = "EUR" }) => (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{label}</div>
        <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          step="0.5"
          data-testid={`zip-pricing-${field}`}
          className="w-24 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-sm font-mono font-bold text-right outline-none focus:ring-2 focus:ring-amber-400/40 transition"
          value={rules[field] ?? ""}
          onChange={e => setRules(prev => ({ ...prev, [field]: parseFloat(e.target.value) || 0 }))}
        />
        <span className="text-xs text-slate-400 w-8">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">Zip Fiyatlandırma Kuralları</h2>
          <p className="text-xs text-slate-400 mt-0.5">Zip ürünleri için motor ek ücretleri ve yaylı sistem indirimini buradan yönetin</p>
        </div>
        <button
          data-testid="zip-pricing-save"
          onClick={handleSave}
          disabled={saving || !loaded}
          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>

      <div className="zen-panel p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1.5">
          <Zap className="w-3 h-3" /> Motor Fiyat Ayarlamaları — Dikey Zip & Sky Zip
        </div>
        <p className="text-xs text-slate-400 mb-3">Bu kurallar Dikey Zip ve Sky Zip matris fiyatına uygulanır.</p>
        <Field
          label="Mosel Solar Motor Ek Ücreti"
          desc="Solar motorlu Dikey Zip seçildiğinde matris fiyatına eklenir (+)"
          field="moselSolarExtra"
          suffix="EUR"
        />
        <Field
          label="Yaylı Sistem (Motorsuz) İndirimi"
          desc="Motor yerine yaylı sistem seçildiğinde matris fiyatından çıkarılır (−)"
          field="yayliDiscount"
          suffix="EUR"
        />
        <Field
          label="Otomasyon / RTS / Mosel Motor İndirimi"
          desc="Mosel RTS, Otomasyon veya Mosel 40/12 motor seçildiğinde matris fiyatından çıkarılır (−)"
          field="moselRtsDiscount"
          suffix="EUR"
        />
      </div>

      <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-4 text-xs text-slate-500 dark:text-slate-400 space-y-1">
        <div className="font-semibold text-slate-600 dark:text-slate-300">Nasıl çalışır?</div>
        <div>• Dikey Zip formunda "Mosel Solar Motor" seçilirse fiyata <b>+{rules.moselSolarExtra} EUR</b> eklenir.</div>
        <div>• Dikey Zip formunda "Yaylı Sistem (Motorsuz)" seçilirse fiyattan <b>−{rules.yayliDiscount} EUR</b> çıkarılır.</div>
        <div>• Dikey Zip / Sky Zip'te Mosel RTS, Otomasyon veya Mosel 40/12 motor seçilirse fiyattan <b>−{rules.moselRtsDiscount} EUR</b> çıkarılır.</div>
        <div>• Matris İskonto (%), Admin/Staff kullanıcıların Dikey Zip formunda görebileceği bir alandır ve müşteri profil iskontosu ile önceden doldurulur.</div>
      </div>
    </div>
  );
}

// ─── Motor Formu Modal ────────────────────────────────────────────────────────
function MotorFormModal({ motor, onClose, onSave }) {
  const [f, setF] = useState({
    label: motor?.label || "",
    group: motor?.group || "stor",
    purchasePrice: motor?.purchasePrice || 0,
    purchaseCurrency: motor?.purchaseCurrency || "USD",
    price: motor?.price || 0,
    currency: motor?.currency || "USD",
    code: motor?.code || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!f.label.trim()) { toast.error("Motor adı zorunlu"); return; }
    setSaving(true);
    try {
      let result;
      if (motor?.id) {
        const { data } = await api.put(`/assets/${motor.id}`, { ...f, kind: "motor" });
        result = data;
        toast.success("Motor güncellendi");
      } else {
        const { data } = await api.post("/assets", { ...f, kind: "motor" });
        result = data;
        toast.success("Motor eklendi");
      }
      onSave(result, !!motor?.id);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt hatası"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full bg-white dark:bg-[#0C1220] border border-slate-200 dark:border-[#22304A] rounded-lg px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-[#E5322A]/50 text-slate-800 dark:text-slate-200";
  const labelCls = "block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="motor-form-modal">
      <div className="bg-white dark:bg-[#131B2E] rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-[#22304A] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-[#22304A]">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white">{motor ? "Motoru Düzenle" : "Yeni Motor Ekle"}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className={labelCls}>Motor Adı *</label>
            <input className={inputCls} placeholder="örn: Somfy Sonesse RTS 40 3/30" value={f.label}
              onChange={e => set("label", e.target.value)} autoFocus data-testid="motor-modal-label" />
          </div>
          <div>
            <label className={labelCls}>Grup / Kategori</label>
            <select className={inputCls} value={f.group} onChange={e => set("group", e.target.value)} data-testid="motor-modal-group">
              {MOTOR_GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Alış Fiyatı</label>
              <input type="number" min="0" step="0.01" className={inputCls + " text-right font-mono"}
                value={f.purchasePrice} onChange={e => set("purchasePrice", parseFloat(e.target.value) || 0)}
                data-testid="motor-modal-purchase-price" />
            </div>
            <div>
              <label className={labelCls}>Alış Döviz</label>
              <select className={inputCls} value={f.purchaseCurrency} onChange={e => set("purchaseCurrency", e.target.value)} data-testid="motor-modal-purchase-cur">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="TL">TL</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Satış Fiyatı <span className="text-slate-300 font-normal">(EUR)</span></label>
              <input type="number" min="0" step="0.01" className={inputCls + " text-right font-mono"}
                value={f.price} onChange={e => set("price", parseFloat(e.target.value) || 0)}
                data-testid="motor-modal-price" />
            </div>
            <div>
              <label className={labelCls}>Satış Döviz</label>
              <select className={inputCls} value={f.currency} onChange={e => set("currency", e.target.value)} data-testid="motor-modal-cur">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="TL">TL</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Ürün Kodu <span className="text-slate-300 font-normal">(opsiyonel)</span></label>
            <input className={inputCls + " font-mono"} placeholder="örn: 1003008" value={f.code}
              onChange={e => set("code", e.target.value)} data-testid="motor-modal-code" />
          </div>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-xl border border-slate-200 dark:border-[#22304A] text-xs font-semibold text-slate-500 hover:bg-slate-50 dark:hover:bg-[#0C1220] transition-colors">İptal</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-2 rounded-xl bg-[#E5322A] hover:bg-[#c42a22] text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors"
            data-testid="motor-modal-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {saving ? "Kaydediliyor..." : (motor ? "Güncelle" : "Ekle")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MotorPricesSection() {
  const { user } = useContext(AuthCtx);
  const [motors, setMotors] = useState([]);
  const [rates, setRates] = useState({ usdToTl: 34, eurToTl: 38 });
  const [saving, setSaving] = useState({});
  const [edits, setEdits] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingMotor, setEditingMotor] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/assets?kind=motor"),
      api.get("/rates").catch(() => ({ data: {} })),
    ]).then(([m, r]) => {
      setMotors((m.data || []).filter(mo => !mo.deleted));
      const rd = r.data || {};
      setRates({ usdToTl: rd.usdToTl || rd.usdBuy || 34, eurToTl: rd.eurToTl || rd.eurBuy || 38 });
    }).catch(() => api.get("/assets?kind=motor").then(m => setMotors((m.data || []).filter(mo => !mo.deleted))));
  }, []);

  if (user?.role !== "admin") return null;

  const saveMotorPrice = async (motor) => {
    const edit = edits[motor.id] || {};
    const price = parseFloat(edit.price ?? motor.purchasePrice ?? 0);
    const currency = edit.currency || motor.purchaseCurrency || "USD";
    if (isNaN(price)) return;
    setSaving(p => ({ ...p, [motor.id]: true }));
    try {
      await api.put(`/assets/${motor.id}`, { purchasePrice: price, purchaseCurrency: currency });
      setMotors(prev => prev.map(m => m.id === motor.id ? { ...m, purchasePrice: price, purchaseCurrency: currency } : m));
      setEdits(p => { const n = { ...p }; delete n[motor.id]; return n; });
      toast.success("Fiyat kaydedildi");
    } catch { toast.error("Kayıt hatası"); }
    setSaving(p => { const n = { ...p }; delete n[motor.id]; return n; });
  };

  const handleDelete = async (motor) => {
    if (!window.confirm(`"${motor.label}" motorunu silmek istiyor musunuz?`)) return;
    setDeletingId(motor.id);
    try {
      await api.delete(`/assets/${motor.id}`);
      setMotors(prev => prev.filter(m => m.id !== motor.id));
      toast.success("Motor silindi");
    } catch { toast.error("Silinemedi"); }
    setDeletingId(null);
  };

  const handleModalSave = (result, isUpdate) => {
    if (isUpdate) setMotors(prev => prev.map(m => m.id === result.id ? result : m));
    else setMotors(prev => [...prev, result]);
  };

  const toTl = (price, cur) => {
    const rate = cur === "EUR" ? rates.eurToTl : cur === "USD" ? rates.usdToTl : 1;
    return price > 0 ? Math.round(price * rate) : 0;
  };

  const grouped = motors.reduce((acc, m) => {
    const grp = m.group || "diger"; (acc[grp] = acc[grp] || []).push(m); return acc;
  }, {});

  const missingPriceCount = motors.filter(m => !m.purchasePrice || m.purchasePrice === 0).length;

  return (
    <div className="space-y-5">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">Motor Alış Fiyatları</h2>
          <p className="text-xs text-slate-400 mt-0.5">Teklif hesaplamalarında kullanılır — günlük kur ile TL'ye çevrilir</p>
        </div>
        <div className="flex items-center gap-2">
          {missingPriceCount > 0 && (
            <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
              {missingPriceCount} fiyatsız motor
            </span>
          )}
          <span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg">
            USD={rates.usdToTl} · EUR={rates.eurToTl} TL
          </span>
          <button
            onClick={() => { setEditingMotor(null); setShowModal(true); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#E5322A] hover:bg-[#c42a22] text-white text-xs font-semibold transition-colors"
            data-testid="motor-add-btn">
            <Plus className="w-3.5 h-3.5" /> Yeni Motor
          </button>
        </div>
      </div>

      {/* Gruplar — marka sırasına göre */}
      {Object.entries(grouped)
        .sort(([a], [b]) => {
          const order = Object.keys(MOTOR_GROUP_LABELS);
          const ia = order.indexOf(a); const ib = order.indexOf(b);
          if (ia === -1 && ib === -1) return a.localeCompare(b, "tr");
          if (ia === -1) return 1; if (ib === -1) return -1;
          return ia - ib;
        })
        .map(([grp, grpMotors]) => (
        <div key={grp} className="zen-panel p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {MOTOR_GROUP_LABELS[grp] || grp}
              <span className="ml-2 font-normal text-slate-400">({grpMotors.length})</span>
            </h3>
            <button
              onClick={() => { setEditingMotor({ group: grp }); setShowModal(true); }}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-slate-400 border border-slate-200 dark:border-[#22304A] hover:border-emerald-400 hover:text-emerald-500 transition-colors"
              data-testid={`motor-add-to-group-${grp}`}>
              <Plus className="w-3 h-3" /> Bu gruba ekle
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="text-left pb-2 pr-4">Motor Adı</th>
                  <th className="text-center pb-2 px-2 w-28">Alış Fiyatı</th>
                  <th className="text-center pb-2 px-2 w-16">Döviz</th>
                  <th className="text-right pb-2 pl-4 w-28">≈ TL</th>
                  <th className="pb-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {grpMotors.map(motor => {
                  const ed = edits[motor.id] || {};
                  const curPrice = ed.price !== undefined ? ed.price : (motor.purchasePrice || "");
                  const curCur = ed.currency || motor.purchaseCurrency || "USD";
                  const isDirty = ed.price !== undefined || ed.currency !== undefined;
                  const tlVal = toTl(parseFloat(curPrice) || 0, curCur);
                  const missingPrice = !motor.purchasePrice || motor.purchasePrice === 0;
                  return (
                    <tr key={motor.id}
                      className={`group border-t border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${missingPrice ? "bg-amber-50/40 dark:bg-amber-900/10" : ""}`}>
                      <td className="py-2 pr-4 font-medium">
                        <span className={missingPrice ? "text-amber-600 dark:text-amber-400" : ""}>{motor.label}</span>
                        {motor.code && <span className="ml-1.5 text-[9px] font-mono text-slate-400">{motor.code}</span>}
                        {missingPrice && <span className="ml-1.5 text-[9px] font-bold text-amber-500 bg-amber-100 dark:bg-amber-900/30 px-1 rounded">FİYAT GİR</span>}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <input type="number" min="0" step="0.01"
                          className="w-24 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-xs font-mono text-right outline-none focus:ring-1 focus:ring-[#E5322A]/40 focus:border-[#E5322A]/40"
                          value={curPrice}
                          onChange={e => setEdits(p => ({ ...p, [motor.id]: { ...p[motor.id], price: e.target.value } }))}
                          data-testid={`motor-purchase-price-${motor.id}`}
                        />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <select
                          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-1.5 py-1 text-[10px] outline-none focus:ring-1 focus:ring-[#E5322A]/40"
                          value={curCur}
                          onChange={e => setEdits(p => ({ ...p, [motor.id]: { ...p[motor.id], currency: e.target.value } }))}>
                          <option>USD</option><option>EUR</option><option>TL</option>
                        </select>
                      </td>
                      <td className={`py-2 pl-4 text-right font-mono font-semibold ${tlVal > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-slate-300"}`}>
                        {tlVal > 0 ? `${tlVal.toLocaleString("tr-TR")} ₺` : "—"}
                      </td>
                      <td className="py-2 pl-2">
                        <div className="flex items-center justify-end gap-1">
                          {isDirty && (
                            <button onClick={() => saveMotorPrice(motor)} disabled={saving[motor.id]}
                              className="px-2.5 py-1 bg-[#E5322A] text-white rounded-lg text-[10px] font-semibold hover:bg-[#c42a23] disabled:opacity-50 transition-colors"
                              data-testid={`motor-save-${motor.id}`}>
                              {saving[motor.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Kaydet"}
                            </button>
                          )}
                          <button
                            onClick={() => { setEditingMotor(motor); setShowModal(true); }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
                            title="Düzenle" data-testid={`motor-edit-${motor.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(motor)}
                            disabled={deletingId === motor.id}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all disabled:opacity-50"
                            title="Sil" data-testid={`motor-delete-${motor.id}`}>
                            {deletingId === motor.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {motors.length === 0 && (
        <div className="text-center py-16 text-slate-400">
          <Zap className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-semibold mb-1">Henüz motor eklenmemiş</p>
          <p className="text-xs mb-4">Motor alış fiyatlarını buradan yönetin</p>
          <button onClick={() => { setEditingMotor(null); setShowModal(true); }}
            className="px-4 py-2 bg-[#E5322A] text-white rounded-xl text-xs font-semibold hover:bg-[#c42a22] transition-colors">
            İlk Motoru Ekle
          </button>
        </div>
      )}

      {showModal && (
        <MotorFormModal
          motor={editingMotor}
          onClose={() => { setShowModal(false); setEditingMotor(null); }}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

// ─── Ayarlar > Somfy ZIP Aksesuar Fiyatları ──────────────────────────────────
const SOMFY_KIND_LABELS = { sensor: "Sensörler", control: "Kumandalar & Kontrolörler" };
const SOMFY_GROUP_SLUG_OPTS = [
  { value: "zip_art110_helio", label: "Zip Blind Formu (Sensör Dropdown)" },
  { value: "zip_aksesuar",     label: "Zip Aksesuar Siparişi" },
];

function SomfyFormModal({ asset, onClose, onSave }) {
  const isEdit = !!asset?.id;
  const inputCls = "w-full bg-white dark:bg-[#0C1220] border border-slate-200 dark:border-[#22304A] rounded-lg px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-[#E5322A]/50 text-slate-800 dark:text-slate-200";
  const labelCls = "block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1";
  const [f, setF] = useState({
    label:         asset?.label || "",
    code:          asset?.code?.replace(/^SMF-/i, "") || "",
    kind:          asset?.kind || "control",
    purchasePrice: asset?.purchasePrice ?? 0,
    price:         asset?.price ?? 0,
    groupSlugs:    asset?.groupSlugs || ["zip_aksesuar"],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleSlug = (slug) => setF(p => ({
    ...p,
    groupSlugs: p.groupSlugs.includes(slug)
      ? p.groupSlugs.filter(s => s !== slug)
      : [...p.groupSlugs, slug],
  }));

  const handleSave = async () => {
    if (!f.label.trim()) { toast.error("Ürün adı zorunlu"); return; }
    if (!f.code.trim())  { toast.error("Ürün kodu zorunlu"); return; }
    if (f.groupSlugs.length === 0) { toast.error("En az bir kullanım alanı seçilmeli"); return; }
    const fullCode = f.code.trim().toUpperCase().startsWith("SMF-")
      ? f.code.trim().toUpperCase()
      : `SMF-${f.code.trim().toUpperCase()}`;
    const payload = {
      label: f.label.trim(),
      code: fullCode,
      kind: f.kind,
      purchasePrice: parseFloat(f.purchasePrice) || 0,
      purchaseCurrency: "EUR",
      price: parseFloat(f.price) || 0,
      currency: "EUR",
      groupSlugs: f.groupSlugs,
    };
    setSaving(true);
    try {
      let result;
      if (isEdit) {
        const { data } = await api.put(`/assets/${asset.id}`, payload);
        result = data;
        toast.success("Ürün güncellendi");
      } else {
        const { data } = await api.post("/assets", payload);
        result = data;
        toast.success("Somfy ürünü eklendi");
      }
      onSave(result, isEdit);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt hatası"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="somfy-form-modal">
      <div className="bg-white dark:bg-[#131B2E] rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-[#22304A] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-[#22304A]">
          <h3 className="text-sm font-bold text-slate-800 dark:text-white">
            {isEdit ? "Somfy Ürününü Düzenle" : "Yeni Somfy Aksesuar Ekle"}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className={labelCls}>Ürün Adı *</label>
            <input className={inputCls} placeholder="örn: Somfy Sunis Sensor IO" value={f.label}
              onChange={e => set("label", e.target.value)} autoFocus data-testid="somfy-modal-label" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Kısa Kod * <span className="font-normal text-slate-300">(SMF- otomatik eklenir)</span></label>
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-2 rounded-l-lg border border-r-0 border-slate-200 dark:border-[#22304A]">SMF-</span>
                <input className={inputCls + " font-mono rounded-l-none"} placeholder="RAIN-RTS"
                  value={f.code} onChange={e => set("code", e.target.value)} data-testid="somfy-modal-code" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Tür *</label>
              <select className={inputCls} value={f.kind} onChange={e => set("kind", e.target.value)} data-testid="somfy-modal-kind">
                <option value="control">Kumanda / Kontrolör</option>
                <option value="sensor">Sensör</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Alış Fiyatı (EUR)</label>
              <input type="number" min="0" step="0.01" className={inputCls + " text-right font-mono"}
                value={f.purchasePrice} onChange={e => set("purchasePrice", e.target.value)}
                data-testid="somfy-modal-purchase-price" />
            </div>
            <div>
              <label className={labelCls}>Satış Fiyatı (EUR)</label>
              <input type="number" min="0" step="0.01" className={inputCls + " text-right font-mono"}
                value={f.price} onChange={e => set("price", e.target.value)}
                data-testid="somfy-modal-price" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Kullanım Alanları *</label>
            <div className="space-y-2 mt-1">
              {SOMFY_GROUP_SLUG_OPTS.map(opt => (
                <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
                  <input type="checkbox" checked={f.groupSlugs.includes(opt.value)}
                    onChange={() => toggleSlug(opt.value)} data-testid={`somfy-modal-slug-${opt.value}`}
                    className="w-3.5 h-3.5 rounded accent-[#E5322A]" />
                  <span className="text-xs text-slate-600 dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-white transition-colors">
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-xl border border-slate-200 dark:border-[#22304A] text-xs font-semibold text-slate-500 hover:bg-slate-50 dark:hover:bg-[#0C1220] transition-colors">
            İptal
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-2 rounded-xl bg-[#E5322A] hover:bg-[#c42a22] text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors"
            data-testid="somfy-modal-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {saving ? "Kaydediliyor…" : (isEdit ? "Güncelle" : "Ekle")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SomfyAksesuarSection() {
  const { user } = useContext(AuthCtx);
  const [assets, setAssets] = useState([]);
  const [rates, setRates] = useState({ eurToTl: 38 });
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/assets?kind=sensor"),
      api.get("/assets?kind=control"),
      api.get("/rates").catch(() => ({ data: {} })),
    ]).then(([s, c, r]) => {
      const somfy = [
        ...(s.data || []).filter(a => a.code?.startsWith("SMF-")),
        ...(c.data || []).filter(a => a.code?.startsWith("SMF-")),
      ].filter(a => !a.deleted);
      somfy.sort((a, b) => a.label.localeCompare(b.label, "tr"));
      setAssets(somfy);
      const rd = r.data || {};
      setRates({ eurToTl: rd.eurBuy || rd.eurToTl || 38 });
    }).finally(() => setLoading(false));
  }, []);

  if (user?.role !== "admin") return null;

  const setEdit = (id, field, val) => setEdits(prev => ({
    ...prev, [id]: { ...(prev[id] || {}), [field]: val },
  }));

  const saveAsset = async (asset) => {
    const edit = edits[asset.id] || {};
    const price = parseFloat(edit.price ?? asset.price ?? 0);
    const purchasePrice = parseFloat(edit.purchasePrice ?? asset.purchasePrice ?? 0);
    if (isNaN(price) || isNaN(purchasePrice)) return;
    setSaving(p => ({ ...p, [asset.id]: true }));
    try {
      await api.put(`/assets/${asset.id}`, { price, purchasePrice, currency: "EUR", purchaseCurrency: "EUR" });
      setAssets(prev => prev.map(a => a.id === asset.id ? { ...a, price, purchasePrice } : a));
      setEdits(prev => { const n = { ...prev }; delete n[asset.id]; return n; });
      toast.success("Fiyat kaydedildi");
    } catch { toast.error("Kayıt hatası"); }
    setSaving(p => { const n = { ...p }; delete n[asset.id]; return n; });
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`"${asset.label}" ürününü silmek istiyor musunuz?`)) return;
    setDeletingId(asset.id);
    try {
      await api.delete(`/assets/${asset.id}`);
      setAssets(prev => prev.filter(a => a.id !== asset.id));
      toast.success("Ürün silindi");
    } catch { toast.error("Silinemedi"); }
    setDeletingId(null);
  };

  const handleModalSave = (result, isUpdate) => {
    if (isUpdate) {
      setAssets(prev => prev.map(a => a.id === result.id ? result : a));
    } else {
      // Yeni ürün sadece SMF- kodluysa listeye ekle
      if (result.code?.startsWith("SMF-")) {
        setAssets(prev => [...prev, result].sort((a, b) => a.label.localeCompare(b.label, "tr")));
      }
    }
  };

  const openAdd = () => { setEditingAsset(null); setShowModal(true); };
  const openEdit = (asset) => { setEditingAsset(asset); setShowModal(true); };

  const hasEdit = (id) => !!edits[id];
  const val = (asset, field) => edits[asset.id]?.[field] ?? asset[field] ?? 0;

  const grouped = assets.reduce((acc, a) => {
    (acc[a.kind] = acc[a.kind] || []).push(a); return acc;
  }, {});

  const toTL = (eur) => eur > 0 ? `≈ ${Math.round(eur * rates.eurToTl).toLocaleString("tr-TR")} ₺` : "—";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800 dark:text-slate-200">Somfy ZIP Aksesuar Fiyatları</h2>
          <p className="text-xs text-slate-400 mt-0.5">Teklif ve maliyet hesaplamalarında kullanılan alış / satış fiyatları</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg">
            EUR={rates.eurToTl} ₺
          </span>
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#E5322A] hover:bg-[#c42a22] text-white text-xs font-semibold transition-colors"
            data-testid="somfy-add-btn">
            <Plus className="w-3.5 h-3.5" /> Yeni Ürün
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Yükleniyor…</div>
      ) : assets.length === 0 ? (
        <div className="zen-panel p-8 text-center text-slate-400 text-sm">
          <p>Henüz Somfy aksesuar eklenmemiş.</p>
          <button onClick={openAdd} className="mt-3 px-4 py-2 bg-[#E5322A] text-white rounded-xl text-xs font-semibold hover:bg-[#c42a22] transition-colors">
            İlk Ürünü Ekle
          </button>
        </div>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([kind, items]) => (
          <div key={kind} className="zen-panel p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {SOMFY_KIND_LABELS[kind] || kind}
                <span className="ml-2 font-normal text-slate-400">({items.length})</span>
              </h3>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 text-left text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-2 font-semibold">Ürün Adı</th>
                  <th className="px-3 py-2 font-semibold w-40 text-right">Alış (EUR)</th>
                  <th className="px-3 py-2 font-semibold w-40 text-right">Satış (EUR)</th>
                  <th className="px-3 py-2 font-semibold w-32 text-right">≈ TL</th>
                  <th className="px-3 py-2 w-36 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {items.map(asset => (
                  <tr key={asset.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${hasEdit(asset.id) ? "bg-amber-50/50 dark:bg-amber-900/10" : ""}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-700 dark:text-slate-300">{asset.label}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-mono">{asset.code}</span>
                        {asset.groupSlugs?.includes("zip_art110_helio") && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium">Zip Form</span>
                        )}
                        {asset.groupSlugs?.includes("zip_aksesuar") && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 font-medium">Aksesuar</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number" step="0.01" min="0"
                        className="w-28 text-right px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono outline-none focus:ring-1 focus:ring-[#E5322A]/40"
                        value={val(asset, "purchasePrice")}
                        onChange={e => setEdit(asset.id, "purchasePrice", e.target.value)}
                        data-testid={`somfy-purchase-${asset.code}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number" step="0.01" min="0"
                        className="w-28 text-right px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono outline-none focus:ring-1 focus:ring-[#E5322A]/40"
                        value={val(asset, "price")}
                        onChange={e => setEdit(asset.id, "price", e.target.value)}
                        data-testid={`somfy-price-${asset.code}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500 font-mono">
                      {toTL(parseFloat(val(asset, "price")) || 0)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          disabled={!hasEdit(asset.id) || saving[asset.id]}
                          onClick={() => saveAsset(asset)}
                          className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#E5322A] text-white hover:bg-[#c42a22] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          data-testid={`somfy-save-${asset.code}`}
                        >
                          {saving[asset.id] ? "…" : "Kaydet"}
                        </button>
                        <button
                          onClick={() => openEdit(asset)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 transition-colors"
                          title="Düzenle"
                          data-testid={`somfy-edit-${asset.code}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(asset)}
                          disabled={deletingId === asset.id}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-30"
                          title="Sil"
                          data-testid={`somfy-delete-${asset.code}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {showModal && (
        <SomfyFormModal
          asset={editingAsset}
          onClose={() => { setShowModal(false); setEditingAsset(null); }}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}
function DovizKdvSettings() {
  const [rates, setRates] = useState(null);
  const [override, setOverride] = useState({ eurBuy: "", eurSell: "", usdBuy: "", usdSell: "" });
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [kdvRates, setKdvRates] = useState([]);
  const [newKdv, setNewKdv] = useState("");

  useEffect(() => {
    api.get("/rates").then(({ data }) => {
      setRates(data);
      setOverride({
        eurBuy:  data.eurBuy  || data.eurToTl || "",
        eurSell: data.eurSell || data.eurToTl || "",
        usdBuy:  data.usdBuy  || data.usdToTl || "",
        usdSell: data.usdSell || data.usdToTl || "",
      });
    });
    api.get("/settings", { params: { category: "kdv_rates" } }).then(({ data }) => {
      const row = data.find(r => r.key === "rates");
      if (row?.value) setKdvRates(row.value.split(",").map(v => v.trim()).filter(Boolean));
      else setKdvRates(["1", "10", "20"]);
    }).catch(() => setKdvRates(["1", "10", "20"]));
  }, []);

  const saveOverride = async () => {
    setSaving(true);
    try {
      const eurBuyVal  = parseFloat(override.eurBuy)  || rates?.eurBuy  || rates?.eurToTl;
      const eurSellVal = parseFloat(override.eurSell) || rates?.eurSell || rates?.eurToTl;
      const usdBuyVal  = parseFloat(override.usdBuy)  || rates?.usdBuy  || rates?.usdToTl;
      const usdSellVal = parseFloat(override.usdSell) || rates?.usdSell || rates?.usdToTl;
      const updated = await api.put("/rates", {
        eurBuy: eurBuyVal, eurSell: eurSellVal,
        usdBuy: usdBuyVal, usdSell: usdSellVal,
        eurToTl: eurSellVal, usdToTl: usdSellVal, // geriye dönük uyumluluk
        source: "manuel",
      });
      setRates(updated.data);
      toast.success("Kurlar kaydedildi");
    } catch (e) { toast.error("Kayıt hatası"); }
    setSaving(false);
  };

  const refreshTcmb = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/rates/refresh-tcmb");
      if (res.data?.ok === false) { toast.error(res.data.error || "TCMB hatası"); }
      else {
        const updated = await api.get("/rates");
        setRates(updated.data);
        setOverride({
          eurBuy:  updated.data.eurBuy  || updated.data.eurToTl,
          eurSell: updated.data.eurSell || updated.data.eurToTl,
          usdBuy:  updated.data.usdBuy  || updated.data.usdToTl,
          usdSell: updated.data.usdSell || updated.data.usdToTl,
        });
        toast.success("TCMB kuru güncellendi");
      }
    } catch (e) { toast.error("TCMB bağlantı hatası"); }
    setRefreshing(false);
  };

  const saveKdv = async (list) => {
    const val = list.join(",");
    const existing = (await api.get("/settings", { params: { category: "kdv_rates" } })).data;
    const row = existing.find(r => r.key === "rates");
    if (row) await api.put(`/settings/${row.id}`, { category: "kdv_rates", key: "rates", value: val, label: "KDV Oranları" });
    else await api.post("/settings", { category: "kdv_rates", key: "rates", value: val, label: "KDV Oranları" });
  };

  const addKdv = async () => {
    const v = newKdv.trim();
    if (!v || isNaN(Number(v))) return toast.error("Geçerli bir oran girin");
    if (kdvRates.includes(v)) return toast.error("Bu oran zaten mevcut");
    const list = [...kdvRates, v].sort((a, b) => Number(a) - Number(b));
    setKdvRates(list);
    setNewKdv("");
    await saveKdv(list);
    toast.success(`%${v} KDV oranı eklendi`);
  };

  const removeKdv = async (v) => {
    const list = kdvRates.filter(r => r !== v);
    setKdvRates(list);
    await saveKdv(list);
    toast.success(`%${v} kaldırıldı`);
  };

  return (
    <div className="space-y-5">
      {/* TCMB Canlı Kur */}
      <div className="zen-panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm">Döviz Kurları</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">TCMB otomatik güncelleme (+0.50 TRY marj) · Manuel override</p>
          </div>
          <button onClick={refreshTcmb} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/10 text-sky-500 hover:bg-sky-500/20 transition-colors disabled:opacity-50"
            data-testid="btn-refresh-tcmb">
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Güncelleniyor…" : "TCMB'den Çek"}
          </button>
        </div>

        {/* Mevcut kurlar — Alış/Satış */}
        {rates && (
          <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-slate-50 dark:bg-[#0C1220]">
            {/* EUR */}
            <div className="rounded-lg border border-slate-200 dark:border-[#22304A] p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">€ EUR/TL</div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-[9px] text-emerald-500 font-semibold uppercase mb-0.5">ALIŞ</div>
                  <div className="font-extrabold text-base zen-mono text-emerald-600 dark:text-emerald-400">{Number(rates.eurBuy || rates.eurToTl || 0).toFixed(4)}</div>
                </div>
                <div>
                  <div className="text-[9px] text-[#E5322A] font-semibold uppercase mb-0.5">SATIŞ</div>
                  <div className="font-extrabold text-base zen-mono text-[#E5322A]">{Number(rates.eurSell || rates.eurToTl || 0).toFixed(4)}</div>
                </div>
              </div>
            </div>
            {/* USD */}
            <div className="rounded-lg border border-slate-200 dark:border-[#22304A] p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">$ USD/TL</div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-[9px] text-emerald-500 font-semibold uppercase mb-0.5">ALIŞ</div>
                  <div className="font-extrabold text-base zen-mono text-emerald-600 dark:text-emerald-400">{Number(rates.usdBuy || rates.usdToTl || 0).toFixed(4)}</div>
                </div>
                <div>
                  <div className="text-[9px] text-[#E5322A] font-semibold uppercase mb-0.5">SATIŞ</div>
                  <div className="font-extrabold text-base zen-mono text-[#E5322A]">{Number(rates.usdSell || rates.usdToTl || 0).toFixed(4)}</div>
                </div>
              </div>
            </div>
            <div className="col-span-2 text-center">
              <span className="text-[10px] text-slate-400">Kaynak: <strong>{rates.source || "?"}</strong> · {rates.tcmbDate || rates.updatedAt?.slice(0,10) || "—"}</span>
            </div>
          </div>
        )}

        {/* Manuel Kur Override — Alış / Satış */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 block mb-1">EUR Alış (ALIŞ)</label>
            <input className="zen-input" type="number" step="0.0001" value={override.eurBuy}
              onChange={e => setOverride(p => ({ ...p, eurBuy: e.target.value }))}
              placeholder="Örn: 37.60" data-testid="input-eur-buy" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">EUR Satış (SATIŞ)</label>
            <input className="zen-input" type="number" step="0.0001" value={override.eurSell}
              onChange={e => setOverride(p => ({ ...p, eurSell: e.target.value }))}
              placeholder="Örn: 38.25" data-testid="input-eur-sell" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">USD Alış (ALIŞ)</label>
            <input className="zen-input" type="number" step="0.0001" value={override.usdBuy}
              onChange={e => setOverride(p => ({ ...p, usdBuy: e.target.value }))}
              placeholder="Örn: 33.60" data-testid="input-usd-buy" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">USD Satış (SATIŞ)</label>
            <input className="zen-input" type="number" step="0.0001" value={override.usdSell}
              onChange={e => setOverride(p => ({ ...p, usdSell: e.target.value }))}
              placeholder="Örn: 34.15" data-testid="input-usd-sell" />
          </div>
        </div>

        <button onClick={saveOverride} disabled={saving}
          className="zen-btn-primary w-full" data-testid="btn-save-rates">
          {saving ? "Kaydediliyor…" : "Kurları Kaydet"}
        </button>
      </div>

      {/* KDV Oranları */}
      <div className="zen-panel p-5 space-y-4">
        <div>
          <h3 className="font-bold text-sm">KDV Oranları</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Teklif formlarında kullanılacak oranlar</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {kdvRates.map(v => (
            <span key={v} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 dark:bg-[#1A2740] text-sm font-bold">
              %{v}
              <button onClick={() => removeKdv(v)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`btn-remove-kdv-${v}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="zen-input flex-1" type="number" min="0" max="100" value={newKdv}
            onChange={e => setNewKdv(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addKdv()}
            placeholder="Yeni KDV oranı (ör: 8)" data-testid="input-new-kdv" />
          <button onClick={addKdv} className="zen-btn-primary px-4" data-testid="btn-add-kdv">Ekle</button>
        </div>
      </div>
    </div>
  );
}

// ─── Ayarlar > Bayi Yönetimi ─────────────────────────────────────────────────
// MatrixCell — BayiYonetimiSettings dışında tanımlandı (re-render sorunu önlenir)
function MatrixCell({ slug, fabricGroup, lookup, onUpdate }) {
  const key = `${slug}|${fabricGroup || ""}`;
  const rule = lookup[key];
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState("");
  const [localType, setLocalType] = useState("discount");
  const [localCur, setLocalCur] = useState("EUR");

  const openEdit = () => {
    setLocalVal(rule?.val ?? "");
    setLocalType(rule?.type ?? "discount");
    setLocalCur(rule?.currency ?? "EUR");
    setEditing(true);
  };

  const commit = () => {
    onUpdate(slug, fabricGroup, localType, localVal, localCur);
    setEditing(false);
  };

  if (editing) return (
    <td className="p-0 min-w-[90px]">
      <div className="p-1 bg-sky-50 dark:bg-sky-900/20 border border-sky-400 rounded">
        <select value={localType} onChange={e => setLocalType(e.target.value)} className="w-full text-[9px] border-0 bg-transparent mb-0.5 font-semibold">
          <option value="discount">% İsk.</option>
          <option value="fixed">m² Fiyat</option>
        </select>
        <div className="flex gap-0.5">
          <input autoFocus type="number" min="0" step="0.5" value={localVal} onChange={e => setLocalVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
            className="w-full text-[10px] font-mono border border-sky-300 rounded px-1 py-0 bg-white dark:bg-slate-900" />
          {localType === "fixed" && (
            <select value={localCur} onChange={e => setLocalCur(e.target.value)} className="text-[9px] border border-sky-300 rounded bg-white dark:bg-slate-900">
              <option>EUR</option><option>TL</option><option>USD</option>
            </select>
          )}
        </div>
        <div className="flex gap-1 mt-0.5">
          <button onClick={commit} className="flex-1 text-[9px] bg-sky-500 text-white rounded py-0.5 font-bold">✓</button>
          <button onClick={() => setEditing(false)} className="flex-1 text-[9px] bg-slate-200 dark:bg-slate-700 rounded py-0.5">✕</button>
        </div>
      </div>
    </td>
  );

  return (
    <td onClick={openEdit}
      className={`text-center text-[11px] font-mono px-2 py-1.5 cursor-pointer border border-slate-100 dark:border-slate-800 transition-colors ${rule?.val > 0 ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-bold" : "text-slate-300 dark:text-slate-600 hover:bg-sky-50 dark:hover:bg-sky-900/20"}`}>
      {rule?.val > 0 ? (rule.type === "fixed" ? `${rule.val}${rule.currency === "EUR" ? "€" : rule.currency === "USD" ? "$" : "₺"}/m²` : `%${rule.val}`) : "—"}
    </td>
  );
}

function DealerAccountsPanel() {
  const [dealers, setDealers] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [saving, setSaving] = useState(null);

  const load = () => {
    api.get("/auth/users").then(({ data }) => setDealers((data || []).filter(u => u.role === "dealer")));
    api.get("/product-groups").then(({ data }) => setProductGroups(data || []));
  };
  useEffect(() => { load(); }, []);

  const toggleGroup = async (dealer, slug) => {
    const cur = dealer.allowedProductGroups || [];
    const next = cur.includes(slug) ? cur.filter(s => s !== slug) : [...cur, slug];
    setSaving(dealer.id);
    setDealers(ds => ds.map(d => d.id === dealer.id ? { ...d, allowedProductGroups: next } : d));
    try {
      await api.put(`/auth/users/${dealer.id}`, { allowedProductGroups: next });
    } catch { toast.error("Kaydedilemedi"); load(); }
    setSaving(null);
  };

  const openUsersTab = () => { window.__zenSettingsInit = { tab: "users_roles" }; window.dispatchEvent(new CustomEvent("zenSettingsNav")); };

  return (
    <div className="zen-panel p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-sm">Bayi Hesapları & Ürün Grubu Yetkileri</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Her bayinin görebileceği ürün gruplarını buradan sınırlayın — seçilmeyen gruplar o bayi için tamamen gizlenir.</p>
        </div>
        <button onClick={openUsersTab} data-testid="btn-goto-dealer-users" className="text-xs font-semibold text-teal-500 hover:underline">+ Yeni Bayi Hesabı</button>
      </div>
      {dealers.length === 0 && (
        <p className="text-sm text-slate-400 py-3">Henüz bayi hesabı yok. "Kullanıcılar & Roller" bölümünden "Bayi" rolüyle bir hesap oluşturun.</p>
      )}
      <div className="space-y-3">
        {dealers.map(d => (
          <div key={d.id} data-testid={`dealer-account-row-${d.id}`} className="border border-slate-200 dark:border-[#22304A] rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">{d.name} <span className="text-xs text-slate-400 font-normal">{d.countryCode}{d.phone}</span></div>
              {saving === d.id && <span className="text-[10px] text-slate-400">Kaydediliyor…</span>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {productGroups.map(g => {
                const active = (d.allowedProductGroups || []).includes(g.slug);
                return (
                  <button key={g.slug} type="button" data-testid={`dealer-toggle-${d.id}-${g.slug}`} onClick={() => toggleGroup(d, g.slug)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${active ? "bg-teal-500 text-white border-teal-500" : "border-slate-200 dark:border-[#22304A] text-slate-400"}`}>
                    {g.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BayiYonetimiSettings() {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("liste"); // "liste" | "matris"
  const [globalMargins, setGlobalMargins] = useState({});
  const [globalSaving, setGlobalSaving] = useState(false);
  const [gmSettingId, setGmSettingId] = useState(null);
  const [supplierDiscounts, setSupplierDiscounts] = useState({});
  const [sdSaving, setSdSaving] = useState(false);
  const [sdSettingId, setSdSettingId] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(null); // productTypeKey | null

  const COLORS = ["#E5322A","#F59E0B","#10B981","#3B82F6","#8B5CF6","#EC4899","#6B7280","#0EA5E9"];

  const PRODUCT_TYPES = PRICE_RULE_PRODUCT_TYPES;
  const FABRIC_GROUPS_BY_TYPE = PRICE_RULE_FABRIC_GROUPS;

  // Matris için sütun yapısı: her ürün tipi için [Genel, ...fabricGroups]
  const MATRIX_COLS = PRODUCT_TYPES.reduce((acc, pt) => {
    acc[pt.key] = [{ key: "", label: "Genel" }, ...(FABRIC_GROUPS_BY_TYPE[pt.key] || [])];
    return acc;
  }, {});

  // priceRules dizisinden matris lookup oluştur
  const buildMatrixLookup = (rules) => {
    const m = {};
    (rules || []).forEach(r => {
      const k = `${r.slug}|${r.fabricGroup || ""}`;
      m[k] = r;
    });
    return m;
  };

  // Matris hücresini güncelle → priceRules'a yansıt
  const updateMatrixCell = (slug, fabricGroup, type, val, currency) => {
    setForm(prev => {
      const rules = [...(prev.priceRules || [])];
      const idx = rules.findIndex(r => r.slug === slug && (r.fabricGroup || "") === (fabricGroup || ""));
      const newRule = { slug, fabricGroup: fabricGroup || "", type: type || "discount", val: parseFloat(val) || 0, currency: currency || "EUR" };
      if (idx >= 0) {
        if (newRule.val === 0 && type === "discount") { rules.splice(idx, 1); }
        else { rules[idx] = newRule; }
      } else if (newRule.val > 0 || type === "fixed") {
        rules.push(newRule);
      }
      return { ...prev, priceRules: rules };
    });
  };

  const load = () => api.get("/dealer-groups").then(({ data }) => setGroups(data));
  useEffect(() => {
    load();
    api.get("/settings?category=pricing_defaults").then(({ data }) => {
      const entry = (data || []).find(s => s.key === "global_margins");
      if (entry) { setGlobalMargins(entry.value || {}); setGmSettingId(entry.id); }
      const sdEntry = (data || []).find(s => s.key === "supplier_discounts");
      if (sdEntry) { setSupplierDiscounts(sdEntry.value || {}); setSdSettingId(sdEntry.id); }
    }).catch(() => {});
  }, []);

  const save = async () => {
    if (!form?.name?.trim()) return toast.error("Grup adı zorunlu");
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/dealer-groups/${form.id}`, form);
        toast.success("Grup güncellendi");
      } else {
        await api.post("/dealer-groups", form);
        toast.success("Grup eklendi");
      }
      setForm(null);
      load();
    } catch (e) { toast.error("Kayıt hatası"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Bu grubu silmek istiyor musunuz?")) return;
    await api.delete(`/dealer-groups/${id}`);
    toast.success("Silindi");
    load();
  };

  const addRule = () => setForm(p => ({ ...p, priceRules: [...(p.priceRules || []), { slug: "stor", fabricGroup: "", type: "discount", val: 0, currency: "EUR" }] }));
  const removeRule = (i) => setForm(p => ({ ...p, priceRules: (p.priceRules || []).filter((_, idx) => idx !== i) }));
  const updateRule = (i, key, val) => setForm(p => {
    const rules = [...(p.priceRules || [])];
    rules[i] = { ...rules[i], [key]: val };
    if (key === "slug") rules[i].fabricGroup = "";
    return { ...p, priceRules: rules };
  });

  // Matris hücresi bileşeni
  const MatrixCell = ({ slug, fabricGroup, lookup }) => {
    const key = `${slug}|${fabricGroup || ""}`;
    const rule = lookup[key];
    const [editing, setEditing] = useState(false);
    const [localVal, setLocalVal] = useState(rule?.val || "");
    const [localType, setLocalType] = useState(rule?.type || "discount");
    const [localCur, setLocalCur] = useState(rule?.currency || "EUR");

    const commit = () => {
      updateMatrixCell(slug, fabricGroup, localType, localVal, localCur);
      setEditing(false);
    };

    if (editing) return (
      <td className="p-0 min-w-[90px]">
        <div className="p-1 bg-sky-50 dark:bg-sky-900/20 border border-sky-400 rounded">
          <select value={localType} onChange={e => setLocalType(e.target.value)} className="w-full text-[9px] border-0 bg-transparent mb-0.5 font-semibold">
            <option value="discount">% İsk.</option>
            <option value="fixed">m² Fiyat</option>
          </select>
          <div className="flex gap-0.5">
            <input autoFocus type="number" min="0" step="0.5" value={localVal} onChange={e => setLocalVal(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
              className="w-full text-[10px] font-mono border border-sky-300 rounded px-1 py-0 bg-white dark:bg-slate-900" />
            {localType === "fixed" && (
              <select value={localCur} onChange={e => setLocalCur(e.target.value)} className="text-[9px] border border-sky-300 rounded bg-white dark:bg-slate-900">
                <option>EUR</option><option>TL</option><option>USD</option>
              </select>
            )}
          </div>
          <div className="flex gap-1 mt-0.5">
            <button onClick={commit} className="flex-1 text-[9px] bg-sky-500 text-white rounded py-0.5 font-bold">✓</button>
            <button onClick={() => setEditing(false)} className="flex-1 text-[9px] bg-slate-200 dark:bg-slate-700 rounded py-0.5">✕</button>
          </div>
        </div>
      </td>
    );

    return (
      <td onClick={() => { setLocalVal(rule?.val || ""); setLocalType(rule?.type || "discount"); setLocalCur(rule?.currency || "EUR"); setEditing(true); }}
        className={`text-center text-[11px] font-mono px-2 py-1.5 cursor-pointer border border-slate-100 dark:border-slate-800 transition-colors ${rule?.val > 0 ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-bold" : "text-slate-300 dark:text-slate-600 hover:bg-sky-50 dark:hover:bg-sky-900/20"}`}>
        {rule?.val > 0 ? (rule.type === "fixed" ? `${rule.val}${rule.currency || "€"}/m²` : `%${rule.val}`) : "—"}
      </td>
    );
  };

  const saveSupplierDiscounts = async () => {
    setSdSaving(true);
    try {
      if (sdSettingId) {
        await api.put(`/settings/${sdSettingId}`, { category: "pricing_defaults", key: "supplier_discounts", value: supplierDiscounts, label: "Tedarikçi Alış İskonto Oranları" });
      } else {
        const { data } = await api.post("/settings", { category: "pricing_defaults", key: "supplier_discounts", value: supplierDiscounts, label: "Tedarikçi Alış İskonto Oranları" });
        setSdSettingId(data.id);
      }
      toast.success("Tedarikçi iskonto oranları kaydedildi");
    } catch { toast.error("Kayıt hatası"); }
    setSdSaving(false);
  };

  const saveGlobalMargins = async () => {
    setGlobalSaving(true);
    try {
      if (gmSettingId) {
        await api.put(`/settings/${gmSettingId}`, { category: "pricing_defaults", key: "global_margins", value: globalMargins, label: "Global Kâr Marjı Varsayılanları" });
      } else {
        const { data } = await api.post("/settings", { category: "pricing_defaults", key: "global_margins", value: globalMargins, label: "Global Kâr Marjı Varsayılanları" });
        setGmSettingId(data.id);
      }
      toast.success("Global kâr marjları kaydedildi");
    } catch { toast.error("Kayıt hatası"); }
    setGlobalSaving(false);
  };

  const bulkApplyMargin = async (ptKey) => {
    const pct = Number(globalMargins[ptKey]);
    if (!pct || pct <= 0) { toast.error("Önce bu ürün grubu için geçerli bir marj girin"); return; }
    const ptLabel = PRICE_RULE_PRODUCT_TYPES.find(p => p.key === ptKey)?.label || ptKey;
    if (!window.confirm(`Tüm müşterilere "${ptLabel}" için %${pct} kâr marjı uygulanacak.\nMevcut özel kuralların üzerine yazılacak.\n\nDevam edilsin mi?`)) return;
    setBulkApplying(ptKey);
    try {
      const { data: customers } = await api.get("/customers");
      const eligible = customers.filter(c => c.type !== "Tedarikçi" && c.type !== "Çalışan");
      let updated = 0;
      for (const cust of eligible) {
        const rules = Array.isArray(cust.priceRules) ? [...cust.priceRules] : [];
        // Bu ürün tipi için mevcut kuralı (fabricGroup olmayan) kaldır, yenisini ekle
        const filtered = rules.filter(r => !(r.slug === ptKey && !r.fabricGroup));
        filtered.push({ slug: ptKey, fabricGroup: "", type: "margin", val: pct, currency: "EUR" });
        await api.put(`/customers/${cust.id}`, { ...cust, priceRules: filtered });
        updated++;
      }
      toast.success(`${updated} müşteriye %${pct} kâr marjı uygulandı (${ptLabel})`);
    } catch { toast.error("Toplu uygulama hatası"); }
    setBulkApplying(null);
  };

  return (
    <div className="space-y-4">
      <DealerAccountsPanel />
      {/* ── Global Kâr Marjı Varsayılanları ── */}
      <div className="zen-panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm">Varsayılan Kâr Marjları</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Müşteriye özel kural tanımlanmamışsa bu oranlar uygulanır
            </p>
          </div>
          <button onClick={saveGlobalMargins} disabled={globalSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 transition-colors disabled:opacity-50"
            data-testid="btn-save-global-margins">
            {globalSaving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {PRICE_RULE_PRODUCT_TYPES.map(pt => (
            <div key={pt.key}>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 block mb-1">{pt.label}</label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min="0" max="200" step="0.5"
                  value={globalMargins[pt.key] ?? ""}
                  onChange={e => setGlobalMargins(prev => ({ ...prev, [pt.key]: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                  className="zen-input !py-1.5 !text-xs w-full font-mono"
                  data-testid={`global-margin-${pt.key}`}
                />
                <span className="text-slate-500 text-xs">%</span>
                <button
                  type="button"
                  title={`"${pt.label}" için bu marjı tüm müşterilere uygula`}
                  onClick={() => bulkApplyMargin(pt.key)}
                  disabled={bulkApplying === pt.key || !globalMargins[pt.key]}
                  className="shrink-0 px-2 py-1.5 rounded-lg text-[10px] font-semibold bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`btn-bulk-apply-${pt.key}`}
                >
                  {bulkApplying === pt.key ? "…" : "Tümüne"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* ── Tedarikçi Alış İskontoları ── */}
      <div className="zen-panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm">Tedarikçi Alış İskontoları</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Tedarikçi liste fiyatına karşı benim alış iskonto oranlarım — müşteri fiyatlandırma tablosunda gösterilir
            </p>
          </div>
          <button onClick={saveSupplierDiscounts} disabled={sdSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-600/20 text-sky-400 hover:bg-sky-600/30 transition-colors disabled:opacity-50"
            data-testid="btn-save-supplier-discounts">
            {sdSaving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {PRICE_RULE_PRODUCT_TYPES.map(pt => (
            <div key={pt.key}>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 block mb-1">
                {pt.label}{pt.sub ? ` — ${pt.sub}` : ""}
              </label>
              <div className="flex items-center gap-1">
                <input
                  type="number" min="0" max="100" step="0.5"
                  value={supplierDiscounts[pt.key] ?? ""}
                  onChange={e => setSupplierDiscounts(prev => ({ ...prev, [pt.key]: parseFloat(e.target.value) || 0 }))}
                  placeholder="—"
                  className="zen-input !py-1.5 !text-xs w-full font-mono"
                  data-testid={`supplier-discount-${pt.key}`}
                />
                <span className="text-slate-500 text-xs shrink-0">% isk</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* ── Bayi Grupları ── */}
      <div className="zen-panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm">Bayi Grupları</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">İskonto oranlarını ve müşteri gruplarını yönetin</p>
          </div>
          <button onClick={() => setForm({ name: "", discountPct: 0, color: COLORS[0], priceRules: [] })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#E5322A]/10 text-[#E5322A] hover:bg-[#E5322A]/20 transition-colors"
            data-testid="btn-add-dealer-group">
            <Plus size={12} /> Yeni Grup
          </button>
        </div>

        {groups.length === 0 && !form && (
          <div className="text-center py-10 text-slate-400 text-sm">Henüz grup yok. "Yeni Grup" ile ekleyin.</div>
        )}

        <div className="space-y-2">
          {groups.map(g => (
            <div key={g.id} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-[#22304A] hover:border-[#E5322A]/30 transition-colors group">
              <span className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: g.color || "#6B7280" }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{g.name}</div>
                <div className="text-[11px] text-slate-400">Genel İskonto: %{g.discountPct || 0}</div>
                {(g.priceRules || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(g.priceRules || []).map((r, i) => {
                      const pt = PRODUCT_TYPES.find(x => x.key === r.slug);
                      const fg = r.fabricGroup ? (FABRIC_GROUPS_BY_TYPE[r.slug] || []).find(x => x.key === r.fabricGroup) : null;
                      return (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20">
                          {pt?.label}{fg ? ` › ${fg.label}` : ""}: {r.type === "fixed" ? `${r.val} ${r.currency || "EUR"}/m²` : `%${r.val} iskonto`}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setForm({ ...g, priceRules: Array.isArray(g.priceRules) ? [...g.priceRules] : [] })}
                  className="p-1.5 rounded-lg hover:bg-sky-500/10 text-sky-500 transition-colors"
                  data-testid={`btn-edit-dealer-${g.id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => del(g.id)}
                  className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500 transition-colors"
                  data-testid={`btn-del-dealer-${g.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Grup Formu */}
      {form && (
        <div className="zen-panel p-5 space-y-4 border-2 border-[#E5322A]/20">
          <h3 className="font-bold text-sm">{form.id ? "Grubu Düzenle" : "Yeni Bayi Grubu"}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Grup Adı *</label>
              <input className="zen-input" value={form.name || ""} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Platin, Altın, Standart…" data-testid="input-dealer-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Genel İskonto (%)</label>
              <input className="zen-input" type="number" min="0" max="100" step="0.5"
                value={form.discountPct || ""} onChange={e => setForm(p => ({ ...p, discountPct: parseFloat(e.target.value) || 0 }))}
                placeholder="0" data-testid="input-dealer-discount" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-2">Renk</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => setForm(p => ({ ...p, color: c }))}
                  className={`w-6 h-6 rounded-full border-2 transition-all ${form.color === c ? "border-white scale-125 shadow-lg" : "border-transparent"}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          {/* Fiyat Kuralları — Liste / Matris görünümü */}
          <div className="border-t border-slate-200 dark:border-[#22304A] pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ürün Bazlı Fiyat Kuralları</label>
                <p className="text-[10px] text-slate-400 mt-0.5">Ürün tipi veya kumaş serisine göre sabit fiyat ya da iskonto</p>
              </div>
              {/* Görünüm seçici */}
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <button onClick={() => setViewMode("liste")}
                    className={`px-3 py-1 text-[11px] font-semibold transition-colors ${viewMode === "liste" ? "bg-[#E5322A] text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                    data-testid="btn-view-liste">Liste</button>
                  <button onClick={() => setViewMode("matris")}
                    className={`px-3 py-1 text-[11px] font-semibold transition-colors ${viewMode === "matris" ? "bg-[#E5322A] text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                    data-testid="btn-view-matris">Matris</button>
                </div>
                {viewMode === "liste" && (
                  <button type="button" onClick={addRule}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-sky-500/10 text-sky-600 hover:bg-sky-500/20 transition-colors"
                    data-testid="btn-add-price-rule">
                    <Plus size={11} /> Kural Ekle
                  </button>
                )}
              </div>
            </div>

            {/* ── LİSTE GÖRÜNÜMÜ ── */}
            {viewMode === "liste" && (
              <>
                {(form.priceRules || []).length === 0 && (
                  <div className="text-center py-4 text-slate-400 text-[11px] rounded-lg border border-dashed border-slate-300 dark:border-slate-700">
                    Henüz kural yok. "Kural Ekle" ile ekleyin veya Matris görünümüne geçin.
                  </div>
                )}
                <div className="space-y-2">
                  {(form.priceRules || []).map((rule, i) => {
                    const fabricOptions = FABRIC_GROUPS_BY_TYPE[rule.slug] || [];
                    return (
                      <div key={i} className="p-3 rounded-xl border border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#0C1220]/50 space-y-2" data-testid={`price-rule-${i}`}>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 block mb-0.5">Ürün Grubu</label>
                            <select className="zen-input !text-xs !py-1" value={rule.slug} onChange={e => updateRule(i, "slug", e.target.value)} data-testid={`rule-slug-${i}`}>
                              {PRODUCT_TYPES.map(pt => <option key={pt.key} value={pt.key}>{pt.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 block mb-0.5">Kumaş Serisi (opsiyonel)</label>
                            <select className="zen-input !text-xs !py-1" value={rule.fabricGroup || ""} onChange={e => updateRule(i, "fabricGroup", e.target.value)} data-testid={`rule-fabric-${i}`} disabled={fabricOptions.length === 0}>
                              <option value="">— Tüm Serilere —</option>
                              {fabricOptions.map(fg => <option key={fg.key} value={fg.key}>{fg.label}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500 block mb-0.5">Kural Tipi</label>
                            <select className="zen-input !text-xs !py-1" value={rule.type} onChange={e => updateRule(i, "type", e.target.value)} data-testid={`rule-type-${i}`}>
                              <option value="discount">% İskonto</option>
                              <option value="fixed">Sabit m² Fiyatı</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500 block mb-0.5">{rule.type === "fixed" ? "Fiyat (m²)" : "İskonto (%)"}</label>
                            <input type="number" min="0" step="0.01" className="zen-input !text-xs !py-1" value={rule.val || ""} onChange={e => updateRule(i, "val", parseFloat(e.target.value) || 0)} data-testid={`rule-val-${i}`} />
                          </div>
                          {rule.type === "fixed" && (
                            <div>
                              <label className="text-[10px] text-slate-500 block mb-0.5">Para Birimi</label>
                              <select className="zen-input !text-xs !py-1" value={rule.currency || "EUR"} onChange={e => updateRule(i, "currency", e.target.value)} data-testid={`rule-currency-${i}`}>
                                <option value="EUR">EUR</option>
                                <option value="TL">TL</option>
                                <option value="USD">USD</option>
                              </select>
                            </div>
                          )}
                          {rule.type !== "fixed" && <div />}
                        </div>
                        <div className="flex justify-end">
                          <button type="button" onClick={() => removeRule(i)} className="text-[10px] text-red-500 hover:text-red-600 flex items-center gap-1" data-testid={`rule-del-${i}`}>
                            <Trash2 size={11} /> Kuralı Sil
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── MATRİS GÖRÜNÜMÜ ── */}
            {viewMode === "matris" && (() => {
              const lookup = buildMatrixLookup(form.priceRules);
              return (
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-[#22304A]" data-testid="dealer-price-matrix">
                  <p className="text-[10px] text-slate-400 p-2 bg-slate-50 dark:bg-[#0C1220]/50">
                    Hücreye tıklayarak iskonto % veya sabit m² fiyatı girin. Boş hücre = Genel iskonto uygulanır.
                  </p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-100 dark:bg-[#131B2E]">
                        <th className="text-left px-3 py-2 font-bold text-[11px] text-slate-600 dark:text-slate-300 min-w-[130px]">Ürün Tipi</th>
                        <th className="text-center px-2 py-2 font-bold text-[11px] text-slate-600 dark:text-slate-300 min-w-[80px]">Genel</th>
                        <th className="text-center px-2 py-2 font-bold text-[11px] text-slate-500 dark:text-slate-400 min-w-[90px] bg-slate-200/50 dark:bg-slate-800/50" colSpan={Math.max(...PRODUCT_TYPES.map(pt => (FABRIC_GROUPS_BY_TYPE[pt.key] || []).length))}>
                          Kumaş Serisi Bazlı Kurallar →
                        </th>
                      </tr>
                      <tr className="bg-slate-50 dark:bg-[#0C1220]/30">
                        <th />
                        <th className="text-center text-[9px] text-slate-400 font-semibold px-1 py-1">Tüm Seriler</th>
                        {/* En fazla kumaş grubu olan product tipi kadar sütun göster */}
                        {(FABRIC_GROUPS_BY_TYPE.stor || []).map(fg => (
                          <th key={fg.key} className="text-center text-[9px] text-slate-400 font-semibold px-1 py-1 bg-slate-100/50 dark:bg-slate-800/30 max-w-[80px] truncate">{fg.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PRODUCT_TYPES.map(pt => {
                        const fabricCols = FABRIC_GROUPS_BY_TYPE[pt.key] || [];
                        const storCols = FABRIC_GROUPS_BY_TYPE.stor || [];
                        return (
                          <tr key={pt.key} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-[#0C1220]/30 transition-colors">
                            <td className="px-3 py-2 font-semibold text-[11px] text-slate-700 dark:text-slate-300">{pt.label}</td>
                            {/* Genel kolonu */}
                            <MatrixCell slug={pt.key} fabricGroup="" lookup={lookup} onUpdate={updateMatrixCell} />
                            {/* Kumaş grubu sütunları (stor col count kullan) */}
                            {storCols.map((storFg, ci) => {
                              const myFg = fabricCols[ci];
                              if (!myFg) return <td key={ci} className="bg-slate-50/50 dark:bg-slate-900/20" />;
                              return <MatrixCell key={myFg.key} slug={pt.key} fabricGroup={myFg.key} lookup={lookup} onUpdate={updateMatrixCell} />;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="zen-btn-primary flex-1" data-testid="btn-save-dealer-group">
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
            <button onClick={() => setForm(null)} className="zen-btn-ghost px-4">İptal</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ayarlar > PDF Şablonları ─────────────────────────────────────────────────
function PdfSablonSettings() {
  const [cfg, setCfg] = useState({
    marginTop: "15",
    marginRight: "15",
    marginBottom: "15",
    marginLeft: "15",
    logoWidth: "40",
    primaryColor: "#E5322A",
    showBankInfo: "true",
    showNotes: "true",
    showQrCode: "false",
    labelWidth: "100",
    labelHeight: "60",
    labelFont: "10",
    footerText: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings", { params: { category: "pdf_template" } }).then(({ data }) => {
      if (!data?.length) return;
      const map = {};
      data.forEach(r => { map[r.key] = r.value; });
      setCfg(prev => ({ ...prev, ...map }));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const existing = (await api.get("/settings", { params: { category: "pdf_template" } })).data;
      const idMap = {};
      existing.forEach(r => { idMap[r.key] = r.id; });
      for (const [key, value] of Object.entries(cfg)) {
        if (idMap[key]) await api.put(`/settings/${idMap[key]}`, { category: "pdf_template", key, value: String(value), label: key });
        else await api.post("/settings", { category: "pdf_template", key, value: String(value), label: key });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success("PDF şablon ayarları kaydedildi");
    } catch { toast.error("Kayıt hatası"); }
    setSaving(false);
  };

  const F = ({ label, name, type = "text", options, min, max, step, placeholder }) => (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      {options ? (
        <select className="zen-input" value={cfg[name] || ""} onChange={e => setCfg(p => ({ ...p, [name]: e.target.value }))} data-testid={`pdf-cfg-${name}`}>
          {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input className="zen-input" type={type} min={min} max={max} step={step} placeholder={placeholder}
          value={cfg[name] || ""} onChange={e => setCfg(p => ({ ...p, [name]: e.target.value }))} data-testid={`pdf-cfg-${name}`} />
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Teklif PDF */}
      <div className="zen-panel p-5 space-y-4">
        <div>
          <h3 className="font-bold text-sm">Teklif PDF Ayarları</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Kağıt boyutu, kenar boşlukları ve görsel tercihler</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Kağıt Boyutu" name="paperSize" options={[{v:"A4",l:"A4 (210×297mm)"},{v:"A5",l:"A5 (148×210mm)"},{v:"A3",l:"A3 (297×420mm)"}]} />
          <div>
            <label className="text-xs text-slate-500 block mb-1">Ana Renk (Başlık)</label>
            <div className="flex gap-2 items-center">
              <input type="color" value={cfg.primaryColor || "#E5322A"}
                onChange={e => setCfg(p => ({ ...p, primaryColor: e.target.value }))}
                className="w-10 h-8 rounded border border-slate-200 dark:border-[#22304A] cursor-pointer bg-transparent" data-testid="pdf-cfg-primaryColor" />
              <span className="text-xs zen-mono text-slate-400">{cfg.primaryColor}</span>
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-2">Kenar Boşlukları (mm)</label>
          <div className="grid grid-cols-4 gap-2">
            <div><label className="text-[10px] text-slate-400 block mb-1 text-center">Üst</label>
              <input className="zen-input text-center" type="number" min="5" max="40" value={cfg.marginTop} onChange={e => setCfg(p => ({ ...p, marginTop: e.target.value }))} data-testid="pdf-cfg-marginTop" /></div>
            <div><label className="text-[10px] text-slate-400 block mb-1 text-center">Sağ</label>
              <input className="zen-input text-center" type="number" min="5" max="40" value={cfg.marginRight} onChange={e => setCfg(p => ({ ...p, marginRight: e.target.value }))} data-testid="pdf-cfg-marginRight" /></div>
            <div><label className="text-[10px] text-slate-400 block mb-1 text-center">Alt</label>
              <input className="zen-input text-center" type="number" min="5" max="40" value={cfg.marginBottom} onChange={e => setCfg(p => ({ ...p, marginBottom: e.target.value }))} data-testid="pdf-cfg-marginBottom" /></div>
            <div><label className="text-[10px] text-slate-400 block mb-1 text-center">Sol</label>
              <input className="zen-input text-center" type="number" min="5" max="40" value={cfg.marginLeft} onChange={e => setCfg(p => ({ ...p, marginLeft: e.target.value }))} data-testid="pdf-cfg-marginLeft" /></div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Logo Genişliği (mm)" name="logoWidth" type="number" min="15" max="80" step="1" placeholder="40" />
          <F label="Altbilgi Metni" name="footerText" placeholder="Özel altbilgi (boş = varsayılan)" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={cfg.showBankInfo === "true"} onChange={e => setCfg(p => ({ ...p, showBankInfo: String(e.target.checked) }))} className="rounded" data-testid="pdf-cfg-showBankInfo" />
            <span className="text-xs">Banka Bilgisi Göster</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={cfg.showNotes === "true"} onChange={e => setCfg(p => ({ ...p, showNotes: String(e.target.checked) }))} className="rounded" data-testid="pdf-cfg-showNotes" />
            <span className="text-xs">Notlar Göster</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={cfg.showQrCode === "true"} onChange={e => setCfg(p => ({ ...p, showQrCode: String(e.target.checked) }))} className="rounded" data-testid="pdf-cfg-showQrCode" />
            <span className="text-xs">QR Kod Göster</span>
          </label>
        </div>
      </div>

      {/* Etiket */}
      <div className="zen-panel p-5 space-y-4">
        <div>
          <h3 className="font-bold text-sm">Etiket Çıktısı Ayarları</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Koli ve ürün etiketleri için boyut ve yazı tipi</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Etiket Genişliği (mm)" name="labelWidth" type="number" min="30" max="200" step="1" placeholder="100" />
          <F label="Etiket Yüksekliği (mm)" name="labelHeight" type="number" min="20" max="150" step="1" placeholder="60" />
          <F label="Yazı Boyutu (pt)" name="labelFont" type="number" min="6" max="16" step="0.5" placeholder="10" />
        </div>
      </div>

      <button onClick={save} disabled={saving}
        className={`zen-btn-primary w-full ${saved ? "bg-emerald-500 hover:bg-emerald-500" : ""}`}
        data-testid="btn-save-pdf-settings">
        {saving ? "Kaydediliyor…" : saved ? "Kaydedildi ✓" : "Ayarları Kaydet"}
      </button>
    </div>
  );
}


// ─── Ayarlar > Banka Hesapları ────────────────────────────────────────────────
function BankHesaplariSettings() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const CURRENCIES = ["TL", "EUR", "USD", "GBP"];

  const load = () => api.get("/bank-accounts").then(({ data }) => setAccounts(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form?.bankName?.trim()) return toast.error("Banka adı zorunlu");
    if (!form?.iban?.trim()) return toast.error("IBAN zorunlu");
    setSaving(true);
    try {
      if (form.id) { await api.put(`/bank-accounts/${form.id}`, form); toast.success("Hesap güncellendi"); }
      else { await api.post("/bank-accounts", form); toast.success("Hesap eklendi"); }
      setForm(null); load();
    } catch { toast.error("Kayıt hatası"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Bu hesabı silmek istiyor musunuz?")) return;
    await api.delete(`/bank-accounts/${id}`);
    toast.success("Silindi"); load();
  };

  const setDefault = async (acc) => {
    await api.put(`/bank-accounts/${acc.id}`, { ...acc, isDefault: true });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="zen-panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm">Banka Hesapları</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">Tekliflerde ve PDF'lerde görünecek hesaplarınız</p>
          </div>
          <button onClick={() => setForm({ bankName: "", accountHolder: "", iban: "", currency: "TL", branch: "", swiftCode: "", isDefault: false })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#E5322A]/10 text-[#E5322A] hover:bg-[#E5322A]/20 transition-colors"
            data-testid="btn-add-bank-account">
            <Plus size={12} /> Yeni Hesap
          </button>
        </div>

        {accounts.length === 0 && !form && (
          <div className="text-center py-10 text-slate-400 text-sm">Henüz hesap eklenmedi.</div>
        )}

        <div className="space-y-2">
          {accounts.map(a => (
            <div key={a.id} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 dark:border-[#22304A] hover:border-[#E5322A]/30 transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{a.bankName}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-[#1A2740] font-bold">{a.currency}</span>
                  {a.isDefault && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 font-bold">Varsayılan</span>}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">{a.accountHolder}{a.branch ? ` — ${a.branch}` : ""}</div>
                <div className="text-[11px] zen-mono text-slate-500 mt-0.5 truncate">{a.iban}</div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                {!a.isDefault && (
                  <button onClick={() => setDefault(a)} className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-500 transition-colors text-[10px] font-semibold px-2"
                    data-testid={`btn-default-bank-${a.id}`} title="Varsayılan yap">Varsayılan</button>
                )}
                <button onClick={() => setForm({ ...a })} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-sky-500 transition-colors" data-testid={`btn-edit-bank-${a.id}`}><Pencil size={13} /></button>
                <button onClick={() => del(a.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500 transition-colors" data-testid={`btn-del-bank-${a.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {form && (
        <div className="zen-panel p-5 space-y-4 border-2 border-[#E5322A]/20">
          <h3 className="font-bold text-sm">{form.id ? "Hesabı Düzenle" : "Yeni Banka Hesabı"}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Banka Adı *</label>
              <input className="zen-input" value={form.bankName || ""} onChange={e => setForm(p => ({ ...p, bankName: e.target.value }))}
                placeholder="Ziraat Bankası, İş Bankası…" data-testid="input-bank-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Para Birimi</label>
              <select className="zen-input" value={form.currency || "TL"} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))} data-testid="input-bank-currency">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-500 block mb-1">Hesap Sahibi *</label>
              <input className="zen-input" value={form.accountHolder || ""} onChange={e => setForm(p => ({ ...p, accountHolder: e.target.value }))}
                placeholder="Firma unvanı veya ad soyad" data-testid="input-bank-holder" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-500 block mb-1">IBAN *</label>
              <input className="zen-input zen-mono" value={form.iban || ""} onChange={e => setForm(p => ({ ...p, iban: e.target.value }))}
                placeholder="TR00 0000 0000 0000 0000 0000 00" data-testid="input-bank-iban" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Şube</label>
              <input className="zen-input" value={form.branch || ""} onChange={e => setForm(p => ({ ...p, branch: e.target.value }))}
                placeholder="Şube adı" data-testid="input-bank-branch" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">SWIFT / BIC</label>
              <input className="zen-input zen-mono" value={form.swiftCode || ""} onChange={e => setForm(p => ({ ...p, swiftCode: e.target.value }))}
                placeholder="TCZBTR2A" data-testid="input-bank-swift" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.isDefault} onChange={e => setForm(p => ({ ...p, isDefault: e.target.checked }))} className="rounded" data-testid="input-bank-default" />
            <span className="text-xs">Bu hesabı varsayılan olarak kullan</span>
          </label>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="zen-btn-primary flex-1" data-testid="btn-save-bank-account">
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
            <button onClick={() => setForm(null)} className="zen-btn-ghost px-4">İptal</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ayarlar > Yazıcılar & Etiketler ─────────────────────────────────────────────────────

// HTML etiket boyutları
const HTML_LABEL_SIZES = {
  "kargo":  { w: 100, h: 75,  label: "Kargo Etiketi (100×75mm)" },
  "kasa":   { w: 100, h: 50,  label: "Kasa Etiketi (100×50mm)" },
  "custom": { w: 100, h: 60,  label: "Özel Boyut" },
};

// HTML label default template (kargo)
const HTML_KARGO_DEFAULT = {
  size: "kargo",
  elements: [
    { id: "h1", type: "static_text",         x_mm: 3,  y_mm: 3,  fontSize: 40, text: "ZEN PERDE" },
    { id: "h2", type: "hline",               x_mm: 3,  y_mm: 11, fontSize: 2,  widthMm: 94 },
    { id: "h3", type: "field_customer_name", x_mm: 3,  y_mm: 14, fontSize: 34 },
    { id: "h4", type: "field_customer_city", x_mm: 3,  y_mm: 22, fontSize: 24 },
    { id: "h5", type: "hline",               x_mm: 3,  y_mm: 30, fontSize: 2,  widthMm: 94 },
    { id: "h6", type: "field_order_no",      x_mm: 3,  y_mm: 33, fontSize: 40, text: "No: " },
    { id: "h7", type: "field_date",          x_mm: 60, y_mm: 33, fontSize: 24 },
    { id: "h8", type: "barcode",             x_mm: 3,  y_mm: 50, fontSize: 3,  heightMm: 18, widthMm: 80 },
  ],
};

// HTML label default template (kasa)
const HTML_KASA_DEFAULT = {
  size: "kasa",
  elements: [
    { id: "k1", type: "static_text",          x_mm: 3,  y_mm: 3,  fontSize: 36, text: "ZEN PERDE" },
    { id: "k2", type: "field_order_no",       x_mm: 3,  y_mm: 11, fontSize: 28, text: "SP: " },
    { id: "k3", type: "field_customer_name",  x_mm: 3,  y_mm: 19, fontSize: 26 },
    { id: "k4", type: "field_dimensions",     x_mm: 3,  y_mm: 26, fontSize: 24 },
    { id: "k5", type: "field_product_name",   x_mm: 3,  y_mm: 34, fontSize: 20 },
    { id: "k6", type: "field_date",           x_mm: 60, y_mm: 34, fontSize: 20 },
  ],
};

// HTML Etiket Editörü — kargo ve kasa etiketleri için görsel tasarım
// ── Evrensel Şablon Havuzu ────────────────────────────────────────────────────
const PRINT_TPL_PAGES = [
  { id: "orders",    label: "Siparişler" },
  { id: "customers", label: "Cari / Müşteriler" },
  { id: "stock",     label: "Stok" },
  { id: "quotes",    label: "Teklifler" },
  { id: "invoice",   label: "Faturalar" },
];
const DATA_SCHEMAS_LOCAL = {
  customers: {
    label: "Müşteri (Cari)",
    vars: ["name","code","balance","phone","email","city","type","currency","taxNo","address"],
    labels: { name:"Müşteri Adı", code:"Cari Kodu", balance:"Bakiye", phone:"Telefon", email:"E-posta", city:"Şehir", type:"Cari Tipi", currency:"Para Birimi", taxNo:"Vergi No", address:"Adres" },
  },
  orders: {
    label: "Sipariş",
    vars: ["orderNo","customerName","total","status","date","city","currency","note"],
    labels: { orderNo:"Sipariş No", customerName:"Müşteri Adı", total:"Tutar", status:"Durum", date:"Tarih", city:"Şehir", currency:"Para Birimi", note:"Not" },
  },
  stock: {
    label: "Stok Ürün",
    vars: ["name","sku","qty","unit","price"],
    labels: { name:"Ürün Adı", sku:"SKU/Kod", qty:"Stok", unit:"Birim", price:"Fiyat" },
  },
};
const SIZE_PRESETS = [
  { label: "A4 Dikey",   w: 210, h: 297 },
  { label: "A4 Yatay",   w: 297, h: 210 },
  { label: "A5",         w: 148, h: 210 },
  { label: "100×70 mm",  w: 100, h: 70  },
  { label: "70×50 mm",   w: 70,  h: 50  },
  { label: "Özel",       w: null, h: null },
];
const EMPTY_TPL = () => ({
  name: "",
  pages: [],
  dataSchema: "customers",
  size: { w: 210, h: 297 },
  elements: [],
  active: true,
});
const EMPTY_EL = () => ({ id: `el_${Date.now()}`, type: "text", text: "", x_mm: 10, y_mm: 10, fontSize: 36, bold: true, widthMm: 80 });

function PrintTemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null | tpl object
  const [saving, setSaving] = useState(false);
  const [activeEl, setActiveEl] = useState(null); // index
  const [refresh, setRefresh] = useState(0);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  // ── Yükle ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings", { params: { category: "print_templates_v2" } });
        const parsed = (data || []).map(r => {
          try { return { _sid: r.id, ...JSON.parse(r.value || "{}") }; } catch { return null; }
        }).filter(Boolean);
        setTemplates(parsed);
      } catch { /* ilk kurulumda boş */ }
    })();
  }, [refresh]);

  // ── Kaydet ────────────────────────────────────────────────────────────────
  const saveTemplate = async () => {
    if (!editing.name.trim()) { toast.error("Şablon adı zorunlu"); return; }
    setSaving(true);
    try {
      const body = { ...editing };
      const sid = body._sid;
      delete body._sid;
      if (sid) {
        await api.put(`/settings/${sid}`, { value: JSON.stringify(body) });
      } else {
        const newId = `TPL-${Date.now()}`;
        await api.post("/settings", {
          category: "print_templates_v2",
          key: `tpl_${Date.now()}`,
          value: JSON.stringify({ ...body, id: newId, createdAt: new Date().toISOString() }),
        });
      }
      toast.success("Şablon kaydedildi");
      setEditing(null);
      setRefresh(r => r + 1);
    } catch (e) { toast.error("Kayıt hatası: " + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const delTemplate = async (tpl) => {
    if (!window.confirm(`"${tpl.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/settings/${tpl._sid}`);
      setRefresh(r => r + 1);
      toast.success("Silindi");
    } catch (e) { toast.error("Silme hatası"); }
  };

  // ── Element helpers ────────────────────────────────────────────────────────
  const updEl = (idx, patch) => {
    const els = [...editing.elements];
    els[idx] = { ...els[idx], ...patch };
    setEditing(e => ({ ...e, elements: els }));
  };
  const addEl = () => {
    const el = EMPTY_EL();
    el.y_mm = (editing.elements.length * 12) + 10;
    setEditing(e => ({ ...e, elements: [...e.elements, el] }));
    setActiveEl(editing.elements.length);
  };
  const removeEl = (idx) => {
    const els = editing.elements.filter((_, i) => i !== idx);
    setEditing(e => ({ ...e, elements: els }));
    setActiveEl(null);
  };
  const insertVar = (varKey) => {
    if (activeEl === null) { toast.info("Önce bir element seçin"); return; }
    const el = editing.elements[activeEl];
    updEl(activeEl, { text: (el.text || "") + `{{${varKey}}}` });
  };

  // ── Önizleme ──────────────────────────────────────────────────────────────
  const preview = () => {
    const schema = DATA_SCHEMAS_LOCAL[editing.dataSchema] || DATA_SCHEMAS_LOCAL.customers;
    const sampleData = schema.vars.reduce((acc, v) => ({ ...acc, [v]: schema.labels[v] || v }), {});
    // Build HTML from elements
    const size = editing.size || { w: 210, h: 297 };
    const elHtml = (editing.elements || []).map(el => {
      const text = (el.text || "").replace(/\{\{(\w+)\}\}/g, (_, k) => sampleData[k] || `{${k}}`);
      const ptSize = ((el.fontSize || 36) * 0.354 * 0.125 * 3.78).toFixed(1);
      const bold = el.bold !== false ? "font-weight:700;" : "";
      return `<div style="position:absolute;left:${el.x_mm}mm;top:${el.y_mm}mm;font-size:${ptSize}pt;${bold}font-family:Arial;white-space:pre-wrap;">${String(text).replace(/</g,"&lt;")}</div>`;
    }).join("");
    setPreviewHtml(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>@page{size:${size.w}mm ${size.h}mm;margin:0}body,html{margin:0;padding:0;width:${size.w}mm;height:${size.h}mm;background:#fff;overflow:hidden}</style></head><body><div style="position:relative;width:${size.w}mm;height:${size.h}mm;border:0.3mm solid #ddd;">${elHtml}</div></body></html>`);
    setShowPreview(true);
  };

  // ── Liste görünümü ─────────────────────────────────────────────────────────
  if (!editing) return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-sm">Evrensel Yazdırma Şablonları</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Her sayfa için özel şablonlar tanımlayın — Yazdır menüsünde otomatik görünür</p>
        </div>
        <button onClick={() => setEditing(EMPTY_TPL())} className="zen-btn-primary !text-xs">
          <Plus className="w-3.5 h-3.5" /> Yeni Şablon
        </button>
      </div>

      {templates.length === 0 && (
        <div className="zen-panel !p-8 text-center text-slate-500">
          <Layers className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Henüz şablon tanımlanmamış</p>
          <p className="text-xs mt-1">Şablon oluşturun, hangi sayfada görüneceğini seçin, değişkenlerle doldurun</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {templates.map(tpl => (
          <div key={tpl._sid} className="zen-panel !p-4 flex flex-col gap-2.5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-bold text-sm">{tpl.name || "İsimsiz Şablon"}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {tpl.size?.w}×{tpl.size?.h}mm · {DATA_SCHEMAS_LOCAL[tpl.dataSchema]?.label || tpl.dataSchema}
                </div>
              </div>
              <div className={`w-2 h-2 rounded-full mt-1 ${tpl.active !== false ? "bg-emerald-500" : "bg-slate-500"}`} />
            </div>
            <div className="flex flex-wrap gap-1">
              {(tpl.pages || []).map(p => (
                <span key={p} className="zen-chip !text-[9px] bg-sky-500/10 text-sky-400">
                  {PRINT_TPL_PAGES.find(x => x.id === p)?.label || p}
                </span>
              ))}
            </div>
            <div className="text-[10px] text-slate-500">{(tpl.elements || []).length} element</div>
            <div className="flex gap-1.5 mt-auto">
              <button onClick={() => setEditing({ ...tpl })} className="zen-btn-ghost !text-xs flex-1"><Pencil className="w-3 h-3" /> Düzenle</button>
              <button onClick={() => delTemplate(tpl)} className="zen-btn-ghost !text-xs text-rose-400 border-rose-500/20"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Editör görünümü ────────────────────────────────────────────────────────
  const schema = DATA_SCHEMAS_LOCAL[editing.dataSchema] || DATA_SCHEMAS_LOCAL.customers;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => setEditing(null)} className="zen-btn-ghost !text-xs flex items-center gap-1.5">
          <ChevronLeft className="w-4 h-4" /> Şablon Listesi
        </button>
        <div className="flex gap-2">
          <button onClick={preview} className="zen-btn-ghost !text-xs"><Eye className="w-3.5 h-3.5" /> Önizle</button>
          <button onClick={saveTemplate} disabled={saving} className="zen-btn-primary !text-xs">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Kaydediliyor…</> : <><Check className="w-3.5 h-3.5" /> Kaydet</>}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_280px] gap-4">
        {/* ── Sol: Element listesi ─────────────────────────── */}
        <div className="space-y-3">
          <div className="zen-panel !p-4 space-y-3">
            <h4 className="text-xs font-bold uppercase text-slate-400">Şablon Bilgileri</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Şablon Adı *</label>
                <input className="zen-input !py-1.5 !text-sm" value={editing.name}
                  onChange={e => setEditing(s => ({ ...s, name: e.target.value }))}
                  placeholder="Örn: Müşteri Cari Kartı" />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1">Veri Şeması</label>
                <select className="zen-input !py-1.5 !text-sm" value={editing.dataSchema}
                  onChange={e => setEditing(s => ({ ...s, dataSchema: e.target.value }))}>
                  {Object.entries(DATA_SCHEMAS_LOCAL).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1.5">Aktif Sayfalar</label>
              <div className="flex flex-wrap gap-2">
                {PRINT_TPL_PAGES.map(p => (
                  <label key={p.id} className="flex items-center gap-1.5 cursor-pointer group">
                    <input type="checkbox"
                      checked={(editing.pages || []).includes(p.id)}
                      onChange={e => {
                        const pages = e.target.checked
                          ? [...(editing.pages || []), p.id]
                          : (editing.pages || []).filter(x => x !== p.id);
                        setEditing(s => ({ ...s, pages }));
                      }}
                      className="rounded accent-[#E5322A]" />
                    <span className="text-xs group-hover:text-white transition-colors">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-1.5">Kağıt Boyutu</label>
              <div className="flex flex-wrap gap-1.5">
                {SIZE_PRESETS.map(pr => {
                  const active = pr.w === null
                    ? !SIZE_PRESETS.slice(0,-1).some(pp => pp.w === editing.size?.w && pp.h === editing.size?.h)
                    : editing.size?.w === pr.w && editing.size?.h === pr.h;
                  return (
                    <button key={pr.label} onClick={() => pr.w && setEditing(s => ({ ...s, size: { w: pr.w, h: pr.h } }))}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors border ${active ? "border-[#E5322A] bg-[#E5322A]/10 text-[#E5322A]" : "border-white/10 text-slate-400 hover:border-white/20"}`}>
                      {pr.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-2">
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-slate-500">G:</span>
                  <input type="number" className="zen-input !py-1 !w-16 !text-xs" value={editing.size?.w || ""} min="30" max="600"
                    onChange={e => setEditing(s => ({ ...s, size: { ...s.size, w: +e.target.value } }))} />
                  <span className="text-[10px] text-slate-500">mm</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-slate-500">Y:</span>
                  <input type="number" className="zen-input !py-1 !w-16 !text-xs" value={editing.size?.h || ""} min="20" max="600"
                    onChange={e => setEditing(s => ({ ...s, size: { ...s.size, h: +e.target.value } }))} />
                  <span className="text-[10px] text-slate-500">mm</span>
                </div>
              </div>
            </div>
          </div>

          {/* Element listesi */}
          <div className="zen-panel !p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase text-slate-400">İçerik Elemanları</h4>
              <button onClick={addEl} className="zen-btn-ghost !text-xs !py-1"><Plus className="w-3 h-3" /> Metin Ekle</button>
            </div>
            {editing.elements.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-4">Henüz element yok — "Metin Ekle" ile başlayın</div>
            )}
            {editing.elements.map((el, idx) => (
              <div key={el.id}
                onClick={() => setActiveEl(idx)}
                className={`rounded-lg border p-3 space-y-2 cursor-pointer transition-all ${activeEl === idx ? "border-[#E5322A]/50 bg-[#E5322A]/5" : "border-white/5 hover:border-white/10"}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-500">#{idx + 1}</span>
                  <input className="zen-input !py-0.5 !text-xs flex-1" value={el.text}
                    onChange={e => updEl(idx, { text: e.target.value })}
                    placeholder="Metin veya {{değişken}}" onClick={e => { e.stopPropagation(); setActiveEl(idx); }} />
                  <button onClick={(e) => { e.stopPropagation(); removeEl(idx); }} className="text-rose-400 hover:text-rose-300 p-1"><X className="w-3.5 h-3.5" /></button>
                </div>
                {activeEl === idx && (
                  <div className="grid grid-cols-4 gap-2 pt-1">
                    <div><label className="text-[9px] text-slate-500 block">X (mm)</label>
                      <input type="number" className="zen-input !py-0.5 !text-xs" value={el.x_mm} onChange={e => updEl(idx, { x_mm: +e.target.value })} /></div>
                    <div><label className="text-[9px] text-slate-500 block">Y (mm)</label>
                      <input type="number" className="zen-input !py-0.5 !text-xs" value={el.y_mm} onChange={e => updEl(idx, { y_mm: +e.target.value })} /></div>
                    <div><label className="text-[9px] text-slate-500 block">Boyut</label>
                      <input type="number" className="zen-input !py-0.5 !text-xs" value={el.fontSize} onChange={e => updEl(idx, { fontSize: +e.target.value })} /></div>
                    <div><label className="text-[9px] text-slate-500 block">Kalın</label>
                      <input type="checkbox" checked={el.bold !== false} onChange={e => updEl(idx, { bold: e.target.checked })} className="mt-2 rounded accent-[#E5322A]" /></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Sağ: Değişken paleti ──────────────────────────── */}
        <div className="space-y-3">
          <div className="zen-panel !p-4 space-y-2">
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Veri Değişkenleri</h4>
            <p className="text-[10px] text-slate-500">Seçili elemente eklemek için tıklayın:</p>
            <div className="space-y-1">
              {schema.vars.map(v => (
                <button key={v} onClick={() => insertVar(v)}
                  className="w-full text-left px-2.5 py-1.5 rounded-md text-xs hover:bg-white/5 transition-colors flex items-center justify-between group border border-transparent hover:border-white/10"
                >
                  <span className="text-slate-300">{schema.labels[v]}</span>
                  <span className="zen-mono text-[10px] text-slate-500 group-hover:text-emerald-400">{`{{${v}}}`}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="zen-panel !p-4">
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Durum</h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={editing.active !== false}
                onChange={e => setEditing(s => ({ ...s, active: e.target.checked }))}
                className="rounded accent-[#E5322A]" />
              <span className="text-xs">Aktif (sayfalarda görünsün)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Önizleme modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="zen-panel w-full max-w-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm">Önizleme (örnek verilerle)</h3>
              <button onClick={() => setShowPreview(false)}><X className="w-5 h-5" /></button>
            </div>
            <iframe
              srcDoc={previewHtml}
              className="w-full border border-white/10 rounded"
              style={{ height: Math.min((editing.size?.h || 297) * 3, 500) + "px" }}
              title="Şablon Önizleme"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── HtmlLabelEditor ───────────────────────────────────────────────────────────
function HtmlLabelEditor() {
  const [labelType, setLabelType] = useState("kargo");   // "kargo" | "kasa"
  const [customW, setCustomW] = useState(100);
  const [customH, setCustomH] = useState(60);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [leftTab, setLeftTab] = useState("palet"); // "palet" | "inspector" | "kod"
  const [showGrid, setShowGrid] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [wrapW, setWrapW] = useState(850);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(e => setWrapW(e[0]?.contentRect?.width || 850));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);
  const canvasRef = useRef(null);

  // Firma logosunu component mount'ta yükle
  useEffect(() => {
    loadLogoDataUrl().then((url) => { if (url) setLogoDataUrl(url); }).catch(() => {});
  }, []);

  const effectiveSize = labelType === "custom"
    ? { w: customW || 100, h: customH || 60 }
    : (HTML_LABEL_SIZES[labelType] || HTML_LABEL_SIZES["kargo"]);

  // Canvas: sağ paneli dolduracak şekilde - dinamik ölçek
  const maxCanvasH = Math.min(700, Math.max(400, window.innerHeight * 0.66));
  const canvasScale = Math.min(Math.max(3, (wrapW - 56) / effectiveSize.w), maxCanvasH / effectiveSize.h);
  const canvasW = effectiveSize.w * canvasScale;
  const canvasH = effectiveSize.h * canvasScale;

  // HTML kod önizlemesi (Kod sekmesi için)
  const htmlCode = useMemo(() => {
    const w = effectiveSize.w; const h = effectiveSize.h;
    const demoOrder = { orderNo: "SP-2026-001", customerName: "Test Müşteri", customerCity: "İstanbul", note: "Kırılabilir!", projectName: "Demo Proje", deadline: new Date().toISOString(), items: [{ product: "Stor Perde", widthCm: 150, heightCm: 200, qty: 2, fabricCode: "BLACKOUT-01", ralCode: "7016" }] };
    try {
      const htmlEls = elements.map((el) => {
        const def = ZPL_FIELD_TYPES.find((t) => t.key === el.type);
        const value = def ? def.resolver(demoOrder, el.text || "") : "";
        const left = `${el.x_mm}mm`; const top = `${el.y_mm}mm`;
        if (el.type === "hline") return `<div style="position:absolute;left:${left};top:${top};width:${el.widthMm || w-6}mm;height:${(el.fontSize||2)*0.5}px;background:#000;"></div>`;
        if (el.type === "barcode") return `<div style="position:absolute;left:${left};top:${top};width:${el.widthMm||60}mm;height:${el.heightMm||18}mm;background:repeating-linear-gradient(90deg,#000 0 1px,transparent 1px 3px);"></div>`;
        if (el.type === "rect") return `<div style="position:absolute;left:${left};top:${top};width:${el.widthMm||40}mm;height:${el.heightMm||20}mm;border:${el.borderThick||0.5}mm solid #000;"></div>`;
        if (el.type === "image_logo") return `<img src="[LOGO]" style="position:absolute;left:${left};top:${top};width:${el.widthMm||30}mm;height:${el.heightMm||12}mm;object-fit:contain;" />`;
        return `<div style="position:absolute;left:${left};top:${top};font-size:${(el.fontSize||28)*0.352778}mm;font-weight:700;white-space:nowrap;${el.rotation ? "transform:rotate(90deg);transform-origin:top left;" : ""}">${value}</div>`;
      }).join("\n  ");
      return `<!DOCTYPE html>\n<html lang="tr">\n<head>\n  <meta charset="utf-8">\n  <style>\n    @page { size: ${w}mm ${h}mm; margin: 0; }\n    *, html, body { box-sizing: border-box; margin: 0; padding: 0; }\n    body { width: ${w}mm; height: ${h}mm; background: #fff; overflow: hidden; }\n  </style>\n</head>\n<body>\n  <div style="position:relative;width:${w}mm;height:${h}mm;">\n  ${htmlEls}\n  </div>\n</body>\n</html>`;
    } catch { return ""; }
  }, [elements, effectiveSize.w, effectiveSize.h]);

  const exportToZPL = async () => {
    setExporting(true);
    try {
      const { w, h } = effectiveSize;
      const matching = ZPL_PRESET_SIZES.find(s => s.w === w && s.h === h);
      const zplKey = matching ? matching.key : "custom";
      const keyStr = zplKey === "custom" ? `custom_${w}x${h}` : zplKey;
      const idStr = `ZPL-${keyStr.toUpperCase().replace(/[^A-Z0-9]/g, "")}`;
      const payload = { size: zplKey, ...(zplKey === "custom" ? { customW: w, customH: h } : {}), elements };
      await api.post("/settings", {
        id: idStr, category: "zpl_templates", key: keyStr,
        value: JSON.stringify(payload),
        label: `ZPL Şablon ${w}×${h}mm`,
        active: true, createdAt: new Date().toISOString(),
      });
      toast.success(`ZPL şablonuna aktarıldı (${w}×${h}mm) — ZPL Editörde açın`);
    } catch(e) { toast.error("Aktarım hatası: " + e.message); }
    finally { setExporting(false); }
  };

  const loadForType = async (type) => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings", { params: { category: "html_label_templates" } });
      const row = (data || []).find((r) => r.key === type);
      let tpl = null;
      if (row?.value) { try { tpl = JSON.parse(row.value); } catch { /* */ } }
      if (!tpl) tpl = type === "kasa" ? HTML_KASA_DEFAULT : HTML_KARGO_DEFAULT;
      if (tpl.customW) setCustomW(tpl.customW);
      if (tpl.customH) setCustomH(tpl.customH);
      setElements(tpl.elements || []);
      setSelectedId(null);
    } catch { toast.error("Şablon yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadForType(labelType); /* eslint-disable-next-line */ }, [labelType]);

  const addElement = (type) => {
    const def = ZPL_FIELD_TYPES.find((t) => t.key === type);
    const newEl = {
      id: `h${Date.now()}`,
      type,
      x_mm: 4, y_mm: 4,
      fontSize: def?.isShape ? 2 : (def?.isBarcode ? 3 : 28),
      rotation: 0,
      text: def?.fill || "",
      ...(def?.isBarcode ? { heightMm: 18, widthMm: 60 } : {}),
      ...((def?.isShape && type === "hline") ? { widthMm: effectiveSize.w - 6 } : {}),
      ...((def?.isShape && type === "rect") ? { widthMm: 40, heightMm: 20, borderThick: 0.5 } : {}),
      ...(def?.isImage ? { widthMm: 30, heightMm: 12 } : {}),
    };
    setElements((prev) => [...prev, newEl]);
    setSelectedId(newEl.id);
  };

  const updateSelected = (patch) => setElements((prev) => prev.map((e) => (e.id === selectedId ? { ...e, ...patch } : e)));
  const deleteSelected = () => { setElements((prev) => prev.filter((e) => e.id !== selectedId)); setSelectedId(null); };

  const startDrag = (e, id) => {
    e.stopPropagation(); e.preventDefault();
    setSelectedId(id);
    const el = elements.find((x) => x.id === id);
    if (!el) return;
    setDragState({ id, startMouseX: e.clientX, startMouseY: e.clientY, startX: el.x_mm, startY: el.y_mm });
  };
  const selectOnly = (e, id) => { e.stopPropagation(); setSelectedId(id); };
  const startResize = (e, id, handle) => {
    e.stopPropagation(); e.preventDefault();
    const el = elements.find((x) => x.id === id);
    if (!el) return;
    setResizeState({ id, handle, startMouseX: e.clientX, startMouseY: e.clientY, startEl: { ...el } });
  };

  useEffect(() => {
    if (!dragState && !resizeState) return;
    const onMove = (e) => {
      if (dragState) {
        const nx = Math.max(0, Math.min(effectiveSize.w - 2, dragState.startX + (e.clientX - dragState.startMouseX) / canvasScale));
        const ny = Math.max(0, Math.min(effectiveSize.h - 2, dragState.startY + (e.clientY - dragState.startMouseY) / canvasScale));
        setElements((prev) => prev.map((el) => (el.id === dragState.id ? { ...el, x_mm: nx, y_mm: ny } : el)));
      }
      if (resizeState) {
        const dxMm = (e.clientX - resizeState.startMouseX) / canvasScale;
        const dyMm = (e.clientY - resizeState.startMouseY) / canvasScale;
        const sel = resizeState.startEl;
        const updates = {};
        if (resizeState.handle === "se") { updates.widthMm = Math.max(5, (sel.widthMm || 40) + dxMm); updates.heightMm = Math.max(5, (sel.heightMm || 20) + dyMm); }
        else if (resizeState.handle === "e") updates.widthMm = Math.max(5, (sel.widthMm || 40) + dxMm);
        else if (resizeState.handle === "s") updates.heightMm = Math.max(5, (sel.heightMm || 20) + dyMm);
        setElements((prev) => prev.map((el) => (el.id === resizeState.id ? { ...el, ...updates } : el)));
      }
    };
    const onUp = () => { setDragState(null); setResizeState(null); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, [dragState, resizeState, canvasScale, effectiveSize.w, effectiveSize.h]);

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const payload = { size: labelType, ...(labelType === "custom" ? { customW, customH } : {}), elements };
      await api.post("/settings", { id: `HTML-LABEL-${labelType.toUpperCase()}`, category: "html_label_templates", key: labelType, value: JSON.stringify(payload), label: `HTML Etiket ${HTML_LABEL_SIZES[labelType]?.label || labelType}`, active: true, createdAt: new Date().toISOString() });
      toast.success(`${HTML_LABEL_SIZES[labelType]?.label || labelType} şablonu kaydedildi`);
    } catch (e) { toast.error("Kayıt hatası: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const previewHtml = async () => {
    setPreviewing(true);
    try {
      const tpl = { size: labelType, ...(labelType === "custom" ? { customW, customH } : {}), elements };
      const demoOrder = { orderNo: "SP-2026-001", customerName: "Test Müşteri", customerCity: "İstanbul", note: "Kırılabilir!", projectName: "Demo Proje", deadline: new Date().toISOString(), items: [{ product: "Stor Perde", widthCm: 150, heightCm: 200, qty: 2, fabricCode: "BLACKOUT-01", ralCode: "7016" }] };
      const { w, h } = effectiveSize;
      const htmlEls = elements.map((el) => {
        const def = ZPL_FIELD_TYPES.find((t) => t.key === el.type);
        const value = def ? def.resolver(demoOrder, el.text || "") : "";
        const x = (el.x_mm || 0).toFixed(2); const y = (el.y_mm || 0).toFixed(2);
        const ptSize = ((el.fontSize || 26) * 0.354 * 0.125 * 3.78).toFixed(1);
        if (el.type === "rect") return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm||40).toFixed(2)}mm;height:${(el.heightMm||20).toFixed(2)}mm;border:${(el.borderThick||0.5).toFixed(2)}mm solid #000;box-sizing:border-box;"></div>`;
        if (el.type === "image_logo") return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm||30).toFixed(2)}mm;height:${(el.heightMm||12).toFixed(2)}mm;">${logoDataUrl ? `<img src="${logoDataUrl}" style="width:100%;height:100%;object-fit:contain;" />` : `<div style="border:0.2mm solid #ccc;display:flex;align-items:center;justify-content:center;width:100%;height:100%;font-size:6pt;color:#999;">LOGO</div>`}</div>`;
        if (el.type === "hline") return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm||w-4).toFixed(2)}mm;height:${Math.max(0.3,(el.fontSize||2)*0.03).toFixed(2)}mm;background:#000;"></div>`;
        if (el.type === "barcode") return `<div style="position:absolute;left:${x}mm;top:${y}mm;width:${(el.widthMm||60).toFixed(2)}mm;height:${(el.heightMm||18).toFixed(2)}mm;border:0.2mm solid #ccc;display:flex;align-items:center;justify-content:center;font-family:monospace;font-size:5pt;">▐▌▌▐▌▐▌▐▌▐▌▐▐▌ ${String(value).replace(/</g,"&lt;")}</div>`;
        const rot = el.rotation ? "transform:rotate(90deg);transform-origin:top left;" : "";
        return `<div style="position:absolute;left:${x}mm;top:${y}mm;font-size:${ptSize}pt;font-weight:700;font-family:Arial,sans-serif;white-space:nowrap;${rot}">${String(value||"").replace(/</g,"&lt;")}</div>`;
      }).join("\n");
      const html = `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><style>@page{size:${w}mm ${h}mm;margin:0;}*{box-sizing:border-box;}html,body{width:${w}mm;height:${h}mm;background:#fff;margin:0;padding:0;overflow:hidden;}</style></head><body><div style="position:relative;width:${w}mm;height:${h}mm;">${htmlEls}</div></body></html>`;
      printViaIframe(html, false);
    } catch (e) { toast.error("Önizleme hatası: " + e.message); }
    finally { setPreviewing(false); }
  };

  const selected = elements.find((e) => e.id === selectedId);
  const selectedDef = selected ? ZPL_FIELD_TYPES.find((t) => t.key === selected.type) : null;
  const TEXT_FIELD_OPTIONS = ZPL_FIELD_TYPES.filter((t) => t.isText || t.isBarcode);

  const renderCanvasEl = (el) => {
    const def = ZPL_FIELD_TYPES.find((t) => t.key === el.type);
    const isSel = el.id === selectedId;
    const pxFontSize = Math.max(6, el.fontSize * canvasScale * 0.125);
    const commonStyle = { position: "absolute", left: el.x_mm * canvasScale, top: el.y_mm * canvasScale, cursor: "move", userSelect: "none", touchAction: "none" };
    const hasResize = isSel && (el.type === "rect" || el.type === "barcode" || el.type === "image_logo" || el.type === "hline");
    const resizeHandles = hasResize ? (
      <>
        <div onPointerDown={(e) => startResize(e, el.id, "se")} style={{ position: "absolute", right: -4, bottom: -4, width: 8, height: 8, background: "#3b82f6", cursor: "se-resize", borderRadius: 2 }} />
        <div onPointerDown={(e) => startResize(e, el.id, "e")} style={{ position: "absolute", right: -4, top: "50%", transform: "translateY(-50%)", width: 8, height: 8, background: "#3b82f6", cursor: "e-resize", borderRadius: 2 }} />
        <div onPointerDown={(e) => startResize(e, el.id, "s")} style={{ position: "absolute", bottom: -4, left: "50%", transform: "translateX(-50%)", width: 8, height: 8, background: "#3b82f6", cursor: "s-resize", borderRadius: 2 }} />
      </>
    ) : null;

    if (el.type === "hline") return (<div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`html-el-${el.id}`} style={{ ...commonStyle, width: (el.widthMm || effectiveSize.w - 6) * canvasScale, height: Math.max(2, (el.fontSize || 2) * 0.5), background: isSel ? "#3b82f6" : "#0f172a", outline: isSel ? "2px solid #3b82f6" : "none" }}>{resizeHandles}</div>);
    if (el.type === "barcode") return (<div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`html-el-${el.id}`} style={{ ...commonStyle, width: (el.widthMm || 60) * canvasScale, height: (el.heightMm || 18) * canvasScale, background: "repeating-linear-gradient(90deg,#0f172a 0 2px,transparent 2px 4px,#0f172a 4px 5px,transparent 5px 8px)", outline: isSel ? "2px solid #3b82f6" : "1px dashed #94a3b8", display: "flex", alignItems: "flex-end", justifyContent: "center", fontSize: 8, color: "#0f172a", paddingBottom: 2, fontFamily: "monospace" }} title="Barkod">{def?.fill || ""}{resizeHandles}</div>);
    if (el.type === "rect") return (<div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`html-el-${el.id}`} style={{ ...commonStyle, width: (el.widthMm || 40) * canvasScale, height: (el.heightMm || 20) * canvasScale, border: `2px solid ${isSel ? "#3b82f6" : "#0f172a"}`, background: "transparent", outline: isSel ? "2px solid rgba(59,130,246,0.3)" : "none" }}>{resizeHandles}</div>);
    if (el.type === "image_logo") return (<div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`html-el-${el.id}`} style={{ ...commonStyle, width: (el.widthMm || 30) * canvasScale, height: (el.heightMm || 12) * canvasScale, border: `1px dashed ${isSel ? "#3b82f6" : "#94a3b8"}`, background: logoDataUrl ? "transparent" : "rgba(59,130,246,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#94a3b8", fontFamily: "monospace", overflow: "hidden" }}>
      {logoDataUrl ? <img src={logoDataUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} /> : "LOGO"}
      {resizeHandles}
    </div>);

    const previewText = el.type === "static_text" ? (el.text || def?.fill || "…") : (def?.fill || el.text || "");
    const rotStyle = el.rotation ? { transform: "rotate(90deg)", transformOrigin: "top left" } : {};
    return (<div key={el.id} onPointerDown={(e) => startDrag(e, el.id)} onClick={(e) => selectOnly(e, el.id)} data-testid={`html-el-${el.id}`} style={{ ...commonStyle, fontSize: pxFontSize, fontWeight: 700, fontFamily: "Arial,sans-serif", color: "#0f172a", whiteSpace: "nowrap", padding: "0 2px", outline: isSel ? "2px solid #3b82f6" : "1px dashed transparent", background: isSel ? "rgba(59,130,246,0.08)" : "transparent", ...rotStyle }}>{previewText}</div>);
  };

  return (
    <div className="space-y-3" data-testid="html-label-editor-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-blue-500" />
          <h3 className="text-lg font-extrabold">HTML Etiket Editörü</h3>
          <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full font-semibold">Kargo & Kasa</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={previewHtml} disabled={previewing} className="zen-btn-ghost !text-xs" data-testid="html-label-preview-btn">
            <Eye className="w-3.5 h-3.5" /> {previewing ? "..." : "Önizle & Yazdır"}
          </button>
          <button onClick={exportToZPL} disabled={exporting || elements.length === 0} className="zen-btn-ghost !text-xs" data-testid="html-label-export-zpl-btn" title="Bu tasarımı ZPL şablonuna aktar">
            <Share2 className="w-3.5 h-3.5" /> {exporting ? "..." : "ZPL'e Aktar"}
          </button>
          <button onClick={saveTemplate} disabled={saving} className="zen-btn-primary !text-xs" data-testid="html-label-save-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> {saving ? "..." : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Etiket tipi seçici */}
      <div className="zen-panel p-2.5 flex items-center gap-2 flex-wrap" data-testid="html-label-type-selector">
        <span className="text-[11px] font-bold text-slate-500 mr-1">ETİKET TİPİ:</span>
        {Object.entries(HTML_LABEL_SIZES).map(([key, val]) => (
          <button key={key} onClick={() => setLabelType(key)} data-testid={`html-label-type-${key}`}
            className={`px-3 py-1 rounded text-[11px] font-bold ${labelType === key ? "bg-blue-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>
            {val.label}
          </button>
        ))}
        {labelType === "custom" && (
          <div className="flex items-center gap-1 ml-2">
            <input type="number" min="20" max="400" value={customW} onChange={(e) => setCustomW(Number(e.target.value || 100))}
              className="w-16 px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" placeholder="mm" />
            <span className="text-[10px] text-slate-500">×</span>
            <input type="number" min="20" max="400" value={customH} onChange={(e) => setCustomH(Number(e.target.value || 60))}
              className="w-16 px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" placeholder="mm" />
            <span className="text-[10px] text-slate-500">mm</span>
          </div>
        )}
        <span className="text-[10px] text-slate-400 ml-auto font-mono">{effectiveSize.w}×{effectiveSize.h}mm</span>
      </div>

      {loading ? (
        <div className="zen-panel p-8 text-center text-sm text-slate-500">Yükleniyor...</div>
      ) : (
        <div className="flex gap-3 items-start">
          {/* ── SOL PANEL: Palet / Inspector / Kod ── */}
          <div className="w-52 flex-shrink-0 flex flex-col gap-2">
            {/* Sol panel sekme çubuğu */}
            <div className="zen-panel p-1 flex gap-1">
              {[["palet","Palet"], ["inspector","Inspector"], ["kod","HTML Kodu"]].map(([id, label]) => (
                <button key={id} onClick={() => setLeftTab(id)}
                  className={`flex-1 py-1 rounded text-[11px] font-bold transition-colors ${leftTab === id ? "bg-blue-500 text-white" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Palet sekmesi */}
            {leftTab === "palet" && (
              <div className="zen-panel p-3 space-y-1" data-testid="html-label-palette">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Veri Alanları</div>
                {ZPL_FIELD_TYPES.filter((t) => t.isText).map((t) => (
                  <button key={t.key} onClick={() => addElement(t.key)} data-testid={`html-add-${t.key}`}
                    className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold text-left bg-slate-50 dark:bg-slate-800 hover:bg-blue-500 hover:text-white transition-colors">
                    <PlusCircle className="w-3 h-3 shrink-0" /><span className="truncate">{t.l}</span>
                  </button>
                ))}
                <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-2 mb-0.5">Şekil & Özel</div>
                {ZPL_FIELD_TYPES.filter((t) => !t.isText).map((t) => (
                  <button key={t.key} onClick={() => addElement(t.key)} data-testid={`html-add-${t.key}`}
                    className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold text-left bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-500 hover:text-white transition-colors">
                    <PlusCircle className="w-3 h-3 shrink-0" /><span className="truncate">{t.l}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Inspector sekmesi */}
            {leftTab === "inspector" && (
              <div className="zen-panel p-3 space-y-2.5" data-testid="html-label-inspector">
                <div className="flex items-center gap-2 mb-1">
                  <Info className="w-3.5 h-3.5 text-blue-500" />
                  <div className="text-[11px] font-bold">Element Özellikleri</div>
                </div>
                {!selected ? (
                  <div className="text-[11px] text-slate-500 py-4 text-center">Önizlemede bir element seçin</div>
                ) : (
                  <>
                    {(selectedDef?.isText || selectedDef?.isBarcode) && (
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500">Veri Kaynağı</label>
                        <select value={selected.type} onChange={(e) => updateSelected({ type: e.target.value, text: ZPL_FIELD_TYPES.find(t => t.key === e.target.value)?.fill || selected.text })}
                          className="w-full px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" data-testid="html-inspector-field-type">
                          {TEXT_FIELD_OPTIONS.map((t) => (<option key={t.key} value={t.key}>{t.l}</option>))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] text-slate-500">Tip: <span className="font-bold text-blue-600">{selectedDef?.l || selected.type}</span></div>
                      {(selectedDef?.isText || selectedDef?.isBarcode) && (
                        <button onClick={() => updateSelected({ rotation: selected.rotation ? 0 : 1 })}
                          className={`text-[10px] px-2 py-0.5 rounded border font-semibold transition-colors ${selected.rotation ? "bg-indigo-500 text-white border-indigo-500" : "border-slate-300 text-slate-600 hover:bg-indigo-50"}`}
                          data-testid="html-inspector-rotation">
                          {selected.rotation ? "↺ Dikey" : "→ Yatay"}
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[10px] font-semibold text-slate-500">X (mm)</label>
                        <input type="number" min="0" max={effectiveSize.w} step="0.5" value={Math.round(selected.x_mm * 10) / 10} onChange={(e) => updateSelected({ x_mm: parseFloat(e.target.value || 0) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                      <div><label className="text-[10px] font-semibold text-slate-500">Y (mm)</label>
                        <input type="number" min="0" max={effectiveSize.h} step="0.5" value={Math.round(selected.y_mm * 10) / 10} onChange={(e) => updateSelected({ y_mm: parseFloat(e.target.value || 0) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                    </div>
                    {selected.type !== "hline" && selected.type !== "barcode" && selected.type !== "rect" && selected.type !== "image_logo" && (
                      <div><label className="text-[10px] font-semibold text-slate-500">Font Boyutu (dot)</label>
                        <input type="number" min="10" max="120" step="2" value={selected.fontSize || 28} onChange={(e) => updateSelected({ fontSize: parseInt(e.target.value || 28) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                    )}
                    {(selected.type === "rect" || selected.type === "barcode" || selected.type === "image_logo") && (
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-[10px] font-semibold text-slate-500">Genişlik (mm)</label>
                          <input type="number" min="5" max={effectiveSize.w} step="1" value={Math.round((selected.widthMm || 40) * 10) / 10} onChange={(e) => updateSelected({ widthMm: parseFloat(e.target.value || 40) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                        <div><label className="text-[10px] font-semibold text-slate-500">Yükseklik (mm)</label>
                          <input type="number" min="5" max={effectiveSize.h} step="1" value={Math.round((selected.heightMm || 20) * 10) / 10} onChange={(e) => updateSelected({ heightMm: parseFloat(e.target.value || 20) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                      </div>
                    )}
                    {selected.type === "hline" && (
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-[10px] font-semibold text-slate-500">Genişlik (mm)</label>
                          <input type="number" min="5" max={effectiveSize.w} step="1" value={selected.widthMm || effectiveSize.w - 6} onChange={(e) => updateSelected({ widthMm: parseFloat(e.target.value || 0) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                        <div><label className="text-[10px] font-semibold text-slate-500">Kalınlık (dot)</label>
                          <input type="number" min="1" max="20" value={selected.fontSize || 2} onChange={(e) => updateSelected({ fontSize: parseInt(e.target.value || 2) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                      </div>
                    )}
                    {selected.type === "rect" && (
                      <div><label className="text-[10px] font-semibold text-slate-500">Kenar Kalınlığı (mm)</label>
                        <input type="number" min="0.1" max="5" step="0.1" value={selected.borderThick || 0.5} onChange={(e) => updateSelected({ borderThick: parseFloat(e.target.value || 0.5) })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs font-mono" /></div>
                    )}
                    {selected.type === "static_text" && (
                      <div><label className="text-[10px] font-semibold text-slate-500">Metin İçeriği</label>
                        <input type="text" value={selected.text || ""} onChange={(e) => updateSelected({ text: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" data-testid="html-inspector-text" /></div>
                    )}
                    {selectedDef?.isText && selected.type !== "static_text" && (
                      <div><label className="text-[10px] font-semibold text-slate-500">Önek (opsiyonel)</label>
                        <input type="text" value={selected.text || ""} onChange={(e) => updateSelected({ text: e.target.value })} placeholder="Ör: 'Sipariş: '" className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-transparent text-xs" /></div>
                    )}
                    <button onClick={deleteSelected} className="w-full mt-1 px-2 py-1.5 rounded text-[11px] font-bold bg-rose-500/15 text-rose-500 border border-rose-500/30 hover:bg-rose-500/25" data-testid="html-inspector-delete">
                      <Trash2 className="w-3 h-3 inline-block mr-1" /> Sil
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Kod sekmesi: Oluşturulan HTML */}
            {leftTab === "kod" && (
              <div className="zen-panel p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">HTML Kodu</div>
                  <button onClick={() => { navigator.clipboard?.writeText(htmlCode); toast.success("Kopyalandı"); }}
                    className="zen-btn-ghost !text-[10px] !py-0.5">
                    <Copy className="w-3 h-3" /> Kopyala
                  </button>
                </div>
                <textarea readOnly value={htmlCode}
                  className="w-full font-mono text-[10px] bg-slate-950 text-emerald-400 p-2 rounded resize-none leading-relaxed"
                  style={{ minHeight: "360px" }} spellCheck={false} />
                <p className="text-[9px] text-slate-400">Demo verilerle üretilmiştir. "Önizle & Yazdır" gerçek baskıyı açar.</p>
              </div>
            )}
          </div>

          {/* ── SAĞ PANEL: Büyük Beyaz Önizleme ── */}
          <div ref={wrapRef} className="flex-1 zen-panel p-4 flex flex-col gap-3" data-testid="html-label-canvas-wrapper">
            {/* Önizleme araç çubuğu */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Önizleme</span>
              <span className="text-[10px] font-mono text-slate-400">{effectiveSize.w}×{effectiveSize.h}mm</span>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setShowGrid(v => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-semibold border transition-colors ${showGrid ? "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-400" : "border-slate-300 dark:border-slate-600 text-slate-500 hover:border-indigo-400"}`}
                  data-testid="html-grid-toggle">
                  <Grid className="w-3.5 h-3.5" /> {showGrid ? "Grid Kapat" : "mm Grid"}
                </button>
                <span className="text-[10px] text-slate-400">{elements.length} element</span>
              </div>
            </div>
            {/* Canvas */}
            <div className="flex justify-center">
              <div ref={canvasRef} onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
                className="relative"
                style={{
                  width: canvasW,
                  height: canvasH,
                  background: '#ffffff',
                  colorScheme: 'light',
                  border: "1.5px solid #cbd5e1",
                  boxShadow: "0 4px 32px rgba(0,0,0,0.22), 0 1px 4px rgba(0,0,0,0.08)",
                }}
                data-testid="html-label-canvas">
                {/* mm grid overlay */}
                {showGrid && (
                  <>
                    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(99,102,241,0.12) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,0.12) 1px,transparent 1px)", backgroundSize: `${canvasScale * 1}px ${canvasScale * 1}px` }} />
                    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(99,102,241,0.25) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,0.25) 1px,transparent 1px)", backgroundSize: `${canvasScale * 5}px ${canvasScale * 5}px` }} />
                  </>
                )}
                {elements.map(renderCanvasEl)}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 text-center">
              {showGrid ? "Grid: 1mm ince · 5mm koyu" : "Grid kapalı"} · HTML/CSS baskı çıktısı · Sürükleyerek yerleştirin
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const PRINT_TEMPLATES = [
  {
    id: "kargo_etiketi",
    label: "Kargo Etiketi",
    desc: "Gönderi/kargo etiketi",
    icon: "truck",
    defaultW: 100, defaultH: 75,
    fields: ["Müşteri Adı", "Sipariş No", "Adres", "Barkod"],
  },
  {
    id: "kasa_etiketi",
    label: "Kasa Etiketi",
    desc: "Ürün kasasına yapıştırılan kimlik etiketi",
    icon: "tag",
    defaultW: 100, defaultH: 50,
    fields: ["Sipariş No", "Ürün Adı", "Ölçüler", "Müşteri", "Tarih"],
  },
  {
    id: "mali_tablo",
    label: "Mali Tablo",
    desc: "Sipariş bazlı maliyet özeti",
    icon: "receipt",
    defaultW: 210, defaultH: 297, // A4
    fields: ["Sipariş No", "Maliyet Kalemleri", "Toplam", "Marj"],
  },
  {
    id: "uretim_foyu",
    label: "Üretim Föyü",
    desc: "İmalat için kesim ve montaj detayları",
    icon: "factory",
    defaultW: 210, defaultH: 297, // A4
    fields: ["Kalem Listesi", "Ölçüler", "Kumaş/Renk", "Montaj Notu"],
  },
];

const PAPER_SIZES = [
  { label: "A4 (210×297mm)", w: 210, h: 297 },
  { label: "A5 (148×210mm)", w: 148, h: 210 },
  { label: "50×25mm", w: 50, h: 25 },
  { label: "70×40mm", w: 70, h: 40 },
  { label: "100×50mm", w: 100, h: 50 },
  { label: "100×75mm", w: 100, h: 75 },
  { label: "Özel", w: 0, h: 0 },
];

function PaperPreview({ w, h }) {
  const scale = Math.min(200 / Math.max(w, h), 1.5);
  const pw = Math.round(w * scale);
  const ph = Math.round(h * scale);
  const rulerMarks = (len, step = 10) => {
    const marks = [];
    for (let i = 0; i <= len; i += step) marks.push(i);
    return marks;
  };
  return (
    <div className="flex items-start gap-1 select-none">
      {/* Sol cetvel */}
      <div className="flex flex-col items-end pt-4" style={{ width: 22 }}>
        {rulerMarks(h).map(m => (
          <div key={m} style={{ height: step => step * scale }} className="relative">
            <div className="absolute right-0 text-[7px] text-slate-400 leading-none" style={{ top: 0 }}>{m}</div>
            <div className="absolute right-0 w-1.5 h-px bg-slate-300" style={{ top: 0 }} />
          </div>
        ))}
      </div>
      <div>
        {/* Üst cetvel */}
        <div className="flex mb-0.5 pl-0" style={{ height: 14 }}>
          {rulerMarks(w).map(m => (
            <div key={m} className="relative" style={{ width: 10 * scale }}>
              <div className="absolute left-0 text-[7px] text-slate-400 leading-none" style={{ top: 0 }}>{m}</div>
              <div className="absolute left-0 h-1.5 w-px bg-slate-300" style={{ top: 6 }} />
            </div>
          ))}
        </div>
        {/* Kağıt */}
        <div
          className="bg-white border-2 border-slate-300 dark:border-slate-600 shadow-md relative"
          style={{ width: pw, height: ph, borderRadius: 2 }}
        >
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: "repeating-linear-gradient(0deg, #94a3b8 0, #94a3b8 1px, transparent 1px, transparent 8px), repeating-linear-gradient(90deg, #94a3b8 0, #94a3b8 1px, transparent 1px, transparent 8px)",
            }}
          />
          <div className="absolute top-1 left-1 right-1 flex gap-0.5 flex-wrap">
            {[40, 65, 50, 80].map((ww, i) => (
              <div key={i} className="h-1.5 rounded-sm bg-slate-200 dark:bg-slate-600" style={{ width: `${ww}%`, opacity: 0.7 }} />
            ))}
          </div>
          <div className="absolute bottom-1 right-1 text-[8px] text-slate-300 font-mono">{w}×{h}mm</div>
        </div>
      </div>
    </div>
  );
}

function WriticiAyarlariSettings() {
  const [innerTab, setInnerTab] = useState("printers");   // "printers" | "document_texts" | "jspm"
  const [activeTemplate, setActiveTemplate] = useState("kasa_etiketi");
  // Harici (Dashboard rozet) tetikleyicisi: window.__zenSettingsInit.innerTab
  useEffect(() => {
    const init = window.__zenSettingsInit;
    if (init && init.innerTab) {
      setInnerTab(init.innerTab);
      setTimeout(() => { window.__zenSettingsInit = null; }, 300);
    }
  }, []);
  const [templates, setTemplates] = useState(() =>
    Object.fromEntries(PRINT_TEMPLATES.map(t => [t.id, { w: t.defaultW, h: t.defaultH, paperSize: "", showBorder: true, showLogo: true, fontSize: 10 }]))
  );
  const [prefs, setPrefs] = useState({ printerName: "", defaultZoom: "100", orientation: "portrait" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings", { params: { category: "printer_settings" } }).then(({ data }) => {
      if (data?.length) {
        const map = {};
        data.forEach(r => { map[r.key] = r.value; });
        // genel prefs
        setPrefs(prev => ({
          ...prev,
          printerName: map["printerName"] || prev.printerName,
          defaultZoom: map["defaultZoom"] || prev.defaultZoom,
          orientation: map["orientation"] || prev.orientation,
        }));
        // şablon ayarları
        PRINT_TEMPLATES.forEach(t => {
          const raw = map[`tpl_${t.id}`];
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              setTemplates(prev => ({ ...prev, [t.id]: { ...prev[t.id], ...parsed } }));
            } catch {}
          }
        });
      }
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const existing = (await api.get("/settings", { params: { category: "printer_settings" } })).data;
      const idMap = {};
      existing.forEach(r => { idMap[r.key] = r.id; });
      const allSettings = {
        ...prefs,
        ...Object.fromEntries(PRINT_TEMPLATES.map(t => [`tpl_${t.id}`, JSON.stringify(templates[t.id])])),
      };
      for (const [key, value] of Object.entries(allSettings)) {
        if (idMap[key]) await api.put(`/settings/${idMap[key]}`, { category: "printer_settings", key, value: String(value), label: key });
        else await api.post("/settings", { category: "printer_settings", key, value: String(value), label: key });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success("Yazıcı & etiket ayarları kaydedildi");
    } catch { toast.error("Kayıt hatası"); }
    setSaving(false);
  };

  const testPrint = () => {
    const html = `<!DOCTYPE html><html><head><title>Test</title>
<style>body{font-family:Arial;font-size:12pt;padding:30px;} h1{color:#E5322A;}</style>
</head><body><h1>ZEN ERP — Yazıcı Testi</h1><p>Tarih: ${new Date().toLocaleString("tr-TR")}</p></body></html>`;
    printViaIframe(html, false);
  };

  const tpl = PRINT_TEMPLATES.find(t => t.id === activeTemplate);
  const tplSettings = templates[activeTemplate] || {};

  const setTpl = (key, val) => setTemplates(prev => ({
    ...prev,
    [activeTemplate]: { ...prev[activeTemplate], [key]: val },
  }));

  // Kağıt boyutu değişince w/h güncelle
  const onPaperSize = (sizeLabel) => {
    const found = PAPER_SIZES.find(p => p.label === sizeLabel);
    if (found && found.w > 0) {
      setTpl("w", found.w);
      setTpl("h", found.h);
      setTpl("paperSize", sizeLabel);
    } else {
      setTpl("paperSize", sizeLabel);
    }
  };

  return (
    <div className="space-y-5">
      {/* Yazıcı Ayarları alt-sekmeler: Yazıcı Şablonları | Belge Metinleri */}
      <div className="flex items-center gap-1 border-b border-slate-200 dark:border-[#22304A] pb-0">
        <button
          onClick={() => setInnerTab("printers")}
          data-testid="printer-inner-tab-templates"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            innerTab === "printers"
              ? "border-[#E5322A] text-[#E5322A]"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Printer className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Yazıcı Şablonları
        </button>
        <button
          onClick={() => setInnerTab("document_texts")}
          data-testid="printer-inner-tab-doctexts"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            innerTab === "document_texts"
              ? "border-[#E5322A] text-[#E5322A]"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <FileText className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Belge Metinleri
        </button>
        <button
          onClick={() => setInnerTab("jspm")}
          data-testid="printer-inner-tab-jspm"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            innerTab === "jspm"
              ? "border-[#E5322A] text-[#E5322A]"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Radio className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Yazıcı Bağlantısı (JSPM)
        </button>
        <button
          onClick={() => setInnerTab("zpl_editor")}
          data-testid="printer-inner-tab-zpl-editor"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            innerTab === "zpl_editor"
              ? "border-[#E5322A] text-[#E5322A]"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Barcode className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          ZPL Şablon Editörü (Zebra)
        </button>
        <button
          onClick={() => setInnerTab("html_label_editor")}
          data-testid="printer-inner-tab-html-label"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            innerTab === "html_label_editor"
              ? "border-blue-500 text-blue-500"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Tag className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          HTML Etiket Editörü
        </button>
        <button
          onClick={() => setInnerTab("print_tpl_manager")}
          data-testid="printer-inner-tab-print-tpl"
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            innerTab === "print_tpl_manager"
              ? "border-emerald-500 text-emerald-400"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Layers className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Evrensel Şablonlar
        </button>
      </div>

      {innerTab === "document_texts" ? <DocumentTextsSettings /> :
       innerTab === "jspm" ? <JSPMConnectionSettings /> :
       innerTab === "zpl_editor" ? <ZPLTemplateEditor /> :
       innerTab === "html_label_editor" ? <HtmlLabelEditor /> :
       innerTab === "print_tpl_manager" ? <PrintTemplateManager /> : (
      <>
      {/* Şablon sekmeler */}
      <div className="zen-panel p-4 space-y-4">
        <h3 className="font-bold text-sm flex items-center gap-2"><Tag className="w-4 h-4 text-indigo-500" /> Etiket & Belge Şablonları</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {PRINT_TEMPLATES.map(t => (
            <button
              key={t.id}
              data-testid={`printer-tpl-${t.id}`}
              onClick={() => setActiveTemplate(t.id)}
              className={`rounded-xl p-3 text-left border-2 transition ${
                activeTemplate === t.id
                  ? "border-indigo-500 bg-indigo-500/10"
                  : "border-slate-200 dark:border-[#22304A] hover:border-indigo-300"
              }`}
            >
              <div className="text-xs font-bold">{t.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{t.desc}</div>
              <div className="text-[10px] text-slate-400 mt-1 font-mono">{templates[t.id]?.w || t.defaultW}×{templates[t.id]?.h || t.defaultH}mm</div>
            </button>
          ))}
        </div>

        {/* Seçili şablon ayarları */}
        {tpl && (
          <div className="grid md:grid-cols-2 gap-5 mt-2 pt-4 border-t border-slate-200 dark:border-[#22304A]">
            {/* Sol: Ayarlar */}
            <div className="space-y-3">
              <div className="text-xs font-bold text-slate-600 dark:text-slate-300">{tpl.label} — Sayfa Ayarları</div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Kağıt Boyutu</label>
                <select className="zen-input !text-xs" value={tplSettings.paperSize || ""} onChange={e => onPaperSize(e.target.value)}
                  data-testid="printer-paper-size-select">
                  <option value="">— Seçin —</option>
                  {PAPER_SIZES.map(p => <option key={p.label} value={p.label}>{p.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Genişlik (mm)</label>
                  <input type="number" className="zen-input !text-xs" value={tplSettings.w || tpl.defaultW}
                    onChange={e => setTpl("w", Number(e.target.value))} data-testid="printer-w-input" />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 block mb-1">Yükseklik (mm)</label>
                  <input type="number" className="zen-input !text-xs" value={tplSettings.h || tpl.defaultH}
                    onChange={e => setTpl("h", Number(e.target.value))} data-testid="printer-h-input" />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Yazı Boyutu (pt)</label>
                <input type="number" min="6" max="18" className="zen-input !text-xs" value={tplSettings.fontSize || 10}
                  onChange={e => setTpl("fontSize", Number(e.target.value))} data-testid="printer-font-size" />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!tplSettings.showBorder} onChange={e => setTpl("showBorder", e.target.checked)}
                    data-testid="printer-show-border" className="rounded" />
                  Kenarlık Göster
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!tplSettings.showLogo} onChange={e => setTpl("showLogo", e.target.checked)}
                    data-testid="printer-show-logo" className="rounded" />
                  Logo Ekle
                </label>
              </div>
              <div className="text-[10px] text-slate-400 bg-slate-100 dark:bg-[#0C1220] rounded p-2">
                <b>İçerik:</b> {tpl.fields.join(", ")}
              </div>
            </div>

            {/* Sağ: Kağıt önizleme */}
            <div className="flex flex-col items-center gap-2">
              <div className="text-[11px] text-slate-500 font-semibold">Önizleme (ölçekli)</div>
              <PaperPreview w={tplSettings.w || tpl.defaultW} h={tplSettings.h || tpl.defaultH} />
              <div className="text-[10px] text-slate-400 text-center">
                {tplSettings.w || tpl.defaultW}mm × {tplSettings.h || tpl.defaultH}mm
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Genel yazıcı ayarları */}
      <div className="zen-panel p-4 space-y-3">
        <h3 className="font-bold text-sm flex items-center gap-2"><Printer className="w-4 h-4 text-slate-500" /> Genel Yazıcı Tercihleri</h3>
        <div className="p-3 rounded-xl bg-sky-500/5 border border-sky-500/20 text-xs text-sky-600 dark:text-sky-400 flex gap-2">
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <span>Web uygulaması yazıcıya doğrudan erişemez. Yazdırırken tarayıcının yazdırma diyaloğu açılır.</span>
        </div>
        <div>
          <label className="text-[11px] text-slate-500 block mb-1">Tercih Edilen Yazıcı Adı (not)</label>
          <input className="zen-input !text-xs" value={prefs.printerName} onChange={e => setPrefs(p => ({ ...p, printerName: e.target.value }))}
            placeholder="Örn: HP LaserJet Pro M404dn" data-testid="input-printer-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Varsayılan Yönlendirme</label>
            <select className="zen-input !text-xs" value={prefs.orientation} onChange={e => setPrefs(p => ({ ...p, orientation: e.target.value }))} data-testid="input-print-orientation">
              <option value="portrait">Dikey (Portrait)</option>
              <option value="landscape">Yatay (Landscape)</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Varsayılan Yakınlaştırma (%)</label>
            <input className="zen-input !text-xs" type="number" min="50" max="200" step="5" value={prefs.defaultZoom}
              onChange={e => setPrefs(p => ({ ...p, defaultZoom: e.target.value }))} data-testid="input-print-zoom" />
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={save} disabled={saving}
            className={`zen-btn-primary flex-1 !text-xs ${saved ? "bg-emerald-500 hover:bg-emerald-500" : ""}`}
            data-testid="btn-save-printer-settings">
            {saving ? "Kaydediliyor…" : saved ? "Kaydedildi ✓" : "Tüm Ayarları Kaydet"}
          </button>
          <button onClick={testPrint} className="zen-btn-ghost !text-xs flex items-center gap-1.5 px-4" data-testid="btn-test-print">
            <Printer size={14} /> Test Yazdır
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

// ─── Sistem Temizliği ─────────────────────────────────────────────────────
const CLEANUP_COLLECTIONS = [
  { id: "orders",          label: "Satış Siparişleri",              desc: "Tüm satış siparişleri ve onay kayıtları silinir",        danger: true },
  { id: "quotes",          label: "Verilen Teklifler",              desc: "Tüm teklif kayıtları ve revizyonlar silinir",             danger: true },
  { id: "purchase_orders", label: "Alış Siparişleri",              desc: "Tüm tedarikçi satın alma siparişleri silinir",            danger: false },
  { id: "customers",       label: "Müşteri / Tedarikçi Kayıtları", desc: "Tüm cari hesap kayıtları silinir — dikkatli olun!",       danger: true },
  { id: "invoices",        label: "Fatura / İrsaliye Kayıtları",   desc: "Tüm fatura ve irsaliye verileri silinir",                 danger: false },
  { id: "payments",        label: "Ödeme & Muhasebe Hareketleri",  desc: "Tüm ödeme ve muhasebe giriş kayıtları silinir",           danger: false },
  { id: "crm_leads",       label: "CRM Kayıtları",                 desc: "Tüm müşteri adayı ve takip kayıtları silinir",            danger: false },
  { id: "announcements",   label: "Duyurular",                     desc: "Tüm sistem duyuruları silinir",                           danger: false },
];

function SystemCleanupSettings() {
  const [selected, setSelected] = useState(new Set(["orders", "quotes", "purchase_orders"]));
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const REQUIRED_TEXT = "ONAYLIYORUM";
  const canDelete = confirmText === REQUIRED_TEXT && selected.size > 0;

  const toggleCol = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const selectAll = () => setSelected(new Set(CLEANUP_COLLECTIONS.map(c => c.id)));
  const selectNone = () => setSelected(new Set());

  const handleCleanup = async () => {
    if (!canDelete) return;
    if (!window.confirm(`DİKKAT: ${selected.size} koleksiyondaki TÜM veriler kalıcı olarak silinecek. Bu işlem GERİ ALINAMAZ! Devam etmek istiyor musunuz?`)) return;
    setLoading(true);
    try {
      const { data } = await api.delete("/admin/clear-test-data", { data: { collections: [...selected] } });
      setLastResult(data.results);
      setConfirmText("");
      toast.success(`Temizlik tamamlandı! Toplam ${data.total_deleted} kayıt silindi.`);
    } catch (e) {
      toast.error("Temizlik sırasında hata oluştu: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const totalSelected = selected.size;

  return (
    <div className="space-y-4">
      {/* Uyarı başlığı */}
      <div className="zen-panel p-4 border border-red-300 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-red-700 dark:text-red-400">Tehlikeli Alan — Sistemi Yayına Almadan Önce Temizlik</h3>
            <p className="text-xs text-red-600/80 dark:text-red-400/70 mt-1 leading-relaxed">
              Bu araç, canlıya geçmeden önce tüm test verilerini silmenizi sağlar.
              Seçtiğiniz koleksiyonlardaki <strong>TÜM kayıtlar kalıcı olarak silinir</strong>. Yedek almadan devam etmeyin.
            </p>
          </div>
        </div>
      </div>

      {/* Koleksiyon seçimi */}
      <div className="zen-panel p-4 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold">Silinecek Veriler</h3>
          <div className="flex gap-2">
            <button onClick={selectAll} className="text-[11px] text-slate-500 hover:text-[#E5322A] transition-colors font-medium">Tümünü seç</button>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            <button onClick={selectNone} className="text-[11px] text-slate-500 hover:text-[#E5322A] transition-colors font-medium">Tümünü kaldır</button>
          </div>
        </div>
        <div className="space-y-1.5">
          {CLEANUP_COLLECTIONS.map(col => {
            const isChecked = selected.has(col.id);
            return (
              <label
                key={col.id}
                data-testid={`cleanup-col-${col.id}`}
                className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all border
                  ${isChecked
                    ? col.danger
                      ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50"
                      : "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40"
                    : "bg-slate-50 dark:bg-slate-800/30 border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                  }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleCol(col.id)}
                  className="mt-0.5 accent-[#E5322A]"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-200">{col.label}</span>
                    {col.danger && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400">KRİTİK</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{col.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Onay ve silme */}
      <div className="zen-panel p-4 space-y-3">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">Silme Onayı</h3>
        {totalSelected === 0 ? (
          <p className="text-xs text-slate-400 italic">Hiçbir koleksiyon seçilmedi.</p>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
              <strong>{totalSelected} koleksiyon</strong> seçildi:&nbsp;
              {[...selected].map(id => CLEANUP_COLLECTIONS.find(c => c.id === id)?.label).join(", ")}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                Devam etmek için aşağıya <code className="bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded font-mono text-[#E5322A]">ONAYLIYORUM</code> yazın:
              </label>
              <input
                type="text"
                data-testid="cleanup-confirm-input"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="ONAYLIYORUM"
                className="zen-input w-full font-mono text-sm uppercase"
                autoComplete="off"
              />
            </div>
            <button
              data-testid="cleanup-delete-btn"
              onClick={handleCleanup}
              disabled={!canDelete || loading}
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm transition-all
                ${canDelete && !loading
                  ? "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30 active:scale-[0.98]"
                  : "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                }`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {loading ? "Siliniyor..." : `${totalSelected} Koleksiyondaki Tüm Verileri Sil`}
            </button>
          </div>
        )}
      </div>

      {/* Son işlem sonucu */}
      {lastResult && (
        <div className="zen-panel p-4 space-y-2 border border-green-200 dark:border-green-900/40 bg-green-50/50 dark:bg-green-950/20">
          <div className="flex items-center gap-2 text-sm font-bold text-green-700 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4" /> Son Temizlik Sonucu
          </div>
          <div className="space-y-1">
            {Object.entries(lastResult).map(([col, count]) => (
              <div key={col} className="flex justify-between text-xs text-slate-600 dark:text-slate-400">
                <span>{CLEANUP_COLLECTIONS.find(c => c.id === col)?.label || col}</span>
                <span className="font-mono font-bold text-red-600 dark:text-red-400">{count} kayıt silindi</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// YAMA MERKEZİ (FAZ 5) — MiMo ile program düzeltme
// Kullanıcı bir sorun yazar → MiMo yamayı üretir (diff görünür) →
// onayla → update-agent YEDEK ALIR, uygular, yeniden derler;
// sağlık kontrolü başarısızsa OTOMATİK GERİ ALINIR.
// ════════════════════════════════════════════════════════════════════
function PatchCenterSettings() {
  const [request, setRequest] = useState("");
  const [busy, setBusy] = useState(false);
  const [patch, setPatch] = useState(null);
  const [applying, setApplying] = useState(false);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/patch/list");
      setHistory(Array.isArray(data) ? data : []);
    } catch { /* ilk yüklemede liste boş olabilir */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const propose = async () => {
    if (!request.trim()) { toast.error("Önce sorunu veya isteğinizi yazın"); return; }
    setBusy(true); setPatch(null);
    try {
      const { data } = await api.post("/patch/propose", { request }, { timeout: 300000 });
      setPatch(data.patch || null);
      toast.success("Yama hazır — inceleyip onaylayın");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yama önerisi alınamadı");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!patch?.id) return;
    setApplying(true);
    try {
      await api.post("/patch/apply", { patchId: patch.id }, { timeout: 120000 });
      toast.success("Yama uygulanıyor — sistem yeniden derleniyor (~1-2 dk)");
      // Durum takibi: sistem yeniden başlarken kısa kesinti olabilir
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 8000));
        try {
          const { data } = await api.get("/patch/list", { timeout: 10000 });
          const rows = Array.isArray(data) ? data : [];
          const me = rows.find(r => r.id === patch.id);
          if (me?.status === "applied") { setStatus("Yama uygulandı ✅"); break; }
          if (me?.status === "failed") { setStatus("Yama başarısız — sistem eski hâline döndü"); break; }
        } catch { /* yeniden derleme sırasında bağlantı kesilebilir */ }
      }
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uygulama başlatılamadı");
    } finally { setApplying(false); }
  };

  const rollback = async (patchId) => {
    if (!window.confirm("Bu yama geri alınsın mı? (Sistem son yedeğe döner)")) return;
    try {
      await api.post("/patch/rollback", { patchId }, { timeout: 120000 });
      toast.success("Geri alma başlatıldı");
      setTimeout(loadHistory, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri alma başarısız");
    }
  };

  const STATUS_TR = {
    pending: "Onay bekliyor", applied: "Uygulandı", rolled_back: "Geri alındı", failed: "Başarısız",
  };

  return (
    <div className="zen-panel p-6 space-y-5 text-slate-900 dark:text-slate-100">
      <div>
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-amber-500" />
          <h3 className="text-base font-extrabold">Yama Merkezi — Program Kendini Onarır</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Faz 5</span>
        </div>
        <p className="text-xs text-slate-400 mt-1 leading-relaxed">
          Sorunu veya isteğinizi yazın; <b>MiMo</b> düzeltmeyi (yamayı) hazırlar ve <b>değişiklik dökümünü</b> (diff) gösterir.
          Onayladığınızda sistem <b>otomatik yedek alır</b>, yamayı uygular ve yeniden derler;
          bir sorun olursa <b>eski hâline kendiliğinden döner</b>. Güvenlik: sözdizimi doğrulaması geçemeyen yamalar reddedilir.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-[#22304A] p-4 space-y-3">
        <label className="block text-xs font-semibold text-slate-500">Sorun / İstek</label>
        <textarea
          className="zen-input min-h-[90px] text-sm"
          placeholder="Örn: 'Sipariş listesinde tutar sütunu iki satıra bölünüyor' ya da 'Ödeme ekranına açıklama ekle'"
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          data-testid="patch-request-input"
        />
        <button onClick={propose} disabled={busy} className="zen-btn-primary !text-xs" data-testid="patch-propose-btn">
          {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> MiMo hazırlıyor…</> : <><Sparkles className="w-3.5 h-3.5" /> Yama Öner</>}
        </button>
      </div>

      {patch && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/40 dark:bg-amber-950/20 p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-extrabold">Yama Hazır</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 font-mono">{patch.file}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">doğrulandı</span>
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300">{patch.explanation}</div>
          <pre className="text-[10px] leading-relaxed bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto max-h-72 overflow-y-auto whitespace-pre-wrap">{patch.diff}</pre>
          <div className="flex items-center gap-2">
            <button onClick={apply} disabled={applying} className="zen-btn-primary !text-xs" data-testid="patch-apply-btn">
              {applying ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uygulanıyor…</> : "✓ Uygula"}
            </button>
            <button onClick={() => setPatch(null)} className="zen-btn-ghost !text-xs">Vazgeç</button>
            {status && <span className="text-[11px] text-slate-500">{status}</span>}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-extrabold flex items-center gap-1.5"><History className="w-3.5 h-3.5 text-slate-400" /> Yama Geçmişi</h4>
          <button onClick={loadHistory} className="zen-btn-ghost !text-[11px]"><RefreshCw className="w-3 h-3" /> Yenile</button>
        </div>
        {history.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 dark:border-[#22304A] p-6 text-center text-xs text-slate-500">
            Henüz yama yok. İlk yamanızı yukarıdan önerin — her yamadan önce sistem otomatik yedek alır.
          </div>
        ) : (
          <div className="space-y-1.5">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-[#0F172A] border border-slate-200/70 dark:border-[#22304A]">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{h.explanation || h.request || h.id}</div>
                  <div className="text-[10px] text-slate-400 truncate">{h.file} · {h.createdAt ? new Date(h.createdAt).toLocaleString("tr-TR") : ""}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${h.status === "applied" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : h.status === "failed" ? "bg-rose-100 text-rose-600 dark:bg-rose-900/40" : h.status === "rolled_back" ? "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}>
                  {STATUS_TR[h.status] || h.status}
                </span>
                {h.status === "applied" && (
                  <button onClick={() => rollback(h.id)} className="zen-btn-ghost !text-[10px] whitespace-nowrap" data-testid={`patch-rollback-${h.id}`}>
                    <RotateCcw className="w-3 h-3" /> Geri Al
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { SettingsModule };
export default SettingsModule;
